import { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import Modal from 'react-modal';
import { 
  FaPlus, FaPrint, FaSave, FaTrash, FaShareAlt, FaComment,
  FaCopy, FaCut, FaTimes, FaPencilAlt,
  FaMinus, FaArrowRight, FaFont, FaSmile, FaLayerGroup, FaTint, FaDrawPolygon,
  FaMagic, FaUndo, FaRedo, FaEraser,
  FaWhatsapp, FaBluetooth, FaEnvelope, FaInstagram, FaFacebookF,
  FaPalette, FaCheckSquare, FaSquare,
  FaVectorSquare, FaMousePointer, FaRegSquare, FaRegCircle, FaBroom,
  FaChevronRight, FaChevronLeft,
  FaLock, FaUnlock, FaSyncAlt, FaCheck, FaCrop, FaGem, FaFingerprint,
  FaFolder, FaCamera, FaCloud, FaCloudUploadAlt, FaFile, FaPaintBrush, FaImage,
  FaSun, FaAdjust, FaRobot, FaPaperPlane, FaHistory, FaSlidersH
} from 'react-icons/fa';
import { FaArrowsUpDownLeftRight, FaEyeDropper, FaSprayCan, FaPen } from "react-icons/fa6";
import { listObjectsInFolder, listObjectsWithMetadata, uploadToStorage, readObjectContent, deleteFromStorage, renameInStorage, getEnvStorageBucket, getDownloadUrl } from '../utils/storageUtils';
import { formatFileSize } from '../utils/format';
import { getAuth } from 'firebase/auth';
import { collection, addDoc, getDocs, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { ref as stampStorageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getFirebase } from '../services/firebase';
import { useAlert } from '../contexts/ToastContext';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import './css/ImageStudio.css';
import BottomMenu from '../components/BottomMenu';

const A4_WIDTH_CM = 21;
const A4_HEIGHT_CM = 29.7;
const A4_RATIO = A4_HEIGHT_CM / A4_WIDTH_CM;

const PRINT_SIZES = {
  'A4P': { width: 21,   height: 29.7, label: 'A4',  orient: 'portrait',  display: 'A4 Portrait' },
  'A4L': { width: 29.7, height: 21,   label: 'A4',  orient: 'landscape', display: 'A4 Landscape' },
  'A5P': { width: 14.8, height: 21,   label: 'A5',  orient: 'portrait',  display: 'A5 Portrait' },
  'A5L': { width: 21,   height: 14.8, label: 'A5',  orient: 'landscape', display: 'A5 Landscape' },
  'A6P': { width: 10.5, height: 14.8, label: 'A6',  orient: 'portrait',  display: 'A6 Portrait' },
  'A6L': { width: 14.8, height: 10.5, label: 'A6',  orient: 'landscape', display: 'A6 Landscape' },
};
const PRINT_SIZE_ORDER = ['A4P', 'A4L', 'A5P', 'A5L', 'A6P', 'A6L'];

// Hex colour -> {r, g, b}
const hexToRgb = (hex) => {
  const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return r ? { r: parseInt(r[1], 16), g: parseInt(r[2], 16), b: parseInt(r[3], 16) } : null;
};

// Stack-based flood fill on a flat Uint8ClampedArray (RGBA, widthxheight).
// Returns the number of pixels filled, or 0 if nothing changed.
const floodFill = (data, width, height, sx, sy, fill) => {
  const startI = (sy * width + sx) * 4;
  const tR = data[startI], tG = data[startI + 1], tB = data[startI + 2], tA = data[startI + 3];
  if (tR === fill.r && tG === fill.g && tB === fill.b && tA === 255) return 0;
  const tol = 15;
  // For transparent-source fills: require exact alpha=0 match so the fill never
  // touches anti-aliased stroke edge pixels (alpha 1-20), giving a tight bbox.
  const tolA = tA < 10 ? 0 : tol;
  const matches = (i) => (
    Math.abs(data[i+3] - tA) <= tolA &&
    Math.abs(data[i]   - tR) <= tol &&
    Math.abs(data[i+1] - tG) <= tol &&
    Math.abs(data[i+2] - tB) <= tol
  );
  const visited = new Uint8Array(width * height);
  const stack = [sx + sy * width];
  let count = 0;
  while (stack.length > 0) {
    const pos = stack.pop();
    if (visited[pos]) continue;
    visited[pos] = 1;
    const i = pos * 4;
    if (!matches(i)) continue;
    data[i] = fill.r; data[i + 1] = fill.g; data[i + 2] = fill.b; data[i + 3] = 255;
    count++;
    const x = pos % width, y = Math.floor(pos / width);
    if (x > 0)          stack.push(pos - 1);
    if (x < width - 1)  stack.push(pos + 1);
    if (y > 0)          stack.push(pos - width);
    if (y < height - 1) stack.push(pos + width);
  }
  return count;
};

// Convert RGB to hex colour string
const rgbToHex = (r, g, b) => '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');

// Replace every non-transparent pixel in a fill-shape PNG with a new colour.
const recolourFill = (dataUrl, newRgb) => new Promise(resolve => {
  const img = new Image();
  img.onload = () => {
    const c = document.createElement('canvas');
    c.width = img.width; c.height = img.height;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0);
    const id = ctx.getImageData(0, 0, c.width, c.height);
    const d = id.data;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] > 0) { d[i] = newRgb.r; d[i + 1] = newRgb.g; d[i + 2] = newRgb.b; }
    }
    ctx.putImageData(id, 0, 0);
    resolve(c.toDataURL('image/png'));
  };
  img.src = dataUrl;
});

Modal.setAppElement('#root');

// Render a single drawing path (pen, eraser, or spray) onto a canvas context.
// scaleX/scaleY map from the path's original canvas size to the destination canvas size.
const renderDrawingPath = (ctx, path, scaleX, scaleY) => {
  if (path.type === 'spray') {
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = path.color;
    const dotR = Math.max(0.3, (path.size || 5) * 0.10 * scaleX);
    for (const pt of path.path) {
      ctx.beginPath();
      ctx.arc(pt.x * scaleX, pt.y * scaleY, dotR, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalCompositeOperation = 'source-over';
    return;
  }
  if (path.type === 'pencil') {
    if (path.path.length < 2) return;
    ctx.globalCompositeOperation = 'source-over';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = path.color;
    const size = path.size * scaleX;
    // More strands for richer pencil effect
    const strands = Math.max(3, Math.min(11, Math.ceil(size * 1.2)));
    const strandW = Math.max(0.3, size / strands) * 0.85;
    // Add slight randomness to strand positions for natural look
    for (let s = 0; s < strands; s++) {
      const offsetFactor = (s - (strands - 1) / 2);
      const dx = offsetFactor * size * 0.18 + (Math.random() - 0.5) * size * 0.08;
      ctx.beginPath();
      ctx.moveTo(path.path[0].x * scaleX + dx, path.path[0].y * scaleY);
      for (let i = 1; i < path.path.length; i++) {
        ctx.lineTo(path.path[i].x * scaleX + dx, path.path[i].y * scaleY);
      }
      // Gradient opacity: centre is darkest, edges fade
      const mid = (strands - 1) / 2;
      const dist = Math.abs(s - mid) / (mid || 1);
      ctx.globalAlpha = 0.85 - dist * 0.55;
      ctx.lineWidth = strandW;
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    return;
  }
  if (path.path.length < 2) return;
  ctx.beginPath();
  ctx.moveTo(path.path[0].x * scaleX, path.path[0].y * scaleY);
  for (let i = 1; i < path.path.length; i++) {
    ctx.lineTo(path.path[i].x * scaleX, path.path[i].y * scaleY);
  }
  if (path.color === 'eraser') {
    ctx.globalCompositeOperation = 'destination-out';
    ctx.strokeStyle = 'rgba(0,0,0,1)';
  } else {
    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = path.color;
  }
  ctx.lineWidth = path.size * scaleX;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke();
  ctx.globalCompositeOperation = 'source-over';
};

// Module-level cache: survives modal open/close, cleared on upload/delete/rename
let _bucketImagesCache = null;
let _bucketImagesCacheKey = '';

const ImageStudio = ({ userRole }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromCustom = location.state?.fromCustom === true;
  const fromCustomRef = useRef(fromCustom);
  const showAlert = useAlert();
  const fileInputRef = useRef(null);
  const connectMenuRef = useRef(null);
  const cameraInputRef = useRef(null);
  const cloudUploadInputRef = useRef(null);
  const canvasRef = useRef(null);
  const drawingCanvasRef = useRef(null);
  
  // State
  const [canvasImages, setCanvasImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  // In fromCustom mode auto-fall back to the only image so buttons work without tapping
  const effectiveImageId = fromCustom ? (selectedImage || canvasImages[0]?.id || null) : selectedImage;
  const [resizeWidth, setResizeWidth] = useState(10);
  const [resizeHeight, setResizeHeight] = useState(10);
  const [isConnectMenuOpen, setIsConnectMenuOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [bucketDeleteMsg, setBucketDeleteMsg] = useState(null); // { text, ok }
  const [confirmDeleteBucket, setConfirmDeleteBucket] = useState(false);
  const [bucketSearch, setBucketSearch]           = useState('');
  const [bucketSortBy, setBucketSortBy]           = useState('date');  // 'date' | 'size' | 'name'
  const [bucketSortDir, setBucketSortDir]         = useState('desc');  // 'asc' | 'desc'
  const [renamingBucketFile, setRenamingBucketFile] = useState(null); // { fileName, displayName, newName }
  // True for any phone or tablet web browser. Used to swap Print→Share and Share→Chat
  // in the bottom toolbar (mobile/tablet browsers can't iframe-print reliably).
  // Detection covers:
  //   • Android phones & tablets (UA contains "Android")
  //   • iPhones and older iPads (UA contains "iPhone"/"iPad")
  //   • iPadOS 13+ which reports as Macintosh but has maxTouchPoints > 1
  //   • Any other touch-enabled device with a screen ≤ 1366 px wide (generic tablets)
  const isMobileWeb = !Capacitor.isNativePlatform() && (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 1 && /Macintosh/i.test(navigator.userAgent)) ||
    (navigator.maxTouchPoints > 0 && window.innerWidth <= 1366)
  );

  const [draggingImage, setDraggingImage] = useState(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 }); // ref so reads are always current, no extra re-render
  const [resizingImage, setResizingImage] = useState(null);
  const [rotatingImage, setRotatingImage] = useState(null);
  const [showBucketModal, setShowBucketModal] = useState(false);
  const [bucketImages, setBucketImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showImageControls, setShowImageControls] = useState(null);
  const [activeTool, setActiveTool] = useState(null);
  const [saveType, setSaveType] = useState('file');
  const [fileName, setFileName] = useState('Edited-PhotoS');
  const [drawingColor, setDrawingColor] = useState(() => {
    return document.documentElement.getAttribute('data-theme') === 'dark' ? '#FFFFFF' : '#000000';
  });
  const [brushSize, setBrushSize] = useState(5);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingPaths, setDrawingPaths] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [eyedropperActive, setEyedropperActive] = useState(false); // colour-picker mode
  const [pickedColor, setPickedColor] = useState(null); // last eyedropper-sampled colour
  const [saveProgress, setSaveProgress] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [printSize, setPrintSize] = useState('A4P');
  const [vpSize, setVpSize] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  const [drawingHistory, setDrawingHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [drawingMode, setDrawingMode] = useState('pen');
  const [fillModeActive, setFillModeActive] = useState(false);
  const [hasClosedArea, setHasClosedArea] = useState(false);
  const [canvasCursor, setCanvasCursor] = useState('default');
  const [eraserPos, setEraserPos] = useState(null); // {x, y} in canvas px for eraser circle overlay
  // Magic Wand AI tool
  const [magicSubTool, setMagicSubTool] = useState(null); // 'removeImageBg'|'removePlate'|'contrast'|'brightness'|'chat'|'color'
  const [magicProcessing, setMagicProcessing] = useState(false);
  const [magicChatImages, setMagicChatImages] = useState([]); // image IDs selected for multi-image chat
  const [magicCakeColorBase, setMagicCakeColorBase] = useState(null); // selected base colour name in colour picker
  const [magicBrightness, setMagicBrightness] = useState(0); // -50 to +50
  const [magicContrast, setMagicContrast] = useState(0);    // -50 to +50
  // Live preview values while dragging (not yet committed); reset to 0 when slider released
  const [magicFilterPreview, setMagicFilterPreview] = useState({ brightness: 0, contrast: 0 });
  const [magicChatMessages, setMagicChatMessages] = useState([]); // [{role:'user'|'assistant', content, imageUrl?}]
  const [magicChatInput, setMagicChatInput] = useState('');
  const [magicChatLoading, setMagicChatLoading] = useState(false);
  const [magicChatStep, setMagicChatStep] = useState(''); // 'preparing'|'sending'|'waiting'|''
  const [magicChatError, setMagicChatError] = useState(''); // error message for status strip
  const [magicPromptHistory, setMagicPromptHistory] = useState([]); // last 10 unique prompts
  const [showPromptHistory, setShowPromptHistory] = useState(false);
  const magicChatScrollRef = useRef(null);
  const magicFilterOrigUrlRef = useRef(null); // original image URL captured on entering brightness/contrast mode
  const magicTouchStartRef = useRef(null); // { id, x, y } for tap-vs-drag detection on touch devices
  const [bucketModalMode, setBucketModalMode] = useState('select');
  const [selectedBucketImages, setSelectedBucketImages] = useState([]);
  const [selectionMode, setSelectionMode] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState({ x: 0, y: 0 });
  const [selectionEnd, setSelectionEnd] = useState({ x: 0, y: 0 });
  const [selectionPath, setSelectionPath] = useState([]);
  const [selectionTouchPos, setSelectionTouchPos] = useState(null); // live pen-cursor position for freeform/smart on touch
  const smartLassoPathRef = useRef([]);                             // ref-based smart lasso path (avoids stale React state on mobile)
  const touchSelectionActiveRef = useRef(false);                    // true only while a touchstart-on-canvas is in progress (prevents fastpath firing from stray events)
  const TOUCH_PEN_OFFSET_Y = 40;                                    // CSS px the drawing tip sits above the finger
  // For circle crop: track drag interaction (move or resize) on the pre-placed overlay
  const circleCropInteractRef = useRef(null); // null | { type:'move'|'resize', startX, startY, origStart, origEnd }
  // Smart crop (object detection) state
  const [smartCropRunning, setSmartCropRunning] = useState(false);
  const smartDetectedRef = useRef(false); // true = polygon shown, waiting for confirm ✓
  const [smartDetected, setSmartDetected] = useState(false); // mirrors ref, triggers SVG re-render
  const smartCropMaskRef = useRef(null); // pixel-level FG mask canvas for precise crop cutout
  const [showLeftPanel, setShowLeftPanel] = useState(true);
  const [showRightPanel, setShowRightPanel] = useState(false);
  const [showToolsPanel, setShowToolsPanel] = useState(false);
  const [toolsTouchStartY, setToolsTouchStartY] = useState(0);
  const [toolsWrapperHeight, setToolsWrapperHeight] = useState(0);
  const toolsWrapperRef = useRef(null);
  const toolsContentRef = useRef(null);
  const [showTopControls, setShowTopControls] = useState(false);
  const [currentControlSection, setCurrentControlSection] = useState(null);
  const [lockAspectRatio, setLockAspectRatio] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(1);
  const [showColorShades, setShowColorShades] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedBaseColor, setSelectedBaseColor] = useState('#000000');
  const [imageSelectionMode, setImageSelectionMode] = useState(false);
  const topControlsRef = useRef(null);
  const canvasImagesRef = useRef([]);
  const drawingHistoryRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const dragStartImagesRef = useRef(null);
  const resizeStartImagesRef = useRef(null);
  const [saveMessage, setSaveMessage] = useState(null);
  const [pendingNavPath, setPendingNavPath] = useState(null);
  const isSavedRef = useRef(false);
  const canvasAreaRef = useRef(null);
  const panRef = useRef({ active: false, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 });
  const resizeDirectionRef = useRef(null);

  const [canvasShapes, setCanvasShapes]               = useState([]);
  const [selectedShapeId, setSelectedShapeId]         = useState(null);
  const [selectedShapeIds, setSelectedShapeIds]       = useState(new Set());   // multi-select
  const [marqueeRect, setMarqueeRect]                 = useState(null); // {x1,y1,x2,y2} pct
  const [activeShapeTool, setActiveShapeTool]         = useState(null);
  const [lastUsedShapeTool, setLastUsedShapeTool]     = useState('triangle');
  const [showShapesPanel, setShowShapesPanel]         = useState(false);
  const [isDrawingShape, setIsDrawingShape]           = useState(false);
  const [shapeDrawStart, setShapeDrawStart]           = useState(null);
  const [shapeDrawEnd, setShapeDrawEnd]               = useState(null);
  const [shapeStrokeColor, setShapeStrokeColor]       = useState(() =>
    document.documentElement.getAttribute('data-theme') === 'dark' ? '#FFFFFF' : '#000000'
  );
  const [shapeFillColor, setShapeFillColor]           = useState('none');
  const [shapeStrokeWidth, setShapeStrokeWidth]       = useState(2);
  const [shapeColorTarget, setShapeColorTarget]       = useState('stroke');
  const [showShapeColorPicker, setShowShapeColorPicker] = useState(false);
  const [showShapeColorShades, setShowShapeColorShades] = useState(false);
  const [shapeSelectedBase, setShapeSelectedBase]     = useState('#000000');
  const [shapeFontSize, setShapeFontSize]             = useState(() => Math.round(Math.max(12, Math.min(24, window.innerWidth * (29.7 / 21) * 0.028))));
  const [shapeFontFamily, setShapeFontFamily]         = useState('Brownie Cake');
  const [shapeTextBold, setShapeTextBold]             = useState(false);
  const [shapeTextItalic, setShapeTextItalic]         = useState(false);
  const [pendingTextPos, setPendingTextPos]           = useState(null);
  const [pendingTextValue, setPendingTextValue]       = useState('');
  const [pendingTextShapeId, setPendingTextShapeId]   = useState(null);
  const [selectedEmoji, setSelectedEmoji]             = useState(null);
  const [studioStamps, setStudioStamps]               = useState([]);
  const [stampUploading, setStampUploading]           = useState(false);
  const [stampRemoving, setStampRemoving]             = useState(false);
  const [selectedStampIds, setSelectedStampIds]       = useState(new Set());
  const [stampSelectMode, setStampSelectMode]         = useState(false);
  const stampImageCacheRef = useRef(new Map());
  const stampGridRef = useRef(null);
  const draggingShapeRef         = useRef(null);
  const multiDragRef             = useRef(null);  // [{id, offsetX, offsetY}] for multi-shape drag
  const isMarqueeSelectingRef    = useRef(false);
  const marqueeJustFinishedRef   = useRef(false); // prevents handleCanvasClick from clearing marquee selection
  const marqueeRectRef           = useRef(null);  // mirrors marqueeRect for sync access in mouseUp
  const resizingShapeRef = useRef(null);
  const rotatingShapeRef = useRef(null);
  const rotateImageCenterRef = useRef(null); // { id, cx, cy (rect-relative px), before }
  const shapeBeforeRef = useRef(null);
  const shapeDraggedRef = useRef(false);
  const shapeClickedRef = useRef(false);
  const textEditCandidateRef = useRef(null); // id of selected text shape tapped, for edit-on-double-tap
  const textLastTapRef       = useRef({ id: null, time: 0 }); // tracks last tap for double-tap detection
  const canvasShapesRef = useRef([]);
  const [isResizingShape, setIsResizingShape]         = useState(false);
  const [isRotatingShape, setIsRotatingShape]         = useState(false);
  const [selectMoveControlMode, setSelectMoveControlMode] = useState('position'); // 'position' | 'rotate' | 'resize'
  const [formatPainterActive, setFormatPainterActive] = useState(false);
  const formatPainterDataRef = useRef(null);
  const [showStrokeWidthMenu, setShowStrokeWidthMenu] = useState(false);
  const [strokeMenuPos, setStrokeMenuPos] = useState({ top: 0, left: 0 });
  const strokeWidthBtnRef = useRef(null);
  const strokeMenuRef = useRef(null);
  const [shapeDashed, setShapeDashed] = useState(false);
  const [showBrushSizeMenu, setShowBrushSizeMenu] = useState(false);
  const [brushMenuPos, setBrushMenuPos] = useState({ top: 0, left: 0 });
  const brushSizeBtnRef = useRef(null);
  const brushMenuRef = useRef(null);
  const [showDrawPalette, setShowDrawPalette] = useState(false);
  const [drawPalettePos, setDrawPalettePos] = useState({ top: 0, left: 0 });
  const drawPaletteRef = useRef(null);
  const drawPaletteBtnRef = useRef(null);
  const lastDragDirRef = useRef({ x: 1, y: 1 }); // tracks last drag direction for leading-edge display
  const [canvasBackground, setCanvasBackground]       = useState('transparent');
  const [showEmojiPopdown, setShowEmojiPopdown]       = useState(false);
  const [emojiPopdownPos, setEmojiPopdownPos]         = useState({ top: 0, left: 0 });
  const emojiPopdownRef = useRef(null);
  const emojiBtnRef    = useRef(null);
  const [showColorPopdown, setShowColorPopdown]     = useState(false);
  const [colorPopdownPos, setColorPopdownPos]       = useState({ top: 0, left: 0 });
  const colorPopdownRef = useRef(null);
  const colorBtnRef    = useRef(null);
  const [showBgColorPopdown, setShowBgColorPopdown] = useState(false);
  const [bgColorPopdownPos, setBgColorPopdownPos]   = useState({ top: 0, left: 0 });
  const bgColorPopdownRef = useRef(null);
  const bgColorBtnRef     = useRef(null);
  const layerCounterRef = useRef(1000);
  // Tracks the rendered canvas rect so the chat panel can align to it
  const [canvasRect, setCanvasRect] = useState({ left: 0, width: 0 });

  const markDirty = () => { isSavedRef.current = false; };

  const _rcDirs = ['n','ne','e','se','s','sw','w','nw'];
  const getRotatedCursor = (dir, rotation) => {
    const idx = _rcDirs.indexOf(dir);
    if (idx === -1) return `${dir}-resize`;
    const steps = (Math.round((rotation || 0) / 45) % 8 + 8) % 8;
    return `${_rcDirs[(idx + steps) % 8]}-resize`;
  };

  // ---- Shape constants & renderer ---------------------------------------
  const SHAPE_FONTS   = ['Brownie Cake','Atop','Soo Chlato','Frosting','Nimbu','Strawberry Script','Gummy Candy','Food Buka','Mc Donuts','Candy Nougat','Sweet Affogato','Sunny Spells'];

  const handleShapeMouseDown = (e, shapeId) => {
    e.stopPropagation();
    shapeClickedRef.current = true;

    // Format painter: apply stored format to the touched shape then deactivate
    if (formatPainterActive && formatPainterDataRef.current) {
      const fmt = formatPainterDataRef.current;
      setCanvasShapes(prev => prev.map(s => {
        if (s.id !== shapeId) return s;
        if (s.type === 'text') {
          return {
            ...s,
            strokeColor: fmt.strokeColor,
            ...(fmt.fontSize != null   && { fontSize: fmt.fontSize }),
            ...(fmt.fontFamily != null && { fontFamily: fmt.fontFamily }),
            ...(fmt.bold != null       && { bold: fmt.bold }),
            ...(fmt.italic != null     && { italic: fmt.italic }),
          };
        }
        if (s.type === 'emoji') return s;
        return { ...s, strokeColor: fmt.strokeColor, fillColor: fmt.fillColor, strokeWidth: fmt.strokeWidth, dashed: fmt.dashed };
      }));
      markDirty();
      setFormatPainterActive(false);
      formatPainterDataRef.current = null;
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || (e.touches && e.touches[0].clientX)) - rect.left;
    const y = (e.clientY || (e.touches && e.touches[0].clientY)) - rect.top;
    const xPct = (x / rect.width) * 100;
    const yPct = (y / rect.height) * 100;

    // Multi-drag: user presses down on one of the already-multi-selected shapes/images
    if (imageSelectionMode && selectedShapeIds.size > 1 && selectedShapeIds.has(shapeId)) {
      shapeBeforeRef.current = canvasShapesRef.current;
      dragStartImagesRef.current = canvasImagesRef.current;
      const _msShapes = canvasShapesRef.current.filter(s => selectedShapeIds.has(s.id));
      const _msImages = canvasImagesRef.current.filter(i => selectedShapeIds.has(i.id));
      multiDragRef.current = [
        ..._msShapes.map(s => ({ id: s.id, offsetX: x - (s.x1 / 100) * rect.width, offsetY: y - (s.y1 / 100) * rect.height })),
        ..._msImages.map(i => ({ id: i.id, offsetX: x - (i.x / 100) * rect.width, offsetY: y - (i.y / 100) * rect.height, isImage: true })),
      ];
      return;
    }

    // Clicking outside the multi-selected set -> clear multi-select
    if (selectedShapeIds.size > 0 && !selectedShapeIds.has(shapeId)) {
      setSelectedShapeIds(new Set());
    }

    // If clicking an already-selected TEXT shape, set up drag normally but mark for
    // edit-mode activation on mouseup (only if no drag movement happened)
    if (shapeId === selectedShapeId) {
      const shape = canvasShapesRef.current.find(s => s.id === shapeId);
      if (shape && shape.type === 'text') {
        textEditCandidateRef.current = shape.id;
        shapeBeforeRef.current = canvasShapesRef.current;
        shapeClickedRef.current = true;
        draggingShapeRef.current = {
          id: shape.id,
          offsetX: x - (shape.x1 / 100) * rect.width,
          offsetY: y - (shape.y1 / 100) * rect.height,
        };
        return;
      }
    }

    // If clicking an already-selected shape, cycle to the next shape underneath
    let targetId = shapeId;
    if (shapeId === selectedShapeId) {
      const shapesAtPoint = canvasShapesRef.current
        .filter(s => {
          if (!s.id) return false;
          const minX = Math.min(s.x1, s.x2), maxX = Math.max(s.x1, s.x2);
          const minY = Math.min(s.y1, s.y2), maxY = Math.max(s.y1, s.y2);
          if (s.type === 'circle') {
            const cx = (s.x1 + s.x2) / 2, cy = (s.y1 + s.y2) / 2;
            const rx = (maxX - minX) / 2, ry = (maxY - minY) / 2;
            if (rx === 0 || ry === 0) return false;
            return ((xPct - cx) / rx) ** 2 + ((yPct - cy) / ry) ** 2 <= 1;
          }
          return xPct >= minX && xPct <= maxX && yPct >= minY && yPct <= maxY;
        })
        .sort((a, b) => (b.layerIndex ?? 4) - (a.layerIndex ?? 4));
      const curIdx = shapesAtPoint.findIndex(s => s.id === shapeId);
      if (curIdx !== -1 && shapesAtPoint.length > 1) {
        targetId = shapesAtPoint[(curIdx + 1) % shapesAtPoint.length].id;
      }
    }

    setSelectedShapeId(targetId);
    setSelectedImage(null);
    setShowImageControls(null);
    const shape = canvasShapesRef.current.find(s => s.id === targetId);
    if (shape) {
      // Reset resize/rotate mode so colour controls show for the freshly selected shape
      setIsResizingShape(false);
      setIsRotatingShape(false);
      shapeBeforeRef.current = canvasShapesRef.current;
      draggingShapeRef.current = {
        id: targetId,
        offsetX: x - (shape.x1 / 100) * rect.width,
        offsetY: y - (shape.y1 / 100) * rect.height,
      };
      if (!imageSelectionMode) {
        const section = shape.type === 'emoji' ? 'shapes-emoji' : shape.type === 'text' ? 'shapes-text' : 'shapes-draw';
        setActiveShapeTool(shape.type);
        setActiveTool('shapes');
        setCurrentControlSection(section);
        // Sync stored shape properties to toolbar controls
        if (shape.strokeColor) setShapeStrokeColor(shape.strokeColor);
        if (shape.fillColor)   setShapeFillColor(shape.fillColor);
        if (shape.strokeWidth) setShapeStrokeWidth(shape.strokeWidth);
        setShapeDashed(shape.dashed || false);
        if (shape.type === 'text' || shape.type === 'emoji') {
          if (shape.fontSize)    setShapeFontSize(shape.fontSize || shape.emojiSize || 18);
          if (shape.fontFamily)  setShapeFontFamily(shape.fontFamily);
          if (shape.bold   != null) setShapeTextBold(shape.bold);
          if (shape.italic != null) setShapeTextItalic(shape.italic);
        }
        setShowTopControls(true);
      } else {
        // select/move mode -- show persistent position controls, no colour section
        setCurrentControlSection('selectmove');
        setSelectMoveControlMode(prev => prev === 'resize' ? 'resize' : 'position');
        setShowTopControls(true);
      }
    }
  };

  const renderShapeElement = (shape, cW, cH) => {
    const x1 = (shape.x1 / 100) * cW;
    const y1 = (shape.y1 / 100) * cH;
    const x2 = (shape.x2 / 100) * cW;
    const y2 = (shape.y2 / 100) * cH;
    const cx = (x1 + x2) / 2;
    const cy = (y1 + y2) / 2;
    const rx = Math.abs(x2 - x1) / 2;
    const ry = Math.abs(y2 - y1) / 2;
    const stroke = shape.strokeColor || '#000000';
    const fill   = shape.fillColor   || 'none';
    const sw     = shape.strokeWidth || 2;
    const isPreview = shape.id === 'preview';
    const isSel  = !isPreview && (selectedShapeId === shape.id || selectedShapeIds.has(shape.id));
    const rot    = shape.rotation || 0;
    const evProps = isPreview ? {} : {
      style: {
        cursor: 'move',
        pointerEvents: (shape.type === 'circle' || shape.type === 'triangle' || shape.type === 'rectangle')
          ? 'all'
          : 'visiblePainted',
        filter: isSel ? 'drop-shadow(0 0 3px #fa4848) drop-shadow(0 0 6px #fa4848) drop-shadow(0 0 10px rgba(250,72,72,0.7))' : undefined,
      },
      onMouseDown: (e) => handleShapeMouseDown(e, shape.id),
      onTouchStart: (e) => { e.stopPropagation(); e.preventDefault(); handleShapeMouseDown(e, shape.id); },
    };
    const common = { stroke, strokeWidth: sw, vectorEffect: 'non-scaling-stroke', ...(shape.dashed ? { strokeDasharray: '6 3' } : {}) };
    // Helper: wrap element in rotation group if needed
    const withRotation = (el, pivotX, pivotY) => {
      if (!rot || isPreview) return el;
      return <g key={shape.id} transform={`rotate(${rot} ${pivotX} ${pivotY})`}>{el}</g>;
    };

    switch (shape.type) {
      case 'line':
        return withRotation(<line key={shape.id} x1={x1} y1={y1} x2={x2} y2={y2} {...common} fill="none" {...evProps} />, cx, cy);

      case 'arrow': {
        const angle = Math.atan2(y2 - y1, x2 - x1);
        const aLen  = Math.max(10, sw * 5);
        const p1x = x2 - aLen * Math.cos(angle - 0.45);
        const p1y = y2 - aLen * Math.sin(angle - 0.45);
        const p2x = x2 - aLen * Math.cos(angle + 0.45);
        const p2y = y2 - aLen * Math.sin(angle + 0.45);
        return withRotation(
          <g key={shape.id} {...evProps}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="transparent" strokeWidth={Math.max(14, sw + 10)}
              style={{ pointerEvents: 'stroke', cursor: 'move' }}
              onMouseDown={isPreview ? undefined : (e) => handleShapeMouseDown(e, shape.id)}
              onTouchStart={isPreview ? undefined : (e) => { e.stopPropagation(); e.preventDefault(); handleShapeMouseDown(e, shape.id); }}
            />
            <line x1={x1} y1={y1} x2={x2} y2={y2} {...common} fill="none" style={{ pointerEvents: 'none' }} />
            <polygon points={`${x2},${y2} ${p1x},${p1y} ${p2x},${p2y}`} fill={stroke} stroke="none" style={{ pointerEvents: 'none' }} />
          </g>, cx, cy
        );
      }

      case 'circle':
        return withRotation(<ellipse key={shape.id} cx={cx} cy={cy} rx={Math.max(1,rx)} ry={Math.max(1,ry)} {...common} fill={fill} {...evProps} />, cx, cy);

      case 'triangle': {
        const tx = cx, ty1v = Math.min(y1, y2);
        const tx2v = Math.min(x1, x2), ty2v = Math.max(y1, y2);
        const tx3v = Math.max(x1, x2), ty3v = Math.max(y1, y2);
        return withRotation(<polygon key={shape.id} points={`${tx},${ty1v} ${tx2v},${ty2v} ${tx3v},${ty3v}`} {...common} fill={fill} {...evProps} />, cx, cy);
      }

      case 'rectangle':
        return withRotation(<rect key={shape.id} x={Math.min(x1,x2)} y={Math.min(y1,y2)} width={Math.abs(x2-x1)} height={Math.abs(y2-y1)} {...common} fill={fill} {...evProps} />, cx, cy);

      case 'text': {
        const fs  = shape.fontSize   || 12;
        const ff  = shape.fontFamily || 'Arial';
        const fw  = shape.bold   ? 'bold'   : 'normal';
        const fi  = shape.italic ? 'italic' : 'normal';
        const tX  = Math.min(x1, x2);
        const tY  = Math.min(y1, y2);
        const tW  = Math.abs(x2 - x1);
        const tH  = Math.abs(y2 - y1);
        return withRotation(
          <text key={shape.id}
            x={tX} y={tY}
            dominantBaseline="hanging"
            fill={stroke} fontSize={fs} fontFamily={ff} fontWeight={fw} fontStyle={fi}
            textLength={tW > 0 ? tW : undefined} lengthAdjust="spacingAndGlyphs"
            clipPath={`inset(0 0 0 0)`}
            style={{ cursor: 'move', pointerEvents: 'visiblePainted', userSelect: 'none', filter: isSel ? 'drop-shadow(0 0 3px #fa4848) drop-shadow(0 0 6px #fa4848) drop-shadow(0 0 10px rgba(250,72,72,0.7))' : undefined }}
            onMouseDown={isPreview ? undefined : (e) => handleShapeMouseDown(e, shape.id)}
            onTouchStart={isPreview ? undefined : (e) => { e.stopPropagation(); e.preventDefault(); handleShapeMouseDown(e, shape.id); }}
          >{shape.text}</text>, cx, cy
        );
      }

      case 'emoji': {
        const sW = Math.abs(x2 - x1) || 80;
        const sH = Math.abs(y2 - y1) || 80;
        const sX = Math.min(x1, x2);
        const sY = Math.min(y1, y2);
        // Actual image rendered as HTML <img> in a separate absolutely-positioned layer
        // (SVG <image> and <foreignObject> both fail cross-origin without CORS headers)
        // This rect is only for pointer events + selection indicator
        return withRotation(
          <rect key={shape.id} x={sX} y={sY} width={sW} height={sH}
            fill="transparent"
            stroke={isSel ? '#fa4848' : 'none'}
            strokeWidth={isSel ? 1.5 : 0}
            strokeDasharray={isSel ? '5 3' : undefined}
            style={{ cursor: 'move', pointerEvents: 'all' }}
            onMouseDown={isPreview ? undefined : (e) => handleShapeMouseDown(e, shape.id)}
            onTouchStart={isPreview ? undefined : (e) => { e.stopPropagation(); e.preventDefault(); handleShapeMouseDown(e, shape.id); }}
          />, cx, cy
        );
      }

      default: return null;
    }
  };
  // -----------------------------------------------------------------------

  // Background drag-to-pan -- disabled (canvas is static)
  const handleAreaMouseDown = () => {};
  const handleAreaMouseMove = () => {};
  const handleAreaMouseUp   = () => {};

  // Deselect when clicking/touching the free area around the canvas
  const handleAreaDeselect = (e) => {
    // Only act when the click target is the area itself (not the canvas or its children)
    if (canvasRef.current && canvasRef.current.contains(e.target)) return;
    // Don't fire when touching/clicking a canvas image overlay (prevents onTouchEnd bubbling from images)
    if (e.target && e.target.closest && e.target.closest('[data-image-id]')) return;
    if (shapeClickedRef.current) { shapeClickedRef.current = false; shapeDraggedRef.current = false; return; }
    // Dismiss magic chat status on any canvas area click while magic wand is active
    if (activeTool === 'magic-wand') {
      setMagicChatStep('');
      setMagicChatError('');
      setShowPromptHistory(false);
      // Reset active sub-tool (colour, brightness, contrast) -- chat persists for multi-image selection
      if (magicSubTool && magicSubTool !== 'chat') {
        setMagicSubTool(null);
        setCurrentControlSection(null);
        setShowTopControls(false);
      }
    }
    if (selectedImage || selectedShapeId || selectedShapeIds.size > 0 || imageSelectionMode) {
      setSelectedImage(null);
      setShowImageControls(null);
      setSelectedShapeId(null);
      setSelectedShapeIds(new Set());
      setActiveShapeTool(null);
      setImageSelectionMode(false);
      // Keep magic-wand active -- only the magic wand button itself should deactivate it
      if (activeTool !== 'magic-wand') setActiveTool(null);
      setCurrentControlSection(null);
      setCanvasCursor('default');
      setShowTopControls(false);
    }
  };

  // Clear chat input when leaving chat mode; reset filter preview when leaving brightness/contrast
  useEffect(() => {
    if (magicSubTool !== 'chat') {
      setMagicChatInput('');
      setShowPromptHistory(false);
      setMagicChatImages([]);
    }
    if (magicSubTool !== 'brightness' && magicSubTool !== 'contrast') {
      setMagicFilterPreview({ brightness: 0, contrast: 0 });
      setMagicBrightness(0);
      setMagicContrast(0);
    }
    if (magicSubTool !== 'color') {
      setMagicCakeColorBase(null);
    }
  }, [magicSubTool]);

  // Capture original image URL when entering brightness/contrast mode (so every bake is from original)
  // Also initialise sliders from the last-applied values stored on the image.
  useEffect(() => {
    if ((magicSubTool === 'brightness' || magicSubTool === 'contrast') && selectedImage) {
      const img = canvasImagesRef.current.find(i => i.id === selectedImage);
      if (img) {
        // Use the stored pre-filter original URL so re-adjusting always starts from the true original
        magicFilterOrigUrlRef.current = img._filterOrigUrl || img.url;
        // Restore last applied values so sliders show the current state
        setMagicBrightness(img._brightness ?? 0);
        setMagicContrast(img._contrast ?? 0);
        setMagicFilterPreview({ brightness: 0, contrast: 0 });
      }
    } else {
      magicFilterOrigUrlRef.current = null;
    }
  }, [magicSubTool, selectedImage]);

  // Touch-friendly states
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Access control
  useEffect(() => {
    if (!['Admin', 'Super', 'Mover', 'Buyer'].includes(userRole)) {
      navigate('/login', { replace: true });
    }
  }, [userRole, navigate]);

  // Measure tools wrapper height via ResizeObserver so canvas top tracks it exactly
  useLayoutEffect(() => {
    const el = toolsWrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setToolsWrapperHeight(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Sync refs with latest state
  useEffect(() => { canvasImagesRef.current = canvasImages; }, [canvasImages]);
  useEffect(() => { drawingHistoryRef.current = drawingHistory; }, [drawingHistory]);
  useEffect(() => { historyIndexRef.current = historyIndex; }, [historyIndex]);
  useEffect(() => { canvasShapesRef.current = canvasShapes; }, [canvasShapes]);

  // Auto-load image when launched from CustomLargeCardSuper
  useEffect(() => {
    if (!fromCustom) return;
    const imageBase64 = location.state?.imageBase64;
    if (!imageBase64) return;
    const img = new Image();
    img.onload = () => {
      const naturalAspectRatio = img.naturalWidth / img.naturalHeight;
      const psW = PRINT_SIZES[printSize].width;
      const psH = PRINT_SIZES[printSize].height;
      const imageCanvasAR = naturalAspectRatio * psH / psW;
      const _fillFrac = 0.88; // fill most of the canvas in fromCustom mode
      let width, height;
      if (imageCanvasAR >= 1) {
        width  = _fillFrac * 21;
        height = _fillFrac * psW * 29.7 / (naturalAspectRatio * psH);
      } else {
        height = _fillFrac * 29.7;
        width  = naturalAspectRatio * _fillFrac * psH * 21 / psW;
      }
      width  = Math.max(0.5, Math.min(21, width));
      height = Math.max(0.5, Math.min(29.7, height));
      const newImage = {
        id: Date.now(),
        layerIndex: ++layerCounterRef.current,
        url: imageBase64,
        name: 'cake-image',
        size: '',
        x: (50 - (width / 21 * 50)),
        y: (50 - (height / 29.7 * 50)),
        width,
        height,
        source: 'local',
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
      };
      setCanvasImages([newImage]);
      setSelectedImage(newImage.id);
      setResizeWidth(width);
      setResizeHeight(height);
      setAspectRatio(naturalAspectRatio);
      setActiveTool('magic-wand');
      setImageSelectionMode(false);
      setCurrentControlSection(null);
      setShowTopControls(false);
      setCanvasCursor('pointer');
    };
    img.src = imageBase64;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close brush size menu when clicking outside
  useEffect(() => {
    if (!showBrushSizeMenu) return;
    const handler = (e) => {
      if (
        brushMenuRef.current && !brushMenuRef.current.contains(e.target) &&
        brushSizeBtnRef.current && !brushSizeBtnRef.current.contains(e.target)
      ) {
        setShowBrushSizeMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showBrushSizeMenu]);

  // Close draw palette dropdown when clicking outside
  useEffect(() => {
    if (!showDrawPalette) return;
    const handler = (e) => {
      if (
        drawPaletteRef.current && !drawPaletteRef.current.contains(e.target) &&
        drawPaletteBtnRef.current && !drawPaletteBtnRef.current.contains(e.target)
      ) {
        setShowDrawPalette(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showDrawPalette]);

  // Close stroke width menu when clicking outside
  useEffect(() => {
    if (!showStrokeWidthMenu) return;
    const handler = (e) => {
      if (
        strokeMenuRef.current && !strokeMenuRef.current.contains(e.target) &&
        strokeWidthBtnRef.current && !strokeWidthBtnRef.current.contains(e.target)
      ) {
        setShowStrokeWidthMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showStrokeWidthMenu]);

  // Close emoji popdown when clicking outside
  useEffect(() => {
    if (!showEmojiPopdown) return;
    const handler = (e) => {
      if (
        emojiPopdownRef.current && !emojiPopdownRef.current.contains(e.target) &&
        emojiBtnRef.current && !emojiBtnRef.current.contains(e.target)
      ) {
        setShowEmojiPopdown(false);
        setSelectedStampIds(new Set());
        setStampSelectMode(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showEmojiPopdown]);

  // Update canvas rect whenever viewport/printSize changes
  useEffect(() => {
    const update = () => {
      if (canvasRef.current) {
        const r = canvasRef.current.getBoundingClientRect();
        setCanvasRect({ left: r.left, width: r.width });
      }
    };
    // Small delay to let layout settle
    const t = setTimeout(update, 60);
    return () => clearTimeout(t);
  }, [vpSize, printSize]);

  // Close color popdown when clicking outside
  useEffect(() => {
    if (!showColorPopdown) return;
    const handler = (e) => {
      if (
        colorPopdownRef.current && !colorPopdownRef.current.contains(e.target) &&
        colorBtnRef.current && !colorBtnRef.current.contains(e.target)
      ) {
        setShowColorPopdown(false);
        setMagicCakeColorBase(null);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showColorPopdown]);

  // Close bg-color popdown when clicking outside
  useEffect(() => {
    if (!showBgColorPopdown) return;
    const handler = (e) => {
      if (
        bgColorPopdownRef.current && !bgColorPopdownRef.current.contains(e.target) &&
        bgColorBtnRef.current && !bgColorBtnRef.current.contains(e.target)
      ) {
        setShowBgColorPopdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showBgColorPopdown]);

  // Track viewport size for canvas centering
  useEffect(() => {
    const onResize = () => setVpSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Render drawing paths onto canvas ? destination-out eraser works reliably here
  useEffect(() => {
    const canvas = drawingCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (w > 0 && h > 0) {
      canvas.width = w;
      canvas.height = h;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const allPaths = [
      ...drawingPaths,
      ...(currentPath.length > 0 ? [{
        path: currentPath,
        color: drawingMode === 'eraser' ? 'eraser' : drawingColor,
        size: brushSize,
        type: drawingMode,
        canvasW: canvas.width,
        canvasH: canvas.height,
      }] : [])
    ];
    allPaths.forEach(path => {
      const scaleX = canvas.width / (path.canvasW || canvas.width);
      const scaleY = canvas.height / (path.canvasH || canvas.height);
      renderDrawingPath(ctx, path, scaleX, scaleY);
    });
    ctx.globalCompositeOperation = 'source-over';
  }, [drawingPaths, currentPath, drawingColor, drawingMode, brushSize, vpSize]);

  // Detect whether the drawing canvas contains a closed (enclosed) area.
  // BFS from (0,0) through transparent pixels: if some transparent pixels are
  // not reachable from the edge, they are enclosed by strokes.
  useEffect(() => {
    const timer = setTimeout(() => {
      const canvas = drawingCanvasRef.current;
      if (!canvas || !canvas.width || !canvas.height || drawingPaths.length === 0) {
        setHasClosedArea(false);
        return;
      }
      const cW = canvas.width, cH = canvas.height;
      const total = cW * cH;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      const data = ctx.getImageData(0, 0, cW, cH).data;
      // BFS flood from (0,0) through pixels with alpha <= 10 (transparent / near-transparent)
      const visited = new Uint8Array(total);
      const stack = [0];
      let reached = 0;
      while (stack.length > 0) {
        const pos = stack.pop();
        if (visited[pos]) continue;
        visited[pos] = 1;
        if (data[pos * 4 + 3] > 10) continue; // stroke pixel -- barrier
        reached++;
        const x = pos % cW, y = Math.floor(pos / cW);
        if (x > 0)          stack.push(pos - 1);
        if (x < cW - 1)     stack.push(pos + 1);
        if (y > 0)           stack.push(pos - cW);
        if (y < cH - 1)     stack.push(pos + cW);
      }
      // Count total transparent pixels; if some were not reached, a closed area exists
      let transparent = 0;
      for (let i = 3; i < data.length; i += 4) { if (data[i] <= 10) transparent++; }
      setHasClosedArea(reached < transparent);
    }, 80);
    return () => clearTimeout(timer);
  }, [drawingPaths]);

  // Auto-clear save message after 4 s
  useEffect(() => {
    if (saveMessage) {
      const t = setTimeout(() => setSaveMessage(null), 4000);
      return () => clearTimeout(t);
    }
  }, [saveMessage]);

  const isDirty = (canvasImages.length > 0 || drawingPaths.length > 0 || canvasShapes.length > 0) && !isSavedRef.current;

  const handleGuardedNavigate = (path) => {
    if (isDirty) {
      setPendingNavPath(path ?? '__back__');
      setCurrentControlSection(null);
      setShowTopControls(true);
    } else {
      if (path === null || path === '__back__') navigate(-1);
      else navigate(path);
    }
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if we're clicking outside the connect menu
      if (connectMenuRef.current && 
          !connectMenuRef.current.contains(event.target) &&
          !event.target.closest('.connect-dropdown-vertical')) {
        setIsConnectMenuOpen(false);
      }
      
      // Close color picker when clicking outside
      if ((showColorPicker || showColorShades || showShapeColorPicker || showShapeColorShades) && 
          !event.target.closest('.color-picker-container') &&
          !event.target.closest('.base-colors-controls') &&
          !event.target.closest('.color-shades-controls') &&
          !event.target.closest('.color-palette-btn')) {
        setShowColorPicker(false);
        setShowColorShades(false);
        setShowShapeColorPicker(false);
        setShowShapeColorShades(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showColorPicker, showColorShades, showShapeColorPicker, showShapeColorShades, isConnectMenuOpen]);

  // Keyboard event listener for delete key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Delete' && selectedImage) {
        const image = canvasImages.find(img => img.id === selectedImage);
        if (image) {
          handleDeleteImage(image.id);
        }
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedShapeId) {
        const _sBefore = canvasShapesRef.current;
        const _sAfter = _sBefore.filter(s => s.id !== selectedShapeId);
        setCanvasShapes(_sAfter);
        setSelectedShapeId(null);
        setActiveTool(null);
        setActiveShapeTool(null);
        setSelectedEmoji(null);
        setCanvasCursor('default');
        setCurrentControlSection(null);
        setShowTopControls(false);
        pushShapeHistory(_sBefore, _sAfter);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        handleRedo();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImage, canvasImages, selectedShapeId]);

  useEffect(() => {
    const updateTopControlHeight = () => {
      if (showTopControls && topControlsRef.current) {
        const height = topControlsRef.current.offsetHeight;
        document.documentElement.style.setProperty('--top-control-height', `${height}px`);
      } else {
        document.documentElement.style.setProperty('--top-control-height', '0px');
      }
    };

    updateTopControlHeight();

    // Observe height changes (e.g., window resize, content change)
    const observer = new ResizeObserver(updateTopControlHeight);
    if (topControlsRef.current) {
      observer.observe(topControlsRef.current);
    }

    return () => observer.disconnect();
  }, [showTopControls]);

  useEffect(() => {
    document.body.classList.add('no-scroll');
    return () => document.body.classList.remove('no-scroll');
  }, []);

  // ---- Load shared stamps from Firestore --------------------------------
  const loadStampsFromFirestore = async () => {
    try {
      const { db } = getFirebase();
      const snap = await getDocs(collection(db, 'studioStamps'));
      const stamps = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
      setStudioStamps(stamps);
      stamps.forEach(s => {
        if (s.url && !stampImageCacheRef.current.has(s.url)) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => stampImageCacheRef.current.set(s.url, img);
          img.src = s.url;
        }
      });
    } catch (e) {
      console.error('Failed to load stamps:', e);
    }
  };

  useEffect(() => { loadStampsFromFirestore(); }, []);

  // High-quality image draw with progressive halving for large downscales.
  // Browsers perform a single-step bilinear/Lanczos pass even with
  // imageSmoothingQuality='high', which can produce aliasing/moiré when the
  // source is many times larger than the target. Halving the source repeatedly
  // until it is within 2× of the target dimensions gives mipmap-style filtering
  // with no visible aliasing — the technique used by professional photo apps.
  // dw/dh are positive pixel sizes; the existing context transforms (translate,
  // rotate, clip) still apply.
  const drawImageHQ = (ctx, srcImg, dx, dy, dw, dh) => {
    const sw = srcImg.naturalWidth  || srcImg.width;
    const sh = srcImg.naturalHeight || srcImg.height;
    if (!sw || !sh || dw <= 0 || dh <= 0) {
      ctx.drawImage(srcImg, dx, dy, dw, dh);
      return;
    }
    // No meaningful downscale needed — single draw is best (no extra blur)
    if (sw <= dw * 2 && sh <= dh * 2) {
      ctx.drawImage(srcImg, dx, dy, dw, dh);
      return;
    }
    let curW = sw, curH = sh;
    let cur = srcImg;
    // Halve in both axes together until EITHER dimension is within 2× of target.
    // Using || (not &&) prevents a single large-ratio axis from skipping the loop
    // entirely, which would cause a one-shot 3-5× downscale and visible blurring.
    while (curW > dw * 2 || curH > dh * 2) {
      const nextW = Math.max(Math.ceil(dw), Math.floor(curW / 2));
      const nextH = Math.max(Math.ceil(dh), Math.floor(curH / 2));
      if (nextW === curW && nextH === curH) break;
      const tmp = document.createElement('canvas');
      tmp.width  = nextW;
      tmp.height = nextH;
      const tctx = tmp.getContext('2d');
      tctx.imageSmoothingEnabled = true;
      tctx.imageSmoothingQuality = 'high';
      tctx.drawImage(cur, 0, 0, nextW, nextH);
      cur  = tmp;
      curW = nextW;
      curH = nextH;
    }
    ctx.drawImage(cur, dx, dy, dw, dh);
  };

  // Load an HTTP image via fetch -> blob URL to avoid canvas taint.
  // Falls back to crossOrigin=anonymous if fetch fails.
  const loadImgSafe = (url) => new Promise(async (resolve) => {
    if (!url || !url.startsWith('http')) {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = url;
      return;
    }
    try {
      const res = await fetch(url, { mode: 'cors' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(blobUrl); resolve(img); };
      img.onerror = () => { URL.revokeObjectURL(blobUrl); resolve(null); };
      img.src = blobUrl;
    } catch {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = url;
    }
  });

  // Prepares an image for Kling AI submission.
  // Firebase Storage download URLs are returned as-is (Kling's servers can fetch them
  // directly, so we avoid the CORS-blocked XHR that occurs when the browser tries to
  // download them). Local data URLs are resized and re-uploaded to temp Firebase Storage.
  // Returns { imageUrl, w, h, tempRef }. Caller must deleteObject(tempRef) on cleanup.
  const prepareImageForKling = async (imgObj, { min = 300, max = 1024, suffix = '' } = {}) => {
    const url = imgObj?.url || '';

    // Resolve the effective source URL.
    // For Firebase Storage images we route through the backend proxy so the browser
    // can read the raw bytes (no CORS block) and then draw to canvas, which:
    //   1. Strips EXIF orientation data — phones often store images with orientation 2
    //      (horizontal flip) so Kling was receiving a mirrored raw file and returning
    //      a mirrored result.
    //   2. Resizes to a Kling-friendly resolution before upload to temp storage.
    let srcUrl = url;
    if (url.startsWith('https://firebasestorage.googleapis.com')) {
      const backendUrl = (!Capacitor.isNativePlatform() && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
        ? (import.meta.env.VITE_BACKEND_URL_LOCAL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:3002')
        : (import.meta.env.VITE_BACKEND_URL || window.location.origin);
      srcUrl = `${backendUrl}/proxy-image?url=${encodeURIComponent(url)}`;
    }

    // Load via loadImgSafe (handles blob/data/http URLs and CORS-safe object URLs).
    const el = await loadImgSafe(srcUrl);
    if (!el) throw new Error('Could not load image');
    let w = el.naturalWidth || el.width || 512;
    let h = el.naturalHeight || el.height || 512;
    if (w < min || h < min) { const s = Math.max(min / w, min / h); w = Math.round(w * s); h = Math.round(h * s); }
    if (w > max || h > max) { const s = Math.min(max / w, max / h); w = Math.round(w * s); h = Math.round(h * s); }
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#cccccc';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(el, 0, 0, w, h);
    const blob = await new Promise(resolve => c.toBlob(resolve, 'image/png'));
    const { storage } = getFirebase();
    const uid = getAuth().currentUser?.uid || 'anon';
    const tempPath = `ai-temp/${uid}_${Date.now()}${suffix ? '_' + suffix : ''}.png`;
    const tempRef = stampStorageRef(storage, tempPath);
    await uploadBytes(tempRef, blob, { contentType: 'image/png' });
    const imageUrl = await getDownloadURL(tempRef);
    return { imageUrl, w, h, tempRef };
  };

  // (Kept only for reference / fallback experiments -- two-image AI edit now goes
  // through Gemini via /magic-ai-edit-multi which natively supports multi-image input.)
  // eslint-disable-next-line no-unused-vars
  const prepareCompositeForKling = async (editImg, refImg, { maxSize = 1024 } = {}) => {
    const loadOne = async (imgObj) => {
      const url = imgObj?.url || '';
      let srcUrl = url;
      if (url.startsWith('https://firebasestorage.googleapis.com')) {
        const backendUrl = (!Capacitor.isNativePlatform() && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
          ? (import.meta.env.VITE_BACKEND_URL_LOCAL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:3002')
          : (import.meta.env.VITE_BACKEND_URL || window.location.origin);
        srcUrl = `${backendUrl}/proxy-image?url=${encodeURIComponent(url)}`;
      }
      const el = await loadImgSafe(srcUrl);
      if (!el) throw new Error('Could not load image');
      return el;
    };
    const [elEdit, elRef] = await Promise.all([loadOne(editImg), loadOne(refImg)]);

    // Fit EDIT into maxSize while preserving aspect ratio
    const enw = elEdit.naturalWidth  || elEdit.width  || 512;
    const enh = elEdit.naturalHeight || elEdit.height || 512;
    const editScale = Math.min(maxSize / enw, maxSize / enh, 1);
    const editW = Math.max(1, Math.round(enw * editScale));
    const editH = Math.max(1, Math.round(enh * editScale));

    // Inset (REF) takes up ~30% of EDIT's smallest dimension, top-right corner with margin
    const insetMaxSize = Math.round(Math.min(editW, editH) * 0.30);
    const rnw = elRef.naturalWidth  || elRef.width  || 512;
    const rnh = elRef.naturalHeight || elRef.height || 512;
    const refScale = Math.min(insetMaxSize / rnw, insetMaxSize / rnh, 1);
    const refW = Math.max(1, Math.round(rnw * refScale));
    const refH = Math.max(1, Math.round(rnh * refScale));
    const margin = Math.round(Math.min(editW, editH) * 0.025);
    const insetX = editW - refW - margin;
    const insetY = margin;

    const c = document.createElement('canvas');
    c.width = editW; c.height = editH;
    const ctx = c.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, editW, editH);

    // Draw EDIT as the full-canvas base
    ctx.drawImage(elEdit, 0, 0, editW, editH);

    // White backing card for the inset so it stands out from EDIT's background
    const cardPad = Math.max(2, Math.round(refW * 0.04));
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(insetX - cardPad, insetY - cardPad, refW + cardPad * 2, refH + cardPad * 2 + 22);
    // Black border around the card
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.strokeRect(insetX - cardPad, insetY - cardPad, refW + cardPad * 2, refH + cardPad * 2 + 22);

    // Draw REF inside the card
    ctx.drawImage(elRef, insetX, insetY, refW, refH);

    // Label below the inset
    ctx.fillStyle = '#000000';
    ctx.font = `bold ${Math.max(10, Math.round(refW * 0.10))}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('ADD THIS', insetX + refW / 2, insetY + refH + 4);

    const blob = await new Promise(resolve => c.toBlob(resolve, 'image/png'));
    const { storage } = getFirebase();
    const uid = getAuth().currentUser?.uid || 'anon';
    const tempPath = `ai-temp/${uid}_${Date.now()}_composite.png`;
    const tempRef = stampStorageRef(storage, tempPath);
    await uploadBytes(tempRef, blob, { contentType: 'image/png' });
    const imageUrl = await getDownloadURL(tempRef);
    return {
      imageUrl,
      w: editW,
      h: editH,
      tempRef,
      editBox: { x: 0, y: 0, w: editW, h: editH },
      insetBox: {
        x: insetX - cardPad,
        y: insetY - cardPad,
        w: refW + cardPad * 2,
        h: refH + cardPad * 2 + 22,
      },
    };
  };

  // ─── Smart Crop helpers ────────────────────────────────────────────────────
  // Flood-fill from (startX, startY) in imageData; returns the bounding box
  // of all connected pixels that are either non-transparent (alpha-first) or
  // within a colour tolerance (fallback for fully-opaque images).
  const floodFillBounds = (imageData, startX, startY) => {
    const { data, width, height } = imageData;
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
    startX = clamp(startX, 0, width  - 1);
    startY = clamp(startY, 0, height - 1);
    const ALPHA_THRESHOLD = 30;

    // ── Helper: bounding box of all non-transparent pixels ──────────────────
    const allVisibleBbox = () => {
      let ax = width, bx = 0, ay = height, by = 0;
      for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
        if (data[(y * width + x) * 4 + 3] > ALPHA_THRESHOLD) {
          if (x < ax) ax = x; if (x > bx) bx = x;
          if (y < ay) ay = y; if (y > by) by = y;
        }
      }
      return { minX: ax, maxX: bx, minY: ay, maxY: by };
    };

    // ── Check for meaningful transparency ────────────────────────────────────
    let hasTransparency = false;
    for (let i = 3; i < data.length; i += 4) { if (data[i] < ALPHA_THRESHOLD) { hasTransparency = true; break; } }

    if (hasTransparency) {
      // Alpha path: flood-fill connected visible pixels from the click point
      const startA = data[(startY * width + startX) * 4 + 3];
      if (startA < ALPHA_THRESHOLD) return allVisibleBbox(); // clicked on transparent → full bbox
      const visited = new Uint8Array(width * height);
      const stack = [];
      const pushA = (x, y) => {
        if (x < 0 || x >= width || y < 0 || y >= height) return;
        const i = y * width + x; if (visited[i]) return; visited[i] = 1;
        if (data[i * 4 + 3] < ALPHA_THRESHOLD) return;
        stack.push(i);
      };
      pushA(startX, startY);
      let minX = startX, maxX = startX, minY = startY, maxY = startY;
      while (stack.length) {
        const i = stack.pop();
        const x = i % width, y = (i / width) | 0;
        if (x < minX) minX = x; if (x > maxX) maxX = x;
        if (y < minY) minY = y; if (y > maxY) maxY = y;
        pushA(x+1,y); pushA(x-1,y); pushA(x,y+1); pushA(x,y-1);
      }
      return { minX, maxX, minY, maxY };
    }

    // ── Opaque image: gutter-scan approach ───────────────────────────────────
    // Works for grids of cards AND isolated objects.
    // Strategy: determine background colour from image edges (majority cluster),
    // then scan outward from the click point in each direction until we hit a
    // "gutter" — a row/col where ≥50% of pixels are background colour.
    // The gutter boundaries define the cell containing the clicked object.
    const rgb = (x, y) => { const p = (y * width + x) * 4; return [data[p], data[p+1], data[p+2]]; };
    const dist = (a, b) => Math.abs(a[0]-b[0]) + Math.abs(a[1]-b[1]) + Math.abs(a[2]-b[2]);

    // Sample 16 border pixels; use majority cluster to find bgColor.
    const edgeSamples = [
      rgb(0,0), rgb(width-1,0), rgb(0,height-1), rgb(width-1,height-1),
      rgb(width>>1,0), rgb(width>>1,height-1), rgb(0,height>>1), rgb(width-1,height>>1),
      rgb(width>>2,0), rgb(3*(width>>2),0), rgb(width>>2,height-1), rgb(3*(width>>2),height-1),
      rgb(0,height>>2), rgb(0,3*(height>>2)), rgb(width-1,height>>2), rgb(width-1,3*(height>>2)),
    ];
    const CLUSTER_TOL = 50 * 3;
    let bgColor = edgeSamples[0], bestClusterCount = 0;
    for (const cand of edgeSamples) {
      let cnt = 0, sr = 0, sg = 0, sb = 0;
      for (const s of edgeSamples) {
        if (dist(s, cand) < CLUSTER_TOL) { cnt++; sr += s[0]; sg += s[1]; sb += s[2]; }
      }
      if (cnt > bestClusterCount) { bestClusterCount = cnt; bgColor = [sr/cnt, sg/cnt, sb/cnt]; }
    }

    const BG_TOL = 60 * 3;
    const isBg = (x, y) => dist(rgb(x, y), bgColor) < BG_TOL;

    // A row is a gutter if ≥50% of its pixels are background
    const rowIsGutter = (y) => {
      let n = 0;
      for (let x = 0; x < width; x++) if (isBg(x, y)) n++;
      return n / width >= 0.5;
    };
    // A col is a gutter if ≥50% of its pixels are background
    const colIsGutter = (x) => {
      let n = 0;
      for (let y = 0; y < height; y++) if (isBg(x, y)) n++;
      return n / height >= 0.5;
    };

    // Scan outward from click to find the nearest gutter in each direction
    let minY = startY, maxY = startY, minX = startX, maxX = startX;
    while (minY > 0        && !rowIsGutter(minY)) minY--;
    while (maxY < height-1 && !rowIsGutter(maxY)) maxY++;
    while (minX > 0        && !colIsGutter(minX)) minX--;
    while (maxX < width-1  && !colIsGutter(maxX)) maxX++;

    // If click itself is on background (gutter), return bbox of all non-background pixels
    if (isBg(startX, startY)) {
      let ax = width, bx = 0, ay = height, by = 0;
      for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
        if (!isBg(x, y)) {
          if (x < ax) ax = x; if (x > bx) bx = x;
          if (y < ay) ay = y; if (y > by) by = y;
        }
      }
      return { minX: ax, maxX: bx, minY: ay, maxY: by };
    }

    return { minX, maxX, minY, maxY };
  };

  // Frame peeler: samples the current bbox corners to determine this layer's color,
  // then scans rows/cols inward from each edge — peeling any that are ≥70% that color.
  // One call = one frame layer stripped. Loop in runSmartCrop handles multiple layers
  // (e.g. dark shadow → white card frame → stop at princess content).
  const shrinkFrameBbox = (imageData, bbox) => {
    const { data, width } = imageData;
    let { minX, maxX, minY, maxY } = bbox;
    const origBw = maxX - minX, origBh = maxY - minY;
    if (origBw < 20 || origBh < 20) return bbox;

    const px = (x, y) => { const p = (y * width + x) * 4; return [data[p], data[p+1], data[p+2]]; };
    const dist = (a, b) => Math.abs(a[0]-b[0]) + Math.abs(a[1]-b[1]) + Math.abs(a[2]-b[2]);

    // Sample corners + edge midpoints to determine the current outer frame color
    const mx = (minX + maxX) >> 1, my = (minY + maxY) >> 1;
    const samples = [
      px(minX,minY), px(maxX,minY), px(minX,maxY), px(maxX,maxY),
      px(mx, minY), px(mx, maxY), px(minX, my), px(maxX, my),
    ];
    const frameMean = [0,0,0];
    for (const s of samples) { frameMean[0]+=s[0]; frameMean[1]+=s[1]; frameMean[2]+=s[2]; }
    frameMean[0]/=samples.length; frameMean[1]/=samples.length; frameMean[2]/=samples.length;

    // If the sampled edges are too varied, they're content — no frame to peel
    let edgeVariance = 0;
    for (const s of samples) edgeVariance += dist(s, frameMean);
    edgeVariance /= samples.length;
    if (edgeVariance > 75) return bbox;

    // A row/col is "frame" if ≥70% of its pixels match frameMean within tolerance
    const FRAME_TOL = 55; // L1 per-pixel tolerance
    const FRAME_FRAC = 0.85; // 85% of row/col must match frame color
    const rowIsFrame = (y, x0, x1) => {
      const n = x1 - x0 + 1; let m = 0;
      for (let x = x0; x <= x1; x++) if (dist(px(x, y), frameMean) < FRAME_TOL) m++;
      return m / n >= FRAME_FRAC;
    };
    const colIsFrame = (x, y0, y1) => {
      const n = y1 - y0 + 1; let m = 0;
      for (let y = y0; y <= y1; y++) if (dist(px(x, y), frameMean) < FRAME_TOL) m++;
      return m / n >= FRAME_FRAC;
    };

    while (minY < maxY - 10 && rowIsFrame(minY, minX, maxX)) minY++;
    while (maxY > minY + 10 && rowIsFrame(maxY, minX, maxX)) maxY--;
    while (minX < maxX - 10 && colIsFrame(minX, minY, maxY)) minX++;
    while (maxX > minX + 10 && colIsFrame(maxX, minY, maxY)) maxX--;

    if ((maxX - minX) < origBw * 0.15 || (maxY - minY) < origBh * 0.15) return bbox;
    return { minX, maxX, minY, maxY };
  };

  // Detect the object under (canvasX, canvasY) and set the silhouette selection.
  // Optional drawnBox {x,y,w,h} constrains detection to the user-drawn area.
  const runSmartCrop = async (canvasX, canvasY, drawnBox = null) => {
    const canvas = canvasRef.current;
    if (!canvas || smartCropRunning) return;
    const rect = canvas.getBoundingClientRect();
    // Find the topmost image under the click
    const sorted = [...canvasImages].sort((a, b) => (b.layerIndex ?? 1) - (a.layerIndex ?? 1));
    let targetImg = null, relX = 0, relY = 0, rendW = 0, rendH = 0;
    for (const img of sorted) {
      const left = (img.x / 100) * rect.width;
      const top  = (img.y / 100) * rect.height;
      const w    = (img.width  / 21)   * rect.width;
      const h    = (img.height / 29.7) * rect.height;
      if (canvasX >= left && canvasX <= left + w && canvasY >= top && canvasY <= top + h) {
        targetImg = img; relX = canvasX - left; relY = canvasY - top; rendW = w; rendH = h;
        break;
      }
    }
    if (!targetImg) {
      // No image under lasso area — reset
      setIsSelecting(false);
      setSelectionPath([]);
      setSelectionStart({ x: 0, y: 0 }); setSelectionEnd({ x: 0, y: 0 });
      return;
    }
    setSmartCropRunning(true);
    setSelectionPath([]); // clear lasso; will be replaced by detected polygon
    try {
      const imgEl = await loadImgSafe(targetImg.url);
      if (!imgEl) return;
      // Sample at ≤800px wide for fast flood fill
      const MAX_W = 800;
      const scale = Math.min(1, MAX_W / (imgEl.naturalWidth || rendW));
      const sw = Math.max(1, Math.round((imgEl.naturalWidth || rendW) * scale));
      const sh = Math.max(1, Math.round((imgEl.naturalHeight || rendH) * scale));
      const tmp = document.createElement('canvas');
      tmp.width = sw; tmp.height = sh;
      tmp.getContext('2d').drawImage(imgEl, 0, 0, sw, sh);
      const imgData = tmp.getContext('2d').getImageData(0, 0, sw, sh);
      const sampleX = Math.floor((relX / rendW) * sw);
      const sampleY = Math.floor((relY / rendH) * sh);
      const imgLeft = (targetImg.x / 100) * rect.width;
      const imgTop  = (targetImg.y / 100) * rect.height;

      // If user drew a box, clamp detection to within that box on the image
      let clampMinX = 0, clampMinY = 0, clampMaxX = sw - 1, clampMaxY = sh - 1;
      if (drawnBox) {
        const boxLeft   = Math.max(0, drawnBox.x - imgLeft);
        const boxTop    = Math.max(0, drawnBox.y - imgTop);
        const boxRight  = Math.min(rendW, drawnBox.x + drawnBox.w - imgLeft);
        const boxBottom = Math.min(rendH, drawnBox.y + drawnBox.h - imgTop);
        clampMinX = Math.max(0, Math.floor((boxLeft   / rendW) * sw));
        clampMinY = Math.max(0, Math.floor((boxTop    / rendH) * sh));
        clampMaxX = Math.min(sw - 1, Math.ceil((boxRight  / rendW) * sw));
        clampMaxY = Math.min(sh - 1, Math.ceil((boxBottom / rendH) * sh));
      }

      let bounds = floodFillBounds(imgData, sampleX, sampleY);
      // Clamp to drawn box
      bounds = {
        minX: Math.max(bounds.minX, clampMinX), maxX: Math.min(bounds.maxX, clampMaxX),
        minY: Math.max(bounds.minY, clampMinY), maxY: Math.min(bounds.maxY, clampMaxY),
      };

      // Check for transparency BEFORE shrinking — transparent images are already
      // alpha-masked (polygon-clipped), so shrinkFrameBbox must be skipped.
      // If we don't skip it, transparent border pixels (alpha=0, rgb=0,0,0) look like
      // a solid black "frame" and the bbox collapses to a thin sliver.
      const { data: pd, width: pw } = imgData;
      let hasAlphaPath = false;
      for (let ai = 3; ai < pd.length; ai += 4) if (pd[ai] < 30) { hasAlphaPath = true; break; }

      // Iteratively peel nested frame layers (dark shadow → white card → content).
      // Only for opaque images — transparent images are already correctly bounded.
      if (!hasAlphaPath) {
        for (let pass = 0; pass < 10; pass++) {
          const refined = shrinkFrameBbox(imgData, bounds);
          if (
            refined.minX === bounds.minX && refined.maxX === bounds.maxX &&
            refined.minY === bounds.minY && refined.maxY === bounds.maxY
          ) break;
          bounds = refined;
        }
      }
      let { minX, maxX, minY, maxY } = bounds;

      // ── Detect inner background color from the LASSO BOUNDARY pixels ─────
      // The lasso boundary (clamp edges) is drawn by the user OUTSIDE the object
      // but INSIDE the actual background (e.g. white card, not the dark outer frame).
      // Sampling here avoids picking up the dark outer frame color that the full
      // image border samples would return — which would cause dark dress/hair pixels
      // to be misclassified as "similar to background" and punched out of the mask.
      const ppx = (x, y) => { const p = (y*pw+x)*4; return [pd[p],pd[p+1],pd[p+2]]; };
      const pdt = (a, b) => Math.abs(a[0]-b[0])+Math.abs(a[1]-b[1])+Math.abs(a[2]-b[2]);
      let polyBgColor = [255, 255, 255];
      if (!hasAlphaPath) {
        const bsamp = [];
        const bstep = Math.max(1, Math.round(Math.min(clampMaxX - clampMinX, clampMaxY - clampMinY) / 8));
        for (let x = clampMinX; x <= clampMaxX; x += bstep) {
          bsamp.push(ppx(x, clampMinY)); bsamp.push(ppx(x, clampMaxY));
        }
        for (let y = clampMinY + bstep; y < clampMaxY; y += bstep) {
          bsamp.push(ppx(clampMinX, y)); bsamp.push(ppx(clampMaxX, y));
        }
        const CLUSTER_TOL = 50 * 3;
        let best = 0;
        for (const c of bsamp) {
          let cnt=0,r=0,g=0,b=0;
          for (const s of bsamp) if (pdt(s,c) < CLUSTER_TOL) { cnt++; r+=s[0]; g+=s[1]; b+=s[2]; }
          if (cnt > best) { best=cnt; polyBgColor=[r/cnt, g/cnt, b/cnt]; }
        }
      }
      const POLY_BG_TOL = 60 * 3;
      const isFgPx = (x, y) => {
        if (hasAlphaPath) return pd[(y*pw+x)*4+3] > 30;
        const p = (y*pw+x)*4;
        return Math.abs(pd[p]-polyBgColor[0])+Math.abs(pd[p+1]-polyBgColor[1])+Math.abs(pd[p+2]-polyBgColor[2]) >= POLY_BG_TOL;
      };

      // ── Build pixel-level FG mask via connected-BG flood fill ─────────────
      // Seed BFS from lasso boundary pixels that match the background color.
      // The BFS spreads through all connected background-colored pixels within
      // the lasso area. Pixels NOT reachable = foreground.
      //
      // Why this beats per-pixel threshold:
      //   Armpit gap (white, connected to outer bg via path around the arm) → BFS
      //     reaches it → correctly marked background.
      //   Face skin (light pink, NOT connected to outer bg) → BFS blocked by
      //     surrounding body pixels → correctly stays foreground, no holes.
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = sw; maskCanvas.height = sh;
      const mkCtx = maskCanvas.getContext('2d');
      const mkData = mkCtx.createImageData(sw, sh);
      let fgMinX = sw, fgMaxX = 0, fgMinY = sh, fgMaxY = 0;

      if (hasAlphaPath) {
        // Transparent images: alpha channel is exact — direct copy, no flood fill needed
        for (let fy = clampMinY; fy <= clampMaxY; fy++) {
          for (let fx = clampMinX; fx <= clampMaxX; fx++) {
            if (pd[(fy*pw+fx)*4+3] > 30) {
              const mi = (fy*sw+fx)*4;
              mkData.data[mi] = mkData.data[mi+1] = mkData.data[mi+2] = 255; mkData.data[mi+3] = 255;
              if (fx < fgMinX) fgMinX = fx; if (fx > fgMaxX) fgMaxX = fx;
              if (fy < fgMinY) fgMinY = fy; if (fy > fgMaxY) fgMaxY = fy;
            }
          }
        }
      } else {
        const visited = new Uint8Array(sw * sh);
        const bfsQ = new Int32Array(sw * sh);
        let bfsHead = 0, bfsTail = 0;
        const seed = (x, y) => {
          const idx = y * sw + x;
          if (!visited[idx] && !isFgPx(x, y)) { visited[idx] = 1; bfsQ[bfsTail++] = idx; }
        };
        for (let x = clampMinX; x <= clampMaxX; x++) { seed(x, clampMinY); seed(x, clampMaxY); }
        for (let y = clampMinY + 1; y < clampMaxY; y++) { seed(clampMinX, y); seed(clampMaxX, y); }
        const DX = [-1, 1, 0, 0], DY = [0, 0, -1, 1];
        while (bfsHead < bfsTail) {
          const idx = bfsQ[bfsHead++];
          const px = idx % sw, py = (idx / sw) | 0;
          for (let d = 0; d < 4; d++) {
            const nx = px + DX[d], ny = py + DY[d];
            if (nx < clampMinX || nx > clampMaxX || ny < clampMinY || ny > clampMaxY) continue;
            const nidx = ny * sw + nx;
            if (!visited[nidx] && !isFgPx(nx, ny)) { visited[nidx] = 1; bfsQ[bfsTail++] = nidx; }
          }
        }
        for (let fy = clampMinY; fy <= clampMaxY; fy++) {
          for (let fx = clampMinX; fx <= clampMaxX; fx++) {
            if (!visited[fy * sw + fx]) {
              const mi = (fy * sw + fx) * 4;
              mkData.data[mi] = mkData.data[mi+1] = mkData.data[mi+2] = 255; mkData.data[mi+3] = 255;
              if (fx < fgMinX) fgMinX = fx; if (fx > fgMaxX) fgMaxX = fx;
              if (fy < fgMinY) fgMinY = fy; if (fy > fgMaxY) fgMaxY = fy;
            }
          }
        }
      }
      // ── Hole-fill pass via morphological closing (dilate → erode) ────────
      // Closes background "leaks" where BFS bled through highlights/shading and
      // punched an interior region transparent. Preserves the silhouette outline
      // because we only ADD newly-closed pixels — never remove originals.
      // Skipped for transparent-source images (mask is already exact).
      //
      // Uses separable (1-D horizontal then 1-D vertical) kernels for speed,
      // and treats out-of-bounds neighbors as foreground in the erode step so
      // holes that touch the silhouette near a canvas/clamp edge get filled too.
      if (!hasAlphaPath && fgMaxX > fgMinX && fgMaxY > fgMinY) {
        // Closing kernel scaled to image size (≈1.5% of min dimension, min 8 px)
        // → bridges gaps up to ~2·R wide (≈24 px on an 800-px sample).
        const CLOSE_R = Math.max(8, Math.round(Math.min(sw, sh) * 0.015));

        const fgArr = new Uint8Array(sw * sh);
        for (let i = 0; i < sw * sh; i++) {
          if (mkData.data[i * 4 + 3] === 255) fgArr[i] = 1;
        }

        const x0 = Math.max(0, fgMinX - CLOSE_R), x1 = Math.min(sw - 1, fgMaxX + CLOSE_R);
        const y0 = Math.max(0, fgMinY - CLOSE_R), y1 = Math.min(sh - 1, fgMaxY + CLOSE_R);

        // === DILATE — separable: horizontal pass, then vertical pass ===
        const dilH = new Uint8Array(sw * sh);
        for (let y = y0; y <= y1; y++) {
          const rowBase = y * sw;
          for (let x = x0; x <= x1; x++) {
            const xMin = x - CLOSE_R < 0 ? 0 : x - CLOSE_R;
            const xMax = x + CLOSE_R > sw - 1 ? sw - 1 : x + CLOSE_R;
            for (let nx = xMin; nx <= xMax; nx++) {
              if (fgArr[rowBase + nx]) { dilH[rowBase + x] = 1; break; }
            }
          }
        }
        const dil = new Uint8Array(sw * sh);
        for (let x = x0; x <= x1; x++) {
          for (let y = y0; y <= y1; y++) {
            const yMin = y - CLOSE_R < 0 ? 0 : y - CLOSE_R;
            const yMax = y + CLOSE_R > sh - 1 ? sh - 1 : y + CLOSE_R;
            for (let ny = yMin; ny <= yMax; ny++) {
              if (dilH[ny * sw + x]) { dil[y * sw + x] = 1; break; }
            }
          }
        }

        // === ERODE — separable, with OOB treated as FG (preserves edges) ===
        const erH = new Uint8Array(sw * sh);
        for (let y = y0; y <= y1; y++) {
          const rowBase = y * sw;
          for (let x = x0; x <= x1; x++) {
            if (!dil[rowBase + x]) continue;
            const xMin = x - CLOSE_R < 0 ? 0 : x - CLOSE_R;
            const xMax = x + CLOSE_R > sw - 1 ? sw - 1 : x + CLOSE_R;
            let all = 1;
            for (let nx = xMin; nx <= xMax; nx++) {
              if (!dil[rowBase + nx]) { all = 0; break; }
            }
            if (all) erH[rowBase + x] = 1;
          }
        }
        // Strict source-colour gate — distinguishes:
        //   - LEAK-INDUCED HOLES (e.g. dress highlight: fuzzy-near-bg colour
        //       within BFS POLY_BG_TOL=180 but well above STRICT_BG_TOL) → FILL
        //   - TRUE BG POCKETS (e.g. white between hair strands: essentially the
        //       exact polyBgColor) → SKIP (otherwise they'd render as visible
        //       white patches in the cropped output)
        const STRICT_BG_TOL = 60; // ≈ ±20 per channel from polyBgColor
        const bgR = polyBgColor[0], bgG = polyBgColor[1], bgB = polyBgColor[2];

        for (let x = x0; x <= x1; x++) {
          for (let y = y0; y <= y1; y++) {
            if (!erH[y * sw + x]) continue;
            const yMin = y - CLOSE_R < 0 ? 0 : y - CLOSE_R;
            const yMax = y + CLOSE_R > sh - 1 ? sh - 1 : y + CLOSE_R;
            let all = 1;
            for (let ny = yMin; ny <= yMax; ny++) {
              if (!erH[ny * sw + x]) { all = 0; break; }
            }
            if (all && !fgArr[y * sw + x]) {
              const p = (y * sw + x) * 4;
              const dist = Math.abs(pd[p] - bgR) + Math.abs(pd[p+1] - bgG) + Math.abs(pd[p+2] - bgB);
              if (dist > STRICT_BG_TOL) {
                mkData.data[p]   = mkData.data[p+1] = mkData.data[p+2] = 255;
                mkData.data[p+3] = 255;
              }
            }
          }
        }
      }

      mkCtx.putImageData(mkData, 0, 0);
      smartCropMaskRef.current = { maskCanvas, imgLeft, imgTop, rendW, rendH, targetImgId: targetImg.id };
      // Expand bounds to true FG pixel extent (includes braid/elbow missed by gutter scan)
      if (fgMaxX > fgMinX && fgMaxY > fgMinY) {
        minX = fgMinX; maxX = fgMaxX; minY = fgMinY; maxY = fgMaxY;
      }

      // Scanline polygon for the neon preview outline (approximate — exact crop uses the mask above)
      const PSTEP = Math.max(1, Math.round((maxY - minY) / 120));
      const leftEdgePts = [], rightEdgePts = [];
      for (let y = minY; y <= maxY; y += PSTEP) {
        let lx = -1, rx = -1;
        for (let x = minX; x <= maxX; x++) if (isFgPx(x, y)) { lx = x; break; }
        for (let x = maxX; x >= minX; x--) if (isFgPx(x, y)) { rx = x; break; }
        if (lx >= 0 && rx >= 0 && rx >= lx) { leftEdgePts.push({ x: lx, y }); rightEdgePts.push({ x: rx, y }); }
      }
      const polyImgPts = [...rightEdgePts, ...leftEdgePts.slice().reverse()];
      const canvasPoly = polyImgPts.map(pt => ({
        x: imgLeft + Math.max(0, Math.min(sw, pt.x)) / sw * rendW,
        y: imgTop  + Math.max(0, Math.min(sh, pt.y)) / sh * rendH,
      }));
      // ─────────────────────────────────────────────────────────────────────

      // Bounding box for crop coords (with 1% padding)
      const padX = Math.max(2, (maxX - minX) * 0.01);
      const padY = Math.max(2, (maxY - minY) * 0.01);
      const startX = Math.max(0, imgLeft + ((minX - padX) / sw) * rendW);
      const startY = Math.max(0, imgTop  + ((minY - padY) / sh) * rendH);
      const endX   = Math.min(rect.width,  imgLeft + ((maxX + padX) / sw) * rendW);
      const endY   = Math.min(rect.height, imgTop  + ((maxY + padY) / sh) * rendH);
      smartDetectedRef.current = canvasPoly.length > 4;
      setSmartDetected(canvasPoly.length > 4);
      setSelectionPath(canvasPoly.length > 4 ? canvasPoly : []);
      setSelectionStart({ x: startX, y: startY });
      setSelectionEnd  ({ x: endX,   y: endY   });
      setIsSelecting(true);
    } catch (err) {
      console.error('Smart crop detection failed:', err);
      smartDetectedRef.current = false;
      setSmartDetected(false);
    } finally {
      setSmartCropRunning(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

  // Pre-load stamp images referenced in shapes (for canvas export)
  const preloadStampShapes = async (shapes) => {
    const urls = [...new Set(shapes.filter(s => s.type === 'emoji' && s.emoji && s.emoji.startsWith('http')).map(s => s.emoji))];
    await Promise.all(urls.map(url => new Promise(resolve => {
      if (stampImageCacheRef.current.has(url)) return resolve();
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { stampImageCacheRef.current.set(url, img); resolve(); };
      img.onerror = resolve;
      img.src = url;
    })));
  };

  // Call backend -> Kling AI to remove background from a canvas element
  // Returns a data URL (PNG with transparent bg) or null on failure
  const removeImageBg = async (canvas) => {
    // Resize to max 800px to keep payload small, then encode as JPEG
    const MAX = 800;
    let src = canvas;
    if (canvas.width > MAX || canvas.height > MAX) {
      const scale = Math.min(MAX / canvas.width, MAX / canvas.height);
      const resized = document.createElement('canvas');
      resized.width  = Math.round(canvas.width  * scale);
      resized.height = Math.round(canvas.height * scale);
      const rCtx2 = resized.getContext('2d');
      rCtx2.fillStyle = '#cccccc'; // prevent transparent areas becoming black in JPEG
      rCtx2.fillRect(0, 0, resized.width, resized.height);
      rCtx2.drawImage(canvas, 0, 0, resized.width, resized.height);
      src = resized;
    }
    const base64 = src.toDataURL('image/jpeg', 0.95);
    const backendUrl = (!Capacitor.isNativePlatform() && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
      ? (import.meta.env.VITE_BACKEND_URL_LOCAL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:3002')
      : (import.meta.env.VITE_BACKEND_URL || window.location.origin);
    const res = await fetch(`${backendUrl}/remove-bg`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ imageBase64: base64 }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) throw new Error(data.error || `Remove BG failed (${res.status})`);
    return data.imageBase64; // data URL: "data:image/png;base64,..."
  };

  // Upload current canvas as a shared stamp
  const _doSaveStamp = async (isHologram = false) => {
    if (drawingPaths.length === 0 && canvasShapes.length === 0 && canvasImages.length === 0) {
      showAlert('Add something to the canvas first, then save it as a stamp.');
      return;
    }
    setStampUploading(true);
    try {
      const cEl = canvasRef.current;
      if (!cEl) { setStampUploading(false); return; }
      const cW = cEl.offsetWidth;
      const cH = cEl.offsetHeight;

      // --- Step 1: composite all visible layers onto a transparent offscreen canvas ---
      const fullCanvas = document.createElement('canvas');
      fullCanvas.width  = cW;
      fullCanvas.height = cH;
      const fullCtx = fullCanvas.getContext('2d', { willReadFrequently: true });

      // Draw canvas images (bottom layer) -- use loadImgSafe to avoid canvas taint
      for (const img of canvasImages) {
        const el = await loadImgSafe(img.url);
        if (el) {
          const left = (img.x / 100) * cW;
          const top  = (img.y / 100) * cH;
          const wPx  = (img.width  / 21)   * cW;
          const hPx  = (img.height / 29.7) * cH;
          const cx   = left + wPx / 2;
          const cy   = top  + hPx / 2;
          fullCtx.save();
          fullCtx.translate(cx, cy);
          fullCtx.rotate(((img.rotation || 0) * Math.PI) / 180);
          if (img.cropShape === 'circle') {
            fullCtx.beginPath();
            fullCtx.ellipse(0, 0, wPx / 2, hPx / 2, 0, 0, Math.PI * 2);
            fullCtx.clip();
          }
          fullCtx.drawImage(el, -wPx / 2, -hPx / 2, wPx, hPx);
          fullCtx.restore();
        }
      }

      // Draw hand-drawn strokes on top (live drawing canvas, same-origin, no CORS issues)
      const drawingEl = drawingCanvasRef.current;
      if (drawingEl && drawingEl.width > 0 && drawingEl.height > 0) {
        fullCtx.drawImage(drawingEl, 0, 0, cW, cH);
      }

      // Draw shapes on top (text, lines, etc. -- skip emoji stamps which live in their own HTML layer)
      await preloadStampShapes(canvasShapes);
      drawShapesOnCanvas(fullCtx, canvasShapes, cW, cH);

      // --- Step 2: find bounding box of non-transparent pixels ---
      // If scan fails (all transparent due to canvas security), fall back to full canvas
      let minX = 0, minY = 0, maxX = cW, maxY = cH;
      try {
        const imgData = fullCtx.getImageData(0, 0, cW, cH);
        const px = imgData.data;
        let bMinX = cW, bMinY = cH, bMaxX = 0, bMaxY = 0;
        for (let y = 0; y < cH; y++) {
          for (let x = 0; x < cW; x++) {
            if (px[(y * cW + x) * 4 + 3] > 8) {
              if (x < bMinX) bMinX = x;
              if (x > bMaxX) bMaxX = x;
              if (y < bMinY) bMinY = y;
              if (y > bMaxY) bMaxY = y;
            }
          }
        }
        if (bMaxX > bMinX && bMaxY > bMinY) {
          // Use detected bounds with padding
          const PAD = 10;
          minX = Math.max(0, bMinX - PAD);
          minY = Math.max(0, bMinY - PAD);
          maxX = Math.min(cW - 1, bMaxX + PAD);
          maxY = Math.min(cH - 1, bMaxY + PAD);
        }
      } catch (_) { /* cross-origin / security error -- use full canvas */ }
      const contentW = maxX - minX;
      const contentH = maxY - minY;

      // --- Step 3: scale cropped content into 5x5 cm @ 150 DPI = 295x295 px ---
      const TARGET = 295;
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width  = TARGET;
      finalCanvas.height = TARGET;
      const finalCtx = finalCanvas.getContext('2d');
      // Transparent background
      const fitScale = Math.min(TARGET / contentW, TARGET / contentH);
      const drawW = contentW * fitScale;
      const drawH = contentH * fitScale;
      const offsetX = (TARGET - drawW) / 2;
      const offsetY = (TARGET - drawH) / 2;
      finalCtx.drawImage(fullCanvas, minX, minY, contentW, contentH, offsetX, offsetY, drawW, drawH);

      // --- Step 4: upload ---
      const blob = await new Promise(resolve => finalCanvas.toBlob(resolve, 'image/png'));
      const auth = getAuth();
      const uid = auth.currentUser?.uid || 'anon';
      const { storage, db } = getFirebase();
      const sRef = stampStorageRef(storage, `studioStamps/${uid}_${Date.now()}.png`);
      await uploadBytes(sRef, blob);
      const downloadUrl = await getDownloadURL(sRef);
      const docRef = await addDoc(collection(db, 'studioStamps'), {
        url: downloadUrl,
        createdBy: uid,
        createdAt: serverTimestamp(),
        ...(isHologram ? { isHologram: true } : {}),
      });
      // Immediately add to local state so it appears without waiting for Firestore push
      const newStamp = { id: docRef.id, url: downloadUrl, createdBy: uid, ...(isHologram ? { isHologram: true } : {}) };
      const newImg = new Image();
      newImg.crossOrigin = 'anonymous';
      newImg.onload = () => stampImageCacheRef.current.set(downloadUrl, newImg);
      newImg.src = downloadUrl;
      setStudioStamps(prev => [...prev, newStamp]);
      setSelectedEmoji(downloadUrl);
    } catch (err) {
      console.error('Stamp upload failed:', err);
      showAlert('Failed to save stamp: ' + (err?.message || String(err)));
    } finally {
      setStampUploading(false);
    }
  };
  const handleUploadStamp = () => _doSaveStamp(false);
  const handleSaveHologramStamp = () => _doSaveStamp(true);

  // Same as handleUploadStamp but runs background removal via Kling AI before saving
  const handleUploadStampRemoveBg = async () => {
    if (drawingPaths.length === 0 && canvasShapes.length === 0 && canvasImages.length === 0) {
      showAlert('Add something to the canvas first.');
      return;
    }
    setStampRemoving(true);
    try {
      const cEl = canvasRef.current;
      if (!cEl) { setStampRemoving(false); return; }
      const cW = cEl.offsetWidth;
      const cH = cEl.offsetHeight;

      // Build composite canvas (same logic as handleUploadStamp)
      const fullCanvas = document.createElement('canvas');
      fullCanvas.width  = cW;
      fullCanvas.height = cH;
      const fullCtx = fullCanvas.getContext('2d', { willReadFrequently: true });

      for (const img of canvasImages) {
        const el = await loadImgSafe(img.url);
        if (el) {
          const left = (img.x / 100) * cW;
          const top  = (img.y / 100) * cH;
          const wPx  = (img.width  / 21)   * cW;
          const hPx  = (img.height / 29.7) * cH;
          const cx   = left + wPx / 2;
          const cy   = top  + hPx / 2;
          fullCtx.save();
          fullCtx.translate(cx, cy);
          fullCtx.rotate(((img.rotation || 0) * Math.PI) / 180);
          if (img.cropShape === 'circle') {
            fullCtx.beginPath();
            fullCtx.ellipse(0, 0, wPx / 2, hPx / 2, 0, 0, Math.PI * 2);
            fullCtx.clip();
          }
          fullCtx.drawImage(el, -wPx / 2, -hPx / 2, wPx, hPx);
          fullCtx.restore();
        }
      }
      const drawingEl = drawingCanvasRef.current;
      if (drawingEl && drawingEl.width > 0) fullCtx.drawImage(drawingEl, 0, 0, cW, cH);
      await preloadStampShapes(canvasShapes);
      drawShapesOnCanvas(fullCtx, canvasShapes, cW, cH);

      // Send composite to Kling for background removal
      const bgRemovedDataUrl = await removeImageBg(fullCanvas);

      // Load result and crop to content
      const resultImg = await new Promise((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = reject;
        el.src = bgRemovedDataUrl;
      });
      const resultCanvas = document.createElement('canvas');
      resultCanvas.width  = resultImg.naturalWidth  || cW;
      resultCanvas.height = resultImg.naturalHeight || cH;
      const rCtx = resultCanvas.getContext('2d', { willReadFrequently: true });
      rCtx.drawImage(resultImg, 0, 0);

      // Bounding box crop
      const rW = resultCanvas.width, rH = resultCanvas.height;
      let minX = 0, minY = 0, maxX = rW, maxY = rH;
      try {
        const px = rCtx.getImageData(0, 0, rW, rH).data;
        let bMinX = rW, bMinY = rH, bMaxX = 0, bMaxY = 0;
        for (let y = 0; y < rH; y++) for (let x = 0; x < rW; x++) {
          if (px[(y * rW + x) * 4 + 3] > 8) {
            if (x < bMinX) bMinX = x; if (x > bMaxX) bMaxX = x;
            if (y < bMinY) bMinY = y; if (y > bMaxY) bMaxY = y;
          }
        }
        if (bMaxX > bMinX && bMaxY > bMinY) {
          const PAD = 6;
          minX = Math.max(0, bMinX - PAD); minY = Math.max(0, bMinY - PAD);
          maxX = Math.min(rW - 1, bMaxX + PAD); maxY = Math.min(rH - 1, bMaxY + PAD);
        }
      } catch (_) {}
      const contentW = maxX - minX, contentH = maxY - minY;
      const TARGET = 295;
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = TARGET; finalCanvas.height = TARGET;
      const finalCtx = finalCanvas.getContext('2d');
      const fitScale = Math.min(TARGET / contentW, TARGET / contentH);
      const drawW = contentW * fitScale, drawH = contentH * fitScale;
      finalCtx.drawImage(resultCanvas, minX, minY, contentW, contentH,
        (TARGET - drawW) / 2, (TARGET - drawH) / 2, drawW, drawH);

      const blob = await new Promise(resolve => finalCanvas.toBlob(resolve, 'image/png'));
      const auth = getAuth();
      const uid  = auth.currentUser?.uid || 'anon';
      const { storage, db } = getFirebase();
      const sRef = stampStorageRef(storage, `studioStamps/${uid}_${Date.now()}.png`);
      await uploadBytes(sRef, blob);
      const downloadUrl = await getDownloadURL(sRef);
      const docRef = await addDoc(collection(db, 'studioStamps'), {
        url: downloadUrl, createdBy: uid, createdAt: serverTimestamp(),
      });
      const newStamp = { id: docRef.id, url: downloadUrl, createdBy: uid };
      const newImg2 = new Image();
      newImg2.crossOrigin = 'anonymous';
      newImg2.onload = () => stampImageCacheRef.current.set(downloadUrl, newImg2);
      newImg2.src = downloadUrl;
      setStudioStamps(prev => [...prev, newStamp]);
      setSelectedEmoji(downloadUrl);
    } catch (err) {
      console.error('Remove BG stamp failed:', err);
      showAlert('Background removal failed. Try "Save as stamp" instead.');
    } finally {
      setStampRemoving(false);
    }
  };

  const handleDeleteStamps = async () => {
    if (selectedStampIds.size === 0) return;
    setStampUploading(true);
    try {
      const { db, storage } = getFirebase();
      const stampsToDelete = studioStamps.filter(s => selectedStampIds.has(s.id));
      // Delete Firestore docs and Storage files in parallel
      await Promise.all([
        ...stampsToDelete.map(s => deleteDoc(doc(db, 'studioStamps', s.id))),
        ...stampsToDelete.map(s => {
          try {
            const fileRef = stampStorageRef(storage, s.url);
            return deleteObject(fileRef).catch(() => {}); // ignore if already gone
          } catch { return Promise.resolve(); }
        }),
      ]);
      const deletedUrls = new Set(stampsToDelete.map(s => s.url));
      if (deletedUrls.has(selectedEmoji)) setSelectedEmoji(null);
      setStudioStamps(prev => prev.filter(s => !selectedStampIds.has(s.id)));
      // Also remove any canvas shapes that reference the deleted stamp URLs
      setCanvasShapes(prev => prev.filter(s => !(s.type === 'emoji' && deletedUrls.has(s.emoji))));
      setSelectedStampIds(new Set());
    } catch (err) {
      console.error('Delete stamp failed:', err);
      showAlert('Failed to delete stamp(s).');
    } finally {
      setStampUploading(false);
    }
  };

  // Mark exactly one selected stamp as the hologram; clear hologram flag on any previous one
  const handleMarkAsHologram = async () => {
    if (selectedStampIds.size !== 1) return;
    const [targetId] = [...selectedStampIds];
    setStampUploading(true);
    try {
      const { db } = getFirebase();
      const { updateDoc } = await import('firebase/firestore');
      const prevHologram = studioStamps.filter(s => s.isHologram && s.id !== targetId);
      await Promise.all([
        ...prevHologram.map(s => updateDoc(doc(db, 'studioStamps', s.id), { isHologram: false })),
        updateDoc(doc(db, 'studioStamps', targetId), { isHologram: true }),
      ]);
      setStudioStamps(prev => prev.map(s => {
        if (s.id === targetId) return { ...s, isHologram: true };
        if (s.isHologram) return { ...s, isHologram: false };
        return s;
      }));
      setSelectedStampIds(new Set());
    } catch (err) {
      console.error('Mark as hologram failed:', err);
      showAlert('Failed to mark as hologram: ' + err.message);
    } finally {
      setStampUploading(false);
    }
  };

  // -- Hologram stamp: bake the designated hologram stamp into the selected/first base image --
  const handleHologramStamp = async () => {
    const hologramStamp = studioStamps.find(s => s.isHologram);
    if (!hologramStamp) {
      showAlert('No hologram stamp set. Select a stamp in the panel and mark it as hologram first.');
      return;
    }
    const targetImg = canvasImagesRef.current[0];
    if (!targetImg) {
      showAlert('Add an image to the canvas first to embed the hologram stamp.');
      return;
    }
    setIsSaving(true);
    try {
      const baseEl = await loadImgSafe(targetImg.url);
      if (!baseEl) { showAlert('Could not load base image.'); return; }
      const imgNW = baseEl.naturalWidth  || baseEl.width  || 512;
      const imgNH = baseEl.naturalHeight || baseEl.height || 512;

      const stampEl = stampImageCacheRef.current.get(hologramStamp.url)
        || await loadImgSafe(hologramStamp.url);
      if (!stampEl) { showAlert('Could not load stamp image.'); return; }

      const off = document.createElement('canvas');
      off.width  = imgNW;
      off.height = imgNH;
      const ctx = off.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(baseEl, 0, 0, imgNW, imgNH);

      // -- Simple hologram stamp -- top-left corner --------------------------
      const S      = Math.min(imgNW, imgNH);
      const stampS = Math.round(S * 0.14);   // 14 % of shorter dim -- subtle watermark size
      const margin = Math.round(S * 0.03);   // 3 % margin from edges
      const icx    = margin + stampS / 2;
      const icy    = margin + stampS / 2;

      ctx.save();
      ctx.beginPath();
      ctx.arc(icx, icy, stampS / 2, 0, Math.PI * 2);
      ctx.clip();
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 0.38;  // low opacity -- shady watermark, not distracting from the image
      ctx.drawImage(stampEl, icx - stampS / 2, icy - stampS / 2, stampS, stampS);
      ctx.restore();

      // Save result as lossless PNG to avoid quality degradation on subsequent saves
      const result = off.toDataURL('image/png');
      const before = canvasImagesRef.current;
      const after  = before.map(i => i.id === targetImg.id ? { ...i, url: result } : i);
      setCanvasImages(after);
      canvasImagesRef.current = after;
      pushImageHistory(before, after);

    } catch (err) {
      console.error('Hologram stamp failed:', err);
      showAlert('Failed to embed hologram: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Update canvas cursor based on active tool
  useEffect(() => {
    if (imageSelectionMode) {
      setCanvasCursor('pointer');
    } else if (activeTool === 'draw') {
      setCanvasCursor((drawingMode === 'pen' || drawingMode === 'pencil') ? 'crosshair' : 'cell');
    } else if (activeTool === 'selection' && selectionMode) {
      setCanvasCursor('crosshair');
    } else if (activeTool === 'resize') {
      setCanvasCursor('default');
    } else {
      setCanvasCursor('default');
    }
  }, [activeTool, drawingMode, selectionMode, imageSelectionMode]);

  useEffect(() => {
    if (activeTool !== 'selection') {
      setIsSelecting(false);
      setSelectionPath([]);
    }
    
    if (activeTool !== 'selection' && activeTool !== 'draw') {
      setCanvasCursor('default');
    }
  }, [activeTool]);

  // Update aspect ratio when image is selected
  useEffect(() => {
    if (selectedImage) {
      const image = canvasImages.find(img => img.id === selectedImage);
      if (image && image.naturalWidth && image.naturalHeight) {
        setAspectRatio(image.naturalWidth / image.naturalHeight);
      }
    }
  }, [selectedImage, canvasImages]);

  // Reset on component mount
  useEffect(() => {
    resetStudio();
  }, []);

  const resetStudio = () => {
    setCanvasImages([]);
    setSelectedImage(null);
    setResizeWidth(10);
    setResizeHeight(10);
    setShowImageControls(null);
    setActiveTool(null);
    setDrawingPaths([]);
    setCurrentPath([]);
    setDrawingColor('#000000');
    setBrushSize(5);
    setDrawingHistory([]);
    setHistoryIndex(-1);
    setShowColorPicker(false);
    setCanvasCursor('default');
    setSelectedBucketImages([]);
    setSelectionMode('rectangle');
    setIsSelecting(false);
    setSelectionStart({ x: 0, y: 0 });
    setSelectionEnd({ x: 0, y: 0 });
    setSelectionPath([]);
    setShowTopControls(false);
    setCurrentControlSection(null);
    setLockAspectRatio(false);
    setAspectRatio(1);
    setImageSelectionMode(false); // Reset image selection mode
    setCanvasShapes([]);
    setSelectedShapeId(null);
    setActiveShapeTool(null);
    setShowShapesPanel(false);
    setCanvasBackground('transparent');
    draggingShapeRef.current = null;
    resizingShapeRef.current = null;
    shapeBeforeRef.current = null;
  };

  const pushShapeHistory = (before, after) => {
    markDirty();
    const newHistory = drawingHistoryRef.current.slice(0, historyIndexRef.current + 1);
    newHistory.push({ type: 'shapes', before, after });
    setDrawingHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  // Draw shapes onto an offscreen canvas (used by save/print/share)
  const drawShapesOnCanvas = (ctx, shapes, renderW, renderH, offXpct = 0, offYpct = 0, contentWpct = 100, contentHpct = 100) => {
    const toX = p => ((p - offXpct) / contentWpct) * renderW;
    const toY = p => ((p - offYpct) / contentHpct) * renderH;
    const scaleW = renderW / (500 * contentWpct / 100);
    shapes.forEach(shape => {
      const x1 = toX(shape.x1), y1 = toY(shape.y1);
      const x2 = toX(shape.x2), y2 = toY(shape.y2);
      const stroke = shape.strokeColor || '#000000';
      const fill = shape.fillColor || 'none';
      const sw = Math.max(0.5, (shape.strokeWidth || 2) * scaleW);
      ctx.save();
      ctx.strokeStyle = stroke;
      ctx.lineWidth = sw;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.setLineDash(shape.dashed ? [6, 3] : []);
      switch (shape.type) {
        case 'line':
          ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
          break;
        case 'arrow': {
          const ang = Math.atan2(y2 - y1, x2 - x1);
          const aLen = Math.max(10, sw * 5);
          ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(x2, y2);
          ctx.lineTo(x2 - aLen * Math.cos(ang - 0.45), y2 - aLen * Math.sin(ang - 0.45));
          ctx.lineTo(x2 - aLen * Math.cos(ang + 0.45), y2 - aLen * Math.sin(ang + 0.45));
          ctx.closePath(); ctx.fillStyle = stroke; ctx.fill();
          break;
        }
        case 'circle': {
          const cx = (x1 + x2) / 2, cy = (y1 + y2) / 2;
          const rx = Math.abs(x2 - x1) / 2, ry = Math.abs(y2 - y1) / 2;
          ctx.beginPath();
          ctx.ellipse(cx, cy, Math.max(1, rx), Math.max(1, ry), 0, 0, Math.PI * 2);
          if (fill !== 'none') { ctx.fillStyle = fill; ctx.fill(); }
          ctx.stroke();
          break;
        }
        case 'triangle': {
          const tcx = (x1 + x2) / 2;
          ctx.beginPath();
          ctx.moveTo(tcx, Math.min(y1, y2));
          ctx.lineTo(Math.min(x1, x2), Math.max(y1, y2));
          ctx.lineTo(Math.max(x1, x2), Math.max(y1, y2));
          ctx.closePath();
          if (fill !== 'none') { ctx.fillStyle = fill; ctx.fill(); }
          ctx.stroke();
          break;
        }
        case 'rectangle': {
          ctx.beginPath();
          ctx.rect(Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1));
          if (fill !== 'none') { ctx.fillStyle = fill; ctx.fill(); }
          ctx.stroke();
          break;
        }
        case 'text': {
          const fs = Math.max(8, (shape.fontSize || 12) * scaleW);
          ctx.font = `${shape.italic ? 'italic ' : ''}${shape.bold ? 'bold ' : ''}${fs}px ${shape.fontFamily || 'Arial'}`;
          ctx.fillStyle = stroke;
          ctx.textBaseline = 'top';
          ctx.fillText(shape.text || '', Math.min(x1,x2), Math.min(y1,y2), Math.abs(x2-x1) || undefined);
          break;
        }
        case 'emoji': {
          const cachedImg = stampImageCacheRef.current.get(shape.emoji);
          if (cachedImg) {
            ctx.drawImage(cachedImg, Math.min(x1,x2), Math.min(y1,y2), Math.abs(x2-x1), Math.abs(y2-y1));
          }
          break;
        }
        default: break;
      }
      ctx.restore();
    });
  };

  // Handle ending selection and crop image.
  // mode = 'copy' (default): keep originals, add a new layer with the selected pixels.
  // mode = 'cut'           : add the new layer AND erase the selected area from the
  //                          underlying image(s) (clipboard-style cut).
  const endSelection = async (mode = 'copy') => {
    if ((!isSelecting && !(selectionMode === 'freeform' && selectionPath.length >= 3)) || !selectionMode || (canvasImages.length === 0 && drawingPaths.length === 0 && canvasShapes.length === 0)) {
      setIsSelecting(false);
      setSelectionPath([]);
      return;
    }

    try {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();

      // Get selection bounds
      let minX, minY, maxX, maxY;

      if (selectionMode === 'rectangle' || selectionMode === 'circle' || selectionMode === 'smart') {
        if (!selectionStart.x || !selectionStart.y || !selectionEnd.x || !selectionEnd.y) {
          setIsSelecting(false);
          setSelectionPath([]);
          return;
        }
        minX = Math.min(selectionStart.x, selectionEnd.x);
        minY = Math.min(selectionStart.y, selectionEnd.y);
        maxX = Math.max(selectionStart.x, selectionEnd.x);
        maxY = Math.max(selectionStart.y, selectionEnd.y);
      } else if (selectionMode === 'freeform') {
        if (selectionPath.length < 3) {
          setIsSelecting(false);
          setSelectionPath([]);
          return;
        }
        minX = Math.min(...selectionPath.map(p => p.x));
        minY = Math.min(...selectionPath.map(p => p.y));
        maxX = Math.max(...selectionPath.map(p => p.x));
        maxY = Math.max(...selectionPath.map(p => p.y));
      } else {
        setIsSelecting(false);
        setSelectionPath([]);
        return;
      }

      const width  = maxX - minX;
      const height = maxY - minY;

      if (width < 10 || height < 10) {
        showAlert('Selection too small. Please select a larger area.');
        setIsSelecting(false);
        setSelectionPath([]);
        return;
      }

      // --- Render the full canvas content to an offscreen canvas at high resolution ---
      // Render at 150 DPI sized to the CURRENT paper (A4/A5/A6 portrait or landscape)
      // so the crop captures full image detail and the offCanvas exactly matches the
      // on-screen canvas proportions (no stretched / cropped bottom rows).
      const CROP_DPI = 150;
      const _ps = PRINT_SIZES[printSize];
      const hiResW = Math.round(_ps.width  * (CROP_DPI / 2.54));
      const hiResH = Math.round(_ps.height * (CROP_DPI / 2.54));
      // Per-axis scale factors — using the X factor for Y produces a 1-px-or-more
      // overshoot at the bottom (because rect.height is rounded), making the crop
      // sample past the offCanvas edge → transparent rows → visible as white space.
      const hiResScaleX = hiResW / rect.width;
      const hiResScaleY = hiResH / rect.height;

      const offCanvas = document.createElement('canvas');
      offCanvas.width  = hiResW;
      offCanvas.height = hiResH;
      const offCtx = offCanvas.getContext('2d');
      offCtx.imageSmoothingEnabled = true;
      offCtx.imageSmoothingQuality = 'high';

      // IMPORTANT: do NOT fill a background colour. Keeping the offscreen canvas
      // transparent means the crop only captures actual image/drawing/shape pixels.
      // When the cropped piece is placed back on a same-coloured canvas, the live
      // canvas shows through transparent areas — no baked-in white rectangle that
      // would otherwise look "raised" against the live background.

      // Draw all images at hi-res in layer order (ascending layerIndex)
      const sortedCropImages = [...canvasImages].sort((a, b) => (a.layerIndex ?? 1) - (b.layerIndex ?? 1));
      for (const img of sortedCropImages) {
        const image = await loadImgSafe(img.url);
        if (!image) continue;
        const imgXPx      = (img.x      / 100)  * hiResW;
        const imgYPx      = (img.y      / 100)  * hiResH;
        const imgWidthPx  = (img.width  / 21)   * hiResW;
        const imgHeightPx = (img.height / 29.7) * hiResH;
        const rot = ((img.rotation || 0) * Math.PI) / 180;
        if (rot) {
          const cx = imgXPx + imgWidthPx / 2;
          const cy = imgYPx + imgHeightPx / 2;
          offCtx.save();
          offCtx.translate(cx, cy);
          offCtx.rotate(rot);
          if (img.cropShape === 'circle') {
            offCtx.beginPath();
            offCtx.ellipse(0, 0, imgWidthPx / 2, imgHeightPx / 2, 0, 0, Math.PI * 2);
            offCtx.clip();
          }
          offCtx.drawImage(image, -imgWidthPx / 2, -imgHeightPx / 2, imgWidthPx, imgHeightPx);
          offCtx.restore();
        } else if (img.cropShape === 'circle') {
          offCtx.save();
          offCtx.beginPath();
          offCtx.ellipse(imgXPx + imgWidthPx / 2, imgYPx + imgHeightPx / 2, imgWidthPx / 2, imgHeightPx / 2, 0, 0, Math.PI * 2);
          offCtx.clip();
          offCtx.drawImage(image, imgXPx, imgYPx, imgWidthPx, imgHeightPx);
          offCtx.restore();
        } else {
          offCtx.drawImage(image, imgXPx, imgYPx, imgWidthPx, imgHeightPx);
        }
      }

      // Draw drawing paths scaled to hi-res
      drawingPaths.forEach(path => {
        const pCanvasW = path.canvasW || 800;
        const pCanvasH = path.canvasH || (800 * A4_RATIO);
        const scaleX = hiResW / pCanvasW;
        const scaleY = hiResH / pCanvasH;
        renderDrawingPath(offCtx, path, scaleX, scaleY);
      });

      // Draw shapes at hi-res
      if (canvasShapes.length > 0) {
        drawShapesOnCanvas(offCtx, canvasShapes, hiResW, hiResH);
      }

      // --- Extract the selected region into a crop canvas (at hi-res coords) ---
      // Clamp to offCanvas bounds so the source rectangle never falls past the edge
      // (fixes "image becomes smaller / white space at bottom" for crops touching
      //  the canvas border).
      const cropX = Math.max(0, Math.round(minX * hiResScaleX));
      const cropY = Math.max(0, Math.round(minY * hiResScaleY));
      const cropW = Math.min(hiResW - cropX, Math.round(width  * hiResScaleX));
      const cropH = Math.min(hiResH - cropY, Math.round(height * hiResScaleY));

      const cropCanvas = document.createElement('canvas');
      cropCanvas.width  = cropW;
      cropCanvas.height = cropH;
      const cropCtx = cropCanvas.getContext('2d');
      cropCtx.imageSmoothingEnabled = true;
      cropCtx.imageSmoothingQuality = 'high';

      if (selectionMode === 'circle') {
        cropCtx.save();
        cropCtx.beginPath();
        cropCtx.ellipse(cropW / 2, cropH / 2, cropW / 2, cropH / 2, 0, 0, Math.PI * 2);
        cropCtx.clip();
      }
      if (selectionMode === 'freeform' && selectionPath.length >= 3) {
        cropCtx.save();
        cropCtx.beginPath();
        const scaledPath = selectionPath.map(pt => ({
          x: (pt.x - minX) * hiResScaleX,
          y: (pt.y - minY) * hiResScaleY,
        }));
        cropCtx.moveTo(scaledPath[0].x, scaledPath[0].y);
        for (let k = 1; k < scaledPath.length; k++) cropCtx.lineTo(scaledPath[k].x, scaledPath[k].y);
        cropCtx.closePath();
        cropCtx.clip();
      }
      // Note: smart crop uses pixel-level mask (destination-in) below instead of a polygon clip.
      // A polygon clip only captures left/right extremes per row and incorrectly includes
      // concave interior gaps (e.g. armpit area between arm and body).

      cropCtx.drawImage(offCanvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

      if (selectionMode === 'circle' || selectionMode === 'freeform') {
        cropCtx.restore();
      }

      // Smart crop: apply pixel-level foreground mask so only the detected object pixels
      // remain (transparent elsewhere). This handles concave shapes correctly.
      if (selectionMode === 'smart' && smartCropMaskRef.current) {
        const { maskCanvas: mc, imgLeft: il, imgTop: it, rendW: rw, rendH: rh } = smartCropMaskRef.current;
        const mX = (il - minX) * hiResScaleX;
        const mY = (it - minY) * hiResScaleY;
        const mW = rw * hiResScaleX;
        const mH = rh * hiResScaleY;
        cropCtx.globalCompositeOperation = 'destination-in';
        cropCtx.drawImage(mc, mX, mY, mW, mH);
        cropCtx.globalCompositeOperation = 'source-over';
      }

      const croppedDataUrl = cropCanvas.toDataURL('image/png');

      const newImage = {
        id: Date.now(),
        layerIndex: ++layerCounterRef.current,
        url: croppedDataUrl,
        name: 'canvas-crop',
        size: 'Canvas crop',
        x: (minX / rect.width)  * 100,
        y: (minY / rect.height) * 100,
        width:  (width  / rect.width)  * 21,
        height: (height / rect.height) * 29.7,
        source: 'crop',
        cropShape: selectionMode === 'circle' ? 'circle' : selectionMode === 'freeform' ? 'freeform' : undefined,
        naturalWidth:  cropW,
        naturalHeight: cropH,
      };

      // For 'cut' mode: also erase the selected area from each underlying image so
      // the new piece behaves like a clipboard cut (the source has a transparent hole).
      // The new piece itself is still added on top.
      let baseImages = canvasImagesRef.current;
      if (mode === 'cut') {
        // Build a polygon (or use the smart mask) representing the selection in canvas-CSS coords.
        let canvasPolygon = null;
        let smartTargetId = null;
        if (selectionMode === 'rectangle') {
          canvasPolygon = [
            { x: minX, y: minY }, { x: maxX, y: minY },
            { x: maxX, y: maxY }, { x: minX, y: maxY },
          ];
        } else if (selectionMode === 'circle') {
          const ccx = (selectionStart.x + selectionEnd.x) / 2;
          const ccy = (selectionStart.y + selectionEnd.y) / 2;
          const crx = Math.abs(selectionEnd.x - selectionStart.x) / 2;
          const cry = Math.abs(selectionEnd.y - selectionStart.y) / 2;
          const N = 64;
          canvasPolygon = [];
          for (let i = 0; i < N; i++) {
            const a = (i / N) * Math.PI * 2;
            canvasPolygon.push({ x: ccx + crx * Math.cos(a), y: ccy + cry * Math.sin(a) });
          }
        } else if (selectionMode === 'freeform' && selectionPath.length >= 3) {
          canvasPolygon = selectionPath;
        } else if (selectionMode === 'smart' && smartCropMaskRef.current) {
          smartTargetId = smartCropMaskRef.current.targetImgId;
        }

        // Polygon bbox for cheap intersection test
        let pbMinX = Infinity, pbMinY = Infinity, pbMaxX = -Infinity, pbMaxY = -Infinity;
        if (canvasPolygon) {
          for (const p of canvasPolygon) {
            if (p.x < pbMinX) pbMinX = p.x;
            if (p.x > pbMaxX) pbMaxX = p.x;
            if (p.y < pbMinY) pbMinY = p.y;
            if (p.y > pbMaxY) pbMaxY = p.y;
          }
        }

        const updates = new Map(); // id -> new url
        const smartMask = smartCropMaskRef.current; // capture before reset
        for (const img of baseImages) {
          if (smartTargetId != null && img.id !== smartTargetId) continue;

          const imgLeftPx   = (img.x      / 100)  * rect.width;
          const imgTopPx    = (img.y      / 100)  * rect.height;
          const imgWidthPx  = (img.width  / 21)   * rect.width;
          const imgHeightPx = (img.height / 29.7) * rect.height;

          if (canvasPolygon) {
            if (pbMaxX < imgLeftPx || pbMinX > imgLeftPx + imgWidthPx ||
                pbMaxY < imgTopPx  || pbMinY > imgTopPx + imgHeightPx) continue;
          }

          const imgEl = await loadImgSafe(img.url);
          if (!imgEl) continue;
          const naturalW = imgEl.naturalWidth || imgEl.width;
          const naturalH = imgEl.naturalHeight || imgEl.height;
          if (!naturalW || !naturalH) continue;

          const oc = document.createElement('canvas');
          oc.width = naturalW;
          oc.height = naturalH;
          const ecx = oc.getContext('2d');
          ecx.imageSmoothingEnabled = true;
          ecx.imageSmoothingQuality = 'high';
          ecx.drawImage(imgEl, 0, 0);

          const rot = ((img.rotation || 0) * Math.PI) / 180;
          const sin = Math.sin(-rot), cos = Math.cos(-rot);
          const cxImg = imgLeftPx + imgWidthPx / 2;
          const cyImg = imgTopPx + imgHeightPx / 2;
          const sx = naturalW / imgWidthPx;
          const sy = naturalH / imgHeightPx;

          ecx.globalCompositeOperation = 'destination-out';

          if (selectionMode === 'smart' && smartMask) {
            const { maskCanvas, imgLeft: il, imgTop: it, rendW, rendH } = smartMask;
            ecx.save();
            ecx.translate(naturalW / 2, naturalH / 2);
            ecx.scale(sx, sy);
            ecx.rotate(-rot);
            ecx.translate(-cxImg, -cyImg);
            ecx.drawImage(maskCanvas, il, it, rendW, rendH);
            ecx.restore();
          } else if (canvasPolygon && canvasPolygon.length >= 3) {
            const localPoly = canvasPolygon.map((p) => {
              const dx = p.x - cxImg, dy = p.y - cyImg;
              const ux = dx * cos - dy * sin;
              const uy = dx * sin + dy * cos;
              const ix = ux + imgWidthPx / 2;
              const iy = uy + imgHeightPx / 2;
              return { x: ix * sx, y: iy * sy };
            });
            ecx.beginPath();
            ecx.moveTo(localPoly[0].x, localPoly[0].y);
            for (let i = 1; i < localPoly.length; i++) ecx.lineTo(localPoly[i].x, localPoly[i].y);
            ecx.closePath();
            ecx.fill();
          }

          ecx.globalCompositeOperation = 'source-over';
          updates.set(img.id, oc.toDataURL('image/png'));
        }

        if (updates.size > 0) {
          baseImages = baseImages.map(im => updates.has(im.id)
            ? { ...im, url: updates.get(im.id) }
            : im);
        }
      }

      const nextImages = [...baseImages, newImage];
      pushImageHistory(canvasImagesRef.current, nextImages);
      setCanvasImages(nextImages);
      setSelectedImage(newImage.id);
      setResizeWidth(newImage.width);
      setResizeHeight(newImage.height);

      setIsSelecting(false);
      setSelectionPath([]);
      setSelectionStart({ x: 0, y: 0 });
      setSelectionEnd({ x: 0, y: 0 });
      setSelectionMode('rectangle');
      smartDetectedRef.current = false;
      smartCropMaskRef.current = null;
      setSmartDetected(false);
      setActiveTool(null);
      setCurrentControlSection(null);
      setShowTopControls(false);
      setImageSelectionMode(false);
      setCanvasCursor('pointer');

    } catch (error) {
      console.error('Error during canvas crop:', error);
      showAlert('Failed to crop. Please try again.');
      setIsSelecting(false);
      setSelectionPath([]);
      setSelectionStart({ x: 0, y: 0 });
      setSelectionEnd({ x: 0, y: 0 });
      smartDetectedRef.current = false;
      smartCropMaskRef.current = null;
      setSmartDetected(false);
    }
  };

  // Clipboard-style cut: keep the selected piece as a new image AND erase it from the
  // underlying source(s). Implementation lives inside endSelection for state coherence.
  const cutSelection = () => endSelection('cut');

  // Clear canvas function
  const clearCanvas = () => {
    if (canvasImages.length > 0 || drawingPaths.length > 0 || canvasShapes.length > 0) {
        resetStudio();
    }
    else {
      showAlert('Canvas is already empty.');
    }
  };

  // Chat sharing
  const handleChatShare = async () => {
    if (canvasImages.length === 0 && drawingPaths.length === 0 && canvasShapes.length === 0) {
      showAlert('Please add images or drawings to the canvas first.');
      return;
    }
    
    setIsSaving(true);
    setSaveProgress(0);

    try {
      const imagesToRender = selectedImage
        ? canvasImages.filter(i => i.id === selectedImage)
        : canvasImages;
      const pathsToRender = selectedImage ? [] : drawingPaths;
      const shapesToRender = selectedImage ? [] : canvasShapes;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Calculate bounding box of all content
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      
      // Get image bounds
      imagesToRender.forEach(img => {
        const imgLeft = img.x;
        const imgRight = img.x + (img.width / 21 * 100);
        const imgTop = img.y;
        const imgBottom = img.y + (img.height / 29.7 * 100);
        
        minX = Math.min(minX, imgLeft);
        minY = Math.min(minY, imgTop);
        maxX = Math.max(maxX, imgRight);
        maxY = Math.max(maxY, imgBottom);
      });
      
      // Get drawing bounds (in percentage)
      pathsToRender.forEach(path => {
        const pCanvasW = path.canvasW || 800;
        const pCanvasH = path.canvasH || (800 * A4_RATIO);
        path.path.forEach(point => {
          const pointXPercent = (point.x / pCanvasW) * 100;
          const pointYPercent = (point.y / pCanvasH) * 100;
          
          minX = Math.min(minX, pointXPercent);
          minY = Math.min(minY, pointYPercent);
          maxX = Math.max(maxX, pointXPercent);
          maxY = Math.max(maxY, pointYPercent);
        });
      });
      
      // Only pad when there are multiple items ? prevents edge clipping.
      // Single-item saves are pixel-exact with no whitespace margin.
      // Include shape bounds
      shapesToRender.forEach(s => {
        minX = Math.min(minX, s.x1, s.x2);
        minY = Math.min(minY, s.y1, s.y2);
        maxX = Math.max(maxX, s.x1, s.x2);
        maxY = Math.max(maxY, s.y1, s.y2);
      });
      const hasMultipleItems = (imagesToRender.length + pathsToRender.length + shapesToRender.length) > 1;
      const isSingleCircle = imagesToRender.length === 1 && imagesToRender[0].cropShape === 'circle' && pathsToRender.length === 0 && shapesToRender.length === 0;
      const padding = hasMultipleItems ? 2 : 0;
      minX = Math.max(0, minX - padding);
      minY = Math.max(0, minY - padding);
      maxX = Math.min(100, maxX + padding);
      maxY = Math.min(100, maxY + padding);
      
      // Calculate content dimensions
      const contentWidth = maxX - minX;
      const contentHeight = maxY - minY;
      
      const dpi = 150;
      const widthInPx = contentWidth * (21 * (dpi / 2.54)) / 100;
      const heightInPx = contentHeight * (29.7 * (dpi / 2.54)) / 100;
      
      canvas.width = widthInPx;
      canvas.height = heightInPx;
      
      if (!isSingleCircle) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, widthInPx, heightInPx);
      }
      setSaveProgress(30);

      // Draw images
      await Promise.all(imagesToRender.map((img, index) => {
        return new Promise((resolve) => {
          const image = new Image();
          image.onload = () => {
            // Calculate image position relative to content bounds
            const imgXPx = ((img.x - minX) / contentWidth) * widthInPx;
            const imgYPx = ((img.y - minY) / contentHeight) * heightInPx;
            const imgWidthPx = (img.width / 21) * (widthInPx / contentWidth) * 100;
            const imgHeightPx = (img.height / 29.7) * (heightInPx / contentHeight) * 100;
            
            const rot = ((img.rotation || 0) * Math.PI) / 180;
            if (rot) {
              const cx = imgXPx + imgWidthPx / 2;
              const cy = imgYPx + imgHeightPx / 2;
              ctx.save();
              ctx.translate(cx, cy);
              ctx.rotate(rot);
              if (img.cropShape === 'circle') {
                ctx.beginPath();
                ctx.ellipse(0, 0, imgWidthPx / 2, imgHeightPx / 2, 0, 0, Math.PI * 2);
                ctx.clip();
              }
              ctx.drawImage(image, -imgWidthPx / 2, -imgHeightPx / 2, imgWidthPx, imgHeightPx);
              ctx.restore();
            } else if (img.cropShape === 'circle') {
              ctx.save();
              ctx.beginPath();
              ctx.ellipse(imgXPx + imgWidthPx / 2, imgYPx + imgHeightPx / 2, imgWidthPx / 2, imgHeightPx / 2, 0, 0, Math.PI * 2);
              ctx.clip();
              ctx.drawImage(image, imgXPx, imgYPx, imgWidthPx, imgHeightPx);
              ctx.restore();
            } else {
              ctx.drawImage(image, imgXPx, imgYPx, imgWidthPx, imgHeightPx);
            }
            setSaveProgress(30 + ((index + 1) / imagesToRender.length) * 60);
            resolve();
          };
          image.onerror = () => {
            console.error('Failed to load image for chat:', img.url);
            setSaveProgress(30 + ((index + 1) / imagesToRender.length) * 60);
            resolve();
          };
          
          if (img.url.startsWith('http') && !img.url.includes(location.hostname)) {
            image.crossOrigin = 'anonymous';
          }
          
          image.src = img.url;
        });
      }));

      // Draw drawings
      if (pathsToRender.length > 0) {
        pathsToRender.forEach(path => {
          const pCanvasW = path.canvasW || 800;
          const pCanvasH = path.canvasH || (800 * A4_RATIO);
          // Scale maps screen pixels → output pixels accounting for bbox crop
          const scaleX = widthInPx  / (pCanvasW * contentWidth  / 100);
          const scaleY = heightInPx / (pCanvasH * contentHeight / 100);
          const offsetX = (minX / contentWidth)  * widthInPx;
          const offsetY = (minY / contentHeight) * heightInPx;
          if (path.type === 'spray') {
            ctx.globalCompositeOperation = 'source-over';
            ctx.fillStyle = path.color;
            const dotR = Math.max(0.5, (path.size || 5) * 0.18 * scaleX);
            for (const pt of path.path) { ctx.beginPath(); ctx.arc(pt.x * scaleX - offsetX, pt.y * scaleY - offsetY, dotR, 0, Math.PI * 2); ctx.fill(); }
            ctx.globalCompositeOperation = 'source-over';
          } else {
            if (path.path.length < 2) return;
            ctx.beginPath();
            ctx.moveTo(path.path[0].x * scaleX - offsetX, path.path[0].y * scaleY - offsetY);
            for (let i = 1; i < path.path.length; i++) { ctx.lineTo(path.path[i].x * scaleX - offsetX, path.path[i].y * scaleY - offsetY); }
            if (path.color === 'eraser') { ctx.globalCompositeOperation = 'destination-out'; ctx.strokeStyle = 'rgba(0,0,0,1)'; }
            else { ctx.globalCompositeOperation = 'source-over'; ctx.strokeStyle = path.color; }
            ctx.lineWidth = path.size * scaleX; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.stroke();
            ctx.globalCompositeOperation = 'source-over';
          }
        });
      }

      // Draw shapes
      if (shapesToRender.length > 0) {
        drawShapesOnCanvas(ctx, shapesToRender, widthInPx, heightInPx, minX, minY, contentWidth, contentHeight);
      }
      
      setSaveProgress(85);
      const chatFileExt = isSingleCircle ? 'png' : 'jpg';
      const chatContentType = isSingleCircle ? 'image/png' : 'image/jpeg';
      const dataUrl = isSingleCircle ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.8);

      // Upload to Firebase Storage so the chat message stores a URL (not a large base64 string)
      const auth = getAuth();
      const uid = auth.currentUser?.uid || 'unknown';
      const timestamp = Date.now();
      const chatFileName = `studio_chat/${uid}/${timestamp}.${chatFileExt}`;
      const blob = await fetch(dataUrl).then(r => r.blob());
      const chatFile = new File([blob], `${fileName}-chat.${chatFileExt}`, { type: chatContentType });
      await uploadToStorage(chatFileName, chatFile, chatContentType, getEnvStorageBucket());
      setSaveProgress(95);
      const downloadUrl = await getDownloadUrl(chatFileName, getEnvStorageBucket());
      setSaveProgress(100);

      const chatImageData = {
        url: downloadUrl,
        fileName: `${fileName}-chat.${chatFileExt}`,
        timestamp: new Date().toISOString(),
        source: 'Image Studio',
        type: canvasImages.length > 1 ? 'collage' : 'single'
      };

      sessionStorage.setItem('chatImageData', JSON.stringify(chatImageData));
      
      const canvasState = {
        canvasImages,
        drawingPaths,
        canvasShapes,
        selectedImage,
        fileName,
        saveType,
        resizeWidth,
        resizeHeight,
        printSize,
      };
      
      localStorage.setItem('imageStudioState', JSON.stringify(canvasState));
      
      if (['Admin', 'Super'].includes(userRole)) {
        navigate('/admin-chat');
      } else {
        navigate('/user-chat');
      }
      
      setTimeout(() => {
        setIsSaving(false);
        setSaveProgress(0);
        if (activeTool !== 'magic-wand') {
          setActiveTool(null);
          setCurrentControlSection(null);
          setShowTopControls(false);
        } else {
          setCurrentControlSection('magic-wand');
        }
      }, 500);

    } catch (error) {
      console.error('Chat share error:', error);
      setIsSaving(false);
      setSaveProgress(0);
      if (activeTool !== 'magic-wand') {
        setActiveTool(null);
        setCurrentControlSection(null);
        setShowTopControls(false);
      } else {
        setCurrentControlSection('magic-wand');
      }
      showAlert('Failed to prepare image for chat. Please try again.');
    }
  };

  // Restore canvas state
  useEffect(() => {
    const savedCanvas = localStorage.getItem('imageStudioState');
    if (savedCanvas) {
      try {
        const { canvasImages: savedImages, drawingPaths: savedPaths, canvasShapes: savedShapes, selectedImage: savedSelected, fileName: savedFileName, printSize: savedPrintSize } = JSON.parse(savedCanvas);
        if ((savedImages && savedImages.length > 0) || (savedPaths && savedPaths.length > 0) || (savedShapes && savedShapes.length > 0)) {
          setCanvasImages(savedImages || []);
          setDrawingPaths(savedPaths || []);
          if (savedShapes && savedShapes.length > 0) setCanvasShapes(savedShapes);
          setFileName(savedFileName || 'Edited-PhotoS');
          if (savedPrintSize && PRINT_SIZES[savedPrintSize]) setPrintSize(savedPrintSize);
          if (savedSelected) {
            setSelectedImage(savedSelected);
          }
        }
      } catch (error) {
        console.error('Failed to restore canvas:', error);
      }
      localStorage.removeItem('imageStudioState');
    }
  }, []);

  // Load bucket images
  const loadBucketImages = async (forceReload = false) => {
    try {
      const _bucketUser = getAuth().currentUser;
      const _bucketFolder = ['Admin', 'Super'].includes(userRole)
        ? 'istudio'
        : `istudio/${_bucketUser?.uid}`;
      const _cacheKey = `${_bucketFolder}:${getEnvStorageBucket()}`;

      // Return cached result immediately if available and not forced
      if (!forceReload && _bucketImagesCacheKey === _cacheKey && _bucketImagesCache) {
        setBucketImages(_bucketImagesCache);
        return _bucketImagesCache;
      }

      setIsLoading(true);
      const { items } = await listObjectsWithMetadata(_bucketFolder, getEnvStorageBucket());
      const imageFiles = items.filter(item =>
        item.name.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i)
      );

      const imagesWithPreviews = await Promise.all(
        imageFiles.map(async (item) => {
          try {
            const result = await readObjectContent(item.name, getEnvStorageBucket());
            return {
              ...item,
              previewUrl: result.downloadUrl,
              fileName: item.name,
              displayName: item.name.split('/').pop()
            };
          } catch (error) {
            return {
              ...item,
              previewUrl: null,
              fileName: item.name,
              displayName: item.name.split('/').pop()
            };
          }
        })
      );

      _bucketImagesCache = imagesWithPreviews;
      _bucketImagesCacheKey = _cacheKey;
      setBucketImages(imagesWithPreviews);
      return imagesWithPreviews;
    } catch (error) {
      console.error('Failed to load bucket images:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // Detect if a loaded image is circle-cropped by checking corner transparency
  const detectCircleCrop = (img) => {
    try {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (!w || !h) return undefined;

      // Sample at a manageable resolution while preserving aspect ratio
      const maxSide = 200;
      const scale = Math.min(maxSide / w, maxSide / h, 1);
      const cw = Math.round(w * scale);
      const ch = Math.round(h * scale);

      const tc = document.createElement('canvas');
      tc.width  = cw;
      tc.height = ch;
      const tCtx = tc.getContext('2d', { willReadFrequently: true });
      tCtx.drawImage(img, 0, 0, cw, ch);

      const px = (x, y) => tCtx.getImageData(
        Math.max(0, Math.min(cw - 1, Math.round(x))),
        Math.max(0, Math.min(ch - 1, Math.round(y))), 1, 1
      ).data;

      // 1. Center pixel must be fully opaque — rules out fully-transparent images
      if (px(cw / 2, ch / 2)[3] < 200) return undefined;

      // 2. All four corner clusters must be transparent
      const m = Math.max(1, Math.floor(Math.min(cw, ch) * 0.08));
      const corners = [px(m, m), px(cw - m - 1, m), px(m, ch - m - 1), px(cw - m - 1, ch - m - 1)];
      if (!corners.every(p => p[3] < 30)) return undefined;

      // 3. Strict ellipse-boundary test: sample 12 angles around the expected ellipse.
      //    Points at 85 % radius must be opaque; points at 105 % radius (if in-canvas)
      //    must be transparent. A random logo/badge with transparent corners rarely
      //    satisfies both conditions across all 12 sampled angles.
      const rx = cw / 2, ry = ch / 2;
      const cx = cw / 2, cy = ch / 2;
      for (let i = 0; i < 12; i++) {
        const angle = (i / 12) * Math.PI * 2;
        const cosA  = Math.cos(angle);
        const sinA  = Math.sin(angle);

        // Inside the ellipse — must be opaque
        const xi = cx + cosA * rx * 0.85;
        const yi = cy + sinA * ry * 0.85;
        if (px(xi, yi)[3] < 150) return undefined;

        // Outside the ellipse (but still within canvas) — must be transparent
        const xo = cx + cosA * rx * 1.05;
        const yo = cy + sinA * ry * 1.05;
        if (xo >= 0 && xo < cw && yo >= 0 && yo < ch) {
          if (px(xo, yo)[3] > 30) return undefined;
        }
      }

      return 'circle';
    } catch (e) {
      // getImageData blocked by CORS — skip detection
    }
    return undefined;
  };

  // Handle local file upload with proper aspect ratio calculation (supports multi-select)
  const handleLocalFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    if (!imageFiles.length) {
      showAlert('Please select image files');
      return;
    }

    const psW = PRINT_SIZES[printSize].width;
    const psH = PRINT_SIZES[printSize].height;
    const _fillFrac = window.innerWidth <= 768 ? 0.45 : 0.38;
    // Cascade offset per image so multiple images don't fully overlap
    const OFFSET_STEP = 3; // percent of canvas per image

    // Read a single file and compute its canvas image object
    const loadOne = (file, index) => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const naturalAspectRatio = img.naturalWidth / img.naturalHeight;
          const imageCanvasAR = naturalAspectRatio * psH / psW;
          let width, height;
          if (imageCanvasAR >= 1) {
            width  = _fillFrac * 21;
            height = _fillFrac * psW * 29.7 / (naturalAspectRatio * psH);
          } else {
            height = _fillFrac * 29.7;
            width  = naturalAspectRatio * _fillFrac * psH * 21 / psW;
          }
          width  = Math.max(0.5, Math.min(21,   width));
          height = Math.max(0.5, Math.min(29.7, height));

          const baseX = 50 - (width  / 21   * 50);
          const baseY = 50 - (height / 29.7 * 50);
          resolve({
            id: Date.now() + index * 10,
            url: event.target.result,
            name: file.name,
            size: formatFileSize(file.size),
            x: Math.min(baseX + index * OFFSET_STEP, 70),
            y: Math.min(baseY + index * OFFSET_STEP, 70),
            width,
            height,
            source: 'local',
            cropShape: detectCircleCrop(img),
            naturalWidth:  img.naturalWidth,
            naturalHeight: img.naturalHeight,
          });
        };
        img.onerror = () => {
          resolve({
            id: Date.now() + index * 10,
            url: event.target.result,
            name: file.name,
            size: formatFileSize(file.size),
            x: 20 + index * OFFSET_STEP,
            y: 20 + index * OFFSET_STEP,
            width: 10,
            height: 10,
            source: 'local',
          });
        };
        img.src = event.target.result;
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });

    const results = await Promise.all(imageFiles.map((f, i) => loadOne(f, i)));
    const validImages = results.filter(Boolean).map(img => ({
      ...img,
      layerIndex: ++layerCounterRef.current,
    }));
    if (!validImages.length) {
      showAlert('Error reading image files');
      return;
    }

    const newList = [...canvasImagesRef.current, ...validImages];
    pushImageHistory(canvasImagesRef.current, newList);
    setCanvasImages(newList);

    const last = validImages[validImages.length - 1];
    setSelectedImage(last.id);
    setResizeWidth(last.width);
    setResizeHeight(last.height);
    setAspectRatio((last.naturalWidth || last.width) / (last.naturalHeight || last.height));
    setImageSelectionMode(false);
    if (activeTool !== 'magic-wand') {
      setActiveTool(null);
      setCurrentControlSection(null);
      setShowTopControls(false);
    }
    setCanvasCursor('pointer');
  };

  // Upload multiple local files directly to Cloud without adding them to the canvas
  const handleDirectCloudUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    if (!imageFiles.length) {
      showAlert('Please select image files');
      return;
    }

    setIsSaving(true);
    setSaveProgress(0);
    setSaveMessage(null);

    try {
      const _saveUser = getAuth().currentUser;
      const _saveFolder = ['Admin', 'Super'].includes(userRole)
        ? 'istudio'
        : `istudio/${_saveUser?.uid}`;

      const { items } = await listObjectsInFolder(_saveFolder, getEnvStorageBucket());
      const existingFiles = new Set(items.map(item => item.name));

      let uploaded = 0;
      for (const file of imageFiles) {
        const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
        const baseName = file.name.replace(/\.[^.]+$/, '').replace(/\s+/g, '_');
        let destName = `${_saveFolder}/${baseName}.${ext}`;
        let counter = 1;
        while (existingFiles.has(destName)) {
          destName = `${_saveFolder}/${baseName}_${counter}.${ext}`;
          counter++;
        }
        existingFiles.add(destName);
        await uploadToStorage(destName, file, file.type, getEnvStorageBucket());
        uploaded++;
        setSaveProgress(Math.round((uploaded / imageFiles.length) * 100));
      }

      _bucketImagesCache = null;
      setSaveMessage({
        text: `${uploaded} image${uploaded > 1 ? 's' : ''} uploaded to Cloud`,
        ok: true
      });
    } catch (err) {
      console.error('Direct cloud upload failed:', err);
      setSaveMessage({ text: 'Upload failed. Please try again.', ok: false });
    } finally {
      setIsSaving(false);
      setSaveProgress(0);
    }
  };

  // Handle bucket image select with proper aspect ratio
  const handleBucketImageSelect = async (imageFileName) => {
    try {
      setIsLoading(true);
      const result = await readObjectContent(imageFileName, getEnvStorageBucket());
      
      // Load image to get dimensions (crossOrigin needed for pixel detection)
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = result.downloadUrl;
      });
      
      const naturalAspectRatio = img.naturalWidth / img.naturalHeight;
      const cropShape = detectCircleCrop(img);
      
      // Back-calculate cm size from 300-DPI pixel dimensions.
      // Images saved from Image Studio are at 300 DPI, so this faithfully restores
      // their original size. For raw phone photos (e.g. 4032×3024) the raw value
      // exceeds the paper bounds, so we scale both axes by the same factor to
      // preserve the original aspect ratio while fitting within the canvas.
      const rawW = img.naturalWidth  * 2.54 / 300;
      const rawH = img.naturalHeight * 2.54 / 300;
      const fitScale = Math.min(1, 21 / rawW, 29.7 / rawH);
      let width  = Math.max(0.5, rawW * fitScale);
      let height = Math.max(0.5, rawH * fitScale);
      
      const newImage = {
        id: Date.now(),
        layerIndex: ++layerCounterRef.current,
        url: result.downloadUrl,
        name: imageFileName.split('/').pop(),
        size: formatFileSize(result.size || 0),
        x: Math.max(0, 50 - (width / 21 * 50)),
        y: Math.max(0, 50 - (height / 29.7 * 50)),
        width: width,
        height: height,
        source: 'bucket',
        cropShape,
        bucketPath: imageFileName,
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight
      };
      
      pushImageHistory(canvasImagesRef.current, [...canvasImagesRef.current, newImage]);
      setCanvasImages(prev => [...prev, newImage]);
      setSelectedImage(newImage.id);
      setResizeWidth(width);
      setResizeHeight(height);
      setAspectRatio(naturalAspectRatio);
      setShowBucketModal(false);
      setBucketModalMode('select');
      setSelectedBucketImages([]);
      setImageSelectionMode(false);
      if (activeTool !== 'magic-wand') {
        setActiveTool(null);
        setCurrentControlSection(null);
        setShowTopControls(false);
      }
      setCanvasCursor('pointer');
      
    } catch (error) {
      console.error('Error loading bucket image:', error);
      showAlert(`Failed to load image from Cloud: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Open bucket modal for deletion
  const handleOpenBucketForDelete = async () => {
    setIsLoading(true);
    const images = await loadBucketImages();
    if (images.length === 0) {
      showAlert('No images found in Cloud to delete');
      setIsLoading(false);
      return;
    }
    setBucketModalMode('delete');
    setSelectedBucketImages([]);
    setBucketSearch('');
    setShowBucketModal(true);
    setIsLoading(false);
  };

  // Handle connect options 
  const handleConnectOption = (option) => {
    setIsConnectMenuOpen(false);
    
    if (option === 'local') {
      fileInputRef.current.click();
      setShowTopControls(false);
      setCurrentControlSection(null);
    } else if (option === 'camera') {
      const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.maxTouchPoints > 1);
      if (!isMobile) {
        showAlert('Camera capture is only available on mobile devices. Use Folder to pick a file.');
        return;
      }
      cameraInputRef.current.click();
      setShowTopControls(false);
      setCurrentControlSection(null);
    } else if (option === 'bucket') {
      handleConnectBucket();
    } else if (option === 'upload-cloud') {
      cloudUploadInputRef.current.click();
      setShowTopControls(false);
      setCurrentControlSection(null);
    }
  };

  // Handle connect to bucket
  const handleConnectBucket = async () => {
    setIsLoading(true);
    try {
      const images = await loadBucketImages();
      if (images.length === 0) {
        showAlert('No images found in Cloud');
        return;
      }
      setBucketModalMode('select');
      setSelectedBucketImages([]);
      setBucketSearch('');
      setShowBucketModal(true);
      setShowTopControls(false);
      setCurrentControlSection(null);
    } catch (error) {
      console.error('Error loading bucket images:', error);
      showAlert('Failed to load Cloud images');
    } finally {
      setIsLoading(false);
    }
  };

  // Image history helper ? safe to call from async contexts via refs
  const pushImageHistory = (before, after) => {
    markDirty();
    const newHistory = drawingHistoryRef.current.slice(0, historyIndexRef.current + 1);
    newHistory.push({ type: 'image', before, after });
    setDrawingHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  // Mouse event handlers
  const handleMouseDown = (e, imageId, action) => {
    if (pendingNavPath) setPendingNavPath(null);
    markDirty();
    e.preventDefault();
    e.stopPropagation();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const image = canvasImages.find(img => img.id === imageId);
    if (!image) return;

    // Multi-drag: pressing on one of the already-multi-selected images/shapes
    if (imageSelectionMode && selectedShapeIds.size > 1 && selectedShapeIds.has(imageId)) {
      shapeBeforeRef.current = canvasShapesRef.current;
      dragStartImagesRef.current = canvasImagesRef.current;
      const _msShapes = canvasShapesRef.current.filter(s => selectedShapeIds.has(s.id));
      const _msImages = canvasImagesRef.current.filter(i => selectedShapeIds.has(i.id));
      multiDragRef.current = [
        ..._msShapes.map(s => ({ id: s.id, offsetX: x - (s.x1 / 100) * rect.width, offsetY: y - (s.y1 / 100) * rect.height })),
        ..._msImages.map(i => ({ id: i.id, offsetX: x - (i.x / 100) * rect.width, offsetY: y - (i.y / 100) * rect.height, isImage: true })),
      ];
      return;
    }
    
    // Select the image if it's not already selected
    if (selectedImage !== imageId) {
      setSelectedImage(imageId);
      setResizeWidth(image.width);
      setResizeHeight(image.height);
      setShowImageControls(imageId);
    }

    // Clicking an image while brightness/contrast is active deactivates the sub-tool
    if (magicSubTool === 'brightness' || magicSubTool === 'contrast') {
      setMagicSubTool(null);
      setCurrentControlSection(null);
      setShowTopControls(false);
    }

    if (action === 'move') {
      dragStartImagesRef.current = canvasImagesRef.current;
      setDraggingImage(imageId);
      dragOffsetRef.current = ({
        x: x - (image.x * rect.width / 100),
        y: y - (image.y * rect.height / 100)
      });
      // Plain move ? keep current tool/section, do not activate resize
    } else if (action === 'resize') {
      resizeStartImagesRef.current = canvasImagesRef.current;
      setResizingImage(imageId);
      dragOffsetRef.current = ({ x, y });
      
      // Show resize controls when resize handle is clicked
      setActiveTool('resize');
      setCurrentControlSection(null);
      setShowTopControls(false);
      setImageSelectionMode(false);
    }
  };

  // Touch handlers
  const handleTouchStart = (e, imageId, action) => {
    if (!e.touches[0]) return;
    e.preventDefault();
    
    const touch = e.touches[0];
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    
    if (action === 'move' || action === 'resize') {
      const image = canvasImages.find(img => img.id === imageId);
      if (!image) return;

      // Multi-drag: pressing on one of the already-multi-selected images/shapes
      if (imageSelectionMode && selectedShapeIds.size > 1 && selectedShapeIds.has(imageId)) {
        shapeBeforeRef.current = canvasShapesRef.current;
        dragStartImagesRef.current = canvasImagesRef.current;
        const _msShapes = canvasShapesRef.current.filter(s => selectedShapeIds.has(s.id));
        const _msImages = canvasImagesRef.current.filter(i => selectedShapeIds.has(i.id));
        multiDragRef.current = [
          ..._msShapes.map(s => ({ id: s.id, offsetX: x - (s.x1 / 100) * rect.width, offsetY: y - (s.y1 / 100) * rect.height })),
          ..._msImages.map(i => ({ id: i.id, offsetX: x - (i.x / 100) * rect.width, offsetY: y - (i.y / 100) * rect.height, isImage: true })),
        ];
        return;
      }
      
      // Select the image if it's not already selected
      if (selectedImage !== imageId) {
        setSelectedImage(imageId);
        setResizeWidth(image.width);
        setResizeHeight(image.height);
        setShowImageControls(imageId);
      }
      
      if (action === 'move') {
        dragStartImagesRef.current = canvasImagesRef.current;
        setDraggingImage(imageId);
        dragOffsetRef.current = ({
          x: x - (image.x * rect.width / 100),
          y: y - (image.y * rect.height / 100)
        });
        // Plain touch move ? keep current tool/section, do not activate resize
      }
    } else if (activeTool === 'draw' && (drawingMode === 'pen' || drawingMode === 'pencil' || drawingMode === 'eraser')) {
      setIsDrawing(true);
      setCurrentPath([{ x, y }]);
    } else if (activeTool === 'selection' && selectionMode) {
      setIsSelecting(true);
      // Circle crop: interact with the pre-placed overlay (move or resize) instead of drawing a new one
      if (selectionMode === 'circle' && selectionStart.x !== 0) {
        const cx = (selectionStart.x + selectionEnd.x) / 2;
        const cy = (selectionStart.y + selectionEnd.y) / 2;
        const rx = Math.abs(selectionEnd.x - selectionStart.x) / 2;
        const ry = Math.abs(selectionEnd.y - selectionStart.y) / 2;
        const dx = x - cx, dy = y - cy;
        const normDist = (ry > 0 && rx > 0) ? Math.sqrt((dx/rx)**2 + (dy/ry)**2) : 2;
        // Ignore clicks far outside the circle — don't reset or resize
        if (normDist > 1.3) return;
        const interactType = normDist < 0.85 ? 'move' : 'resize';
        circleCropInteractRef.current = { type: interactType, startX: x, startY: y, origStart: { ...selectionStart }, origEnd: { ...selectionEnd } };
        setCanvasCursor(interactType === 'move' ? 'grabbing' : 'nwse-resize');
        return;
      }
      // Rectangle crop: interact with the pre-placed overlay (move or resize)
      if (selectionMode === 'rectangle' && selectionStart.x !== 0) {
        const rx1 = Math.min(selectionStart.x, selectionEnd.x);
        const ry1 = Math.min(selectionStart.y, selectionEnd.y);
        const rx2 = Math.max(selectionStart.x, selectionEnd.x);
        const ry2 = Math.max(selectionStart.y, selectionEnd.y);
        const EDGE = 16; // px — near-edge zone treated as resize
        const insideX = x > rx1 + EDGE && x < rx2 - EDGE;
        const insideY = y > ry1 + EDGE && y < ry2 - EDGE;
        const outside = x < rx1 - EDGE || x > rx2 + EDGE || y < ry1 - EDGE || y > ry2 + EDGE;
        // Ignore clicks clearly outside the rectangle
        if (outside) return;
        const interactType = (insideX && insideY) ? 'move' : 'resize';
        circleCropInteractRef.current = { type: interactType, startX: x, startY: y, origStart: { ...selectionStart }, origEnd: { ...selectionEnd } };
        setCanvasCursor(interactType === 'move' ? 'grabbing' : 'nwse-resize');
        return;
      }
      setSelectionStart({ x, y });
      if (selectionMode === 'freeform') {
        setSelectionPath([{ x, y }]);
      } else {
        setSelectionEnd({ x, y });
      }
    }
  };

  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Marquee selection drag
    if (isMarqueeSelectingRef.current) {
      const xPct = (x / rect.width) * 100;
      const yPct = (y / rect.height) * 100;
      const mq = { ...marqueeRectRef.current, x2: xPct, y2: yPct };
      marqueeRectRef.current = mq;
      setMarqueeRect(mq);
      return;
    }

    // Multi-shape drag
    if (multiDragRef.current) {
      shapeDraggedRef.current = true;
      const items = multiDragRef.current;
      setCanvasShapes(prev => prev.map(s => {
        const item = items.find(m => m.id === s.id);
        if (!item) return s;
        const newX1 = ((x - item.offsetX) / rect.width) * 100;
        const newY1 = ((y - item.offsetY) / rect.height) * 100;
        const dx = newX1 - s.x1, dy = newY1 - s.y1;
        return { ...s, x1: s.x1 + dx, y1: s.y1 + dy, x2: s.x2 + dx, y2: s.y2 + dy };
      }));
      setCanvasImages(prev => prev.map(img => {
        const item = items.find(m => m.id === img.id && m.isImage);
        if (!item) return img;
        return { ...img, x: ((x - item.offsetX) / rect.width) * 100, y: ((y - item.offsetY) / rect.height) * 100 };
      }));
      return;
    }

    // Shape resizing
    if (rotatingShapeRef.current) {
      const { id, cx, cy } = rotatingShapeRef.current;
      const deg = Math.atan2(y - cy, x - cx) * (180 / Math.PI) + 90;
      setCanvasShapes(prev => prev.map(s => s.id === id ? { ...s, rotation: deg } : s));
      return;
    }

    if (resizingShapeRef.current) {
      const { corner, id } = resizingShapeRef.current;
      const xPct = (x / rect.width) * 100;
      const yPct = (y / rect.height) * 100;
      // Pre-compute font size sync for text/emoji shapes
      const _rsShape = canvasShapesRef.current?.find(s => s.id === id);
      if (_rsShape && (_rsShape.type === 'text' || _rsShape.type === 'emoji')) {
        let _ny1 = _rsShape.y1, _ny2 = _rsShape.y2;
        if (corner === 'se' || corner === 'p2' || corner === 'sw') { _ny2 = yPct; }
        else if (corner === 'ne' || corner === 'nw' || corner === 'p1') { _ny1 = yPct; }
        else if (corner === 'n') { _ny1 = yPct; }
        else if (corner === 's') { _ny2 = yPct; }
        const _newFs = Math.max(8, Math.round(Math.abs(_ny2 - _ny1) / 100 * rect.height));
        setShapeFontSize(_newFs);
      }
      setCanvasShapes(prev => prev.map(s => {
        if (s.id !== id) return s;
        const ns = { ...s };
        if (corner === 'se' || corner === 'p2') { ns.x2 = xPct; ns.y2 = yPct; }
        else if (corner === 'sw') { ns.x1 = xPct; ns.y2 = yPct; }
        else if (corner === 'ne') { ns.x2 = xPct; ns.y1 = yPct; }
        else if (corner === 'nw' || corner === 'p1') { ns.x1 = xPct; ns.y1 = yPct; }
        else if (corner === 'n') ns.y1 = yPct;
        else if (corner === 's') ns.y2 = yPct;
        else if (corner === 'e') ns.x2 = xPct;
        else if (corner === 'w') ns.x1 = xPct;
        if (lockAspectRatio && (corner === 'se' || corner === 'sw' || corner === 'ne' || corner === 'nw' || corner === 'p1' || corner === 'p2')) {
          const origW = Math.abs(s.x2 - s.x1);
          const origH = Math.abs(s.y2 - s.y1);
          if (origW > 0 && origH > 0) {
            const ratio = origW / origH;
            const newW = Math.abs(ns.x2 - ns.x1);
            const newH = Math.abs(ns.y2 - ns.y1);
            const wChanged = Math.abs(newW - origW) >= Math.abs(newH - origH);
            const signX = ns.x2 >= ns.x1 ? 1 : -1;
            const signY = ns.y2 >= ns.y1 ? 1 : -1;
            if (wChanged) { const lockedH = newW / ratio; if (corner === 'nw' || corner === 'ne' || corner === 'p1') ns.y1 = ns.y2 - signY * lockedH; else ns.y2 = ns.y1 + signY * lockedH; } else { const lockedW = newH * ratio; if (corner === 'nw' || corner === 'sw' || corner === 'p1') ns.x1 = ns.x2 - signX * lockedW; else ns.x2 = ns.x1 + signX * lockedW; }
          }
        }
        // Apply aspect ratio lock for corner handles
        if (lockAspectRatio && (corner === 'se' || corner === 'sw' || corner === 'ne' || corner === 'nw' || corner === 'p1' || corner === 'p2')) {
          const origW = Math.abs(s.x2 - s.x1);
          const origH = Math.abs(s.y2 - s.y1);
          if (origW > 0 && origH > 0) {
            const ratio = origW / origH;
            const newW = Math.abs(ns.x2 - ns.x1);
            const newH = Math.abs(ns.y2 - ns.y1);
            const wChanged = Math.abs(newW - origW) >= Math.abs(newH - origH);
            const signX = ns.x2 >= ns.x1 ? 1 : -1;
            const signY = ns.y2 >= ns.y1 ? 1 : -1;
            if (wChanged) {
              const lockedH = newW / ratio;
              if (corner === 'nw' || corner === 'ne' || corner === 'p1') ns.y1 = ns.y2 - signY * lockedH;
              else ns.y2 = ns.y1 + signY * lockedH;
            } else {
              const lockedW = newH * ratio;
              if (corner === 'nw' || corner === 'sw' || corner === 'p1') ns.x1 = ns.x2 - signX * lockedW;
              else ns.x2 = ns.x1 + signX * lockedW;
            }
          }
        }        if (ns.type === 'text' || ns.type === 'emoji') {
          const newFs = Math.max(8, Math.round(Math.abs(ns.y2 - ns.y1) / 100 * rect.height));
          ns.fontSize = newFs;
          ns.emojiSize = newFs;
        }
        return ns;
      }));
      return;
    }

    // Shape dragging
    if (draggingShapeRef.current) {
      const { id, offsetX, offsetY } = draggingShapeRef.current;
      const xPct = ((x - offsetX) / rect.width) * 100;
      shapeDraggedRef.current = true;
      const yPct = ((y - offsetY) / rect.height) * 100;
      const _prevS = canvasShapesRef.current?.find(s => s.id === id);
      if (_prevS) {
        const _ddx = xPct - _prevS.x1, _ddy = yPct - _prevS.y1;
        if (Math.abs(_ddx) > 0.05) lastDragDirRef.current.x = _ddx > 0 ? 1 : -1;
        if (Math.abs(_ddy) > 0.05) lastDragDirRef.current.y = _ddy > 0 ? 1 : -1;
      }
      setCanvasShapes(prev => prev.map(s => {
        if (s.id !== id) return s;
        const dx = xPct - s.x1;
        const dy = yPct - s.y1;
        return { ...s, x1: s.x1 + dx, y1: s.y1 + dy, x2: s.x2 + dx, y2: s.y2 + dy };
      }));
      return;
    }

    // Shape drawing preview
    if (isDrawingShape) {
      setShapeDrawEnd({ x: (x / rect.width) * 100, y: (y / rect.height) * 100 });
      return;
    }

    if (isSelecting && selectionMode) {
      // Circle crop: move or resize the pre-placed overlay
      if (selectionMode === 'circle') {
        if (circleCropInteractRef.current) {
          const { type, startX, startY, origStart, origEnd } = circleCropInteractRef.current;
          const dx = x - startX, dy = y - startY;
          if (type === 'move') {
            setSelectionStart({ x: origStart.x + dx, y: origStart.y + dy });
            setSelectionEnd  ({ x: origEnd.x   + dx, y: origEnd.y   + dy });
          } else {
            // Resize: scale the circle from its center outward
            const origRx = Math.abs(origEnd.x - origStart.x) / 2;
            const origRy = Math.abs(origEnd.y - origStart.y) / 2;
            const cx = (origStart.x + origEnd.x) / 2;
            const cy = (origStart.y + origEnd.y) / 2;
            const newRx = Math.max(10, origRx + dx);
            const newRy = Math.max(10, origRy + dy);
            setSelectionStart({ x: cx - newRx, y: cy - newRy });
            setSelectionEnd  ({ x: cx + newRx, y: cy + newRy });
          }
        }
        // No interaction active — don't update selection (circle must stay put)
        return;
      }
      // Rectangle crop: move or resize the pre-placed overlay
      if (selectionMode === 'rectangle') {
        if (circleCropInteractRef.current) {
          const { type, startX, startY, origStart, origEnd } = circleCropInteractRef.current;
          const dx = x - startX, dy = y - startY;
          if (type === 'move') {
            setSelectionStart({ x: origStart.x + dx, y: origStart.y + dy });
            setSelectionEnd  ({ x: origEnd.x   + dx, y: origEnd.y   + dy });
          } else {
            // Resize: move the end corner
            setSelectionEnd({ x: origEnd.x + dx, y: origEnd.y + dy });
          }
        }
        return;
      }
      // Smart crop: append to lasso path while drawing; freeze if detection result shown
      if (selectionMode === 'smart') {
        if (smartDetectedRef.current) return; // polygon showing — keep frozen
        setSelectionPath(prev => {
          const last = prev[prev.length - 1];
          if (last && Math.abs(x - last.x) < 3 && Math.abs(y - last.y) < 3) return prev;
          return [...prev, { x, y }];
        });
        setSelectionEnd({ x, y });
        return;
      }
      handleSelectionMouseMove(e);
      return;
    }
    
    if (isDrawing && drawingMode === 'spray') {
      const N = 30, radius = brushSize * 1.2;
      const newDots = [];
      for (let i = 0; i < N; i++) {
        // Uniform disc distribution (not sqrt) keeps dots concentrated at centre
        const angle = (i / N) * Math.PI * 2 + Math.random() * 0.4;
        const r = (0.2 + Math.random() * 0.8) * radius;
        newDots.push({ x: x + r * Math.cos(angle), y: y + r * Math.sin(angle) });
      }
      setCurrentPath(prev => [...prev, ...newDots]);
      return;
    }
    if (isDrawing && (drawingMode === 'pen' || drawingMode === 'pencil' || drawingMode === 'eraser')) {
      // Interpolate intermediate points to avoid gaps on fast strokes
      const last = currentPath[currentPath.length - 1];
      const interp = [];
      if (last) {
        const dx = x - last.x, dy = y - last.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const step = Math.max(1, brushSize * 0.3);
        if (dist > step) {
          const steps = Math.ceil(dist / step);
          for (let k = 1; k < steps; k++) {
            interp.push({ x: last.x + dx * k / steps, y: last.y + dy * k / steps });
          }
        }
      }
      setCurrentPath(prev => [...prev, ...interp, { x, y }]);
      if (drawingMode === 'eraser') setEraserPos({ x, y });
      return;
    }
    if (activeTool === 'draw' && drawingMode === 'eraser' && !isDrawing) {
      setEraserPos({ x, y });
    }
    
    if (draggingImage) {
      const _newImgX = ((x - dragOffsetRef.current.x) / rect.width) * 100;
      const _newImgY = ((y - dragOffsetRef.current.y) / rect.height) * 100;
      const _curDragImg = canvasImagesRef.current?.find(i => i.id === draggingImage);
      if (_curDragImg) {
        const _didx = _newImgX - _curDragImg.x, _didy = _newImgY - _curDragImg.y;
        if (Math.abs(_didx) > 0.05) lastDragDirRef.current.x = _didx > 0 ? 1 : -1;
        if (Math.abs(_didy) > 0.05) lastDragDirRef.current.y = _didy > 0 ? 1 : -1;
      }
      setCanvasImages(prev => prev.map(img => {
        if (img.id === draggingImage) {
          const newX = ((x - dragOffsetRef.current.x) / rect.width) * 100;
          const newY = ((y - dragOffsetRef.current.y) / rect.height) * 100;
          // Clamp so the visual (rotated) bounding box stays within the canvas
          const _rotRad = ((img.rotation || 0) * Math.PI) / 180;
          const _absC = Math.abs(Math.cos(_rotRad));
          const _absS = Math.abs(Math.sin(_rotRad));
          const _wpx = (img.width  / 21)   * rect.width;
          const _hpx = (img.height / 29.7) * rect.height;
          const _hvW = _wpx / 2 * _absC + _hpx / 2 * _absS;
          const _hvH = _wpx / 2 * _absS + _hpx / 2 * _absC;
          const _xMin = (_hvW - _wpx / 2) / rect.width  * 100;
          const _xMax = (rect.width  - _hvW - _wpx / 2) / rect.width  * 100;
          const _yMin = (_hvH - _hpx / 2) / rect.height * 100;
          const _yMax = (rect.height - _hvH - _hpx / 2) / rect.height * 100;
          return {
            ...img,
            x: Math.max(_xMin, Math.min(_xMax, newX)),
            y: Math.max(_yMin, Math.min(_yMax, newY))
          };
        }
        return img;
      }));
    }
    
    if (resizingImage) {
      const dir = resizeDirectionRef.current || 'se';
      // Capture previous position BEFORE updating ref so the updater closure is correct
      const prevX = dragOffsetRef.current.x;
      const prevY = dragOffsetRef.current.y;
      dragOffsetRef.current = { x, y };
      setCanvasImages(prev => prev.map(img => {
        if (img.id !== resizingImage) return img;
        const deltaX = x - prevX;
        const deltaY = y - prevY;
        // Un-rotate screen deltas into image-local space for dimension changes
        const rotRad = ((img.rotation || 0) * Math.PI) / 180;
        const cosR = Math.cos(rotRad);
        const sinR = Math.sin(rotRad);
        const localDX = deltaX * cosR + deltaY * sinR;
        const localDY = -deltaX * sinR + deltaY * cosR;
        const deltaCmX = (localDX / rect.width) * 21;
        const deltaCmY = (localDY / rect.height) * 29.7;
        let newWidth = img.width;
        let newHeight = img.height;
        switch (dir) {
          case 'se': newWidth = Math.max(1, Math.min(21, img.width + deltaCmX)); newHeight = Math.max(1, Math.min(29.7, img.height + deltaCmY)); break;
          case 'sw': newWidth = Math.max(1, Math.min(21, img.width - deltaCmX)); newHeight = Math.max(1, Math.min(29.7, img.height + deltaCmY)); break;
          case 'ne': newWidth = Math.max(1, Math.min(21, img.width + deltaCmX)); newHeight = Math.max(1, Math.min(29.7, img.height - deltaCmY)); break;
          case 'nw': newWidth = Math.max(1, Math.min(21, img.width - deltaCmX)); newHeight = Math.max(1, Math.min(29.7, img.height - deltaCmY)); break;
          case 'n':  newHeight = Math.max(1, Math.min(29.7, img.height - deltaCmY)); break;
          case 's':  newHeight = Math.max(1, Math.min(29.7, img.height + deltaCmY)); break;
          case 'e':  newWidth = Math.max(1, Math.min(21, img.width + deltaCmX)); break;
          case 'w':  newWidth = Math.max(1, Math.min(21, img.width - deltaCmX)); break;
        }
        // Aspect ratio lock
        if (lockAspectRatio && img.width > 0 && img.height > 0) {
          const _ar = img.width / img.height;
          if (dir === 'e' || dir === 'w') {
            newHeight = Math.max(1, Math.min(29.7, newWidth / _ar));
          } else if (dir === 'n' || dir === 's') {
            newWidth = Math.max(1, Math.min(21, newHeight * _ar));
          } else {
            if (Math.abs(newWidth - img.width) >= Math.abs(newHeight - img.height)) {
              newHeight = Math.max(1, Math.min(29.7, newWidth / _ar));
            } else {
              newWidth = Math.max(1, Math.min(21, newHeight * _ar));
            }
          }
        }
        // Anchor-point position correction: keeps the opposite corner fixed under any rotation.
        // aX/aY are the normalized coords {-1,0,1} of the corner that must NOT move.
        const _anchors = { se:[-1,-1], sw:[1,-1], ne:[-1,1], nw:[1,1], n:[0,1], s:[0,-1], e:[-1,0], w:[1,0] };
        const [aX, aY] = _anchors[dir] || [-1,-1];
        const dw_px = (newWidth  - img.width)  / 21   * rect.width;
        const dh_px = (newHeight - img.height) / 29.7 * rect.height;
        const dx_px = -dw_px / 2 * (1 + aX * cosR) + dh_px / 2 * aY * sinR;
        const dy_px = -dh_px / 2 * (1 + aY * cosR) - dw_px / 2 * aX * sinR;
        const newX = img.x + dx_px / rect.width  * 100;
        const newY = img.y + dy_px / rect.height * 100;
        // Rotation-aware minimum bounds so the layout box can go negative when needed
        const _absC = Math.abs(cosR);
        const _absS = Math.abs(sinR);
        const _nWpx = (newWidth  / 21)   * rect.width;
        const _nHpx = (newHeight / 29.7) * rect.height;
        const _xMin = (_nWpx / 2 * _absC + _nHpx / 2 * _absS - _nWpx / 2) / rect.width  * 100;
        const _yMin = (_nWpx / 2 * _absS + _nHpx / 2 * _absC - _nHpx / 2) / rect.height * 100;
        setResizeWidth(newWidth);
        setResizeHeight(newHeight);
        return { ...img, width: newWidth, height: newHeight, x: Math.max(_xMin, newX), y: Math.max(_yMin, newY) };
      }));
    }

    if (rotatingImage && rotateImageCenterRef.current) {
      const { cx, cy } = rotateImageCenterRef.current;
      const deg = Math.atan2(y - cy, x - cx) * (180 / Math.PI) + 90;
      setCanvasImages(prev => prev.map(i => i.id === rotatingImage ? { ...i, rotation: deg } : i));
      return;
    }
  };

  // Touch move handler
  const handleTouchMove = (e) => {
    if (!e.touches[0]) return;
    e.preventDefault();
    
    const touch = e.touches[0];
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;

    // Smart/Freeform crop touch fastpath — runs without depending on `isSelecting` so
    // every touchmove is captured even before React has flushed touchstart's
    // setIsSelecting(true) (mobile stale-closure fix). Gated by touchSelectionActiveRef
    // so it only fires after a confirmed touchstart ON the canvas (prevents stray
    // fingers / cross-element drags from drawing). The drawing tip is lifted above the
    // finger by TOUCH_PEN_OFFSET_Y so the user can actually see what's being drawn.
    if (touchSelectionActiveRef.current && activeTool === 'selection' && selectionMode === 'smart') {
      if (smartDetectedRef.current) return;
      const tipX = x;
      const tipY = Math.max(0, y - TOUCH_PEN_OFFSET_Y);
      const last = smartLassoPathRef.current[smartLassoPathRef.current.length - 1];
      if (!last || Math.abs(tipX - last.x) >= 3 || Math.abs(tipY - last.y) >= 3) {
        smartLassoPathRef.current = [...smartLassoPathRef.current, { x: tipX, y: tipY }];
        setSelectionPath(prev => [...prev, { x: tipX, y: tipY }]);
        setSelectionEnd({ x: tipX, y: tipY });
      }
      setSelectionTouchPos({ x: tipX, y: tipY });
      return;
    }
    if (touchSelectionActiveRef.current && activeTool === 'selection' && selectionMode === 'freeform') {
      const tipX = x;
      const tipY = Math.max(0, y - TOUCH_PEN_OFFSET_Y);
      setSelectionPath(prev => [...prev, { x: tipX, y: tipY }]);
      setSelectionTouchPos({ x: tipX, y: tipY });
      return;
    }

    // Marquee selection drag (touch)
    if (isMarqueeSelectingRef.current) {
      const xPct = (x / rect.width) * 100;
      const yPct = (y / rect.height) * 100;
      const mq = { ...marqueeRectRef.current, x2: xPct, y2: yPct };
      marqueeRectRef.current = mq;
      setMarqueeRect(mq);
      return;
    }

    // Multi-shape drag (touch)
    if (multiDragRef.current) {
      shapeDraggedRef.current = true;
      const items = multiDragRef.current;
      setCanvasShapes(prev => prev.map(s => {
        const item = items.find(m => m.id === s.id);
        if (!item) return s;
        const newX1 = ((x - item.offsetX) / rect.width) * 100;
        const newY1 = ((y - item.offsetY) / rect.height) * 100;
        const dx = newX1 - s.x1, dy = newY1 - s.y1;
        return { ...s, x1: s.x1 + dx, y1: s.y1 + dy, x2: s.x2 + dx, y2: s.y2 + dy };
      }));
      setCanvasImages(prev => prev.map(img => {
        const item = items.find(m => m.id === img.id && m.isImage);
        if (!item) return img;
        return { ...img, x: ((x - item.offsetX) / rect.width) * 100, y: ((y - item.offsetY) / rect.height) * 100 };
      }));
      return;
    }

    // Image rotating (touch)
    if (rotatingImage && rotateImageCenterRef.current) {
      const { cx, cy } = rotateImageCenterRef.current;
      const deg = Math.atan2(y - cy, x - cx) * (180 / Math.PI) + 90;
      setCanvasImages(prev => prev.map(i => i.id === rotatingImage ? { ...i, rotation: deg } : i));
      return;
    }

    // Shape resizing (touch)
    if (rotatingShapeRef.current) {
      const { id, cx, cy } = rotatingShapeRef.current;
      const deg = Math.atan2(y - cy, x - cx) * (180 / Math.PI) + 90;
      setCanvasShapes(prev => prev.map(s => s.id === id ? { ...s, rotation: deg } : s));
      return;
    }

    if (resizingShapeRef.current) {
      const { corner, id } = resizingShapeRef.current;
      const xPct = (x / rect.width) * 100;
      const yPct = (y / rect.height) * 100;
      // Pre-compute font size sync for text/emoji shapes
      const _rsTShape = canvasShapesRef.current?.find(s => s.id === id);
      if (_rsTShape && (_rsTShape.type === 'text' || _rsTShape.type === 'emoji')) {
        let _ny1 = _rsTShape.y1, _ny2 = _rsTShape.y2;
        if (corner === 'se' || corner === 'p2' || corner === 'sw') { _ny2 = yPct; }
        else if (corner === 'ne' || corner === 'nw' || corner === 'p1') { _ny1 = yPct; }
        else if (corner === 'n') { _ny1 = yPct; }
        else if (corner === 's') { _ny2 = yPct; }
        const _newFsT = Math.max(8, Math.round(Math.abs(_ny2 - _ny1) / 100 * rect.height));
        setShapeFontSize(_newFsT);
      }
      setCanvasShapes(prev => prev.map(s => {
        if (s.id !== id) return s;
        const ns = { ...s };
        if (corner === 'se' || corner === 'p2') { ns.x2 = xPct; ns.y2 = yPct; }
        else if (corner === 'sw') { ns.x1 = xPct; ns.y2 = yPct; }
        else if (corner === 'ne') { ns.x2 = xPct; ns.y1 = yPct; }
        else if (corner === 'nw' || corner === 'p1') { ns.x1 = xPct; ns.y1 = yPct; }
        else if (corner === 'n') ns.y1 = yPct;
        else if (corner === 's') ns.y2 = yPct;
        else if (corner === 'e') ns.x2 = xPct;
        else if (corner === 'w') ns.x1 = xPct;
        if (lockAspectRatio && (corner === 'se' || corner === 'sw' || corner === 'ne' || corner === 'nw' || corner === 'p1' || corner === 'p2')) {
          const origW = Math.abs(s.x2 - s.x1);
          const origH = Math.abs(s.y2 - s.y1);
          if (origW > 0 && origH > 0) {
            const ratio = origW / origH;
            const newW = Math.abs(ns.x2 - ns.x1);
            const newH = Math.abs(ns.y2 - ns.y1);
            const wChanged = Math.abs(newW - origW) >= Math.abs(newH - origH);
            const signX = ns.x2 >= ns.x1 ? 1 : -1;
            const signY = ns.y2 >= ns.y1 ? 1 : -1;
            if (wChanged) { const lockedH = newW / ratio; if (corner === 'nw' || corner === 'ne' || corner === 'p1') ns.y1 = ns.y2 - signY * lockedH; else ns.y2 = ns.y1 + signY * lockedH; } else { const lockedW = newH * ratio; if (corner === 'nw' || corner === 'sw' || corner === 'p1') ns.x1 = ns.x2 - signX * lockedW; else ns.x2 = ns.x1 + signX * lockedW; }
          }
        }
        // Apply aspect ratio lock for corner handles
        if (lockAspectRatio && (corner === 'se' || corner === 'sw' || corner === 'ne' || corner === 'nw' || corner === 'p1' || corner === 'p2')) {
          const origW = Math.abs(s.x2 - s.x1);
          const origH = Math.abs(s.y2 - s.y1);
          if (origW > 0 && origH > 0) {
            const ratio = origW / origH;
            const newW = Math.abs(ns.x2 - ns.x1);
            const newH = Math.abs(ns.y2 - ns.y1);
            const wChanged = Math.abs(newW - origW) >= Math.abs(newH - origH);
            const signX = ns.x2 >= ns.x1 ? 1 : -1;
            const signY = ns.y2 >= ns.y1 ? 1 : -1;
            if (wChanged) {
              const lockedH = newW / ratio;
              if (corner === 'nw' || corner === 'ne' || corner === 'p1') ns.y1 = ns.y2 - signY * lockedH;
              else ns.y2 = ns.y1 + signY * lockedH;
            } else {
              const lockedW = newH * ratio;
              if (corner === 'nw' || corner === 'sw' || corner === 'p1') ns.x1 = ns.x2 - signX * lockedW;
              else ns.x2 = ns.x1 + signX * lockedW;
            }
          }
        }        if (ns.type === 'text' || ns.type === 'emoji') {
          const newFs = Math.max(8, Math.round(Math.abs(ns.y2 - ns.y1) / 100 * rect.height));
          ns.fontSize = newFs;
          ns.emojiSize = newFs;
        }
        return ns;
      }));
      return;
    }

    // Shape dragging (touch)
    if (draggingShapeRef.current) {
      const { id, offsetX, offsetY } = draggingShapeRef.current;
      const xPct = ((x - offsetX) / rect.width) * 100;
      const yPct = ((y - offsetY) / rect.height) * 100;
      shapeDraggedRef.current = true;
      const _prevST = canvasShapesRef.current?.find(s => s.id === id);
      if (_prevST) {
        const _ddx = xPct - _prevST.x1, _ddy = yPct - _prevST.y1;
        if (Math.abs(_ddx) > 0.05) lastDragDirRef.current.x = _ddx > 0 ? 1 : -1;
        if (Math.abs(_ddy) > 0.05) lastDragDirRef.current.y = _ddy > 0 ? 1 : -1;
      }
      setCanvasShapes(prev => prev.map(s => {
        if (s.id !== id) return s;
        const dx = xPct - s.x1; const dy = yPct - s.y1;
        return { ...s, x1: s.x1 + dx, y1: s.y1 + dy, x2: s.x2 + dx, y2: s.y2 + dy };
      }));
      return;
    }

    // Shape draw preview (touch)
    if (isDrawingShape) {
      setShapeDrawEnd({ x: (x / rect.width) * 100, y: (y / rect.height) * 100 });
      return;
    }

    if (isSelecting && selectionMode) {
      // Circle crop: move or resize the pre-placed overlay
      if (selectionMode === 'circle') {
        if (circleCropInteractRef.current) {
          const { type, startX, startY, origStart, origEnd } = circleCropInteractRef.current;
          const dx = x - startX, dy = y - startY;
          if (type === 'move') {
            setSelectionStart({ x: origStart.x + dx, y: origStart.y + dy });
            setSelectionEnd  ({ x: origEnd.x   + dx, y: origEnd.y   + dy });
          } else {
            const origRx = Math.abs(origEnd.x - origStart.x) / 2;
            const origRy = Math.abs(origEnd.y - origStart.y) / 2;
            const cx = (origStart.x + origEnd.x) / 2;
            const cy = (origStart.y + origEnd.y) / 2;
            const newRx = Math.max(10, origRx + dx);
            const newRy = Math.max(10, origRy + dy);
            setSelectionStart({ x: cx - newRx, y: cy - newRy });
            setSelectionEnd  ({ x: cx + newRx, y: cy + newRy });
          }
        }
        return;
      }
      // Rectangle crop: move or resize the pre-placed overlay (touch)
      if (selectionMode === 'rectangle') {
        if (circleCropInteractRef.current) {
          const { type, startX, startY, origStart, origEnd } = circleCropInteractRef.current;
          const dx = x - startX, dy = y - startY;
          if (type === 'move') {
            setSelectionStart({ x: origStart.x + dx, y: origStart.y + dy });
            setSelectionEnd  ({ x: origEnd.x   + dx, y: origEnd.y   + dy });
          } else {
            setSelectionEnd({ x: origEnd.x + dx, y: origEnd.y + dy });
          }
        }
        return;
      }
      if (selectionMode === 'freeform') {
        // Lift the drawing tip above the finger so the user can see it.
        const tipX = x;
        const tipY = Math.max(0, y - TOUCH_PEN_OFFSET_Y);
        setSelectionPath(prev => [...prev, { x: tipX, y: tipY }]);
        setSelectionTouchPos({ x: tipX, y: tipY });
      } else {
        setSelectionEnd({ x, y });
      }
      return;
    }
    
    if (isDrawing && drawingMode === 'spray') {
      const N = 30, radius = brushSize * 1.2;
      const newDots = [];
      for (let i = 0; i < N; i++) {
        const angle = (i / N) * Math.PI * 2 + Math.random() * 0.4;
        const r = (0.2 + Math.random() * 0.8) * radius;
        newDots.push({ x: x + r * Math.cos(angle), y: y + r * Math.sin(angle) });
      }
      setCurrentPath(prev => [...prev, ...newDots]);
      return;
    }
    if (isDrawing && (drawingMode === 'pen' || drawingMode === 'pencil' || drawingMode === 'eraser')) {
      // Interpolate intermediate points for sharp eraser edges on touch
      const last = currentPath[currentPath.length - 1];
      const interp = [];
      if (last) {
        const dx = x - last.x, dy = y - last.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const step = Math.max(1, brushSize * 0.3);
        if (dist > step) {
          const steps = Math.ceil(dist / step);
          for (let k = 1; k < steps; k++) {
            interp.push({ x: last.x + dx * k / steps, y: last.y + dy * k / steps });
          }
        }
      }
      setCurrentPath(prev => [...prev, ...interp, { x, y }]);
      if (drawingMode === 'eraser') setEraserPos({ x, y });
      return;
    }
    if (activeTool === 'draw' && drawingMode === 'eraser' && !isDrawing) {
      setEraserPos({ x, y });
    }
    
    if (draggingImage) {
      const _newImgXT = ((x - dragOffsetRef.current.x) / rect.width) * 100;
      const _newImgYT = ((y - dragOffsetRef.current.y) / rect.height) * 100;
      const _curDragImgT = canvasImagesRef.current?.find(i => i.id === draggingImage);
      if (_curDragImgT) {
        const _didxT = _newImgXT - _curDragImgT.x, _didyT = _newImgYT - _curDragImgT.y;
        if (Math.abs(_didxT) > 0.05) lastDragDirRef.current.x = _didxT > 0 ? 1 : -1;
        if (Math.abs(_didyT) > 0.05) lastDragDirRef.current.y = _didyT > 0 ? 1 : -1;
      }
      setCanvasImages(prev => prev.map(img => {
        if (img.id === draggingImage) {
          const newX = ((x - dragOffsetRef.current.x) / rect.width) * 100;
          const newY = ((y - dragOffsetRef.current.y) / rect.height) * 100;
          // Clamp so the visual (rotated) bounding box stays within the canvas
          const _rotRad = ((img.rotation || 0) * Math.PI) / 180;
          const _absC = Math.abs(Math.cos(_rotRad));
          const _absS = Math.abs(Math.sin(_rotRad));
          const _wpx = (img.width  / 21)   * rect.width;
          const _hpx = (img.height / 29.7) * rect.height;
          const _hvW = _wpx / 2 * _absC + _hpx / 2 * _absS;
          const _hvH = _wpx / 2 * _absS + _hpx / 2 * _absC;
          const _xMin = (_hvW - _wpx / 2) / rect.width  * 100;
          const _xMax = (rect.width  - _hvW - _wpx / 2) / rect.width  * 100;
          const _yMin = (_hvH - _hpx / 2) / rect.height * 100;
          const _yMax = (rect.height - _hvH - _hpx / 2) / rect.height * 100;
          return {
            ...img,
            x: Math.max(_xMin, Math.min(_xMax, newX)),
            y: Math.max(_yMin, Math.min(_yMax, newY))
          };
        }
        return img;
      }));
    }
    
    if (resizingImage) {
      const dir = resizeDirectionRef.current || 'se';
      // Capture previous position BEFORE updating ref so the updater closure is correct
      const prevX = dragOffsetRef.current.x;
      const prevY = dragOffsetRef.current.y;
      dragOffsetRef.current = { x, y };
      setCanvasImages(prev => prev.map(img => {
        if (img.id !== resizingImage) return img;
        const deltaX = x - prevX;
        const deltaY = y - prevY;
        // Un-rotate screen deltas into image-local space for dimension changes
        const rotRad = ((img.rotation || 0) * Math.PI) / 180;
        const cosR = Math.cos(rotRad);
        const sinR = Math.sin(rotRad);
        const localDX = deltaX * cosR + deltaY * sinR;
        const localDY = -deltaX * sinR + deltaY * cosR;
        const deltaCmX = (localDX / rect.width) * 21;
        const deltaCmY = (localDY / rect.height) * 29.7;
        let newWidth = img.width;
        let newHeight = img.height;
        switch (dir) {
          case 'se': newWidth = Math.max(1, Math.min(21, img.width + deltaCmX)); newHeight = Math.max(1, Math.min(29.7, img.height + deltaCmY)); break;
          case 'sw': newWidth = Math.max(1, Math.min(21, img.width - deltaCmX)); newHeight = Math.max(1, Math.min(29.7, img.height + deltaCmY)); break;
          case 'ne': newWidth = Math.max(1, Math.min(21, img.width + deltaCmX)); newHeight = Math.max(1, Math.min(29.7, img.height - deltaCmY)); break;
          case 'nw': newWidth = Math.max(1, Math.min(21, img.width - deltaCmX)); newHeight = Math.max(1, Math.min(29.7, img.height - deltaCmY)); break;
          case 'n':  newHeight = Math.max(1, Math.min(29.7, img.height - deltaCmY)); break;
          case 's':  newHeight = Math.max(1, Math.min(29.7, img.height + deltaCmY)); break;
          case 'e':  newWidth = Math.max(1, Math.min(21, img.width + deltaCmX)); break;
          case 'w':  newWidth = Math.max(1, Math.min(21, img.width - deltaCmX)); break;
        }
        // Aspect ratio lock
        if (lockAspectRatio && img.width > 0 && img.height > 0) {
          const _ar = img.width / img.height;
          if (dir === 'e' || dir === 'w') {
            newHeight = Math.max(1, Math.min(29.7, newWidth / _ar));
          } else if (dir === 'n' || dir === 's') {
            newWidth = Math.max(1, Math.min(21, newHeight * _ar));
          } else {
            if (Math.abs(newWidth - img.width) >= Math.abs(newHeight - img.height)) {
              newHeight = Math.max(1, Math.min(29.7, newWidth / _ar));
            } else {
              newWidth = Math.max(1, Math.min(21, newHeight * _ar));
            }
          }
        }
        // Anchor-point position correction: keeps the opposite corner fixed under any rotation.
        // aX/aY are the normalized coords {-1,0,1} of the corner that must NOT move.
        const _anchors = { se:[-1,-1], sw:[1,-1], ne:[-1,1], nw:[1,1], n:[0,1], s:[0,-1], e:[-1,0], w:[1,0] };
        const [aX, aY] = _anchors[dir] || [-1,-1];
        const dw_px = (newWidth  - img.width)  / 21   * rect.width;
        const dh_px = (newHeight - img.height) / 29.7 * rect.height;
        const dx_px = -dw_px / 2 * (1 + aX * cosR) + dh_px / 2 * aY * sinR;
        const dy_px = -dh_px / 2 * (1 + aY * cosR) - dw_px / 2 * aX * sinR;
        const newX = img.x + dx_px / rect.width  * 100;
        const newY = img.y + dy_px / rect.height * 100;
        // Rotation-aware minimum bounds so the layout box can go negative when needed
        const _absC = Math.abs(cosR);
        const _absS = Math.abs(sinR);
        const _nWpx = (newWidth  / 21)   * rect.width;
        const _nHpx = (newHeight / 29.7) * rect.height;
        const _xMin = (_nWpx / 2 * _absC + _nHpx / 2 * _absS - _nWpx / 2) / rect.width  * 100;
        const _yMin = (_nWpx / 2 * _absS + _nHpx / 2 * _absC - _nHpx / 2) / rect.height * 100;
        setResizeWidth(newWidth);
        setResizeHeight(newHeight);
        return { ...img, width: newWidth, height: newHeight, x: Math.max(_xMin, newX), y: Math.max(_yMin, newY) };
      }));
    }
  };

  // Update handleMouseUp function
  const handleMouseUp = () => {
    // Marquee selection end -- find all shapes fully inside the drawn rectangle
    if (isMarqueeSelectingRef.current) {
      isMarqueeSelectingRef.current = false;
      const mq = marqueeRectRef.current;
      marqueeRectRef.current = null;
      setMarqueeRect(null);
      if (mq) {
        const minX = Math.min(mq.x1, mq.x2), maxX = Math.max(mq.x1, mq.x2);
        const minY = Math.min(mq.y1, mq.y2), maxY = Math.max(mq.y1, mq.y2);
        if (maxX - minX > 1 || maxY - minY > 1) {
          const _sel = canvasShapesRef.current.filter(s => {
            const sx1 = Math.min(s.x1, s.x2), sx2 = Math.max(s.x1, s.x2);
            const sy1 = Math.min(s.y1, s.y2), sy2 = Math.max(s.y1, s.y2);
            return sx1 <= maxX && sx2 >= minX && sy1 <= maxY && sy2 >= minY;
          });
          const _selImgs = canvasImagesRef.current.filter(img => {
            const ix1 = img.x, iy1 = img.y;
            const ix2 = img.x + (img.width / 21) * 100;
            const iy2 = img.y + (img.height / 29.7) * 100;
            return ix1 <= maxX && ix2 >= minX && iy1 <= maxY && iy2 >= minY;
          });
          const ids = [..._sel.map(s => s.id), ..._selImgs.map(i => i.id)];
          setSelectedShapeIds(new Set(ids));
          if (ids.length === 1) {
            const _singleIsImg = _selImgs.length === 1 && _sel.length === 0;
            if (_singleIsImg) {
              setSelectedImage(_selImgs[0].id);
              setResizeWidth(_selImgs[0].width);
              setResizeHeight(_selImgs[0].height);
              setSelectedShapeId(null);
            } else {
              setSelectedShapeId(ids[0]);
              setSelectedImage(null);
            }
            setCurrentControlSection('selectmove');
            setSelectMoveControlMode('position');
            setShowTopControls(true);
            marqueeJustFinishedRef.current = true;
          } else if (ids.length > 1) {
            setSelectedShapeId(null);
            setSelectedImage(null);
            setCurrentControlSection('selectmove');
            setSelectMoveControlMode('position');
            setShowTopControls(true);
            marqueeJustFinishedRef.current = true;
          }
        }
      }
      return;
    }

    // Multi-shape/image drag end
    if (multiDragRef.current) {
      multiDragRef.current = null;
      shapeDraggedRef.current = false;
      if (shapeBeforeRef.current) {
        pushShapeHistory(shapeBeforeRef.current, canvasShapesRef.current);
        shapeBeforeRef.current = null;
      }
      if (dragStartImagesRef.current) {
        pushImageHistory(dragStartImagesRef.current, canvasImagesRef.current);
        dragStartImagesRef.current = null;
      }
      markDirty();
      return;
    }

    // Shape rotate end
    if (rotatingShapeRef.current) {
      if (shapeBeforeRef.current) {
        pushShapeHistory(shapeBeforeRef.current, canvasShapesRef.current);
        shapeBeforeRef.current = null;
      }
      rotatingShapeRef.current = null;
      markDirty();
      return;
    }

    // Shape resize end
    if (resizingShapeRef.current) {
      if (shapeBeforeRef.current) {
        pushShapeHistory(shapeBeforeRef.current, canvasShapesRef.current);
        shapeBeforeRef.current = null;
      }
      resizingShapeRef.current = null;
      markDirty();
      return;
    }

    // Shape drag end
    if (draggingShapeRef.current) {
      const { id } = draggingShapeRef.current;
      draggingShapeRef.current = null;
      const didDrag = shapeDraggedRef.current;
      shapeDraggedRef.current = false;
      if (shapeBeforeRef.current) {
        pushShapeHistory(shapeBeforeRef.current, canvasShapesRef.current);
        shapeBeforeRef.current = null;
      }
      markDirty();
      // Double-tap/click on already-selected text shape -> enter inline edit mode
      if (textEditCandidateRef.current === id && !didDrag) {
        const now = Date.now();
        const lastTap = textLastTapRef.current;
        if (lastTap.id === id && now - lastTap.time < 500) {
          // Second tap within 500ms -> open editor
          const shape = canvasShapesRef.current.find(s => s.id === id);
          if (shape) {
            if (shape.strokeColor) setShapeStrokeColor(shape.strokeColor);
            if (shape.fontSize)    setShapeFontSize(shape.fontSize);
            if (shape.fontFamily)  setShapeFontFamily(shape.fontFamily);
            if (shape.bold   != null) setShapeTextBold(shape.bold);
            if (shape.italic != null) setShapeTextItalic(shape.italic);
            setPendingTextShapeId(shape.id);
            setPendingTextValue(shape.text || '');
            setPendingTextPos({ x: shape.x1, y: shape.y1 });
          }
          textLastTapRef.current = { id: null, time: 0 };
        } else {
          textLastTapRef.current = { id, time: now };
        }
      }
      textEditCandidateRef.current = null;
      // After a move-drag, switch to position display
      if (didDrag && imageSelectionMode) {
        if (didDrag) textLastTapRef.current = { id: null, time: 0 }; // reset double-tap on drag
        setCurrentControlSection('selectmove');
        setSelectMoveControlMode('position');
        setShowTopControls(true);
      }
      return;
    }

    // Shape draw finalize
    if (isDrawingShape && shapeDrawStart && shapeDrawEnd) {
      const dx = Math.abs(shapeDrawEnd.x - shapeDrawStart.x);
      const dy = Math.abs(shapeDrawEnd.y - shapeDrawStart.y);
      if (dx > 0.5 || dy > 0.5) {
        const newShape = {
          id: Date.now(), type: activeShapeTool,
          x1: shapeDrawStart.x, y1: shapeDrawStart.y,
          x2: shapeDrawEnd.x,   y2: shapeDrawEnd.y,
          strokeColor: shapeStrokeColor, fillColor: shapeFillColor,
          strokeWidth: shapeStrokeWidth, dashed: shapeDashed, zOrder: canvasShapes.length,
          layerIndex: ++layerCounterRef.current,
        };
        const _before = canvasShapesRef.current;
        const _after = [..._before, newShape];
        setCanvasShapes(_after);
        setSelectedShapeId(newShape.id);
        pushShapeHistory(_before, _after);
      }
      setIsDrawingShape(false);
      setShowToolsPanel(false);
      setShapeDrawStart(null);
      setShapeDrawEnd(null);
      return;
    }

    if (isSelecting) {
      // Circle crop: pointer-up just ends the drag interaction; don't execute the crop yet
      if (selectionMode === 'circle' || selectionMode === 'rectangle') {
        circleCropInteractRef.current = null;
        setCanvasCursor('move');
        return;
      }
      // Freeform crop: don't auto-confirm on release — keep the drawn path visible so
      // the user can pick Copy or Cut from the toolbar. We DO turn off `isSelecting`
      // so subsequent mouse-moves can't extend the polygon (the buttons key off
      // `selectionPath.length > 2`, not `isSelecting`).
      if (selectionMode === 'freeform') {
        setIsSelecting(false);
        return;
      }
      // Smart crop: lasso released — compute bbox from drawn path, run detection inside it
      if (selectionMode === 'smart') {
        // If detection result already showing, don't re-run — user must tap ✓ or draw a new lasso
        if (smartDetectedRef.current) return;
        const path = selectionPath;
        if (path.length > 3) {
          const xs = path.map(p => p.x), ys = path.map(p => p.y);
          const sx = Math.min(...xs), sy = Math.min(...ys);
          const sw2 = Math.max(...xs) - sx, sh2 = Math.max(...ys) - sy;
          if (sw2 > 10 && sh2 > 10) {
            runSmartCrop(sx + sw2 / 2, sy + sh2 / 2, { x: sx, y: sy, w: sw2, h: sh2 });
          } else {
            smartDetectedRef.current = false;
            setSmartDetected(false);
            setIsSelecting(false);
            setSelectionStart({ x: 0, y: 0 }); setSelectionEnd({ x: 0, y: 0 });
            setSelectionPath([]);
          }
        } else {
          smartDetectedRef.current = false;
          setSmartDetected(false);
          setIsSelecting(false);
          setSelectionStart({ x: 0, y: 0 }); setSelectionEnd({ x: 0, y: 0 });
          setSelectionPath([]);
        }
        return;
      }
      endSelection();
      return;
    }
    
    if (isDrawing && currentPath.length > 0) {
      const _rect = canvasRef.current ? canvasRef.current.getBoundingClientRect() : null;
      const newPath = {
        id: Date.now(),
        path: currentPath,
        color: drawingMode === 'eraser' ? 'eraser' : drawingColor,
        size: brushSize,
        type: drawingMode,
        canvasW: _rect ? _rect.width : 800,
        canvasH: _rect ? _rect.height : 800 * A4_RATIO,
      };
      
      setDrawingPaths(prev => [...prev, newPath]);
      markDirty();
      const newHistory = drawingHistory.slice(0, historyIndex + 1);
      newHistory.push({ type: 'draw', path: newPath });
      setDrawingHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
      
      setCurrentPath([]);
    }
    
    const wasDragging = !!draggingImage;
    const wasResizing = !!resizingImage;
    const wasRotatingImage = !!rotatingImage;

    if (wasDragging && dragStartImagesRef.current) {
      pushImageHistory(dragStartImagesRef.current, canvasImagesRef.current);
      dragStartImagesRef.current = null;
    }
    if (wasResizing && resizeStartImagesRef.current) {
      pushImageHistory(resizeStartImagesRef.current, canvasImagesRef.current);
      resizeStartImagesRef.current = null;
    }
    if (wasRotatingImage && rotateImageCenterRef.current?.before) {
      pushImageHistory(rotateImageCenterRef.current.before, canvasImagesRef.current);
      rotateImageCenterRef.current = null;
    }

    setDraggingImage(null);
    setResizingImage(null);
    setRotatingImage(null);
    resizeDirectionRef.current = null;
    setIsDrawing(false);
    // Keep isSelecting true for circle, rectangle and smart crop so the overlay stays visible
    if ((selectionMode !== 'circle' && selectionMode !== 'rectangle' && selectionMode !== 'smart') || activeTool !== 'selection') {
      setIsSelecting(false);
    }

    // After a resize finishes, keep controls visible -- stay in selectmove/resize
    if (wasResizing) {
      setCurrentControlSection('selectmove');
      setSelectMoveControlMode('resize');
      setShowTopControls(true);
    } else if (wasRotatingImage) {
      // After image rotation drag, keep A° visible
      setCurrentControlSection('selectmove');
      setSelectMoveControlMode('rotate');
      setShowTopControls(true);
    } else if (wasDragging && imageSelectionMode) {
      // After a move-drag, switch to position display
      setCurrentControlSection('selectmove');
      setSelectMoveControlMode('position');
      setShowTopControls(true);
    }
  };

  // Handle touch end
  const handleTouchEnd = () => {
    // Always release the touch-active gate so a stray touchmove can't keep drawing.
    touchSelectionActiveRef.current = false;
    // Clear the live pen cursor for selection tools that show one
    if (selectionMode === 'freeform' || selectionMode === 'smart') {
      setSelectionTouchPos(null);
    }

    // Smart crop fastpath: bypass handleMouseUp's stale `selectionPath` closure and use
    // the ref-based path captured during touchmove (mobile fix).
    if (activeTool === 'selection' && selectionMode === 'smart') {
      if (smartDetectedRef.current) return;
      const path = smartLassoPathRef.current;
      if (path.length > 3) {
        const xs = path.map(p => p.x), ys = path.map(p => p.y);
        const sx = Math.min(...xs), sy = Math.min(...ys);
        const sw2 = Math.max(...xs) - sx, sh2 = Math.max(...ys) - sy;
        if (sw2 > 10 && sh2 > 10) {
          runSmartCrop(sx + sw2 / 2, sy + sh2 / 2, { x: sx, y: sy, w: sw2, h: sh2 });
          smartLassoPathRef.current = [];
          return;
        }
      }
      smartLassoPathRef.current = [];
      smartDetectedRef.current = false;
      setSmartDetected(false);
      setIsSelecting(false);
      setSelectionStart({ x: 0, y: 0 });
      setSelectionEnd({ x: 0, y: 0 });
      setSelectionPath([]);
      return;
    }

    handleMouseUp();
  };

  // Handle image click
  const handleImageClick = (imageId, e) => {
    e.stopPropagation();
    const image = canvasImages.find(img => img.id === imageId);
    if (image) {
      setSelectedImage(imageId);
      setSelectedShapeId(null);
      setResizeWidth(image.width);
      setResizeHeight(image.height);
      setShowImageControls(imageId);
      
      // If we're in crop selection mode, stay in crop mode
      if (activeTool === 'selection') {
        // Stay in crop selection mode
        setCurrentControlSection('selection');
        setShowTopControls(true);
        setImageSelectionMode(false);
        setCanvasCursor('crosshair');
      } else if (activeTool === 'draw') {
        // Stay in draw mode
        setCurrentControlSection('draw');
        setShowTopControls(true);
        setImageSelectionMode(false);
      } else if (imageSelectionMode) {
        // Already in select/move mode -- keep it, refresh position display
        setCurrentControlSection('selectmove');
        setSelectMoveControlMode(prev => prev === 'resize' ? 'resize' : 'position');
        setShowTopControls(true);
      } else if (activeTool === 'magic-wand') {
        // In magic wand mode -- select the image without clearing the tool
        if (magicSubTool === 'chat') {
          // Chat mode: toggle image in selection; max 2 images (FIFO eviction)
          setMagicChatImages(prev => {
            if (prev.includes(imageId)) return prev.filter(id => id !== imageId);
            const next = prev.length >= 2 ? [...prev.slice(1), imageId] : [...prev, imageId];
            return next;
          });
        }
        setImageSelectionMode(false);
        // Clicking an image while brightness/contrast is active deactivates the sub-tool
        if (magicSubTool === 'brightness' || magicSubTool === 'contrast') {
          setMagicSubTool(null);
          setCurrentControlSection(null);
          setShowTopControls(false);
        } else if (magicSubTool === 'color') { setCurrentControlSection('magic-color'); setShowTopControls(true); }
        else { setCurrentControlSection(null); setShowTopControls(false); }
        setCanvasCursor('pointer');
      } else {
        // No active tool -- just select the image; user presses Select/Move separately
        setActiveTool(null);
        setImageSelectionMode(false);
        if (image.name === 'fill') {
          // Fill shapes: show colour palette so the user can repaint
          setCurrentControlSection('draw');
          setShowTopControls(true);
        } else {
          setCurrentControlSection(null);
          setShowTopControls(false);
        }
        setCanvasCursor('pointer');
      }
    }
  };

  // Handle image copy
  const handleCopyImage = (imageId, e) => {
    e.stopPropagation();
    const image = canvasImages.find(img => img.id === imageId);
    if (image) {
      const newImage = {
        ...image,
        id: Date.now(),
        layerIndex: ++layerCounterRef.current,
        x: image.x + 2,
        y: image.y + 2,
      };
      pushImageHistory(canvasImages, [...canvasImages, newImage]);
      setCanvasImages(prev => [...prev, newImage]);
      setSelectedImage(newImage.id);
      setShowImageControls(newImage.id);
    }
  };

  const handleCopyShape = () => {
    const shape = canvasShapes.find(s => s.id === selectedShapeId);
    if (shape) {
      const newShape = {
        ...shape,
        id: Date.now(),
        x1: shape.x1 + 2,
        y1: shape.y1 + 2,
        x2: shape.x2 + 2,
        y2: shape.y2 + 2,
        zOrder: canvasShapes.length,
        layerIndex: ++layerCounterRef.current,
      };
      const before = canvasShapesRef.current;
      const after = [...before, newShape];
      setCanvasShapes(after);
      setSelectedShapeId(newShape.id);
      pushShapeHistory(before, after);
    }
  };

  // Handle image delete
  const handleDeleteImage = (imageId) => {
    const image = canvasImages.find(img => img.id === imageId);
    if (!image) return;

    setSelectedImage(imageId);
    setShowImageControls(imageId);
    if (activeTool !== 'magic-wand') setActiveTool('delete');
    setCurrentControlSection('delete');
    setShowTopControls(true);
  };

  // Handle bulk delete options
  const handleBulkDeleteOption = (option) => {
    if (!selectedImage) return;

    if (option === 'multiple') {
      // Open bucket for multiple delete
      setIsBulkDeleteModalOpen(true);
    }
  };

  // Delete from bucket
  const handleDeleteFromBucket = async (image) => {
    if (deleting) return;
    
    setDeleting(true);
    try {
      const imagePath = image.bucketPath || image.name;
      
      await deleteFromStorage(imagePath, getEnvStorageBucket());
      
      // Remove from canvas
      setCanvasImages(prev => prev.filter(img => img.id !== selectedImage));
      setSelectedImage(null);
      setShowImageControls(null);
      setActiveTool(null);
      setCurrentControlSection(null);
      setShowTopControls(false);
      
      // Reload bucket images (force: file was deleted)
      await loadBucketImages(true);
      
    } catch (error) {
      console.error('Failed to delete image:', error);
      showAlert(`Failed to delete image: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // Delete from cloud only (keep on canvas)
  const handleDeleteFromBucketCloudOnly = async (image) => {
    if (deleting) return;
    setDeleting(true);
    try {
      const imagePath = image.bucketPath || image.name;
      await deleteFromStorage(imagePath, getEnvStorageBucket());
      setActiveTool(null);
      setCurrentControlSection(null);
      setShowTopControls(false);
      await loadBucketImages(true);
    } catch (error) {
      console.error('Failed to delete image from Cloud:', error);
      showAlert(`Failed to delete image: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // Delete multiple images from bucket
  const handleDeleteMultipleFromBucket = async () => {
    if (selectedBucketImages.length === 0) {
      showAlert('Please select images to delete');
      return;
    }

    setDeleting(true);
    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error('No authenticated user found');
      
      let deletedCount = 0;
      let errorCount = 0;

      // Delete each selected image
      for (const imageFileName of selectedBucketImages) {
        try {
          await deleteFromStorage(imageFileName, getEnvStorageBucket());
          
          deletedCount++;
          
          // Remove from canvas if it exists there
          const fileNameOnly = imageFileName.split('/').pop();
          setCanvasImages(prev => prev.filter(img => img.name !== fileNameOnly && img.bucketPath !== imageFileName));
          
        } catch (error) {
          console.error(`Failed to delete ${imageFileName}:`, error);
          errorCount++;
        }
      }

      // Silently reload bucket images (no modal/alert — list updates in place)
      await loadBucketImages(true);

      // Reset selection and confirm state
      setSelectedBucketImages([]);
      setConfirmDeleteBucket(false);

      // Show inline feedback in the header center slot, auto-clear after 3 s
      const msg = errorCount === 0
        ? { text: `Deleted ${deletedCount} image${deletedCount !== 1 ? 's' : ''}`, ok: true }
        : { text: `Deleted ${deletedCount}, failed ${errorCount}`, ok: false };
      setBucketDeleteMsg(msg);
      setTimeout(() => setBucketDeleteMsg(null), 3000);

    } catch (error) {
      console.error('Failed to delete images from bucket:', error);
      setBucketDeleteMsg({ text: `Delete failed: ${error.message}`, ok: false });
      setTimeout(() => setBucketDeleteMsg(null), 4000);
    } finally {
      setDeleting(false);
    }
  };

  // Insert multiple images from bucket to canvas
  const handleInsertMultipleFromBucket = async () => {
    if (selectedBucketImages.length === 0) {
      showAlert('Please select images to insert');
      return;
    }
    setIsLoading(true);
    const newImages = [];
    for (const imageFileName of selectedBucketImages) {
      try {
        const result = await readObjectContent(imageFileName, getEnvStorageBucket());
        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = result.downloadUrl;
        });
        const naturalAspectRatio = img.naturalWidth / img.naturalHeight;
        const cropShape = detectCircleCrop(img);
        // Back-calculate cm size from 300-DPI pixel dimensions, preserving aspect ratio.
        const rawW2 = img.naturalWidth  * 2.54 / 300;
        const rawH2 = img.naturalHeight * 2.54 / 300;
        const fitScale2 = Math.min(1, 21 / rawW2, 29.7 / rawH2);
        const width  = Math.max(0.5, rawW2 * fitScale2);
        const height = Math.max(0.5, rawH2 * fitScale2);
        newImages.push({
          id: Date.now() + newImages.length,
          layerIndex: ++layerCounterRef.current,
          url: result.downloadUrl,
          name: imageFileName.split('/').pop(),
          size: formatFileSize(result.size || 0),
          x: Math.max(0, 50 - (width / 21 * 50)),
          y: Math.max(0, 50 - (height / 29.7 * 50)),
          width,
          height,
          source: 'bucket',
          cropShape,
          bucketPath: imageFileName,
          naturalWidth: img.naturalWidth,
          naturalHeight: img.naturalHeight,
        });
      } catch (error) {
        console.error(`Failed to load ${imageFileName}:`, error);
      }
    }
    if (newImages.length > 0) {
      const before = canvasImagesRef.current;
      const after = [...before, ...newImages];
      pushImageHistory(before, after);
      setCanvasImages(after);
      const last = newImages[newImages.length - 1];
      setSelectedImage(last.id);
      setResizeWidth(last.width);
      setResizeHeight(last.height);
      setAspectRatio(last.naturalWidth / last.naturalHeight);
    }
    setSelectedBucketImages([]);
    setImageSelectionMode(false);
    if (activeTool !== 'magic-wand') {
      setActiveTool(null);
      setCurrentControlSection(null);
      setShowTopControls(false);
    }
    setCanvasCursor('pointer');
    setIsLoading(false);
    setShowBucketModal(false);
  };

  // Apply resizeWidth/resizeHeight to the selected canvas image.
  // Aspect-ratio lock is handled at the call-site (step buttons / drag), so just apply values here.
  useEffect(() => {
    // Skip during active drag resize — the drag handler updates canvasImages directly each frame.
    // Running here during drag would overwrite with the previous render's stale dimensions (jitter).
    if (selectedImage && !resizingImage) {
      setCanvasImages(prev => prev.map(img => {
        if (img.id === selectedImage) {
          return { ...img, width: resizeWidth, height: resizeHeight };
        }
        return img;
      }));
    }
  }, [resizeWidth, resizeHeight, selectedImage, resizingImage]);

  // Handle canvas click
  const handleFillClick = (e) => {
    const drawCanvas = drawingCanvasRef.current;
    const container = canvasRef.current;
    if (!drawCanvas || !container) return;
    const rect = container.getBoundingClientRect();
    const cW = drawCanvas.width;
    const cH = drawCanvas.height;
    if (!cW || !cH) return;
    const scaleX = cW / rect.width;
    const scaleY = cH / rect.height;
    const px = Math.round((e.clientX - rect.left) * scaleX);
    const py = Math.round((e.clientY - rect.top) * scaleY);
    if (px < 0 || py < 0 || px >= cW || py >= cH) return;
    const fillRgb = hexToRgb(drawingColor);
    if (!fillRgb) return;
    const ctx = drawCanvas.getContext('2d', { willReadFrequently: true });
    const imageData = ctx.getImageData(0, 0, cW, cH);
    const origData = new Uint8ClampedArray(imageData.data);
    const filled = floodFill(imageData.data, cW, cH, px, py, fillRgb);
    if (!filled || filled > cW * cH * 0.95) return; // nothing or leaked
    // Find bounding box of filled pixels
    let minX = cW, minY = cH, maxX = 0, maxY = 0;
    const fillOnly = new Uint8ClampedArray(cW * cH * 4);
    for (let i = 0; i < imageData.data.length; i += 4) {
      if (imageData.data[i] !== origData[i] || imageData.data[i+1] !== origData[i+1] ||
          imageData.data[i+2] !== origData[i+2] || imageData.data[i+3] !== origData[i+3]) {
        fillOnly[i] = imageData.data[i]; fillOnly[i+1] = imageData.data[i+1];
        fillOnly[i+2] = imageData.data[i+2]; fillOnly[i+3] = 255;
        const pIdx = i / 4;
        const fpx = pIdx % cW, fpy = Math.floor(pIdx / cW);
        if (fpx < minX) minX = fpx; if (fpy < minY) minY = fpy;
        if (fpx > maxX) maxX = fpx; if (fpy > maxY) maxY = fpy;
      }
    }
    if (minX > maxX) return;
    // Tight bbox of flood-fill interior pixels
    const cropW = maxX - minX + 1, cropH = maxY - minY + 1;
    // Guard: if fill density in bbox is very low, the shape likely leaked through a gap
    if (filled / (cropW * cropH) < 0.04) return;
    // Build fill canvas containing only the flood-filled interior pixels (no stroke)
    const fillCanvas = document.createElement('canvas');
    fillCanvas.width = cropW; fillCanvas.height = cropH;
    const fCtx = fillCanvas.getContext('2d');
    const cropData = fCtx.createImageData(cropW, cropH);
    for (let row = 0; row < cropH; row++) {
      for (let col = 0; col < cropW; col++) {
        const si = ((minY + row) * cW + (minX + col)) * 4;
        const di = (row * cropW + col) * 4;
        cropData.data[di]   = fillOnly[si];   cropData.data[di+1] = fillOnly[si+1];
        cropData.data[di+2] = fillOnly[si+2]; cropData.data[di+3] = fillOnly[si+3];
      }
    }
    fCtx.putImageData(cropData, 0, 0);
    const dataUrl = fillCanvas.toDataURL('image/png');
    // width/height in A4 cm units (render always divides by 21 / 29.7)
    const newImage = {
      id: Date.now(),
      layerIndex: ++layerCounterRef.current,
      url: dataUrl,
      name: 'fill',
      size: '',
      x: (minX / cW) * 100,
      y: (minY / cH) * 100,
      width:  (cropW / cW) * 21,
      height: (cropH / cH) * 29.7,
      source: 'local',
    };
    // Remove all drawing strokes that overlap the filled area (the closed boundary lines).
    // Expand by brushSize to ensure stroke centre-points (which lie on/near the bbox edge) are caught.
    const strokeR = Math.ceil(brushSize / 2) + 2;
    setDrawingPaths(prev => prev.filter(path => {
      // path.canvasW stores the rect.width at draw time; since drawCanvas.width == rect.width
      // the scale is always 1, but handle the general case anyway.
      const s = (path.canvasW || cW) / cW;
      const padMinX = (minX - strokeR) * s, padMaxX = (maxX + strokeR) * s;
      const padMinY = (minY - strokeR) * s, padMaxY = (maxY + strokeR) * s;
      return !path.path.some(pt =>
        pt.x >= padMinX && pt.x <= padMaxX &&
        pt.y >= padMinY && pt.y <= padMaxY
      );
    }));
    pushImageHistory(canvasImagesRef.current, [...canvasImagesRef.current, newImage]);
    setCanvasImages(prev => [...prev, newImage]);
    // Sync resizeWidth/resizeHeight BEFORE setting selectedImage so the resize useEffect
    // sees the correct fill dimensions in the same React batch and does not overwrite them.
    setResizeWidth(newImage.width);
    setResizeHeight(newImage.height);
    setSelectedImage(newImage.id);
    setShowImageControls(newImage.id);
    // Select the image silently -- FaSyncAlt (90° snap) is visible in toolbar;
    // user presses Select/Move for fine position/rotation controls.
    setFillModeActive(false);
    setActiveTool(null);
    setActiveShapeTool(null);
    setImageSelectionMode(false);
    setCurrentControlSection(null);
    setShowTopControls(false);
    setCanvasCursor('pointer');
  };

  const handleCanvasClick = async (e) => {
    if (pendingNavPath) setPendingNavPath(null);

    // Eyedropper: sample pixel colour on click
    if (eyedropperActive) {
      const canvas = canvasRef.current;
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        // Try drawing canvas first
        const dc = drawingCanvasRef.current;
        let picked = null;
        if (dc) {
          const dctx = dc.getContext('2d', { willReadFrequently: true });
          const px = dctx.getImageData(Math.floor(cx), Math.floor(cy), 1, 1).data;
          if (px[3] > 10) picked = rgbToHex(px[0], px[1], px[2]);
        }
        if (!picked) {
          // Sample from topmost image under pointer
          const sorted = [...canvasImages].sort((a, b) => (b.layerIndex ?? 1) - (a.layerIndex ?? 1));
          for (const img of sorted) {
            const il = (img.x / 100) * rect.width;
            const it = (img.y / 100) * rect.height;
            const iw = (img.width / 21) * rect.width;
            const ih = (img.height / 29.7) * rect.height;
            if (cx >= il && cx <= il + iw && cy >= it && cy <= it + ih) {
              try {
                const el = new Image();
                el.crossOrigin = 'anonymous';
                await new Promise((res, rej) => { el.onload = res; el.onerror = rej; el.src = img.url; });
                const tc = document.createElement('canvas'); tc.width = 1; tc.height = 1;
                const tctx = tc.getContext('2d');
                tctx.drawImage(el, -(cx - il) * (el.naturalWidth / iw), -(cy - it) * (el.naturalHeight / ih), el.naturalWidth, el.naturalHeight);
                const px2 = tctx.getImageData(0, 0, 1, 1).data;
                if (px2[3] > 10) picked = rgbToHex(px2[0], px2[1], px2[2]);
              } catch (_) {}
              break;
            }
          }
        }
        if (picked) { setDrawingColor(picked); setPickedColor(picked); }
      }
      setEyedropperActive(false);
      setCanvasCursor('crosshair');
      return;
    }

    setShowToolsPanel(false);
    // Reset save/share/print active state on canvas click
    if (['save', 'share', 'print'].includes(activeTool)) {
      setActiveTool(null);
      setCurrentControlSection(null);
      setShowTopControls(false);
      return;
    }
    // Skip if a marquee drag just finished -- the click fires right after mouseup
    if (marqueeJustFinishedRef.current) {
      marqueeJustFinishedRef.current = false;
      return;
    }
    // If we're in image selection mode or no tool is active, handle deselection
    if (imageSelectionMode || (!activeTool && !imageSelectionMode)) {
      if (shapeClickedRef.current) {
        shapeClickedRef.current = false;
        shapeDraggedRef.current = false;
        return;
      }
      setMagicSubTool(null);
      setSelectedImage(null);
      setShowImageControls(null);
      setSelectedShapeId(null);
      setSelectedShapeIds(new Set());
      setActiveShapeTool(null);
      setImageSelectionMode(false);
      setActiveTool(null);
      setCurrentControlSection(null);
      setCanvasCursor('default');
      setShowTopControls(false);
    } else if (activeTool === 'selection') {
      // Smart mode: canvas background click resets if not actively selecting/detecting
      if (selectionMode === 'smart') {
        if (!isSelecting && !smartCropRunning) {
          smartDetectedRef.current = false;
          setSmartDetected(false);
          setSelectionStart({ x: 0, y: 0 }); setSelectionEnd({ x: 0, y: 0 });
          setSelectionPath([]);
          setActiveTool(null); setSelectionMode('rectangle');
          setCurrentControlSection(null); setShowTopControls(false);
          setCanvasCursor('default');
        }
        return;
      }
      // Freeform mode: if a path was drawn, keep it visible so Copy/Cut buttons remain.
      // Only reset on a clean click without a drawn path (user clicking the canvas to cancel).
      if (selectionMode === 'freeform') {
        if (selectionPath.length > 2 || isSelecting) {
          return; // preserve the freeform shape & Copy/Cut buttons
        }
        // empty path → fall through to the catch-all reset below
      }
      // Canvas background click: reset selection overlay and deactivate mode
      // Circle / rectangle crop: clicking OUTSIDE the shape cancels; inside/edge is handled by mousedown drag.
      if (selectionMode === 'circle' || selectionMode === 'rectangle') {
        const canvas = canvasRef.current;
        if (canvas && selectionStart && selectionEnd && selectionStart.x !== 0) {
          const _rect = canvas.getBoundingClientRect();
          const cx = e.clientX - _rect.left;
          const cy = e.clientY - _rect.top;
          if (selectionMode === 'circle') {
            const ecx = (selectionStart.x + selectionEnd.x) / 2;
            const ecy = (selectionStart.y + selectionEnd.y) / 2;
            const erx = Math.abs(selectionEnd.x - selectionStart.x) / 2;
            const ery = Math.abs(selectionEnd.y - selectionStart.y) / 2;
            const normDist = (erx > 0 && ery > 0) ? Math.sqrt(((cx - ecx) / erx) ** 2 + ((cy - ecy) / ery) ** 2) : 2;
            if (normDist <= 1.3) return; // inside or near edge — mousedown drag handled it
          } else {
            const rx1 = Math.min(selectionStart.x, selectionEnd.x);
            const ry1 = Math.min(selectionStart.y, selectionEnd.y);
            const rx2 = Math.max(selectionStart.x, selectionEnd.x);
            const ry2 = Math.max(selectionStart.y, selectionEnd.y);
            const EDGE = 16;
            if (cx >= rx1 - EDGE && cx <= rx2 + EDGE && cy >= ry1 - EDGE && cy <= ry2 + EDGE) return; // inside/near edge
          }
        }
        // Click was outside — cancel crop
        setIsSelecting(false);
        setSelectionStart({ x: 0, y: 0 }); setSelectionEnd({ x: 0, y: 0 });
        setSelectionPath([]);
        setActiveTool(null); setSelectionMode('rectangle');
        setCurrentControlSection(null); setShowTopControls(false);
        setCanvasCursor('default');
        return;
      }
      setIsSelecting(false);
      setSelectionStart({ x: 0, y: 0 });
      setSelectionEnd({ x: 0, y: 0 });
      setSelectionPath([]);
      setActiveTool(null);
      setSelectionMode('rectangle');
      setCurrentControlSection(null);
      setShowTopControls(false);
      setCanvasCursor('default');
      return;
    } else if (activeTool === 'draw') {
      if (fillModeActive) { handleFillClick(e); }
      // Stay in draw mode -- keep controls visible
      return;
    } else if (activeTool === 'shapes') {
      if (shapeClickedRef.current) {
        shapeClickedRef.current = false;
        shapeDraggedRef.current = false;
        return;
      }
      // Create text at click position
      if (activeShapeTool === 'text' && !shapeDraggedRef.current) {
        if (!selectedShapeId) {
          const canvas = canvasRef.current;
          if (canvas) {
            const rect = canvas.getBoundingClientRect();
            const xPct = Math.max(0, Math.min(90, ((e.clientX - rect.left) / rect.width) * 100));
            const _rawYPct = ((e.clientY - rect.top) / rect.height) * 100;
            const y1Pct = Math.max(0, Math.min(95, _rawYPct - (shapeFontSize * 0.85 / rect.height) * 100));
            setPendingTextPos({ x: xPct, y: y1Pct });
            setPendingTextValue('');
          }
        }
        setSelectedShapeId(null);
        setActiveShapeTool(null);
        setActiveTool(null);
        setCanvasCursor('default');
        setCurrentControlSection(null);
        setShowTopControls(false);
        setShowToolsPanel(false);
        shapeDraggedRef.current = false;
        return;
      }
      if (!isDrawingShape) {
        setSelectedShapeId(null);
        setActiveShapeTool(null);
        setActiveTool(null);
        setCanvasCursor('default');
        setShowShapeColorPicker(false);
        setShowShapeColorShades(false);
        setCurrentControlSection(null);
        setShowTopControls(false);
        setShowToolsPanel(false);
      }
      shapeDraggedRef.current = false;
      return;
    } else if (activeTool === 'magic-wand') {
      // Clicking canvas while brightness/contrast is active deactivates the sub-tool
      if (magicSubTool === 'brightness' || magicSubTool === 'contrast') {
        setMagicSubTool(null);
      }
      setSelectedImage(null);
      setShowImageControls(null);
      setSelectedShapeId(null);
      setShowTopControls(false);
      setCurrentControlSection(null);
    } else {
      // For other tools, just clear selection
      setSelectedImage(null);
      setShowImageControls(null);
      setSelectedShapeId(null);
      setShowTopControls(false);
      setCurrentControlSection(null);
      if (magicSubTool === 'brightness' || magicSubTool === 'contrast') setMagicSubTool(null);
    }
  };

  // Update startDrawing function
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Marquee select: when imageSelectionMode is on and user drags canvas background
    if (imageSelectionMode) {
      const xPct = (x / rect.width) * 100;
      const yPct = (y / rect.height) * 100;
      isMarqueeSelectingRef.current = true;
      const mq = { x1: xPct, y1: yPct, x2: xPct, y2: yPct };
      marqueeRectRef.current = mq;
      setMarqueeRect(mq);
      setSelectedShapeIds(new Set());
      setSelectedShapeId(null);
      setShowTopControls(false);
      setCurrentControlSection(null);
      return;
    }

    if (activeTool === 'selection' && selectionMode) {
      e.preventDefault();
      e.stopPropagation();

      // Smart crop: user draws a lasso/pencil path around the object.
      // If detection result is already showing, ignore mousedown so user can tap ✓.
      if (selectionMode === 'smart') {
        if (smartDetectedRef.current) return; // polygon showing — don't restart lasso
        smartDetectedRef.current = false;
        setSmartDetected(false);
        setIsSelecting(true);
        setSelectionPath([{ x, y }]);
        setSelectionStart({ x, y });
        setSelectionEnd({ x, y });
        return;
      }

      setIsSelecting(true);
      // Circle crop: interact with pre-placed overlay
      if (selectionMode === 'circle' && selectionStart.x !== 0) {
        const cx = (selectionStart.x + selectionEnd.x) / 2;
        const cy = (selectionStart.y + selectionEnd.y) / 2;
        const rx = Math.abs(selectionEnd.x - selectionStart.x) / 2;
        const ry = Math.abs(selectionEnd.y - selectionStart.y) / 2;
        const dx = x - cx, dy = y - cy;
        const normDist = (ry > 0 && rx > 0) ? Math.sqrt((dx/rx)**2 + (dy/ry)**2) : 2;
        // Ignore clicks far outside the circle — don't reset or resize
        if (normDist > 1.3) return;
        const interactType = normDist < 0.85 ? 'move' : 'resize';
        circleCropInteractRef.current = { type: interactType, startX: x, startY: y, origStart: { ...selectionStart }, origEnd: { ...selectionEnd } };
        return;
      }
      // Rectangle crop: interact with pre-placed overlay
      if (selectionMode === 'rectangle' && selectionStart.x !== 0) {
        const rx1 = Math.min(selectionStart.x, selectionEnd.x);
        const ry1 = Math.min(selectionStart.y, selectionEnd.y);
        const rx2 = Math.max(selectionStart.x, selectionEnd.x);
        const ry2 = Math.max(selectionStart.y, selectionEnd.y);
        const EDGE = 16;
        const insideX = x > rx1 + EDGE && x < rx2 - EDGE;
        const insideY = y > ry1 + EDGE && y < ry2 - EDGE;
        const outside = x < rx1 - EDGE || x > rx2 + EDGE || y < ry1 - EDGE || y > ry2 + EDGE;
        if (outside) return;
        const interactType = (insideX && insideY) ? 'move' : 'resize';
        circleCropInteractRef.current = { type: interactType, startX: x, startY: y, origStart: { ...selectionStart }, origEnd: { ...selectionEnd } };
        return;
      }
      setSelectionStart({ x, y });
      if (selectionMode === 'freeform') {
        setSelectionPath([{ x, y }]);
      } else {
        setSelectionEnd({ x, y });
      }
      return;
    }
    
    if (eyedropperActive) return; // click handled by handleCanvasClick

    if (activeTool !== 'draw' || (drawingMode !== 'pen' && drawingMode !== 'pencil' && drawingMode !== 'eraser' && drawingMode !== 'spray')) {
      // Shape drawing
      if (activeTool === 'shapes' && activeShapeTool) {
        const xPct = (x / rect.width) * 100;
        const yPct = (y / rect.height) * 100;
        if (activeShapeTool === 'text') {
          // Text is created on click (handleCanvasClick), not mousedown
        } else if (activeShapeTool === 'emoji') {
          if (selectedShapeId) {
            setSelectedShapeId(null);
            setActiveShapeTool(null);
            setActiveTool(null);
            setCanvasCursor('default');
            setCurrentControlSection(null);
            setShowTopControls(false);
            return;
          }
          if (!selectedEmoji) {
            setActiveShapeTool(null);
            setActiveTool(null);
            setCanvasCursor('default');
            return;
          }
          const _eBefore = canvasShapesRef.current;
          const _cW = canvasRef.current?.offsetWidth  || 595;
          const _cH = canvasRef.current?.offsetHeight || 841;
          const _stampW = 20; // 20% of canvas width
          const _stampH = (_cW / _cH) > 1 ? 20 : 20 * (_cW / _cH); // keep square-ish
          const newShape = {
            id: Date.now(), type: 'emoji',
            x1: xPct - _stampW / 2, y1: yPct - _stampH / 2,
            x2: xPct + _stampW / 2, y2: yPct + _stampH / 2,
            strokeColor: shapeStrokeColor, fillColor: 'none',
            strokeWidth: shapeStrokeWidth, dashed: shapeDashed, emoji: selectedEmoji, emojiSize: 0,
            zOrder: canvasShapes.length, layerIndex: ++layerCounterRef.current,
          };
          const _eAfter = [..._eBefore, newShape];
          setCanvasShapes(_eAfter);
          setSelectedShapeId(newShape.id);
          pushShapeHistory(_eBefore, _eAfter);
          markDirty();
          setSelectedEmoji(null);
          setActiveShapeTool(null);
          setActiveTool(null);
          setCanvasCursor('default');
          setShowToolsPanel(false);
        } else {
          setIsDrawingShape(true);
          setShapeDrawStart({ x: xPct, y: yPct });
          setShapeDrawEnd({ x: xPct, y: yPct });
        }
      }
      return;
    }

    if (!fillModeActive) {
      setIsDrawing(true);
      if (drawingMode === 'spray') {
        const N = 25, radius = brushSize * 1.2;
        const initDots = [];
        for (let i = 0; i < N; i++) {
          const angle = (i / N) * Math.PI * 2 + Math.random() * 0.4;
          const r = (0.2 + Math.random() * 0.8) * radius;
          initDots.push({ x: x + r * Math.cos(angle), y: y + r * Math.sin(angle) });
        }
        setCurrentPath(initDots);
      } else {
        setCurrentPath([{ x, y }]);
      }
    }
  };

  const handleSelectionMouseMove = (e) => {
    if (!isSelecting || !selectionMode) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    if (selectionMode === 'freeform') {
      const newPath = [...selectionPath, { x, y }];
      setSelectionPath(newPath);
    } else {
      setSelectionEnd({ x, y });
    }
  };

  // Undo function
  const handleUndo = () => {
    if (historyIndex < 0) return;
    
    const lastAction = drawingHistory[historyIndex];
    if (lastAction.type === 'draw') {
      setDrawingPaths(prev => prev.filter(p => p.id !== lastAction.path.id));
    } else if (lastAction.type === 'image') {
      setCanvasImages(lastAction.before);
      setSelectedImage(null);
    } else if (lastAction.type === 'shapes') {
      setCanvasShapes(lastAction.before);
      setSelectedShapeId(null);
    }
    
    setHistoryIndex(prev => prev - 1);
  };

  // Redo function
  const handleRedo = () => {
    if (historyIndex >= drawingHistory.length - 1) return;
    
    const nextAction = drawingHistory[historyIndex + 1];
    if (nextAction.type === 'draw') {
      setDrawingPaths(prev => [...prev, nextAction.path]);
    } else if (nextAction.type === 'image') {
      setCanvasImages(nextAction.after);
      setSelectedImage(null);
    } else if (nextAction.type === 'shapes') {
      setCanvasShapes(nextAction.after);
      setSelectedShapeId(null);
    }
    
    setHistoryIndex(prev => prev + 1);
  };

  // AI Magic Wand -- activates (toolbar handles sub-tools)
  const handleMagicWand = () => { /* tool activated via handleToolSelect */ };

  // Helper: load an img.src into a canvas element
  const _imgToCanvas = (src) => new Promise((resolve, reject) => {
    const el = new Image();
    el.crossOrigin = 'anonymous';
    el.onload = () => {
      const c = document.createElement('canvas');
      c.width = el.naturalWidth; c.height = el.naturalHeight;
      c.getContext('2d').drawImage(el, 0, 0);
      resolve(c);
    };
    el.onerror = () => reject(new Error('Failed to load image (CORS or invalid URL)'));
    el.src = src;
  });

  // Normalize an AI result data-URL to exactly targetWxtargetH pixels.
  // Some AI models return at a different size or aspect ratio than what was sent.
  // This letterboxes the result so the image content framing and freespace are preserved.
  const normalizeAiResult = (dataUrl, targetW, targetH) => new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth === targetW && img.naturalHeight === targetH) { resolve(dataUrl); return; }
      const c = document.createElement('canvas');
      c.width = targetW; c.height = targetH;
      const ctx = c.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      // Stretch to fill: any pixel-size difference from Kling is tiny (<5%) and imperceptible when stretched.
      // Letterboxing caused the image to appear smaller on canvas due to transparent padding bands.
      ctx.drawImage(img, 0, 0, targetW, targetH);
      resolve(c.toDataURL('image/png'));
    };
    img.onerror = () => resolve(dataUrl); // fallback: use result as-is
    img.src = dataUrl;
  });

  // (Kept only for reference / fallback experiments -- see prepareCompositeForKling.)
  // eslint-disable-next-line no-unused-vars
  const cropCompositeToEditPanel = (dataUrl, sentW, sentH, insetBox, outW, outH) => new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const sx = img.naturalWidth  / sentW;
      const sy = img.naturalHeight / sentH;
      const ix = Math.max(0, Math.round(insetBox.x * sx));
      const iy = Math.max(0, Math.round(insetBox.y * sy));
      const iw = Math.min(img.naturalWidth  - ix, Math.round(insetBox.w * sx));
      const ih = Math.min(img.naturalHeight - iy, Math.round(insetBox.h * sy));

      // Draw the full result first
      const work = document.createElement('canvas');
      work.width = img.naturalWidth; work.height = img.naturalHeight;
      const wctx = work.getContext('2d');
      wctx.drawImage(img, 0, 0);

      // Inpaint the inset area: tile/stretch the strip of pixels just to the LEFT of the
      // inset into the inset region. This usually matches the surrounding scene reasonably.
      const sampleW = Math.max(8, Math.min(ix, Math.round(iw * 0.6)));
      if (sampleW > 0 && iw > 0 && ih > 0) {
        const sampleX = Math.max(0, ix - sampleW);
        wctx.imageSmoothingEnabled = true;
        wctx.imageSmoothingQuality = 'high';
        // Stretch the sample strip horizontally to cover the inset
        wctx.drawImage(work, sampleX, iy, sampleW, ih, ix, iy, iw, ih);
        // Light blur pass over the seam by re-drawing a wider blend strip
        wctx.globalAlpha = 0.55;
        wctx.drawImage(work, sampleX, iy, sampleW, ih, Math.max(0, ix - 4), iy, iw + 8, ih);
        wctx.globalAlpha = 1;
      }

      // Scale to output size
      const c = document.createElement('canvas');
      c.width = outW; c.height = outH;
      const ctx = c.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(work, 0, 0, outW, outH);
      resolve(c.toDataURL('image/png'));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });

  // Center-crop a data URL to match targetAspect (w/h), then scale to outW × outH.
  // Used by beauty/enhance to undo Kling's aspect-ratio snapping (Kling rounds to the
  // nearest of its supported ratios — 1:1, 4:3, 3:2, etc. — which slightly distorts
  // the returned image if the original doesn't land exactly on one of those ratios).
  const cropToAspect = (dataUrl, targetAspect, outW, outH) => new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const sw = img.naturalWidth  || img.width;
      const sh = img.naturalHeight || img.height;
      const srcAspect = sw / sh;
      let cropX = 0, cropY = 0, cropW = sw, cropH = sh;
      if (Math.abs(srcAspect - targetAspect) > 0.005) {
        if (srcAspect > targetAspect) {
          cropH = sh;
          cropW = Math.round(sh * targetAspect);
          cropX = Math.round((sw - cropW) / 2);
          cropY = 0;
        } else {
          cropW = sw;
          cropH = Math.round(sw / targetAspect);
          cropX = 0;
          cropY = Math.round((sh - cropH) / 2);
        }
      }
      const c = document.createElement('canvas');
      c.width = outW; c.height = outH;
      const ctx = c.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, outW, outH);
      resolve(c.toDataURL('image/png'));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
  const handleMagicRemoveBg = async () => {
    if (!effectiveImageId) { showAlert('Please select an image first'); return; }
    const img = canvasImagesRef.current.find(i => i.id === effectiveImageId);
    if (!img) return;
    setMagicProcessing(true);
    try {
      const tmpCanvas = await _imgToCanvas(img.url);
      const result = await removeImageBg(tmpCanvas);
      const before = canvasImagesRef.current;
      const after = before.map(i => i.id === effectiveImageId ? { ...i, url: result } : i);
      setCanvasImages(after);
      canvasImagesRef.current = after;
      pushImageHistory(before, after);
    } catch (err) {
      showAlert('Background removal failed: ' + (err?.message || String(err) || 'Unknown error'));
    } finally {
      setMagicProcessing(false);
    }
  };

  // Converts raw API response errors into user-friendly messages
  const friendlyApiError = (res, data) => {
    const backendMsg = data?.error || '';
    // 402 = subscription / credits / auth error surfaced by the backend
    if (res?.status === 402) return backendMsg || 'AI subscription is not active or has insufficient credits.';
    // 429 from our rate limiter (no specific backend message) vs. Kling rate limit (has one)
    if (res?.status === 429) return backendMsg || 'The AI is busy right now -- please wait a moment and try again';
    // 501 = feature not configured (e.g. missing GEMINI_API_KEY)
    if (res?.status === 501) return backendMsg || 'This AI feature is not configured on the server.';
    // 504 = poll timeout
    if (res?.status === 504) return backendMsg || 'The AI took too long to respond -- please try again';
    // 502/503 = upstream failure; show backend message if it's informative, else generic
    if (res?.status === 503 || res?.status === 502) return backendMsg || 'AI service is temporarily unavailable -- try again shortly';
    if (res?.status === 401 || res?.status === 403) return 'AI service authorisation error -- please contact support';
    return backendMsg || 'AI edit failed';
  };

  // Magic: remove cake plate via Kling AI
  const handleMagicRemovePlate = async () => {
    if (!effectiveImageId) { showAlert('Please select an image first'); return; }
    const img = canvasImagesRef.current.find(i => i.id === effectiveImageId);
    if (!img) return;
    setMagicProcessing(true);
    setMagicChatError('');
    setMagicChatStep('preparing');
    let waitingTimer = null;
    let tempStorageRef = null;
    try {
      const { imageUrl, w, h, tempRef } = await prepareImageForKling(img, { suffix: 'plate' });
      tempStorageRef = tempRef;
      setMagicChatStep('sending');
      waitingTimer = setTimeout(() => setMagicChatStep('waiting'), 5000);
      const backendUrl = (!Capacitor.isNativePlatform() && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
        ? (import.meta.env.VITE_BACKEND_URL_LOCAL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:3002')
        : (import.meta.env.VITE_BACKEND_URL || window.location.origin);
      const res = await fetch(`${backendUrl}/magic-ai-edit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageUrl,
          prompt: 'Remove only the plate, board, or cake drum that the cake is sitting on. Do NOT remove or alter the background in any way -- keep it exactly as it is. Do NOT make the background transparent. The cake and all its decorations must stay completely unchanged. Fill the area where the plate was with the background that is already visible around it.',
          imageWidth: w,
          imageHeight: h,
        }),
      });
      clearTimeout(waitingTimer);
      waitingTimer = null;
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(friendlyApiError(res, data));
      // Normalize result to sent dimensions -- preserves same pixel framing and freespace
      const normedPlate = await normalizeAiResult(data.imageBase64, w, h);
      const before = canvasImagesRef.current;
      const after = before.map(i => i.id === effectiveImageId ? { ...i, url: normedPlate } : i);
      setCanvasImages(after);
      canvasImagesRef.current = after;
      pushImageHistory(before, after);
      setMagicChatStep('');
    } catch (err) {
      if (waitingTimer) { clearTimeout(waitingTimer); waitingTimer = null; }
      setMagicChatStep('');
      setMagicChatError('Plate removal failed: ' + err.message);
    } finally {
      setMagicProcessing(false);
      if (tempStorageRef) { deleteObject(tempStorageRef).catch(() => {}); }
    }
  };

  // Magic: AI photo enhance -- maximise sharpness and quality without changing content
  const handleMagicEnhance = async () => {
    if (!effectiveImageId) { showAlert('Please select an image first'); return; }
    const img = canvasImagesRef.current.find(i => i.id === effectiveImageId);
    if (!img) return;
    setMagicProcessing(true);
    setMagicChatStep('preparing');
    setMagicChatError('');
    let tempRef = null;
    let waitingTimer = null;
    try {
      // Use max:1920 (Kling v3 supports up to 1920px) for the highest quality input.
      // Default max:1024 was limiting the output resolution unnecessarily.
      const { imageUrl, w, h, tempRef: _tempRef } = await prepareImageForKling(img, { max: 1920, suffix: 'enhance' });
      tempRef = _tempRef;

      // Record the original aspect ratio so we can center-crop the result back to it.
      // Kling snaps to its nearest supported ratio (1:1, 4:3, 3:2, 16:9…) which can
      // slightly distort the image if the original doesn't land exactly on one of them.
      const origNatW = img.naturalWidth  || w;
      const origNatH = img.naturalHeight || h;
      const origAspect = origNatW / origNatH;

      setMagicChatStep('sending');
      waitingTimer = setTimeout(() => setMagicChatStep('waiting'), 5000);
      const backendUrl = (!Capacitor.isNativePlatform() && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
        ? (import.meta.env.VITE_BACKEND_URL_LOCAL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:3002')
        : (import.meta.env.VITE_BACKEND_URL || window.location.origin);
      const res = await fetch(`${backendUrl}/magic-ai-edit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageUrl,
          prompt: 'Enhance this photo to the highest possible quality. Maximise sharpness, clarity, and fine detail. Correct any noise, blur, or compression artefacts. Optimise brightness, contrast, and colour balance for a vivid, professional look. Make textures, edges, and fine details crisp and clean. Do NOT change the subject, composition, framing, design, decorations, text, or aspect ratio of the image in any way.',
          imageWidth: w,
          imageHeight: h,
          imageFidelity: 0.95,
        }),
      });
      clearTimeout(waitingTimer); waitingTimer = null;
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(friendlyApiError(res, data));

      // Center-crop the result back to the original aspect ratio (undoes Kling's ratio
      // snapping), then scale to the prepared resolution (w × h) so the URL has the
      // full enhanced resolution. Keep canvas frame (width/height in cm) unchanged.
      const enhanced = await cropToAspect(data.imageBase64, origAspect, w, h);

      const before = canvasImagesRef.current;
      const after = before.map(i => i.id === effectiveImageId
        ? { ...i, url: enhanced, naturalWidth: w, naturalHeight: h }
        : i
      );
      setCanvasImages(after);
      canvasImagesRef.current = after;
      pushImageHistory(before, after);
      setMagicChatStep('');
    } catch (err) {
      if (waitingTimer) { clearTimeout(waitingTimer); }
      setMagicChatStep('');
      setMagicChatError('Enhance failed: ' + err.message);
    } finally {
      setMagicProcessing(false);
      if (tempRef) deleteObject(tempRef).catch(() => {});
    }
  };

  // Magic: remove background from selected image -- tries remove.bg first, falls back to Kling AI
  const handleMagicRemoveImageBg = async () => {
    if (!effectiveImageId) { showAlert('Please select an image first'); return; }
    const img = canvasImagesRef.current.find(i => i.id === effectiveImageId);
    if (!img) return;
    setMagicProcessing(true);
    setMagicChatStep('preparing');
    setMagicChatError('');
    let tempRef = null;
    try {
      // remove.bg is the only reliable way to get true alpha transparency.
      // If it's unavailable, we show an error rather than falling back to Kling,
      // which cannot produce real transparent output.
      const tmpCanvas = await _imgToCanvas(img.url);
      const resultUrl = await removeImageBg(tmpCanvas);
      const before = canvasImagesRef.current;
      const after = before.map(i => i.id === effectiveImageId ? { ...i, url: resultUrl } : i);
      setCanvasImages(after);
      canvasImagesRef.current = after;
      pushImageHistory(before, after);
      setMagicChatStep('');
    } catch (err) {
      setMagicChatStep('');
      const msg = err.message || '';
      setMagicChatError(
        msg.includes('502') || msg.includes('remove.bg')
          ? 'Background removal is temporarily unavailable. Please try again later.'
          : 'BG removal failed: ' + msg
      );
    } finally {
      setMagicProcessing(false);
      if (tempRef) deleteObject(tempRef).catch(() => {});
    }
  };

  // Magic: remove background then fill with a solid colour
  const handleMagicRemoveImageBgWithColor = async (hexColor, colorLabel) => {
    if (!effectiveImageId) { showAlert('Please select an image first'); return; }
    const img = canvasImagesRef.current.find(i => i.id === effectiveImageId);
    if (!img) return;
    setMagicProcessing(true);
    setMagicChatStep('preparing');
    setMagicChatError('');
    let tempRef = null;
    try {
      // Use Kling AI to directly replace the background with the target colour.
      // (remove.bg is skipped here -- Kling cannot produce real transparency,
      // so the two-step transparent-then-composite approach doesn't work reliably.)
      setMagicChatStep('sending');
      const { imageUrl, w, h, tempRef: _tempRef } = await prepareImageForKling(img, { suffix: 'bgcol' });
      tempRef = _tempRef;
      const waitTimer = setTimeout(() => setMagicChatStep('waiting'), 5000);
      const backendUrl = (!Capacitor.isNativePlatform() && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
        ? (import.meta.env.VITE_BACKEND_URL_LOCAL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:3002')
        : (import.meta.env.VITE_BACKEND_URL || window.location.origin);
      const res = await fetch(`${backendUrl}/magic-ai-edit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageUrl,
          prompt: `Remove the existing background from this image completely, then fill the entire background with a solid, flat, uniform ${colorLabel} colour (${hexColor}). The background should be a single clean ${colorLabel} colour with no gradients, shadows, or textures. Keep the main subject (the cake and all its decorations) completely unchanged -- do not alter the cake's shape, colours, text, or any details in any way.`,
          imageWidth: w, imageHeight: h,
        }),
      });
      clearTimeout(waitTimer);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(friendlyApiError(res, data));
      const normedResult = await normalizeAiResult(data.imageBase64, w, h);
      const before = canvasImagesRef.current;
      const after = before.map(i => i.id === effectiveImageId ? { ...i, url: normedResult } : i);
      setCanvasImages(after);
      canvasImagesRef.current = after;
      pushImageHistory(before, after);
      setMagicChatStep('');
    } catch (err) {
      setMagicChatStep('');
      setMagicChatError('BG colour fill failed: ' + err.message);
    } finally {
      setMagicProcessing(false);
      if (tempRef) deleteObject(tempRef).catch(() => {});
    }
  };

  // Magic: change cake colour via Kling AI
  const handleMagicColorChange = async (shade, colorName) => {
    if (!effectiveImageId) { showAlert('Please select an image first'); return; }
    const img = canvasImagesRef.current.find(i => i.id === effectiveImageId);
    if (!img) return;
    setMagicProcessing(true);
    setMagicChatStep('preparing');
    setMagicChatError('');
    let tempRef = null;
    try {
      const { imageUrl, w, h, tempRef: _tempRef } = await prepareImageForKling(img, { suffix: 'color' });
      tempRef = _tempRef;
      setMagicChatStep('sending');
      const waitTimer = setTimeout(() => setMagicChatStep('waiting'), 5000);
      const backendUrl = (!Capacitor.isNativePlatform() && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
        ? (import.meta.env.VITE_BACKEND_URL_LOCAL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:3002')
        : (import.meta.env.VITE_BACKEND_URL || window.location.origin);
      const res = await fetch(`${backendUrl}/magic-ai-edit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageUrl,
          prompt: `Change the colour of the cake's icing, frosting, and fondant to ${colorName} (${shade}). Keep the cake's shape, structure, decorations, text, flowers, and all non-icing elements completely unchanged. Only change the main cake body colour.`,
          imageWidth: w, imageHeight: h,
        }),
      });
      clearTimeout(waitTimer);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(friendlyApiError(res, data));
      const normedColor = await normalizeAiResult(data.imageBase64, w, h);
      const before = canvasImagesRef.current;
      // Always update the existing image in-place -- never add a new one
      const after = before.map(i => i.id === effectiveImageId ? { ...i, url: normedColor } : i);
      setCanvasImages(after);
      canvasImagesRef.current = after;
      pushImageHistory(before, after);
      setMagicChatStep('');
    } catch (err) {
      setMagicChatStep('');
      setMagicChatError('Colour change failed: ' + err.message);
    } finally {
      setMagicProcessing(false);
      if (tempRef) deleteObject(tempRef).catch(() => {});
    }
  };

  // Magic: apply brightness + contrast to selected image and commit
  const applyMagicFilter = async (brightness, contrast) => {
    const br = brightness ?? magicBrightness;
    const ct = contrast ?? magicContrast;
    if (!selectedImage) { showAlert('Please select an image first'); return; }
    const img = canvasImagesRef.current.find(i => i.id === selectedImage);
    if (!img) return;
    // When both values are zero, restore original image (undo all filter changes)
    if (br === 0 && ct === 0) {
      const origUrl = magicFilterOrigUrlRef.current;
      if (origUrl && origUrl !== img.url) {
        const before = canvasImagesRef.current;
        const after = before.map(i => i.id === selectedImage
          ? { ...i, url: origUrl, _filterOrigUrl: undefined, _brightness: undefined, _contrast: undefined }
          : i);
        setCanvasImages(after);
        canvasImagesRef.current = after;
        pushImageHistory(before, after);
      }
      setMagicFilterPreview({ brightness: 0, contrast: 0 });
      return;
    }
    // Always bake from the stored original URL so adjustments never stack
    const origUrl = img._filterOrigUrl || img.url;
    magicFilterOrigUrlRef.current = origUrl;
    const sourceUrl = origUrl;
    setMagicProcessing(true);
    try {
      const el = new Image();
      el.crossOrigin = 'anonymous';
      await new Promise((res, rej) => { el.onload = res; el.onerror = rej; el.src = sourceUrl; });
      const tmpCanvas = document.createElement('canvas');
      tmpCanvas.width = el.naturalWidth; tmpCanvas.height = el.naturalHeight;
      const ctx = tmpCanvas.getContext('2d');
      // -50->+50 range: map to CSS filter values (0.5->1.5 for brightness, 0.5->1.5 for contrast)
      const brF = 1 + br / 50;
      const ctF = 1 + ct / 50;
      ctx.filter = `brightness(${brF}) contrast(${ctF})`;
      ctx.drawImage(el, 0, 0);
      const result = tmpCanvas.toDataURL('image/png');
      const before = canvasImagesRef.current;
      // Store applied values and original URL on the image so re-opening the tool restores them
      const after = before.map(i => i.id === selectedImage
        ? { ...i, url: result, _filterOrigUrl: origUrl, _brightness: br, _contrast: ct }
        : i);
      setCanvasImages(after);
      canvasImagesRef.current = after;
      pushImageHistory(before, after);
      // Clear preview overlay — filter is now baked into the image URL
      setMagicFilterPreview({ brightness: 0, contrast: 0 });
    } catch (err) {
      showAlert('Filter failed: ' + err.message);
    } finally {
      setMagicProcessing(false);
    }
  };

  // Magic: AI chat edit via Kling
  const handleMagicChat = async () => {
    if (!magicChatInput.trim()) return;
    // Determine which images to process: manual selection (filtered to still-existing IDs),
    // falling back to auto Y-sorted canvas images (up to 2) when selection is empty or stale.
    const validSelected = magicChatImages.filter(id => canvasImagesRef.current.find(i => i.id === id));
    const imgIds = validSelected.length > 0
      ? validSelected
      : canvasImagesRef.current.slice().sort((a, b) => (a.y ?? 0) - (b.y ?? 0)).slice(0, 2).map(i => i.id);
    if (imgIds.length === 0) { showAlert('No images on canvas to edit'); return; }
    // If selection had stale IDs, clear them so the UI doesn't show phantom selections
    if (magicChatImages.length > 0 && validSelected.length === 0) setMagicChatImages([]);
    const userMsg = { role: 'user', content: magicChatInput.trim() };
    // Add to history (dedup, max 10, most recent first)
    setMagicPromptHistory(prev => {
      const filtered = prev.filter(p => p !== userMsg.content);
      return [userMsg.content, ...filtered].slice(0, 10);
    });
    // Don't clear input -- keep prompt visible in textarea during processing
    setMagicChatError('');
    setMagicChatLoading(true);
    setMagicChatStep('preparing');

    const backendUrl = (!Capacitor.isNativePlatform() && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
      ? (import.meta.env.VITE_BACKEND_URL_LOCAL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:3002')
      : (import.meta.env.VITE_BACKEND_URL || window.location.origin);

    let lastNewImageId = null;
    const overallErrors = [];

    // Appended to every user prompt to prevent Kling from reframing/zooming the result
    const FRAMING_SUFFIX = ' IMPORTANT: preserve the exact same zoom level, camera distance, crop, and composition as the original photo -- do not zoom in, do not pan, do not reframe, do not change the field of view. The subject must stay at the identical position and size within the frame. Fill any removed area seamlessly with the existing background without altering anything else.';
    const FRAMING_NEG = 'zoomed in, cropped, reframed, different composition, different camera distance, panned, shifted subject, changed framing, distorted, deformed, low quality, blurry, watermark';

    // Helper: resize image to Kling-compatible dimensions and upload, returns { imageUrl, w, h, tempStorageRef }
    const prepareAndUpload = async (img) => {
      const { imageUrl, w, h, tempRef } = await prepareImageForKling(img);
      return { imageUrl, w, h, tempStorageRef: tempRef };
    };

    if (imgIds.length === 2) {
      // -- Two-image mode: sort by Y so topmost image = Edit, bottommost = Reference --
      // Routes to the Gemini multi-image endpoint -- Kling v3 doesn't reliably support
      // cross-image compositing (it either ignores the REF or just preserves the EDIT
      // unchanged). Gemini 2.5 Flash Image natively handles "take subject from image B,
      // put it into image A" prompts.
      const [editId, refId] = [...imgIds].sort((a, b) => {
        const ia = canvasImagesRef.current.find(i => i.id === a);
        const ib = canvasImagesRef.current.find(i => i.id === b);
        return (ia?.y ?? 0) - (ib?.y ?? 0);
      });
      const editImgObj = canvasImagesRef.current.find(i => i.id === editId);
      const refImgObj  = canvasImagesRef.current.find(i => i.id === refId);
      let tempRef0 = null, tempRef1 = null;
      let waitingTimer = null;
      try {
        setMagicChatStep('preparing');
        // Upload both images to temp storage so the backend can fetch them
        const [prepared0, prepared1] = await Promise.all([
          prepareAndUpload(editImgObj),
          prepareAndUpload(refImgObj),
        ]);
        tempRef0 = prepared0.tempStorageRef;
        tempRef1 = prepared1.tempStorageRef;

        setMagicChatStep('sending');
        waitingTimer = setTimeout(() => setMagicChatStep('waiting'), 5000);
        const res = await fetch(`${backendUrl}/magic-ai-edit-multi`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            editImageUrl: prepared0.imageUrl,
            refImageUrl: prepared1.imageUrl,
            prompt: userMsg.content,
          }),
        });
        clearTimeout(waitingTimer); waitingTimer = null;
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) throw new Error(friendlyApiError(res, data));

        // Normalize to original EDIT dimensions so it slots back on canvas cleanly
        const editNatW = editImgObj?.naturalWidth  || prepared0.w;
        const editNatH = editImgObj?.naturalHeight || prepared0.h;
        const normedTwo = await normalizeAiResult(data.imageBase64, editNatW, editNatH);
        const srcImg = canvasImagesRef.current.find(i => i.id === editId);
        const newImage = {
          id: Date.now(),
          layerIndex: ++layerCounterRef.current,
          url: normedTwo,
          name: 'ai-edit',
          size: '',
          x: Math.min((srcImg?.x ?? 0) + 2, 79),
          y: Math.min((srcImg?.y ?? 0) + 2, 70),
          width: srcImg?.width ?? 10,
          height: srcImg?.height ?? 10,
          source: 'ai',
          naturalWidth: editNatW,
          naturalHeight: editNatH,
        };
        const before = canvasImagesRef.current;
        const after = [...before, newImage];
        setCanvasImages(after); canvasImagesRef.current = after;
        pushImageHistory(before, after);
        lastNewImageId = newImage.id;
      } catch (err) {
        if (waitingTimer) clearTimeout(waitingTimer);
        overallErrors.push(err.message);
      } finally {
        if (tempRef0) deleteObject(tempRef0).catch(() => {});
        if (tempRef1) deleteObject(tempRef1).catch(() => {});
      }
    } else {
      // -- Single-image mode (or fallback): edit each image independently ---------
      for (let idx = 0; idx < imgIds.length; idx++) {
        const imgId = imgIds[idx];
        const img = canvasImagesRef.current.find(i => i.id === imgId);
        if (!img) continue;

        let waitingTimer = null;
        let tempStorageRef = null;
        try {
          setMagicChatStep('preparing');
          const { imageUrl, w, h, tempStorageRef: ref } = await prepareAndUpload(img);
          tempStorageRef = ref;

          setMagicChatStep('sending');
          waitingTimer = setTimeout(() => setMagicChatStep('waiting'), 5000);
          const res = await fetch(`${backendUrl}/magic-ai-edit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ imageUrl, prompt: userMsg.content + FRAMING_SUFFIX, imageWidth: w, imageHeight: h, imageFidelity: 0.98, negativePrompt: FRAMING_NEG }),
          });
          clearTimeout(waitingTimer); waitingTimer = null;
          const data = await res.json().catch(() => ({}));
          if (!res.ok || !data.success) throw new Error(friendlyApiError(res, data));
          // Normalize to sent dimensions -- preserves same pixel framing and freespace as original
          const normedChat = await normalizeAiResult(data.imageBase64, w, h);
          const before = canvasImagesRef.current;
          if (fromCustom) {
            // In fromCustom mode replace in-place -- only one image to return
            const after = before.map(i => i.id === imgId ? { ...i, url: normedChat } : i);
            setCanvasImages(after); canvasImagesRef.current = after;
            pushImageHistory(before, after);
          } else {
            const srcImg = canvasImagesRef.current.find(i => i.id === imgId);
            const OFFSET = 2 + idx * 2;
            const newImage = {
              id: Date.now() + idx,
              layerIndex: ++layerCounterRef.current,
              url: normedChat,
              name: 'ai-edit',
              size: '',
              x: Math.min(((srcImg?.x ?? 0) + OFFSET), 79),
              y: Math.min(((srcImg?.y ?? 0) + OFFSET), 70),
              width: srcImg?.width ?? 10,
              height: srcImg?.height ?? 10,
              source: 'ai',
              naturalWidth: w,
              naturalHeight: h,
            };
            const after = [...before, newImage];
            setCanvasImages(after); canvasImagesRef.current = after;
            pushImageHistory(before, after);
            lastNewImageId = newImage.id;
          }
        } catch (err) {
          if (waitingTimer) { clearTimeout(waitingTimer); waitingTimer = null; }
          overallErrors.push(`Image ${idx + 1}: ${err.message}`);
        } finally {
          if (tempStorageRef) deleteObject(tempStorageRef).catch(() => {});
        }
      }
    }

    if (lastNewImageId) setSelectedImage(lastNewImageId);
    setMagicChatInput('');
    setMagicChatStep('');
    if (overallErrors.length > 0) {
      setMagicChatError(overallErrors.join(' . '));
    }
    setMagicChatLoading(false);
  };

  // Save function
  const handleSave = async () => {
    if (canvasImages.length === 0 && drawingPaths.length === 0 && canvasShapes.length === 0) {
      setSaveMessage({ text: 'Please add images or drawings first.', ok: false });
      return;
    }

    setIsSaving(true);
    setSaveProgress(0);

    try {
      const imagesToRender = selectedImage
        ? canvasImages.filter(i => i.id === selectedImage)
        : canvasImages;
      const pathsToRender = selectedImage ? [] : drawingPaths;
      const shapesToRender = selectedImage ? [] : canvasShapes;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      const dpi = 300;
      let minX, minY, contentWidth, contentHeight, widthInPx, heightInPx;

      if (selectedImage) {
        // Individual image save: crop canvas to the image bounding box
        let imgMinX = Infinity, imgMinY = Infinity, imgMaxX = -Infinity, imgMaxY = -Infinity;
        imagesToRender.forEach(img => {
          imgMinX = Math.min(imgMinX, img.x);
          imgMinY = Math.min(imgMinY, img.y);
          imgMaxX = Math.max(imgMaxX, img.x + (img.width  / 21   * 100));
          imgMaxY = Math.max(imgMaxY, img.y + (img.height / 29.7 * 100));
        });
        const hasMultiple = imagesToRender.length > 1;
        const padding = hasMultiple ? 2 : 0;
        minX = Math.max(0,   imgMinX - padding);
        minY = Math.max(0,   imgMinY - padding);
        const maxX = Math.min(100, imgMaxX + padding);
        const maxY = Math.min(100, imgMaxY + padding);
        contentWidth  = maxX - minX;
        contentHeight = maxY - minY;
        widthInPx  = (contentWidth  / 100) * A4_WIDTH_CM  * (dpi / 2.54);
        heightInPx = (contentHeight / 100) * A4_HEIGHT_CM * (dpi / 2.54);
      } else {
        // Page save: use full A4 canvas, but expand if any image extends beyond edges
        // (e.g. due to resize going slightly over bounds or floating-point rounding)
        minX = 0; minY = 0;
        let extW = 100, extH = 100;
        imagesToRender.forEach(img => {
          extW = Math.max(extW, img.x + (img.width  / 21   * 100));
          extH = Math.max(extH, img.y + (img.height / 29.7 * 100));
        });
        contentWidth  = extW;
        contentHeight = extH;
        widthInPx  = extW / 100 * A4_WIDTH_CM  * (dpi / 2.54);
        heightInPx = extH / 100 * A4_HEIGHT_CM * (dpi / 2.54);
      }

      canvas.width  = widthInPx;
      canvas.height = heightInPx;

      // High-quality image smoothing (set after resize -- resize resets context state)
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Fill background for full-canvas saves using the canvas background colour.
      // If no background is set (transparent), leave the canvas transparent so
      // the exported PNG has no background.
      if (!selectedImage && canvasBackground && canvasBackground !== 'transparent') {
        ctx.fillStyle = canvasBackground;
        ctx.fillRect(0, 0, widthInPx, heightInPx);
      }

      setSaveProgress(10);

      // Draw images -- use loadImgSafe so expired/cross-origin Firebase URLs
      // are fetched via blob (avoids canvas taint and silent draw failures)
      for (let i = 0; i < imagesToRender.length; i++) {
        const img = imagesToRender[i];
        const image = await loadImgSafe(img.url);
        if (image) {
            const imgXPx      = ((img.x - minX) / contentWidth)  * widthInPx;
            const imgYPx      = ((img.y - minY) / contentHeight) * heightInPx;
            const imgWidthPx  = (img.width  / 21)   * (widthInPx  / contentWidth)  * 100;
            const imgHeightPx = (img.height / 29.7) * (heightInPx / contentHeight) * 100;

            const rot = ((img.rotation || 0) * Math.PI) / 180;
            if (rot) {
              const cx = imgXPx + imgWidthPx / 2;
              const cy = imgYPx + imgHeightPx / 2;
              ctx.save();
              ctx.translate(cx, cy);
              ctx.rotate(rot);
              if (img.cropShape === 'circle') {
                ctx.beginPath();
                ctx.ellipse(0, 0, imgWidthPx / 2, imgHeightPx / 2, 0, 0, Math.PI * 2);
                ctx.clip();
              }
              drawImageHQ(ctx, image, -imgWidthPx / 2, -imgHeightPx / 2, imgWidthPx, imgHeightPx);
              ctx.restore();
            } else if (img.cropShape === 'circle') {
              ctx.save();
              ctx.beginPath();
              ctx.ellipse(imgXPx + imgWidthPx / 2, imgYPx + imgHeightPx / 2, imgWidthPx / 2, imgHeightPx / 2, 0, 0, Math.PI * 2);
              ctx.clip();
              drawImageHQ(ctx, image, imgXPx, imgYPx, imgWidthPx, imgHeightPx);
              ctx.restore();
            } else {
              drawImageHQ(ctx, image, imgXPx, imgYPx, imgWidthPx, imgHeightPx);
            }
            setSaveProgress(10 + ((i + 1) / imagesToRender.length) * 70);
        } else {
            console.error('Failed to load image for saving:', img.url);
            setSaveProgress(10 + ((i + 1) / imagesToRender.length) * 70);
        }
      }

      // Draw drawings
      if (pathsToRender.length > 0) {
        pathsToRender.forEach(path => {
          const pCanvasW = path.canvasW || 800;
          const pCanvasH = path.canvasH || (800 * A4_RATIO);
          // Scale maps screen pixels → output pixels accounting for bbox crop
          const scaleX = widthInPx  / (pCanvasW * contentWidth  / 100);
          const scaleY = heightInPx / (pCanvasH * contentHeight / 100);
          const offsetX = (minX / contentWidth)  * widthInPx;
          const offsetY = (minY / contentHeight) * heightInPx;
          if (path.type === 'spray') {
            ctx.globalCompositeOperation = 'source-over';
            ctx.fillStyle = path.color;
            const dotR = Math.max(0.5, (path.size || 5) * 0.18 * scaleX);
            for (const pt of path.path) { ctx.beginPath(); ctx.arc(pt.x * scaleX - offsetX, pt.y * scaleY - offsetY, dotR, 0, Math.PI * 2); ctx.fill(); }
            ctx.globalCompositeOperation = 'source-over';
          } else {
            if (path.path.length < 2) return;
            ctx.beginPath();
            ctx.moveTo(path.path[0].x * scaleX - offsetX, path.path[0].y * scaleY - offsetY);
            for (let i = 1; i < path.path.length; i++) { ctx.lineTo(path.path[i].x * scaleX - offsetX, path.path[i].y * scaleY - offsetY); }
            if (path.color === 'eraser') { ctx.globalCompositeOperation = 'destination-out'; ctx.strokeStyle = 'rgba(0,0,0,1)'; }
            else { ctx.globalCompositeOperation = 'source-over'; ctx.strokeStyle = path.color; }
            ctx.lineWidth = path.size * scaleX; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.stroke();
            ctx.globalCompositeOperation = 'source-over';
          }
        });
      }

      // Draw shapes
      if (shapesToRender.length > 0) {
        drawShapesOnCanvas(ctx, shapesToRender, widthInPx, heightInPx, minX, minY, contentWidth, contentHeight);
      }

      setSaveProgress(85);

      if (saveType === 'file') {
        // For file download, use the exact filename from input
        const finalFileName = fileName.trim() || 'Edited PhotoS';
        const link = document.createElement('a');
        link.href = canvas.toDataURL('image/png');
        link.download = `${finalFileName}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setSaveProgress(100);
        setTimeout(() => {
          setIsSaving(false);
          setSaveProgress(0);
          setSaveMessage({ text: 'Image saved successfully!', ok: true });
          setPendingNavPath(null);
          isSavedRef.current = true;
          // Reset per-image filter metadata now that the final image is saved
          setCanvasImages(prev => prev.map(i => ({ ...i, _filterOrigUrl: undefined, _brightness: undefined, _contrast: undefined })));
          setMagicBrightness(0);
          setMagicContrast(0);
        }, 500);
      } else {
        try {
          const blob = await new Promise((resolve, reject) => {
            canvas.toBlob(resolve, 'image/png', 1.0);
          });
          
          if (!blob) {
            throw new Error('Failed to create image blob');
          }
          
          // For bucket save, use the filename from input
          const baseFileName = fileName.trim() || 'Edited PhotoS';
          const cleanFileName = baseFileName.replace(/\s+/g, '_');
          
          const _saveUser = getAuth().currentUser;
          const _saveFolder = ['Admin', 'Super'].includes(userRole)
            ? 'istudio'
            : `istudio/${_saveUser?.uid}`;

          // Check if file exists and append number if needed
          let counter = 1;
          let bucketFileName = `${_saveFolder}/${cleanFileName}.png`;
          
          // Get existing files to check for duplicates
          const { items } = await listObjectsInFolder(_saveFolder, getEnvStorageBucket());
          const existingFiles = items.map(item => item.name);
          
          while (existingFiles.includes(bucketFileName)) {
            bucketFileName = `${_saveFolder}/${cleanFileName}_${counter}.png`;
            counter++;
          }
          
          const file = new File([blob], `${cleanFileName}.png`, { type: 'image/png' });
          
          await uploadToStorage(
            bucketFileName,
            file,
            'image/png',
            getEnvStorageBucket()
          );
          // Bust cache so modal shows newly uploaded file
          _bucketImagesCache = null;
          
          setSaveProgress(100);
          setTimeout(() => {
            setIsSaving(false);
            setSaveProgress(0);
            setSaveMessage({ text: 'Image saved to Cloud', ok: true });
            setPendingNavPath(null);
            isSavedRef.current = true;
            // Reset per-image filter metadata now that the final image is saved
            setCanvasImages(prev => prev.map(i => ({ ...i, _filterOrigUrl: undefined, _brightness: undefined, _contrast: undefined })));
            setMagicBrightness(0);
            setMagicContrast(0);
          }, 500);
        } catch (error) {
          console.error('Error saving to bucket:', error);
          setIsSaving(false);
          setSaveProgress(0);
          setSaveMessage({ text: `Failed to save to Cloud: ${error.message}`, ok: false });
        }
      }
      
    } catch (error) {
      console.error('Save error:', error);
      setIsSaving(false);
      setSaveProgress(0);
      setSaveMessage({ text: `Failed to save: ${error.message}`, ok: false });
    }
  };

  // Print functionality
  const cyclePrintSize = () => {
    const prevKey = printSize;
    const idx = PRINT_SIZE_ORDER.indexOf(prevKey);
    const nextKey = PRINT_SIZE_ORDER[(idx + 1) % PRINT_SIZE_ORDER.length];
    const prevW = PRINT_SIZES[prevKey].width;
    const prevH = PRINT_SIZES[prevKey].height;
    const newW  = PRINT_SIZES[nextKey].width;
    const newH  = PRINT_SIZES[nextKey].height;

    // Scale factor: preserves the physical cm size of each element in the new canvas.
    // Images store width in "21-cm units" and height in "29.7-cm units" (hardcoded references).
    // Multiplying by prevDim/newDim keeps (img.width/21)*canvasCSSWidth the same physical size.
    const sx = prevW / newW;
    const sy = prevH / newH;

    setPrintSize(nextKey);

    if (sx !== 1 || sy !== 1) {
      setCanvasImages(prev => prev.map(img => ({
        ...img,
        x:      img.x      * sx,
        y:      img.y      * sy,
        width:  img.width  * sx,
        height: img.height * sy,
      })));

      setCanvasShapes(prev => prev.map(s => ({
        ...s,
        x1: s.x1 * sx,
        y1: s.y1 * sy,
        x2: s.x2 * sx,
        y2: s.y2 * sy,
      })));
    }
  };

  const handlePrint = async () => {
    if (canvasImages.length === 0 && drawingPaths.length === 0 && canvasShapes.length === 0) {
      showAlert('Please add images or drawings to the canvas first.');
      return;
    }

    // Use native Share on Capacitor apps; hidden iframe for all browsers.
    // iframe.contentWindow.print() runs in the same trusted context as the parent
    // document -- Brave (and all Chromium browsers) apply @page sizing correctly
    // without the extra "fit to page" scale they impose on popup windows.
    const isNative = Capacitor.isNativePlatform();

    setIsSaving(true);
    
    try {
      // Always render ALL canvas images -- not just the selected one.
      // Printing the selected image only would render a small image on a large white page.
      const imagesToRender = canvasImages;
      const pathsToRender = drawingPaths;
      const shapesToRender = canvasShapes;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      // dpi=350: render at slightly above standard print resolution. Chromium's
      // print engine maps CSS cm units to physical dimensions at the printer DPI;
      // having extra source pixels ensures no upscaling artefacts even if the
      // browser's internal rasterisation step uses a 1.1–1.2× scaling factor.
      // 350 DPI on A4 = 2756×3898 px — well within Chrome's canvas size limits.
      const dpi = 350;
      const widthInPx  = PRINT_SIZES[printSize].width  * (dpi / 2.54);
      const heightInPx = PRINT_SIZES[printSize].height * (dpi / 2.54);
      
      canvas.width = widthInPx;
      canvas.height = heightInPx;
      
      // Re-apply smoothing after canvas resize (resize resets context state)
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, widthInPx, heightInPx);
      
      // Load all images and wait for them
      await Promise.all(imagesToRender.map(async (img) => {
        // Use loadImgSafe so that HTTP images are fetched via blob URL (avoids
        // canvas taint and correctly re-fetches images whose cached URLs may have
        // expired -- which causes "old images" to silently fail on plain new Image()).
        const image = await loadImgSafe(img.url);
        if (!image) {
          console.error('Failed to load image for printing:', img.url);
          return;
        }

        // img.width/height are in A4P-space cm (denominator always 21/29.7),
        // expressed as the same fraction of the print canvas pixels -- just like the CSS does.
        const imgWidthPx  = (img.width  / 21)   * widthInPx;
        const imgHeightPx = (img.height / 29.7) * heightInPx;
        const imgXPx = (img.x / 100) * widthInPx;
        const imgYPx = (img.y / 100) * heightInPx;

        const rot = ((img.rotation || 0) * Math.PI) / 180;
        if (rot) {
          const cx = imgXPx + imgWidthPx / 2;
          const cy = imgYPx + imgHeightPx / 2;
          ctx.save();
          ctx.translate(cx, cy);
          ctx.rotate(rot);
          if (img.cropShape === 'circle') {
            ctx.beginPath();
            ctx.ellipse(0, 0, imgWidthPx / 2, imgHeightPx / 2, 0, 0, Math.PI * 2);
            ctx.clip();
          }
          drawImageHQ(ctx, image, -imgWidthPx / 2, -imgHeightPx / 2, imgWidthPx, imgHeightPx);
          ctx.restore();
        } else if (img.cropShape === 'circle') {
          ctx.save();
          ctx.beginPath();
          ctx.ellipse(imgXPx + imgWidthPx / 2, imgYPx + imgHeightPx / 2, imgWidthPx / 2, imgHeightPx / 2, 0, 0, Math.PI * 2);
          ctx.clip();
          drawImageHQ(ctx, image, imgXPx, imgYPx, imgWidthPx, imgHeightPx);
          ctx.restore();
        } else {
          drawImageHQ(ctx, image, imgXPx, imgYPx, imgWidthPx, imgHeightPx);
        }
      }));
      
      // Draw drawings
      pathsToRender.forEach(path => {
        const pCanvasW = path.canvasW || 800;
        const pCanvasH = path.canvasH || (800 * A4_RATIO);
        const scaleX = widthInPx / pCanvasW;
        const scaleY = heightInPx / pCanvasH;
        renderDrawingPath(ctx, path, scaleX, scaleY);
      });

      // Draw shapes
      if (shapesToRender.length > 0) {
        drawShapesOnCanvas(ctx, shapesToRender, widthInPx, heightInPx);
      }
      
      // Build a custom print-preview page: image at top, print bar at bottom
      const ps = PRINT_SIZES[printSize];
      const sizeLabel = `${ps.label} ${ps.orient === 'portrait' ? 'Portrait' : 'Landscape'} &middot; ${ps.width} &times; ${ps.height} cm`;

      if (isNative) {
        // --- Native Capacitor path: use Share plugin (Android / iOS) ---
        // The OS share sheet includes a native Print option on both platforms.
        const fileName = `print-${ps.label}-${ps.orient}.png`;
        const dataUri = canvas.toDataURL('image/png');
        await Share.share({
          title: 'Print Photo',
          url: dataUri,
          dialogTitle: 'Print or share your photo',
        });
      } else {
        // Detect a phone/tablet web browser. Mobile browsers cannot reliably print
        // an iframe (Chrome Mobile, Safari iOS, Samsung Internet all either ignore
        // iframe.print() or print the parent viewport instead). For these we use
        // the Web Share API with a real File object — the OS share sheet exposes
        // a native Print option on both Android and iOS that prints the actual file
        // (not the visible viewport). If Web Share isn't available, we fall back to
        // downloading the PNG so the user can print it from their gallery / Photos.
        const ua = navigator.userAgent || '';
        const isMobileWeb = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)
          || (typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1
              && /Macintosh/i.test(ua)); // iPad on iOS 13+ reports as Macintosh

        if (isMobileWeb) {
          const fileName = `print-${ps.label}-${ps.orient}-${Date.now()}.png`;
          const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
          if (!blob) {
            showAlert('Failed to prepare image for printing.');
            setIsSaving(false);
            if (activeTool !== 'magic-wand') setActiveTool(null);
            return;
          }

          // Try Web Share API with files first (native share sheet → Print)
          try {
            if (navigator.canShare && typeof File !== 'undefined') {
              const file = new File([blob], fileName, { type: 'image/png' });
              if (navigator.canShare({ files: [file] })) {
                await navigator.share({
                  files: [file],
                  title: 'Print Photo',
                  text: 'Tap Print from the share sheet to print this image.',
                });
                setIsSaving(false);
                if (activeTool !== 'magic-wand') setActiveTool(null);
                return;
              }
            }
          } catch (e) {
            // User cancelled the share sheet, or the OS rejected. Fall through to download.
            if (e && e.name === 'AbortError') {
              setIsSaving(false);
              if (activeTool !== 'magic-wand') setActiveTool(null);
              return;
            }
          }

          // Fallback: trigger a download so the user can print from Photos / Files
          const blobUrl = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
          showAlert('Image saved to your downloads. Open it from your gallery or Files app and tap Print.');
          setIsSaving(false);
          if (activeTool !== 'magic-wand') setActiveTool(null);
          return;
        }

        // --- Desktop browser path: hidden iframe with isolated document ---
        // The iframe has its own <html><body> with ONLY the image, so the print
        // engine sees no React app DOM, no global stylesheets, no responsive media
        // queries to fight against. Works on Chrome / Brave / Edge / Firefox.
        const dataUrl = canvas.toDataURL('image/png');

        const iframe = document.createElement('iframe');
        // Off-screen but still rendered (display:none breaks contentDocument paint
        // on some browsers before print() is called).
        iframe.style.cssText = 'position:fixed;right:-99999px;bottom:-99999px;width:1px;height:1px;border:0;visibility:hidden;';
        iframe.setAttribute('aria-hidden', 'true');
        document.body.appendChild(iframe);

        const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Print</title>
<style>
  @page { size: ${ps.width}cm ${ps.height}cm; margin: 0; }
  html, body {
    margin: 0; padding: 0;
    width: ${ps.width}cm; height: ${ps.height}cm;
    background: white;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    color-adjust: exact;
  }
  img {
    display: block;
    width: ${ps.width}cm;
    height: ${ps.height}cm;
    margin: 0; padding: 0;
    image-rendering: high-quality;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    color-adjust: exact;
  }
</style>
</head>
<body>
<img id="p" src="${dataUrl}" alt="">
</body>
</html>`;

        let cleaned = false;
        const cleanup = () => {
          if (cleaned) return;
          cleaned = true;
          clearTimeout(fallbackTimer);
          try { document.body.removeChild(iframe); } catch (_) {}
          if (iframe.contentWindow) iframe.contentWindow.onafterprint = null;
          setIsSaving(false);
          if (activeTool !== 'magic-wand') setActiveTool(null);
        };
        // 10-min safety fallback if onafterprint never fires (rare on some mobile browsers)
        const fallbackTimer = setTimeout(cleanup, 10 * 60 * 1000);

        // Write the document into the iframe and trigger print after the image loads.
        const triggerPrint = () => {
          try {
            const win = iframe.contentWindow;
            if (!win) return;
            // onafterprint inside the iframe fires when the user dismisses the dialog
            win.onafterprint = cleanup;
            win.focus();
            win.print();
          } catch (err) {
            console.error('iframe print failed:', err);
            cleanup();
          }
        };

        iframe.onload = () => {
          // Wait for the <img> inside the iframe to finish loading before print().
          // Mobile browsers especially will print whatever is rasterised at the moment
          // print() is called -- if the image isn't loaded yet, the page prints blank.
          const doc = iframe.contentDocument;
          if (!doc) { triggerPrint(); return; }
          const img = doc.getElementById('p');
          if (img && !img.complete) {
            img.onload  = () => setTimeout(triggerPrint, 100);
            img.onerror = () => triggerPrint();
          } else {
            setTimeout(triggerPrint, 100);
          }
        };

        // Inject the document. Using srcdoc (modern, isolates better than doc.write)
        // with a fallback to document.open/write for older mobile browsers.
        if ('srcdoc' in iframe) {
          iframe.srcdoc = html;
        } else {
          const doc = iframe.contentDocument;
          doc.open(); doc.write(html); doc.close();
          // No load event for doc.write path -- trigger directly after DOM ready
          setTimeout(() => iframe.onload(), 100);
        }

        return; // setIsSaving handled in cleanup
      }
      setIsSaving(false);
      setActiveTool(null);

    } catch (error) {
      console.error('Print preparation error:', error);
      showAlert('Failed to prepare image for printing. Please try again.');
      setIsSaving(false);
      setActiveTool(null);
    }
  };

  // Share functionality
  const handleShare = async (platform = 'download') => {
    if (canvasImages.length === 0 && drawingPaths.length === 0 && canvasShapes.length === 0) {
      showAlert('Please add images or drawings to the canvas first.');
      return;
    }
    
    setIsSaving(true);
    setSaveProgress(0);

    try {
      const imagesToRender = selectedImage
        ? canvasImages.filter(i => i.id === selectedImage)
        : canvasImages;
      const pathsToRender = selectedImage ? [] : drawingPaths;
      const shapesToRender = selectedImage ? [] : canvasShapes;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      // Calculate bounding box of all content
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      
      // Get image bounds
      imagesToRender.forEach(img => {
        const imgLeft = img.x;
        const imgRight = img.x + (img.width / 21 * 100);
        const imgTop = img.y;
        const imgBottom = img.y + (img.height / 29.7 * 100);
        
        minX = Math.min(minX, imgLeft);
        minY = Math.min(minY, imgTop);
        maxX = Math.max(maxX, imgRight);
        maxY = Math.max(maxY, imgBottom);
      });
      
      // Get drawing bounds (in percentage)
      pathsToRender.forEach(path => {
        const pCanvasW = path.canvasW || 800;
        const pCanvasH = path.canvasH || (800 * A4_RATIO);
        path.path.forEach(point => {
          const pointXPercent = (point.x / pCanvasW) * 100;
          const pointYPercent = (point.y / pCanvasH) * 100;
          
          minX = Math.min(minX, pointXPercent);
          minY = Math.min(minY, pointYPercent);
          maxX = Math.max(maxX, pointXPercent);
          maxY = Math.max(maxY, pointYPercent);
        });
      });
      
      // No padding for a single circle crop ? the PNG edge must match the CSS clip precisely
      // Only pad when there are multiple items ? prevents edge clipping.
      // Single-item saves are pixel-exact with no whitespace margin.
      // Include shape bounds
      shapesToRender.forEach(s => {
        minX = Math.min(minX, s.x1, s.x2);
        minY = Math.min(minY, s.y1, s.y2);
        maxX = Math.max(maxX, s.x1, s.x2);
        maxY = Math.max(maxY, s.y1, s.y2);
      });
      const hasMultipleItems = (imagesToRender.length + pathsToRender.length + shapesToRender.length) > 1;
      const padding = hasMultipleItems ? 2 : 0;
      minX = Math.max(0, minX - padding);
      minY = Math.max(0, minY - padding);
      maxX = Math.min(100, maxX + padding);
      maxY = Math.min(100, maxY + padding);
      
      // Calculate content dimensions
      const contentWidth = maxX - minX;
      const contentHeight = maxY - minY;
      
      const dpi = 300;
      const widthInPx = contentWidth * (21 * (dpi / 2.54)) / 100;
      const heightInPx = contentHeight * (29.7 * (dpi / 2.54)) / 100;
      
      canvas.width = widthInPx;
      canvas.height = heightInPx;
      
      // Re-apply smoothing after canvas resize (resize resets context state)
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, widthInPx, heightInPx);
      setSaveProgress(20);

      // Draw images — use loadImgSafe so expired/cross-origin Firebase URLs are
      // fetched via blob (avoids canvas taint and silent draw failures).
      for (let index = 0; index < imagesToRender.length; index++) {
        const img = imagesToRender[index];
        const image = await loadImgSafe(img.url);
        if (image) {
          const imgXPx = ((img.x - minX) / contentWidth) * widthInPx;
          const imgYPx = ((img.y - minY) / contentHeight) * heightInPx;
          const imgWidthPx  = (img.width  / 21)   * (widthInPx  / contentWidth)  * 100;
          const imgHeightPx = (img.height / 29.7) * (heightInPx / contentHeight) * 100;

          const rot = ((img.rotation || 0) * Math.PI) / 180;
          if (rot) {
            const cx = imgXPx + imgWidthPx / 2;
            const cy = imgYPx + imgHeightPx / 2;
            ctx.save();
            ctx.translate(cx, cy);
            ctx.rotate(rot);
            if (img.cropShape === 'circle') {
              ctx.beginPath();
              ctx.ellipse(0, 0, imgWidthPx / 2, imgHeightPx / 2, 0, 0, Math.PI * 2);
              ctx.clip();
            }
            drawImageHQ(ctx, image, -imgWidthPx / 2, -imgHeightPx / 2, imgWidthPx, imgHeightPx);
            ctx.restore();
          } else if (img.cropShape === 'circle') {
            ctx.save();
            ctx.beginPath();
            ctx.ellipse(imgXPx + imgWidthPx / 2, imgYPx + imgHeightPx / 2, imgWidthPx / 2, imgHeightPx / 2, 0, 0, Math.PI * 2);
            ctx.clip();
            drawImageHQ(ctx, image, imgXPx, imgYPx, imgWidthPx, imgHeightPx);
            ctx.restore();
          } else {
            drawImageHQ(ctx, image, imgXPx, imgYPx, imgWidthPx, imgHeightPx);
          }
        }
        setSaveProgress(20 + ((index + 1) / imagesToRender.length) * 70);
      }

      // Draw drawings
      if (pathsToRender.length > 0) {
        pathsToRender.forEach(path => {
          const pCanvasW = path.canvasW || 800;
          const pCanvasH = path.canvasH || (800 * A4_RATIO);
          // Scale maps screen pixels → output pixels accounting for bbox crop
          const scaleX = widthInPx  / (pCanvasW * contentWidth  / 100);
          const scaleY = heightInPx / (pCanvasH * contentHeight / 100);
          const offsetX = (minX / contentWidth)  * widthInPx;
          const offsetY = (minY / contentHeight) * heightInPx;
          if (path.type === 'spray') {
            ctx.globalCompositeOperation = 'source-over';
            ctx.fillStyle = path.color;
            const dotR = Math.max(0.5, (path.size || 5) * 0.18 * scaleX);
            for (const pt of path.path) { ctx.beginPath(); ctx.arc(pt.x * scaleX - offsetX, pt.y * scaleY - offsetY, dotR, 0, Math.PI * 2); ctx.fill(); }
            ctx.globalCompositeOperation = 'source-over';
          } else {
            if (path.path.length < 2) return;
            ctx.beginPath();
            ctx.moveTo(path.path[0].x * scaleX - offsetX, path.path[0].y * scaleY - offsetY);
            for (let i = 1; i < path.path.length; i++) { ctx.lineTo(path.path[i].x * scaleX - offsetX, path.path[i].y * scaleY - offsetY); }
            if (path.color === 'eraser') { ctx.globalCompositeOperation = 'destination-out'; ctx.strokeStyle = 'rgba(0,0,0,1)'; }
            else { ctx.globalCompositeOperation = 'source-over'; ctx.strokeStyle = path.color; }
            ctx.lineWidth = path.size * scaleX; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.stroke();
            ctx.globalCompositeOperation = 'source-over';
          }
        });
      }

      // Draw shapes
      if (shapesToRender.length > 0) {
        drawShapesOnCanvas(ctx, shapesToRender, widthInPx, heightInPx, minX, minY, contentWidth, contentHeight);
      }
      
      setSaveProgress(95);
      const dataUrl = canvas.toDataURL('image/png');
      
      // Unified share: use Web Share API (native picker) for all platforms.
      // Falls back to image download + platform web URL when not available.
      {
        const shareMessages = {
          whatsapp:   'Check out my creation from Image Studio! 🎂',
          instagram:  'Check out my creation from Image Studio!',
          facebook:   'Check out my creation from Image Studio!',
          bluetooth:  fileName,
          email:      'Check out my creation from Image Studio!',
        };

        const blob = await fetch(dataUrl).then(r => r.blob());
        const shareFile = new File([blob], `${fileName}.png`, { type: 'image/png' });

        let nativeShareDone = false;
        if ('share' in navigator) {
          try {
            const canShareFile = navigator.canShare && navigator.canShare({ files: [shareFile] });
            await navigator.share(
              canShareFile
                ? { files: [shareFile], title: fileName, text: shareMessages[platform] }
                : { title: fileName, text: shareMessages[platform] }
            );
            nativeShareDone = true;
          } catch (e) {
            if (e.name === 'AbortError') {
              nativeShareDone = true; // user cancelled -- no fallback needed
            } else {
              console.error('Share error:', e);
            }
          }
        }

        if (!nativeShareDone) {
          // Fallback: download the image file
          const link = document.createElement('a');
          link.href = dataUrl;
          link.download = `${fileName}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Platform-specific web fallback after download
          setTimeout(() => {
            if (platform === 'whatsapp') {
              window.open(`https://wa.me/?text=${encodeURIComponent('Check out my creation! 🎂 (image downloaded)')}`, '_blank');
            } else if (platform === 'facebook') {
              window.open('https://www.facebook.com/', '_blank');
            } else if (platform === 'email') {
              window.open(`mailto:?subject=${encodeURIComponent('My Image Studio Creation')}&body=${encodeURIComponent('Hi! Check out my image creation. I have attached the image.')}`, '_blank');
            }
            // instagram, bluetooth: download is sufficient as fallback
          }, 300);
        }
      }
      
      setSaveProgress(100);
      setTimeout(() => {
        setIsSaving(false);
        setSaveProgress(0);
        if (activeTool !== 'magic-wand') {
          setActiveTool(null);
          setCurrentControlSection(null);
          setShowTopControls(false);
        } else {
          setCurrentControlSection('magic-wand');
        }
      }, 500);

    } catch (error) {
      console.error('Share error:', error);
      setIsSaving(false);
      setSaveProgress(0);
      if (activeTool !== 'magic-wand') {
        setActiveTool(null);
        setCurrentControlSection(null);
        setShowTopControls(false);
      } else {
        setCurrentControlSection('magic-wand');
      }
      showAlert('Failed to share image. Please try again.');
    }
  };

  // Toggle all bucket images selection
  const getDisplayedBucketImages = (images, search, sortBy, sortDir) => {
    const q = search.trim().toLowerCase();
    const filtered = q ? images.filter(img => img.displayName.toLowerCase().includes(q)) : images;
    return [...filtered].sort((a, b) => {
      if (sortBy === 'size') {
        return sortDir === 'desc' ? b.size - a.size : a.size - b.size;
      }
      if (sortBy === 'name') {
        const na = a.displayName.toLowerCase(), nb = b.displayName.toLowerCase();
        return sortDir === 'desc' ? nb.localeCompare(na) : na.localeCompare(nb);
      }
      // default: sort by date (updated)
      const ta = a.updated ? new Date(a.updated).getTime() : 0;
      const tb = b.updated ? new Date(b.updated).getTime() : 0;
      return sortDir === 'desc' ? tb - ta : ta - tb;
    });
  };

  const toggleSelectAllBucketImages = () => {
    const displayed = getDisplayedBucketImages(bucketImages, bucketSearch, bucketSortBy, bucketSortDir);
    const displayedNames = displayed.map(img => img.fileName);
    const allDisplayedSelected = displayedNames.every(n => selectedBucketImages.includes(n));
    if (allDisplayedSelected) {
      setSelectedBucketImages(prev => prev.filter(n => !displayedNames.includes(n)));
    } else {
      setSelectedBucketImages(prev => [...new Set([...prev, ...displayedNames])]);
    }
  };

  const handleBucketRename = async (img, newBaseName) => {
    const ext = img.displayName.includes('.') ? '.' + img.displayName.split('.').pop() : '';
    const folder = img.fileName.substring(0, img.fileName.lastIndexOf('/') + 1);
    const newFileName = folder + newBaseName.trim() + ext;
    if (!newBaseName.trim() || newFileName === img.fileName) {
      setRenamingBucketFile(null);
      return;
    }
    try {
      await renameInStorage(img.fileName, newFileName, getEnvStorageBucket());
      // Bust cache so next open re-fetches
      _bucketImagesCache = null;
      setBucketImages(prev => prev.map(i =>
        i.fileName === img.fileName
          ? { ...i, fileName: newFileName, displayName: newBaseName.trim() + ext }
          : i
      ));
      setSelectedBucketImages(prev =>
        prev.map(n => n === img.fileName ? newFileName : n)
      );
    } catch (e) {
      showAlert('Rename failed: ' + e.message);
    }
    setRenamingBucketFile(null);
  };

  // Handle bucket image selection toggle
  const handleBucketImageToggle = (imageFileName) => {
    if (selectedBucketImages.includes(imageFileName)) {
      setSelectedBucketImages(prev => prev.filter(name => name !== imageFileName));
    } else {
      setSelectedBucketImages(prev => [...prev, imageFileName]);
    }
  };

  // Color picker shades
  const colorShades = {
    '#000000': ['#000000', '#333333', '#666666', '#999999', '#CCCCCC', '#FFFFFF'],
    '#fa4848': ['#fa4848', '#ff5c5c', '#ff8080', '#ffa3a3', '#ffc7c7', '#ffebeb'],
    '#00FF00': ['#00FF00', '#33FF33', '#66FF66', '#99FF99', '#CCFFCC', '#E5FFE5'],
    '#0000FF': ['#0000FF', '#3333FF', '#6666FF', '#9999FF', '#CCCCFF', '#E5E5FF'],
    '#FFFF00': ['#FFFF00', '#FFFF33', '#FFFF66', '#FFFF99', '#FFFFCC', '#FFFFE5'],
    '#FF00FF': ['#FF00FF', '#FF33FF', '#FF66FF', '#FF99FF', '#FFCCFF', '#FFE5FF'],
    '#00FFFF': ['#00FFFF', '#33FFFF', '#66FFFF', '#99FFFF', '#CCFFFF', '#E5FFFF'],
  };

  const baseColors = Object.keys(colorShades);

  // Practical cake colour palette for AI colour-change tool
  const cakePalette = {
    'White':     ['#FFFFFF', '#F8F5F0', '#F0EBE0', '#E8DECB', '#DDD0B8', '#D0C0A0'],
    'Cream':     ['#FFF9E6', '#FFF0CC', '#FFE8B3', '#FFD99A', '#FFCA80', '#FFB84D'],
    'Blush':     ['#FDDDE6', '#FBBCCC', '#F99AB2', '#F57898', '#ED5580', '#E0326A'],
    'Rose':      ['#E8B4B8', '#D88A8F', '#C86069', '#B84050', '#A0303A', '#882028'],
    'Red':       ['#FF6B6B', '#F94444', '#E82020', '#CC0000', '#A30000', '#7A0000'],
    'Lavender':  ['#E8D5F0', '#D4B0E5', '#BD8CD5', '#A366C0', '#854AB0', '#662E98'],
    'Sage':      ['#D0DBBF', '#B4C49E', '#99AD80', '#7E9662', '#657E4D', '#4E663B'],
    'Sky':       ['#C5DEEF', '#A0C8E5', '#7AB2D8', '#559DC8', '#3285B5', '#1A6E9E'],
    'Chocolate': ['#7B5C3E', '#6B4D34', '#5C3F2B', '#4D3122', '#3E241A', '#301814'],
    'Gold':      ['#F5D078', '#EFC055', '#E8AC35', '#DE9618', '#D08000', '#BE6A00'],
    'Burgundy':  ['#9B2335', '#812030', '#691C2C', '#531628', '#3E1022', '#2B0A18'],
    'Black':     ['#888888', '#666666', '#444444', '#2E2E2E', '#1A1A1A', '#000000'],
  };
  const cakePaletteNames = Object.keys(cakePalette);

  // Handle tool selection - show top controls
  const handleToolSelect = (tool) => {
    // When magic-wand is active, save/share/print/delete show their top-bar section
    // without switching away from magic-wand — keeps the AI panel and bottom button active.
    if (activeTool === 'magic-wand' && ['save', 'share', 'print', 'delete'].includes(tool)) {
      setCurrentControlSection(tool);
      setShowTopControls(true);
      setImageSelectionMode(false);
      return;
    }

    if (tool === 'resize') {
      if (!selectedImage) {
        showAlert('Please select an image to resize');
        return;
      }
    }

    if (tool === 'draw' && selectedShapeId) {
      const selectedShape = canvasShapes.find(s => s.id === selectedShapeId);
      if (selectedShape) {
        setActiveShapeTool(selectedShape.type);
        setActiveTool('shapes');
        const section = selectedShape.type === 'emoji' ? 'shapes-emoji' : selectedShape.type === 'text' ? 'shapes-text' : 'shapes-draw';
        setCurrentControlSection(section);
        setShowTopControls(true);
        setShowShapeColorPicker(false);
        setShowShapeColorShades(false);
        setCanvasCursor('default');
        return;
      }
    }
    if (tool === 'selection') { setActiveShapeTool(null); }

    if (tool === 'selection') {
      setCanvasCursor('crosshair');
    }
    
    setActiveTool(tool);
    setImageSelectionMode(false); // Turn off image selection mode when another tool is selected
    setCurrentControlSection(tool);
    setShowTopControls(true);
    setShowColorPicker(false);
    setShowColorShades(false);
    
    if (tool === 'selection') {
      // Place crop overlay on first image; default to rectangle mode if no mode chosen yet
      const canvas = canvasRef.current;
      if (canvas && canvasImages.length > 0) {
        const r = canvas.getBoundingClientRect();
        const firstImg = [...canvasImages].sort((a, b) => (a.layerIndex ?? 1) - (b.layerIndex ?? 1))[0];
        const il = (firstImg.x / 100) * r.width;
        const it = (firstImg.y / 100) * r.height;
        const iw = (firstImg.width  / 21)   * r.width;
        const ih = (firstImg.height / 29.7) * r.height;
        setSelectionStart({ x: il, y: it });
        setSelectionEnd  ({ x: il + iw, y: it + ih });
        setIsSelecting(true);
      }
      if (!selectionMode) setSelectionMode('rectangle');
      setCanvasCursor('move');
    } else if (tool === 'draw') {
      setCanvasCursor((drawingMode === 'pen' || drawingMode === 'pencil') ? 'crosshair' : 'cell');
    } else {
      setCanvasCursor('default');
    }
  };

  // ---- Shape tool helpers ------------------------------------------------
  const handleShapeToolSelect = (tool) => {
    setImageSelectionMode(false);
    setShowShapeColorPicker(false);
    setShowShapeColorShades(false);
    setLastUsedShapeTool(tool);
    // Reset lingering resize/rotate states
    setIsResizingShape(false);
    setIsRotatingShape(false);

    // Reset palette to default for the new tool
    const _defaultStroke = document.documentElement.getAttribute('data-theme') === 'dark' ? '#FFFFFF' : '#000000';
    setShapeStrokeColor(_defaultStroke);
    setShapeFillColor('none');
    setShapeColorTarget('stroke');
    setShapeSelectedBase(_defaultStroke);

    if (tool === 'handwriting') {
      setDrawingColor(_defaultStroke);
      setActiveShapeTool('handwriting');
      if (activeTool === 'draw') {
        // Already drawing — cycle to the next draw mode
        const modes = ['pen', 'pencil', 'spray', 'eraser'];
        const next = modes[(modes.indexOf(drawingMode) + 1) % modes.length];
        setDrawingMode(next);
        setCanvasCursor((next === 'pen' || next === 'pencil') ? 'crosshair' : 'cell');
      } else {
        setActiveTool('draw');
        setCanvasCursor((drawingMode === 'pen' || drawingMode === 'pencil') ? 'crosshair' : 'cell');
      }
      setCurrentControlSection('draw');
      setShowTopControls(true);
      return;
    }

    setFillModeActive(false);

    setActiveShapeTool(tool);
    setActiveTool('shapes');
    setCanvasCursor('crosshair');
    const section = tool === 'emoji' ? 'shapes-emoji' : tool === 'text' ? 'shapes-text' : 'shapes-draw';
    setCurrentControlSection(section);
    setShowTopControls(true);
  };

  const finalizePendingText = () => {
    if (pendingTextPos && pendingTextValue.trim()) {
      const _tBefore = canvasShapesRef.current;
      const _cW = canvasRef.current?.offsetWidth  || 595;
      const _cH = canvasRef.current?.offsetHeight || 841;
      const _fs = shapeFontSize;
      const _estW = Math.max(30, pendingTextValue.length * _fs * 0.55);
      if (pendingTextShapeId) {
        // Editing existing shape -- update in place
        const _tAfter = _tBefore.map(s => s.id === pendingTextShapeId ? {
          ...s,
          text: pendingTextValue,
          x2: s.x1 + (_estW / _cW) * 100,
          y2: s.y1 + (_fs * 1.05 / _cH) * 100,
          strokeColor: shapeStrokeColor,
          fontSize: _fs,
          fontFamily: shapeFontFamily,
          bold: shapeTextBold,
          italic: shapeTextItalic,
        } : s);
        setCanvasShapes(_tAfter);
        setSelectedShapeId(pendingTextShapeId);
        pushShapeHistory(_tBefore, _tAfter);
        markDirty();
      } else {
        // New shape
        const newShape = {
          id: Date.now(),
          type: 'text',
          x1: pendingTextPos.x,
          y1: pendingTextPos.y,
          x2: pendingTextPos.x + (_estW / _cW) * 100,
          y2: pendingTextPos.y + (_fs * 1.05 / _cH) * 100,
          strokeColor: shapeStrokeColor,
          fillColor: 'none',
          strokeWidth: shapeStrokeWidth,
          text: pendingTextValue,
          fontSize: _fs,
          fontFamily: shapeFontFamily,
          bold: shapeTextBold,
          italic: shapeTextItalic,
          zOrder: canvasShapes.length,
          layerIndex: ++layerCounterRef.current,
        };
        const _tAfter = [..._tBefore, newShape];
        setCanvasShapes(_tAfter);
        setSelectedShapeId(newShape.id);
        pushShapeHistory(_tBefore, _tAfter);
        markDirty();
      }
    }
    setPendingTextPos(null);
    setPendingTextValue('');
    setPendingTextShapeId(null);
    // Reset text defaults only when creating new text, not editing
    if (!pendingTextShapeId) {
      const _defStroke = document.documentElement.getAttribute('data-theme') === 'dark' ? '#FFFFFF' : '#000000';
      setShapeStrokeColor(_defStroke);
      setShapeFontSize(Math.round(Math.max(12, Math.min(24, (canvasRef.current?.offsetHeight || window.innerWidth * (29.7 / 21)) * 0.028))));
      setShapeFontFamily('Arial');
      setShapeTextBold(false);
      setShapeTextItalic(false);
      setActiveShapeTool(null);
      setActiveTool(null);
      setCanvasCursor('default');
    }
  };
  // -----------------------------------------------------------------------

  return (
    <>
      <input 
        type="file" 
        ref={fileInputRef} 
        style={{ display: 'none' }} 
        onChange={handleLocalFileUpload}
        accept="image/*"
        multiple
      />
      <input
        type="file"
        ref={cameraInputRef}
        style={{ display: 'none' }}
        onChange={handleLocalFileUpload}
        accept="image/*"
        capture="environment"
      />
      <input
        type="file"
        ref={cloudUploadInputRef}
        style={{ display: 'none' }}
        onChange={handleDirectCloudUpload}
        accept="image/*"
        multiple
      />
      
      <div className={`image-studio${fromCustom ? ' is--from-custom' : ''}${(!fromCustom && vpSize.w > vpSize.h && vpSize.h < 550) ? ' is-landscape' : ''}${(!fromCustom && activeTool === 'magic-wand' && magicSubTool === 'chat') ? ' magic-chat-open' : ''}${(!fromCustom && activeTool === 'magic-wand' && magicSubTool === 'chat' && !!(magicChatStep || magicChatError)) ? ' magic-chat-has-status' : ''}`}>
        {/* TOP TOOLBAR - connect | inline controls | actions + close */}
        <div className="is-top-toolbar" style={{ display: fromCustom ? 'none' : undefined }}>
          {/* LEFT: Connect button */}
          <div className="is-top-toolbar-left" style={{ display: fromCustom || (selectedShapeId || selectedImage) || (showColorPicker || showColorShades || showShapeColorPicker || showShapeColorShades) || (['save', 'share', 'print'].includes(activeTool)) ? 'none' : '' }}>
            <div className="connect-menu-wrapper" ref={connectMenuRef}>
              <button
                className="control-btn vertical"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsConnectMenuOpen(!isConnectMenuOpen);
                }}
                title="Add Image"
              >
                <FaPlus />
              </button>
              {isConnectMenuOpen && (
                <div
                  className="connect-dropdown-vertical"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="connect-dropdown-item"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleConnectOption('local'); }}
                  >
                    <FaFolder style={{marginRight:8,opacity:0.7}}/> Folder
                  </button>
                  <button
                    className="connect-dropdown-item"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleConnectOption('camera'); }}
                  >
                    <FaCamera style={{marginRight:8,opacity:0.7}}/> Camera
                  </button>
                  <button
                    className="connect-dropdown-item"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleConnectOption('bucket'); }}
                  >
                    <FaCloud style={{marginRight:8,opacity:0.7}}/> Cloud
                  </button>
                  <button
                    className="connect-dropdown-item"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleConnectOption('upload-cloud'); }}
                  >
                    <FaCloudUploadAlt style={{marginRight:8,opacity:0.7}}/> Upload to Cloud
                  </button>

                </div>
              )}
            </div>
          </div>

          {/* MIDDLE: Inline control section */}
          <div className="is-top-toolbar-controls" ref={topControlsRef}>
            {pendingNavPath && (
              <div className="unsaved-confirm-section">
                <span className="unsaved-warn-text">Unsaved changes !</span>
              </div>
            )}
            {isSaving && (
              <div className="save-progress-container">
                <div className="save-progress-bar">
                  <div
                    className="save-progress-fill"
                    style={{ width: `${saveProgress}%` }}
                  />
                </div>
                <div className="save-progress-text">{saveProgress}%</div>
              </div>
            )}


            {/* Magic wand status now shown inside magic-wand-toolbar row */}

            {currentControlSection === 'draw' && (
              <div className="draw-controls">
                {showColorShades ? (
                  <div className="color-shades-controls">
                    <div className="color-shades-row" style={{ flexWrap: 'nowrap', gap: 4, padding: 2, maxWidth: '100%' }}>
                      {colorShades[selectedBaseColor]?.map((shade) => (
                        <button
                          key={shade}
                          className={`color-shade-option ${drawingColor === shade ? 'active' : ''}`}
                          style={{ backgroundColor: shade, width: 26, height: 26, minWidth: 26, minHeight: 26 }}
                          onClick={() => {
                            setDrawingColor(shade);
                            setShowColorShades(false);
                            setShowColorPicker(false);
                            // Recolour the selected fill image if one is active
                            const selFill = canvasImages.find(i => i.id === selectedImage && i.name === 'fill');
                            if (selFill) {
                              const rgb = hexToRgb(shade);
                              if (rgb) recolourFill(selFill.url, rgb).then(newUrl =>
                                setCanvasImages(prev => prev.map(i => i.id === selectedImage ? { ...i, url: newUrl } : i))
                              );
                            }
                          }}
                          title={shade}
                        />
                      ))}
                    </div>
                  </div>
                ) : showColorPicker ? (
                  <div className="base-colors-controls">
                    <div className="base-colors-row" style={{ flexWrap: 'nowrap', gap: 4, padding: 2, maxWidth: '100%' }}>
                      {baseColors.map((color) => (
                        <button
                          key={color}
                          className={`base-color-option ${selectedBaseColor === color ? 'active' : ''}`}
                          style={{ backgroundColor: color, width: 26, height: 26, minWidth: 26, minHeight: 26 }}
                          onClick={() => {
                            setSelectedBaseColor(color);
                            setShowColorShades(true);
                          }}
                          title={color}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="draw-controls-row">
                    {/* Tool selector dropdown */}
                    <button
                      ref={drawPaletteBtnRef}
                      className="color-palette-btn"
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => {
                        const r = drawPaletteBtnRef.current.getBoundingClientRect();
                        setDrawPalettePos({ top: r.bottom + 6, left: r.left });
                        setShowDrawPalette(p => !p);
                      }}
                      title="Drawing tool"
                      style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px' }}
                    >
                      {drawingMode === 'pencil' ? <FaPencilAlt /> : drawingMode === 'pen' ? <FaPen /> : drawingMode === 'spray' ? <FaSprayCan /> : <FaEraser />}
                    </button>

                    {/* Colour — shows only the current colour dot */}
                    <button
                      className="color-palette-btn"
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => setShowColorPicker(true)}
                      title="Colour"
                      style={{ display: 'flex', alignItems: 'center', padding: '4px 8px' }}
                    >
                      <div style={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: drawingColor, flexShrink: 0, border: '1px solid rgba(128,128,128,0.4)' }} />
                    </button>

                    {/* Brush size — opens size menu */}
                    {!(selectedImage && canvasImages.find(i => i.id === selectedImage)?.name === 'fill') && (
                      <button
                        ref={brushSizeBtnRef}
                        className="color-palette-btn"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => {
                          const r = brushSizeBtnRef.current.getBoundingClientRect();
                          setBrushMenuPos({ top: r.bottom + 6, left: r.left + r.width / 2 });
                          setShowBrushSizeMenu(p => !p);
                        }}
                        title="Brush size"
                        style={{ display: 'flex', alignItems: 'center', padding: '4px 8px' }}
                      >
                        <svg width="26" height="12" style={{ display: 'block' }}>
                          <line x1="2" y1="6" x2="24" y2="6"
                            stroke="currentColor"
                            strokeWidth={Math.min(brushSize * 0.75, 9)}
                            strokeLinecap="round" />
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ---- SELECT/MOVE controls: position / rotate / resize (persistent after action stops) ---- */}
            {currentControlSection === 'selectmove' && (() => {
              const ps = PRINT_SIZES[printSize];
              const step = 0.1;
              const dir = lastDragDirRef.current;

              if (selectedShapeIds.size > 1) {
                return null;
              }

              if (selectedShapeId) {
                const selShape = canvasShapes.find(s => s.id === selectedShapeId);
                if (!selShape) return null;

                if (selectMoveControlMode === 'rotate') {
                  const curA = Math.round(((selShape.rotation || 0) % 360 + 360) % 360);
                  const changeA = (newA) => setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, rotation: newA } : s));
                  return (
                    <div className="dimension-controls-row">
                      <div className="dimension-control compact">
                        <span className="dim-label">A°</span>
                        <button className="dim-step-btn" onClick={() => changeA(curA - 1)}>-</button>
                        <input type="number" value={curA} step={1} onChange={e => changeA(Number(e.target.value))} />
                        <button className="dim-step-btn" onClick={() => changeA(curA + 1)}>+</button>
                      </div>
                    </div>
                  );
                }

                if (selectMoveControlMode === 'resize') {
                  if (selShape.type === 'line' || selShape.type === 'arrow') {
                    const dx_cm = (selShape.x2 - selShape.x1) / 100 * ps.width;
                    const dy_cm = (selShape.y2 - selShape.y1) / 100 * ps.height;
                    const oldL = Math.sqrt(dx_cm * dx_cm + dy_cm * dy_cm);
                    const curL = parseFloat(oldL.toFixed(1));
                    const changeL = (newL) => {
                      if (oldL === 0) return;
                      const scale = newL / oldL;
                      setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, x2: s.x1 + (s.x2 - s.x1) * scale, y2: s.y1 + (s.y2 - s.y1) * scale } : s));
                    };
                    const curA = Math.round(((-Math.atan2(dy_cm, dx_cm) * 180 / Math.PI) % 360 + 360) % 360);
                    const changeA = (newA) => {
                      const rad = -newA * Math.PI / 180;
                      const len = oldL;
                      setCanvasShapes(prev => prev.map(s => {
                        if (s.id !== selectedShapeId) return s;
                        return { ...s, x2: s.x1 + (len * Math.cos(rad)) / ps.width * 100, y2: s.y1 + (len * Math.sin(rad)) / ps.height * 100 };
                      }));
                    };
                    return (
                      <div className="dimension-controls-row">
                        <div className="dimension-control compact">
                          <span className="dim-label">L</span>
                          <button className="dim-step-btn" onClick={() => changeL(Math.max(0.1, curL - step))}>-</button>
                          <input type="number" value={curL} min="0.1" step={step} onChange={e => changeL(Math.max(0.1, Number(e.target.value)))} />
                          <button className="dim-step-btn" onClick={() => changeL(curL + step)}>+</button>
                        </div>
                        <div className="dimension-control compact">
                          <span className="dim-label">A°</span>
                          <button className="dim-step-btn" onClick={() => changeA(curA - 1)}>-</button>
                          <input type="number" value={curA} step={1} onChange={e => changeA(Number(e.target.value))} />
                          <button className="dim-step-btn" onClick={() => changeA(curA + 1)}>+</button>
                        </div>
                      </div>
                    );
                  } else {
                    const curW = parseFloat((Math.abs(selShape.x2 - selShape.x1) / 100 * ps.width).toFixed(1));
                    const curH = parseFloat((Math.abs(selShape.y2 - selShape.y1) / 100 * ps.height).toFixed(1));
                    const signX = selShape.x2 >= selShape.x1 ? 1 : -1;
                    const signY = selShape.y2 >= selShape.y1 ? 1 : -1;
                    return (
                      <div className="dimension-controls-row">
                        <button
                          className={`aspect-ratio-btn${lockAspectRatio ? ' active' : ''}`}
                          onClick={() => setLockAspectRatio(v => !v)}
                          title={lockAspectRatio ? 'Unlock aspect ratio' : 'Lock aspect ratio'}
                        >
                          {lockAspectRatio ? <FaLock /> : <FaUnlock />}
                        </button>
                        <div className="dimension-control compact">
                          <span className="dim-label">W</span>
                          <button className="dim-step-btn" onClick={() => setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, x2: s.x1 + signX * Math.max(0.1, curW - step) * 100 / ps.width, ...(lockAspectRatio && curH > 0 ? { y2: s.y1 + signY * Math.max(0.1, curW - step) * (curH / curW) * 100 / ps.height } : {}) } : s))}>-</button>
                          <input type="number" value={curW} min="0.1" step={step} onChange={e => { const nw = Math.max(0.1, Number(e.target.value)); setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, x2: s.x1 + signX * nw * 100 / ps.width, ...(lockAspectRatio && curH > 0 ? { y2: s.y1 + signY * nw * (curH / curW) * 100 / ps.height } : {}) } : s)); }} />
                          <button className="dim-step-btn" onClick={() => setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, x2: s.x1 + signX * (curW + step) * 100 / ps.width, ...(lockAspectRatio && curH > 0 ? { y2: s.y1 + signY * (curW + step) * (curH / curW) * 100 / ps.height } : {}) } : s))}>+</button>
                        </div>
                        <div className="dimension-control compact">
                          <span className="dim-label">H</span>
                          <button className="dim-step-btn" onClick={() => setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, y2: s.y1 + signY * Math.max(0.1, curH - step) * 100 / ps.height, ...(lockAspectRatio && curW > 0 ? { x2: s.x1 + signX * Math.max(0.1, curH - step) * (curW / curH) * 100 / ps.width } : {}) } : s))}>-</button>
                          <input type="number" value={curH} min="0.1" step={step} onChange={e => { const nh = Math.max(0.1, Number(e.target.value)); setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, y2: s.y1 + signY * nh * 100 / ps.height, ...(lockAspectRatio && curW > 0 ? { x2: s.x1 + signX * nh * (curW / curH) * 100 / ps.width } : {}) } : s)); }} />
                          <button className="dim-step-btn" onClick={() => setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, y2: s.y1 + signY * (curH + step) * 100 / ps.height, ...(lockAspectRatio && curW > 0 ? { x2: s.x1 + signX * (curH + step) * (curW / curH) * 100 / ps.width } : {}) } : s))}>+</button>
                        </div>
                      </div>
                    );
                  }
                }

                // Default: X/Y position (always left/top edge for stable display)
                const edgeX = Math.min(selShape.x1, selShape.x2);
                const edgeY = Math.min(selShape.y1, selShape.y2);
                const xCm = parseFloat((edgeX / 100 * ps.width).toFixed(1));
                const yCmFromBottom = parseFloat(((100 - edgeY) / 100 * ps.height).toFixed(1));
                const changeX = (newX) => {
                  const newPct = Math.max(0, Math.min(100, newX / ps.width * 100));
                  const delta = newPct - edgeX;
                  setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, x1: s.x1 + delta, x2: s.x2 + delta } : s));
                };
                const changeY = (newYBottom) => {
                  const newYTopPct = Math.max(0, Math.min(100, 100 - newYBottom / ps.height * 100));
                  const delta = newYTopPct - edgeY;
                  setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, y1: s.y1 + delta, y2: s.y2 + delta } : s));
                };
                return (
                  <div className="dimension-controls-row">
                    <div className="dimension-control compact">
                      <span className="dim-label">X</span>
                      <button className="dim-step-btn" onClick={() => changeX(xCm - step)}>-</button>
                      <input type="number" value={xCm} step={step} onChange={e => changeX(Number(e.target.value))} />
                      <button className="dim-step-btn" onClick={() => changeX(xCm + step)}>+</button>
                    </div>
                    <div className="dimension-control compact">
                      <span className="dim-label">Y</span>
                      <button className="dim-step-btn" onClick={() => changeY(yCmFromBottom - step)}>-</button>
                      <input type="number" value={yCmFromBottom} step={step} onChange={e => changeY(Number(e.target.value))} />
                      <button className="dim-step-btn" onClick={() => changeY(yCmFromBottom + step)}>+</button>
                    </div>
                  </div>
                );
              }

              if (selectedImage) {
                const img = canvasImages.find(i => i.id === selectedImage);
                if (!img) return null;

                // Rotate mode
                if (selectMoveControlMode === 'rotate') {
                  const curA = Math.round(((img.rotation || 0) % 360 + 360) % 360);
                  const changeImgA = (newA) => setCanvasImages(prev => prev.map(i => i.id === selectedImage ? { ...i, rotation: newA } : i));
                  return (
                    <div className="dimension-controls-row">
                      <div className="dimension-control compact">
                        <span className="dim-label">A°</span>
                        <button className="dim-step-btn" onClick={() => changeImgA(curA - 1)}>-</button>
                        <input type="number" value={curA} step={1} onChange={e => changeImgA(Number(e.target.value))} />
                        <button className="dim-step-btn" onClick={() => changeImgA(curA + 1)}>+</button>
                      </div>
                    </div>
                  );
                }

                // Resize mode: show W/H controls
                if (selectMoveControlMode === 'resize') {
                  const dispW = parseFloat(((resizeWidth / 21) * ps.width).toFixed(1));
                  const dispH = parseFloat(((resizeHeight / 29.7) * ps.height).toFixed(1));
                  // Aspect ratio (use current display ratio, not natural pixel ratio)
                  const _ar = resizeHeight > 0 ? resizeWidth / resizeHeight : 1;
                  const changeW = (newWcm) => {
                    const w = Math.max(0.1, Math.min(ps.width, newWcm)) * 21 / ps.width;
                    if (lockAspectRatio) {
                      const h = Math.min(29.7, Math.max(0.1, w / _ar));
                      setResizeWidth(w);
                      setResizeHeight(h);
                    } else {
                      setResizeWidth(w);
                    }
                  };
                  const changeH = (newHcm) => {
                    const h = Math.max(0.1, Math.min(ps.height, newHcm)) * 29.7 / ps.height;
                    if (lockAspectRatio) {
                      const w = Math.min(21, Math.max(0.1, h * _ar));
                      setResizeHeight(h);
                      setResizeWidth(w);
                    } else {
                      setResizeHeight(h);
                    }
                  };
                  return (
                    <div className="dimension-controls-row">
                      <button
                        className={`aspect-ratio-btn${lockAspectRatio ? ' active' : ''}`}
                        onClick={() => setLockAspectRatio(v => !v)}
                        title={lockAspectRatio ? 'Unlock aspect ratio' : 'Lock aspect ratio'}
                      >
                        {lockAspectRatio ? <FaLock /> : <FaUnlock />}
                      </button>
                      <div className="dimension-control compact">
                        <span className="dim-label">W</span>
                        <button className="dim-step-btn" onClick={() => changeW(dispW - step)}>-</button>
                        <input type="number" value={dispW} min="0.1" max={ps.width} step={step} onChange={e => changeW(Number(e.target.value))} />
                        <button className="dim-step-btn" onClick={() => changeW(dispW + step)}>+</button>
                      </div>
                      <div className="dimension-control compact">
                        <span className="dim-label">H</span>
                        <button className="dim-step-btn" onClick={() => changeH(dispH - step)}>-</button>
                        <input type="number" value={dispH} min="0.1" max={ps.height} step={step} onChange={e => changeH(Number(e.target.value))} />
                        <button className="dim-step-btn" onClick={() => changeH(dispH + step)}>+</button>
                      </div>
                    </div>
                  );
                }

                // Position mode: show X/Y
                const imgWPct = (img.width / 21) * 100;
                const imgHPct = (img.height / 29.7) * 100;
                const xEdgePct = img.x;
                const yEdgePct = img.y;
                const xCm = parseFloat((xEdgePct / 100 * ps.width).toFixed(1));
                const yCmFromBottom = parseFloat(((100 - yEdgePct) / 100 * ps.height).toFixed(1));
                const changeImgX = (newX) => {
                  const newPct = Math.max(0, Math.min(100, newX / ps.width * 100));
                  const delta = newPct - xEdgePct;
                  setCanvasImages(prev => prev.map(i => i.id === selectedImage ? { ...i, x: i.x + delta } : i));
                };
                const changeImgY = (newYBottom) => {
                  const newYTopPct = Math.max(0, Math.min(100, 100 - newYBottom / ps.height * 100));
                  const delta = newYTopPct - yEdgePct;
                  setCanvasImages(prev => prev.map(i => i.id === selectedImage ? { ...i, y: i.y + delta } : i));
                };
                return (
                  <div className="dimension-controls-row">
                    <div className="dimension-control compact">
                      <span className="dim-label">X</span>
                      <button className="dim-step-btn" onClick={() => changeImgX(xCm - step)}>-</button>
                      <input type="number" value={xCm} step={step} onChange={e => changeImgX(Number(e.target.value))} />
                      <button className="dim-step-btn" onClick={() => changeImgX(xCm + step)}>+</button>
                    </div>
                    <div className="dimension-control compact">
                      <span className="dim-label">Y</span>
                      <button className="dim-step-btn" onClick={() => changeImgY(yCmFromBottom - step)}>-</button>
                      <input type="number" value={yCmFromBottom} step={step} onChange={e => changeImgY(Number(e.target.value))} />
                      <button className="dim-step-btn" onClick={() => changeImgY(yCmFromBottom + step)}>+</button>
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            {/* ---- SHAPE DRAW controls (line/arrow/circle/triangle/rectangle) ---- */}
            {(currentControlSection === 'shapes-draw' || currentControlSection === 'shapes-text') && (() => {
              const isText = currentControlSection === 'shapes-text';
              const setShapeColor = (c) => {
                if (shapeColorTarget === 'stroke') {
                  setShapeStrokeColor(c);
                  if (selectedShapeId) setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, strokeColor: c } : s));
                  else if (selectedShapeIds.size > 0) setCanvasShapes(prev => prev.map(s => selectedShapeIds.has(s.id) ? { ...s, strokeColor: c } : s));
                } else {
                  setShapeFillColor(c);
                  if (selectedShapeId) setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, fillColor: c } : s));
                  else if (selectedShapeIds.size > 0) setCanvasShapes(prev => prev.map(s => selectedShapeIds.has(s.id) ? { ...s, fillColor: c } : s));
                }
                setShowShapeColorShades(false);
                setShowShapeColorPicker(false);
              };
              return (
                <div className="draw-controls">
                  {showShapeColorShades ? (
                    <div className="color-shades-controls">
                      <div className="color-shades-row" style={{ flexWrap: 'nowrap', gap: 4, padding: 2, maxWidth: '100%' }}>
                        {colorShades[shapeSelectedBase]?.map(shade => (
                          <button key={shade}
                            className={`color-shade-option ${(shapeColorTarget === 'stroke' ? shapeStrokeColor : shapeFillColor) === shade ? 'active' : ''}`}
                            style={{ backgroundColor: shade, width: 26, height: 26, minWidth: 26, minHeight: 26 }}
                            onClick={() => setShapeColor(shade)}
                            title={shade}
                          />
                        ))}
                      </div>
                    </div>
                  ) : showShapeColorPicker ? (
                    <div className="base-colors-controls">
                      <div className="base-colors-row" style={{ flexWrap: 'nowrap', gap: 4, padding: 2, maxWidth: '100%' }}>
                        {shapeColorTarget === 'fill' && (
                          <button
                            className={`base-color-option no-color-option ${shapeFillColor === 'none' ? 'active' : ''}`}
                            onClick={() => { setShapeColor('none'); }}
                            title="No fill"
                          />
                        )}
                        {baseColors.map(color => (
                          <button key={color}
                            className={`base-color-option ${shapeSelectedBase === color ? 'active' : ''}`}
                            style={{ backgroundColor: color, width: 26, height: 26, minWidth: 26, minHeight: 26 }}
                            onClick={() => { setShapeSelectedBase(color); setShowShapeColorShades(true); }}
                            title={color}
                          />
                        ))}
                      </div>
                    </div>
                  ) : isText ? (
                    <div className="draw-controls-row" style={{ flexWrap: 'wrap', gap: 4 }}>
                      <select value={shapeFontFamily} onChange={e => {
                          setShapeFontFamily(e.target.value);
                          if (selectedShapeId) setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, fontFamily: e.target.value } : s));
                        }}
                        style={{ fontSize: 12, padding: '2px 4px', borderRadius: 4, border: '1px solid #ccc', cursor: 'pointer', maxWidth: 110 }}>
                        {SHAPE_FONTS.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                      <div className="color-picker-container">
                        <button className="color-palette-btn"
                          onClick={() => { setShapeColorTarget('stroke'); setShowShapeColorPicker(true); }}
                          title="Text Color">
                          <FaPalette />
                          <div className="current-color-indicator" style={{ backgroundColor: shapeStrokeColor }} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                    {isRotatingShape && selectedShapeId && (() => {
                      const selShape = canvasShapes.find(s => s.id === selectedShapeId);
                      if (!selShape) return null;
                      const curA = Math.round(((selShape.rotation || 0) % 360 + 360) % 360);
                      const changeA = (newA) => setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, rotation: newA } : s));
                      return (
                        <div className="dimension-controls-row" style={{ marginTop: 4 }}>
                          <div className="dimension-control compact">
                            <span className="dim-label">A°</span>
                            <button className="dim-step-btn" onClick={() => changeA(curA - 1)}>-</button>
                            <input type="number" value={curA} step={1} onChange={e => changeA(Number(e.target.value))} />
                            <button className="dim-step-btn" onClick={() => changeA(curA + 1)}>+</button>
                          </div>
                        </div>
                      );
                    })()}
                    {!isResizingShape && !isRotatingShape && <div className="draw-controls-row">
                      <div className="color-picker-container">
                        <button className="color-palette-btn"
                          onClick={() => { setShapeColorTarget('stroke'); setShowShapeColorPicker(true); }}
                          title="Border Color">
                          <FaMinus style={{ fontSize: 14 }} />
                          <div className="current-color-indicator" style={{ backgroundColor: shapeStrokeColor }} />
                        </button>
                      </div>
                      {['circle', 'triangle', 'rectangle'].includes(activeShapeTool) && (
                        <div className="color-picker-container">
                          <button className="color-palette-btn"
                            onClick={() => { setShapeColorTarget('fill'); setShowShapeColorPicker(true); }}
                            title="Fill Color">
                            <FaTint style={{ fontSize: 14 }} />
                            <div className="current-color-indicator" style={{ backgroundColor: shapeFillColor === 'none' ? 'transparent' : shapeFillColor, border: '1px solid #ccc' }} />
                          </button>
                        </div>
                      )}
                      <div className="brush-size-control">
                        <button
                          ref={strokeWidthBtnRef}
                          className="color-palette-btn"
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => {
                            const r = strokeWidthBtnRef.current.getBoundingClientRect();
                            setStrokeMenuPos({ top: r.bottom + 4, left: r.left + r.width / 2 });
                            setShowStrokeWidthMenu(p => !p);
                          }}
                          title="Stroke Width"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <svg width="22" height="12" style={{ display: 'block' }}>
                            <line x1="1" y1="6" x2="21" y2="6" stroke="currentColor"
                              strokeWidth={Math.min(Math.max(shapeStrokeWidth * 1.5, 1), 10)}
                              strokeLinecap="round"
                              strokeDasharray={shapeDashed ? '5 3' : undefined} />
                          </svg>
                        </button>
                      </div>
                    </div>}
                    {isResizingShape && selectedShapeId && (() => {
                      const selShape = canvasShapes.find(s => s.id === selectedShapeId);
                      if (!selShape) return null;
                      const ps = PRINT_SIZES[printSize];
                      const step = 0.1;
                      if (selShape.type === 'line' || selShape.type === 'arrow') {
                        const dx_cm = (selShape.x2 - selShape.x1) / 100 * ps.width;
                        const dy_cm = (selShape.y2 - selShape.y1) / 100 * ps.height;
                        const oldL = Math.sqrt(dx_cm * dx_cm + dy_cm * dy_cm);
                        const curL = parseFloat(oldL.toFixed(1));
                        const changeL = (newL) => {
                          if (oldL === 0) return;
                          const scale = newL / oldL;
                          setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, x2: s.x1 + (s.x2 - s.x1) * scale, y2: s.y1 + (s.y2 - s.y1) * scale } : s));
                        };
                        const curA = Math.round(((-Math.atan2(dy_cm, dx_cm) * 180 / Math.PI) % 360 + 360) % 360);
                        const changeA = (newA) => {
                          const rad = -newA * Math.PI / 180;
                          const len = oldL;
                          setCanvasShapes(prev => prev.map(s => {
                            if (s.id !== selectedShapeId) return s;
                            return { ...s, x2: s.x1 + (len * Math.cos(rad)) / ps.width * 100, y2: s.y1 + (len * Math.sin(rad)) / ps.height * 100 };
                          }));
                        };
                        return (
                          <div className="dimension-controls-row" style={{ marginTop: 4 }}>
                            <div className="dimension-control compact">
                              <span className="dim-label">L</span>
                              <button className="dim-step-btn" onClick={() => changeL(Math.max(0.1, curL - step))}>-</button>
                              <input type="number" value={curL} min="0.1" step={step} onChange={e => changeL(Math.max(0.1, Number(e.target.value)))} />
                              <button className="dim-step-btn" onClick={() => changeL(curL + step)}>+</button>
                            </div>
                            <div className="dimension-control compact">
                              <span className="dim-label">A&deg;</span>
                              <button className="dim-step-btn" onClick={() => changeA(curA - 1)}>-</button>
                              <input type="number" value={curA} step={1} onChange={e => changeA(Number(e.target.value))} />
                              <button className="dim-step-btn" onClick={() => changeA(curA + 1)}>+</button>
                            </div>
                          </div>
                        );
                      } else {
                        const curW = parseFloat((Math.abs(selShape.x2 - selShape.x1) / 100 * ps.width).toFixed(1));
                        const curH = parseFloat((Math.abs(selShape.y2 - selShape.y1) / 100 * ps.height).toFixed(1));
                        const signX = selShape.x2 >= selShape.x1 ? 1 : -1;
                        const signY = selShape.y2 >= selShape.y1 ? 1 : -1;
                        const isTextLikeShape = selShape.type === 'text' || selShape.type === 'emoji';
                        const applyH = (newH_cm) => {
                          const _cH = canvasRef.current?.offsetHeight || 841;
                          const newHPct = Math.max(0.1, newH_cm) * 100 / ps.height;
                          const newFs = Math.max(8, Math.round(newHPct / 100 * _cH));
                          setCanvasShapes(prev => prev.map(s => {
                            if (s.id !== selectedShapeId) return s;
                            const ns = { ...s, y2: s.y1 + signY * newHPct };
                            if (s.type === 'text' || s.type === 'emoji') { ns.fontSize = newFs; ns.emojiSize = newFs; }
                            return ns;
                          }));
                          if (isTextLikeShape) setShapeFontSize(newFs);
                        };
                        return (
                          <div className="dimension-controls-row" style={{ marginTop: 4 }}>
                            <div className="dimension-control compact">
                              <span className="dim-label">W</span>
                              <button className="dim-step-btn" onClick={() => setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, x2: s.x1 + signX * Math.max(0.1, curW - step) * 100 / ps.width } : s))}>-</button>
                              <input type="number" value={curW} min="0.1" step={step} onChange={e => setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, x2: s.x1 + signX * Math.max(0.1, Number(e.target.value)) * 100 / ps.width } : s))} />
                              <button className="dim-step-btn" onClick={() => setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, x2: s.x1 + signX * (curW + step) * 100 / ps.width } : s))}>+</button>
                            </div>
                            <div className="dimension-control compact">
                              <span className="dim-label">H</span>
                              <button className="dim-step-btn" onClick={() => applyH(curH - step)}>-</button>
                              <input type="number" value={curH} min="0.1" step={step} onChange={e => applyH(Number(e.target.value))} />
                              <button className="dim-step-btn" onClick={() => applyH(curH + step)}>+</button>
                            </div>
                          </div>
                        );
                      }
                    })()}
                    </>
                  )}
                </div>
              );
            })()}



            {currentControlSection === 'selection' && (
              <div className="selection-controls">
                <div className="selection-controls-row">
                  <div className="selection-buttons">
                    <button
                      className={`selection-mode-btn ${selectionMode === 'rectangle' ? 'active' : ''}`}
                      onClick={() => {
                        setSelectionPath([]);
                        setCanvasCursor('move');
                        // If no selection exists yet, place on first image; otherwise keep same bounds
                        if (!isSelecting || (selectionStart.x === 0 && selectionStart.y === 0)) {
                          const canvas = canvasRef.current;
                          if (canvas && canvasImages.length > 0) {
                            const r = canvas.getBoundingClientRect();
                            const firstImg = [...canvasImages].sort((a, b) => (a.layerIndex ?? 1) - (b.layerIndex ?? 1))[0];
                            const il = (firstImg.x / 100) * r.width;
                            const it = (firstImg.y / 100) * r.height;
                            const iw = (firstImg.width  / 21)   * r.width;
                            const ih = (firstImg.height / 29.7) * r.height;
                            setSelectionStart({ x: il, y: it });
                            setSelectionEnd  ({ x: il + iw, y: it + ih });
                          }
                          setIsSelecting(true);
                        }
                        setSelectionMode('rectangle');
                      }}
                      title="Rectangle Crop"
                    >
                      <FaRegSquare />
                    </button>
                    <button
                      className={`selection-mode-btn ${selectionMode === 'circle' ? 'active' : ''}`}
                      onClick={() => {
                        setSelectionPath([]);
                        setCanvasCursor('move');
                        // If no selection exists yet, place on first image; otherwise keep same bounds
                        if (!isSelecting || (selectionStart.x === 0 && selectionStart.y === 0)) {
                          const canvas = canvasRef.current;
                          if (canvas && canvasImages.length > 0) {
                            const r = canvas.getBoundingClientRect();
                            const firstImg = [...canvasImages].sort((a, b) => (a.layerIndex ?? 1) - (b.layerIndex ?? 1))[0];
                            const il = (firstImg.x / 100) * r.width;
                            const it = (firstImg.y / 100) * r.height;
                            const iw = (firstImg.width  / 21)   * r.width;
                            const ih = (firstImg.height / 29.7) * r.height;
                            setSelectionStart({ x: il, y: it });
                            setSelectionEnd  ({ x: il + iw, y: it + ih });
                          }
                          setIsSelecting(true);
                        }
                        setSelectionMode('circle');
                      }}
                      title="Circle Crop"
                    >
                      <FaRegCircle />
                    </button>
                    <button
                      className={`selection-mode-btn ${selectionMode === 'smart' ? 'active' : ''}`}
                      onClick={() => {
                        setSelectionMode('smart');
                        setCanvasCursor('crosshair');
                        setIsSelecting(false);
                        setSelectionPath([]);
                        setSelectionTouchPos(null);
                        smartLassoPathRef.current = [];
                      }}
                      title="Smart Crop: click on an object to auto-detect its boundary"
                    >
                      <FaFingerprint />
                    </button>
                    <button
                      className={`selection-mode-btn ${selectionMode === 'freeform' ? 'active' : ''}`}
                      onClick={() => {
                        setSelectionMode('freeform');
                        setCanvasCursor('crosshair');
                        setIsSelecting(false);
                        setSelectionPath([]);
                        setSelectionTouchPos(null);
                      }}
                      title="Freeform Crop: draw a custom shape"
                    >
                      <FaDrawPolygon />
                    </button>
                  </div>
                  {(((selectionMode === 'rectangle' || selectionMode === 'circle' || selectionMode === 'smart') && isSelecting) ||
                    (selectionMode === 'freeform' && selectionPath.length > 2)) && (
                    <>
                      <button
                        className="selection-mode-btn active"
                        style={{ marginLeft: 6 }}
                        onClick={() => endSelection()}
                        title="Copy: keep the selected area as a new image"
                      >
                        <FaCopy />
                      </button>
                      <button
                        className="selection-mode-btn active"
                        style={{ marginLeft: 2 }}
                        onClick={() => cutSelection()}
                        title="Cut: lift the selected area as a new image (and remove it from the original)"
                      >
                        <FaCut />
                      </button>
                    </>
                  )}
                  {selectionMode === 'smart' && smartCropRunning && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: '#aaa' }}>Detecting…</span>
                  )}
                </div>
              </div>
            )}

            {currentControlSection === 'save' && !isSaving && (
              <div className="save-controls">
                {canvasImages.length === 0 && drawingPaths.length === 0 && canvasShapes.length === 0 ? (
                  <div className="is-toolbar-msg">Nothing on canvas to save</div>
                ) : !saveMessage ? (
                  <div className="save-controls-row">
                    <input
                      type="text"
                      value={fileName}
                      onChange={(e) => setFileName(e.target.value)}
                      placeholder="File name"
                      className="save-input wide"
                      style={{ background: 'white', backgroundColor: 'white', color: '#333', WebkitTextFillColor: '#333', colorScheme: 'light', boxShadow: '0 0 0 1000px white inset', WebkitBoxShadow: '0 0 0 1000px white inset' }}
                    />
                    <select
                      value={saveType}
                      onChange={(e) => setSaveType(e.target.value)}
                      className="save-select wide"
                      style={{ background: 'white', backgroundColor: 'white', color: '#333', WebkitTextFillColor: '#333', colorScheme: 'light', appearance: 'none', WebkitAppearance: 'none', boxShadow: '0 0 0 1000px white inset', WebkitBoxShadow: '0 0 0 1000px white inset' }}
                    >
                      <option value="file">Download</option>
                      <option value="bucket">Save to Cloud</option>
                    </select>
                    <button
                      className="save-icon-btn"
                      onClick={handleSave}
                      title="Save"
                    >
                      <FaSave />
                    </button>
                  </div>
                ) : (
                  <div className={`save-message ${saveMessage.ok ? 'save-msg-ok' : 'save-msg-err'}`}>
                    {saveMessage.text}
                  </div>
                )}
              </div>
            )}

            {currentControlSection === 'share' && !isSaving && (
              <div className="share-controls">
                {canvasImages.length === 0 && drawingPaths.length === 0 && canvasShapes.length === 0 ? (
                  <div className="is-toolbar-msg">Nothing on canvas to share</div>
                ) : (
                  <div className="share-buttons-row">
                    <button className="share-btn bluetooth" onClick={() => handleShare('bluetooth')} title="Share via Bluetooth / Native Share"><FaBluetooth /></button>
                    <button className="share-btn chat" onClick={handleChatShare} title="Share to Chat"><FaComment /></button>
                    <button className="share-btn whatsapp" onClick={() => handleShare('whatsapp')} title="Share on WhatsApp"><FaWhatsapp /></button>
                    <button className="share-btn instagram" onClick={() => handleShare('instagram')} title="Share on Instagram"><FaInstagram /></button>
                    <button className="share-btn facebook" onClick={() => handleShare('facebook')} title="Share on Facebook"><FaFacebookF /></button>
                    <button className="share-btn email" onClick={() => handleShare('email')} title="Share via Email"><FaEnvelope /></button>
                  </div>
                )}
              </div>
            )}

            {currentControlSection === 'layout' && (
              <div className="is-toolbar-section">
                <span className="is-toolbar-msg">Layout: {PRINT_SIZES[printSize].display}</span>
              </div>
            )}

            {currentControlSection === 'print' && (
              <div className="is-toolbar-section">
                <span className="is-toolbar-msg">Nothing on canvas to print</span>
              </div>
            )}

            {currentControlSection === 'magic-wand' && null}

            {/* === Magic: Remove BG controls === */}
            {currentControlSection === 'magic-removebg' && (
              <div className="dimension-controls-row">
                <button
                  className="delete-confirm-text-btn"
                  onClick={handleMagicRemoveBg}
                  disabled={magicProcessing || !selectedImage}
                  title="Remove background (remove.bg)"
                >
                  {magicProcessing ? 'Working...' : 'Remove BG'}
                </button>
                <div style={{ width: 1, height: 20, background: 'var(--border-color,#ddd)', margin: '0 4px', flexShrink: 0 }} />
                <button
                  className="delete-confirm-text-btn"
                  onClick={handleMagicRemovePlate}
                  disabled={magicProcessing || !selectedImage}
                  title="Remove cake plate/board via AI"
                >
                  {magicProcessing ? 'Working...' : 'Remove Plate'}
                </button>
              </div>
            )}

            {/* === Magic: Contrast control === */}
            {currentControlSection === 'magic-contrast' && (() => {
              const val = magicContrast;
              const pct = ((val + 50) / 100) * 100;
              const s = Math.min(50, pct), e2 = Math.max(50, pct);
              const col = val >= 0 ? '#ff6b35' : '#4a90e2';
              const bg = `linear-gradient(to right,#ccc 0%,#ccc ${s}%,${col} ${s}%,${col} ${e2}%,#ccc ${e2}%,#ccc 100%)`;
              return (
                <div className="dimension-controls-row" style={{ gap: 6 }}>
                  <span className="dim-label" style={{ minWidth: 24, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {val > 0 ? `+${val}` : val}
                  </span>
                  <div className="magic-slider-wrap" style={{ width: 140 }}>
                    <div className="magic-slider-zero-mark" />
                    <input
                      className="magic-slider-inline"
                      type="range" min="-50" max="50" step="1" value={val}
                      style={{ '--slider-bg': bg }}
                      onChange={e => {
                        const n = Number(e.target.value);
                        setMagicContrast(n);
                        setMagicFilterPreview(p => ({ ...p, contrast: n }));
                      }}
                      onMouseUp={e => applyMagicFilter(magicBrightness, Number(e.target.value))}
                      onTouchEnd={e => applyMagicFilter(magicBrightness, Number(e.target.value))}
                      disabled={magicProcessing || !selectedImage}
                    />
                  </div>
                </div>
              );
            })()}

            {/* === Magic: Brightness control === */}
            {currentControlSection === 'magic-brightness' && (() => {
              const val = magicBrightness;
              const pct = ((val + 50) / 100) * 100;
              const s = Math.min(50, pct), e2 = Math.max(50, pct);
              const col = val >= 0 ? '#f5a623' : '#4a90e2';
              const bg = `linear-gradient(to right,#ccc 0%,#ccc ${s}%,${col} ${s}%,${col} ${e2}%,#ccc ${e2}%,#ccc 100%)`;
              return (
                <div className="dimension-controls-row" style={{ gap: 6 }}>
                  <span className="dim-label" style={{ minWidth: 24, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {val > 0 ? `+${val}` : val}
                  </span>
                  <div className="magic-slider-wrap" style={{ width: 140 }}>
                    <div className="magic-slider-zero-mark" />
                    <input
                      className="magic-slider-inline"
                      type="range" min="-50" max="50" step="1" value={val}
                      style={{ '--slider-bg': bg }}
                      onChange={e => {
                        const n = Number(e.target.value);
                        setMagicBrightness(n);
                        setMagicFilterPreview(p => ({ ...p, brightness: n }));
                      }}
                      onMouseUp={e => applyMagicFilter(Number(e.target.value), magicContrast)}
                      onTouchEnd={e => applyMagicFilter(Number(e.target.value), magicContrast)}
                      disabled={magicProcessing || !selectedImage}
                    />
                  </div>
                </div>
              );
            })()}



            {currentControlSection === 'delete' && (
              <div className="delete-controls">
                <div className="delete-confirm-row">
                  {selectedImage ? (
                    (() => {
                      const image = canvasImages.find((img) => img.id === selectedImage);
                      if (!image) return null;
                      const cancelDelete = () => {
                        setActiveTool(null);
                        setCurrentControlSection(null);
                        setShowTopControls(false);
                      };
                      if (image.source === 'bucket') {
                        return (
                          <>
                            <button
                              className="delete-confirm-text-btn"
                              onClick={() => {
                                const _after = canvasImages.filter((img) => img.id !== selectedImage);
                                pushImageHistory(canvasImages, _after);
                                setCanvasImages(_after);
                                setSelectedImage(null);
                                setShowImageControls(null);
                                cancelDelete();
                              }}
                              disabled={deleting}
                              title="Remove from Canvas only"
                            >Canvas</button>
                            <span className="delete-confirm-sep">|</span>
                            <button
                              className="delete-confirm-text-btn"
                              onClick={() => { if (deleting) return; handleDeleteFromBucketCloudOnly(image); }}
                              disabled={deleting}
                              title="Delete from Cloud only"
                            >Cloud</button>
                            <span className="delete-confirm-sep">|</span>
                            <button
                              className="delete-confirm-text-btn"
                              onClick={() => { if (deleting) return; handleDeleteFromBucket(image); }}
                              disabled={deleting}
                              title="Delete from Cloud and Canvas"
                            >Both</button>
                            <button
                              className="delete-confirm-reject"
                              onClick={cancelDelete}
                              title="Cancel"
                            ><FaTimes /></button>
                          </>
                        );
                      } else {
                        return (
                          <>
                            <button
                              className="delete-confirm-text-btn"
                              onClick={() => {
                                const _after = canvasImages.filter((img) => img.id !== selectedImage);
                                pushImageHistory(canvasImages, _after);
                                setCanvasImages(_after);
                                setSelectedImage(null);
                                setShowImageControls(null);
                                cancelDelete();
                              }}
                              disabled={deleting}
                              title="Confirm Delete"
                            >Delete</button>
                            <button
                              className="delete-confirm-reject"
                              onClick={cancelDelete}
                              title="Cancel"
                            ><FaTimes /></button>
                          </>
                        );
                      }
                    })()
                  ) : (
                    <span className="delete-confirm-label">No image selected</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: function buttons + close */}
          <div className="is-top-toolbar-actions" style={{ display: (fromCustom || showColorPicker || showColorShades || showShapeColorPicker || showShapeColorShades) ? 'none' : '' }}>

            {(selectedShapeId || selectedImage) && !imageSelectionMode && !isSaving && !pendingNavPath && activeTool !== 'save' && activeTool !== 'share' && (
              <>
                <button
                  className="function-btn vertical"
                  onClick={() => {
                    if (selectedShapeId) {
                      handleCopyShape();
                    } else {
                      handleCopyImage(selectedImage, { stopPropagation: () => {} });
                    }
                  }}
                  title="Copy"
                  disabled={!selectedImage && !selectedShapeId}
                >
                  <FaCopy />
                </button>
                <button
                  className={`function-btn vertical ${activeTool === 'delete' ? 'active' : ''}`}
                  onClick={() => {
                    if (selectedShapeId) {
                      const _sBefore = canvasShapesRef.current;
                      const _sAfter = _sBefore.filter(s => s.id !== selectedShapeId);
                      setCanvasShapes(_sAfter);
                      setSelectedShapeId(null);
                      setActiveTool(null);
                      setActiveShapeTool(null);
                      setSelectedEmoji(null);
                      setCanvasCursor('default');
                      setCurrentControlSection(null);
                      setShowTopControls(false);
                      pushShapeHistory(_sBefore, _sAfter);
                    } else if (selectedImage) {
                      const _imgToDelete = canvasImages.find(i => i.id === selectedImage);
                      if (_imgToDelete && _imgToDelete.source === 'bucket') {
                        handleToolSelect('delete');
                      } else if (_imgToDelete) {
                        const _after = canvasImages.filter(i => i.id !== selectedImage);
                        pushImageHistory(canvasImages, _after);
                        setCanvasImages(_after);
                        setSelectedImage(null);
                        setShowImageControls(null);
                      }
                    }
                  }}
                  title="Delete"
                  disabled={!selectedImage && !selectedShapeId}
                >
                  <FaTrash />
                </button>
              </>
            )}
            {selectedShapeIds.size > 1 && !isSaving && !pendingNavPath && activeTool !== 'save' && activeTool !== 'share' && (
              <>
                <button
                  className="function-btn vertical"
                  title="Copy all selected shapes"
                  onClick={() => {
                    const before = canvasShapesRef.current;
                    const copies = canvasShapesRef.current
                      .filter(s => selectedShapeIds.has(s.id))
                      .map(s => ({
                        ...s,
                        id: Date.now() + Math.random(),
                        x1: s.x1 + 2, y1: s.y1 + 2,
                        x2: s.x2 + 2, y2: s.y2 + 2,
                        layerIndex: ++layerCounterRef.current,
                      }));
                    const after = [...before, ...copies];
                    setCanvasShapes(after);
                    setSelectedShapeIds(new Set(copies.map(c => c.id)));
                    pushShapeHistory(before, after);
                    markDirty();
                  }}
                ><FaCopy /></button>
                <button
                  className="function-btn vertical"
                  title="Delete all selected shapes"
                  onClick={() => {
                    const before = canvasShapesRef.current;
                    const after = before.filter(s => !selectedShapeIds.has(s.id));
                    setCanvasShapes(after);
                    pushShapeHistory(before, after);
                    setSelectedShapeIds(new Set());
                    setActiveTool(null);
                    setActiveShapeTool(null);
                    setShowTopControls(false);
                    setCurrentControlSection(null);
                    setCanvasCursor('default');
                    markDirty();
                  }}
                ><FaTrash /></button>
              </>
            )}
            <button className="is-close-btn" style={{ display: (selectedShapeId || selectedImage) || (['save', 'share', 'print'].includes(activeTool)) ? 'none' : '' }} onClick={() => {
              if (pendingNavPath) {
                const dest = pendingNavPath;
                setPendingNavPath(null);
                if (dest === '__back__') navigate(-1); else navigate(dest);
              } else {
                handleGuardedNavigate(null);
              }
            }} title="Close">
              <FaTimes />
            </button>
          </div>
        </div>
        {/* LEFT CONTROL PANEL ? hover to reveal, centered on left edge */}
        {/* MAGIC TOOLBAR -- replaces tools panel when Magic Wand is active */}
        {activeTool === 'magic-wand' && ['Admin', 'Super'].includes(userRole) && (
          <div className="magic-wand-toolbar">
            <div className="magic-toolbar-row">
              {/* Remove Image Background -- opens colour options popdown */}
              <button
                ref={bgColorBtnRef}
                className={`control-btn vertical${showBgColorPopdown ? ' active' : ''}`}
                onClick={(e) => {
                  const btnRect = e.currentTarget.getBoundingClientRect();
                  const PANEL_W = 220, PANEL_H = 100;
                  const vw = window.innerWidth, vh = window.innerHeight;
                  let left = btnRect.right + 6;
                  if (left + PANEL_W > vw - 6) left = btnRect.left - PANEL_W - 6;
                  if (left < 6) left = 6;
                  let top = btnRect.top;
                  if (top + PANEL_H > vh - 6) top = vh - PANEL_H - 6;
                  if (top < 6) top = 6;
                  setBgColorPopdownPos({ top, left });
                  setShowBgColorPopdown(v => !v);
                }}
                disabled={magicProcessing}
                title="Remove / Replace Image Background"
              ><FaImage /></button>
              {/* Beauty -- AI maximum quality enhancement */}
              <button
                className="control-btn vertical"
                onClick={handleMagicEnhance}
                disabled={magicProcessing || !effectiveImageId}
                title="Beauty (AI quality enhance)"
              >
                {/* makeup / sparkle face icon */}
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* face circle */}
                  <circle cx="12" cy="13" r="7" fill="currentColor" opacity="0.15" stroke="currentColor" strokeWidth="1.5"/>
                  {/* eyes */}
                  <ellipse cx="9.5" cy="12" rx="1" ry="1.2" fill="currentColor"/>
                  <ellipse cx="14.5" cy="12" rx="1" ry="1.2" fill="currentColor"/>
                  {/* smile */}
                  <path d="M9.5 15.5 Q12 17.5 14.5 15.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
                  {/* sparkles */}
                  <path d="M3 4 L3.7 5.7 L5.4 6.4 L3.7 7.1 L3 8.8 L2.3 7.1 L0.6 6.4 L2.3 5.7Z" fill="currentColor"/>
                  <path d="M19.5 1.5 L20 2.7 L21.2 3.2 L20 3.7 L19.5 4.9 L19 3.7 L17.8 3.2 L19 2.7Z" fill="currentColor"/>
                  <path d="M21 9 L21.4 10 L22.4 10.4 L21.4 10.8 L21 11.8 L20.6 10.8 L19.6 10.4 L20.6 10Z" fill="currentColor"/>
                </svg>
              </button>
              {/* Remove Cake Plate/Board (Kling AI) */}
              <button
                className="control-btn vertical"
                onClick={handleMagicRemovePlate}
                disabled={magicProcessing || !effectiveImageId}
                title="Remove Cake Plate / Board"
              >
                {/* 3D circular plate icon */}
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <ellipse cx="12" cy="15" rx="10" ry="3.5" fill="currentColor" opacity="0.25"/>
                  <ellipse cx="12" cy="14" rx="10" ry="3.5" fill="currentColor" opacity="0.5"/>
                  <ellipse cx="12" cy="13" rx="10" ry="3.5" fill="currentColor"/>
                  <ellipse cx="12" cy="13" rx="7" ry="2.2" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.5"/>
                </svg>
              </button>
              {/* Cake Colour Change */}
              <button
                ref={colorBtnRef}
                className={`control-btn vertical${showColorPopdown ? ' active' : ''}`}
                onClick={(e) => {
                  const btnRect = e.currentTarget.getBoundingClientRect();
                  const PANEL_W = 230, PANEL_H = 80;
                  const vw = window.innerWidth, vh = window.innerHeight;
                  let left = btnRect.right + 6;
                  if (left + PANEL_W > vw - 6) left = btnRect.left - PANEL_W - 6;
                  if (left < 6) left = 6;
                  let top = btnRect.top;
                  if (top + PANEL_H > vh - 6) top = vh - PANEL_H - 6;
                  if (top < 6) top = 6;
                  setColorPopdownPos({ top, left });
                  setMagicCakeColorBase(null);
                  setShowColorPopdown(v => !v);
                }}
                disabled={magicProcessing || !effectiveImageId}
                title="Change Cake Colour"
              ><FaPalette /></button>
              {/* AI Edit (multi-image chat) */}
              <button
                className={`control-btn vertical${magicSubTool === 'chat' ? ' active' : ''}`}
                onClick={() => {
                  const next = magicSubTool === 'chat' ? null : 'chat';
                  setMagicSubTool(next);
                  setCurrentControlSection(null);
                  setShowTopControls(false);
                }}
                title="AI Edit"
              ><FaComment /></button>
              {/* Hologram embed -- always shown in fromCustom mode if a hologram stamp exists */}
              {fromCustom && studioStamps.some(s => s.isHologram) && (() => {
                const _holo = studioStamps.find(s => s.isHologram);
                return (
                  <button
                    className="control-btn vertical"
                    onClick={() => handleHologramStamp()}
                    disabled={isSaving}
                    title="Embed hologram stamp"
                  >
                    <img src={_holo.url} style={{ width: 18, height: 18, objectFit: 'contain' }} alt="hologram" />
                  </button>
                );
              })()}
              {/* Divider */}
              <div className="magic-divider" />
              {/* Undo / Redo or Return to Custom */}
              <button className="control-btn vertical" onClick={handleUndo} title="Undo" disabled={historyIndex < 0}><FaUndo /></button>
              {fromCustom ? (
                <button
                  className="control-btn vertical"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
                  onClick={() => {
                    const img = canvasImages[0];
                    const src = img?.url || img?.src || '';
                    if (src) {
                      sessionStorage.setItem('studio_returned_image', src);
                      sessionStorage.setItem('studio_return_profile_id', location.state?.profileId || '');
                    }
                    navigate('/custom');
                  }}
                  title="Return to Custom"
                >
                  <FaChevronLeft style={{ fontSize: 9 }} /><FaSlidersH style={{ fontSize: 11 }} />
                </button>
              ) : (
                <button className="control-btn vertical" onClick={handleRedo} title="Redo" disabled={historyIndex >= drawingHistory.length - 1}><FaRedo /></button>
              )}
            </div>
            {/* Progress / error row inside AI toolbar -- shown below AI buttons */}
            {(magicChatStep || magicProcessing || magicChatError) && (
              <div style={{ overflow: 'hidden' }}>
                <div className={`magic-toolbar-status${magicChatError ? ' magic-toolbar-status--error' : ''}`}
                  onClick={magicChatError ? () => setMagicChatError('') : undefined}>
                  {magicChatError
                    ? `${magicChatError}  ✕`
                    : magicChatStep === 'preparing' ? 'Preparing image...'
                    : magicChatStep === 'sending' ? 'Sending to AI...'
                    : magicChatStep === 'waiting' ? 'AI is working...'
                    : 'Working...'}
                </div>
                {!magicChatError && (
                  <div style={{ height: 2, background: 'linear-gradient(90deg, transparent, #4a90e2, #7b5ea7, #4a90e2, transparent)', backgroundSize: '200% 100%', animation: 'magic-bar-slide 1.2s linear infinite' }} />
                )}
              </div>
            )}

          </div>
        )}

        {/* COLLAPSIBLE TOOLS PANEL WRAPPER */}
        <div
          className="is-tools-panel-wrapper"
          style={{ display: activeTool === 'magic-wand' ? 'none' : undefined }}
          ref={toolsWrapperRef}
          onTouchStart={e => setToolsTouchStartY(e.touches[0].clientY)}
          onTouchEnd={e => {
            const dy = e.changedTouches[0].clientY - toolsTouchStartY;
            if (dy < -30) setShowToolsPanel(false);
            else if (dy > 30) setShowToolsPanel(true);
          }}
        >
        {/* CANVAS TOOLS ROW ? always visible, arrow at end */}
          <div className="is-tools-row" style={{ display: (showColorPicker || showColorShades || showShapeColorPicker || showShapeColorShades) ? 'none' : '' }}>
            <button
              className={`control-btn vertical ${imageSelectionMode ? 'active' : ''}`}
              onClick={() => {
                if (imageSelectionMode && selectedImage && !selectedShapeId) {
                  // Toggle off for image -- no control panel when not in select/move
                  setImageSelectionMode(false);
                  setCurrentControlSection(null);
                  setActiveTool(null);
                  setShowTopControls(false);
                  setSelectedShapeIds(new Set());
                  return;
                }
                if (imageSelectionMode && selectedShapeIds.size > 0) {
                  // Toggle off multi-select
                  setImageSelectionMode(false);
                  setSelectedShapeIds(new Set());
                  setCurrentControlSection(null);
                  setActiveTool(null);
                  setShowTopControls(false);
                  setCanvasCursor('default');
                  return;
                }
                if (imageSelectionMode && selectedShapeId) {
                  // Toggle off -- return to shape colour section
                  setImageSelectionMode(false);
                  setSelectedShapeIds(new Set());
                  setIsResizingShape(false);
                  setIsRotatingShape(false);
                  const shape = canvasShapes.find(s => s.id === selectedShapeId);
                  if (shape) {
                    const section = shape.type === 'emoji' ? 'shapes-emoji' : shape.type === 'text' ? 'shapes-text' : 'shapes-draw';
                    setCurrentControlSection(section);
                    setActiveShapeTool(shape.type);
                    setShowTopControls(true);
                  }
                  return;
                }
                if (imageSelectionMode) {
                  // Toggle off with nothing selected
                  setImageSelectionMode(false);
                  setCurrentControlSection(null);
                  setActiveTool(null);
                  setShowTopControls(false);
                  setCanvasCursor('default');
                  return;
                }
                setImageSelectionMode(true);
                setActiveTool(null);
                setActiveShapeTool(null);
                // If an object is already selected, immediately show position controls
                if (selectedShapeId || selectedImage) {
                  setCurrentControlSection('selectmove');
                  setSelectMoveControlMode(prev => prev === 'resize' ? 'resize' : 'position');
                  setShowTopControls(true);
                } else {
                  setCurrentControlSection(null);
                  setShowTopControls(false);
                }
                setCanvasCursor('pointer');
                setSelectionMode('rectangle');
                setIsSelecting(false);
                setSelectionPath([]);
              }}
              title="Select / Move"
            >
              <FaArrowsUpDownLeftRight />
            </button>
            {selectedImage && !selectedShapeId && (
            <>
              <button
                className={`control-btn vertical${magicSubTool === 'brightness' ? ' active' : ''}`}
                onClick={() => {
                  const next = magicSubTool === 'brightness' ? null : 'brightness';
                  setMagicSubTool(next);
                  setMagicFilterPreview({ brightness: 0, contrast: 0 });
                  if (next) { setCurrentControlSection('magic-brightness'); setShowTopControls(true); }
                  else { setCurrentControlSection(null); setShowTopControls(false); }
                }}
                title="Brightness"
              ><FaSun /></button>
              <button
                className={`control-btn vertical${magicSubTool === 'contrast' ? ' active' : ''}`}
                onClick={() => {
                  const next = magicSubTool === 'contrast' ? null : 'contrast';
                  setMagicSubTool(next);
                  setMagicFilterPreview({ brightness: 0, contrast: 0 });
                  if (next) { setCurrentControlSection('magic-contrast'); setShowTopControls(true); }
                  else { setCurrentControlSection(null); setShowTopControls(false); }
                }}
                title="Contrast"
              ><FaAdjust /></button>
            </>
            )}
            {(activeTool === 'draw' || (!selectedShapeId && !selectedImage && !activeShapeTool)) && (<button
              className={`control-btn vertical ${activeTool === 'selection' ? 'active' : ''}`}
              onClick={() => handleToolSelect('selection')}
              title="Crop Selection Tool"
              disabled={canvasImages.length === 0 && drawingPaths.length === 0 && canvasShapes.length === 0}
            >
              <FaVectorSquare />
            </button>)}
            {(activeTool === 'draw' || (!selectedShapeId && !selectedImage && !activeShapeTool)) && (<button
              className="control-btn vertical"
              onClick={clearCanvas}
              title="Clear Canvas"
              disabled={canvasImages.length === 0 && drawingPaths.length === 0 && canvasShapes.length === 0}
            >
              <FaBroom />
            </button>)}
            {(selectedShapeId || selectedImage) && <button
              className="control-btn vertical"
              onClick={() => {
                const selectedId = selectedShapeId || selectedImage;
                if (!selectedId) return;
                const allItems = [
                  ...canvasImages.map(i => ({ id: i.id, lx: i.layerIndex ?? 1 })),
                  ...canvasShapes.map(s => ({ id: s.id, lx: s.layerIndex ?? 4 })),
                ].sort((a, b) => a.lx - b.lx);
                if (!allItems.length) return;
                const isAtFront = allItems[allItems.length - 1]?.id === selectedId;
                const newIdx = isAtFront
                  ? (allItems[0]?.lx ?? 0) - 1
                  : (allItems[allItems.length - 1]?.lx ?? 0) + 1;
                if (selectedShapeId) {
                  setCanvasShapes(prev => prev.map(s =>
                    s.id === selectedShapeId ? { ...s, layerIndex: newIdx } : s
                  ));
                } else {
                  setCanvasImages(prev => prev.map(i =>
                    i.id === selectedImage ? { ...i, layerIndex: newIdx } : i
                  ));
                }
              }}
              title="Bring Front / Send Back"
            >
              <FaLayerGroup />
            </button>}
            <button
              className="control-btn vertical"
              onClick={handleUndo}
              title="Undo (Ctrl+Z)"
              disabled={historyIndex < 0}
            >
              <FaUndo />
            </button>
            <button
              className="control-btn vertical"
              onClick={handleRedo}
              title="Redo (Ctrl+Y)"
              disabled={historyIndex >= drawingHistory.length - 1}
            >
              <FaRedo />
            </button>
            {/* Fill bucket -- shown when draw tool is active AND canvas has a closed area */}
            {activeTool === 'draw' && hasClosedArea && (
              <button
                className={`control-btn vertical ${fillModeActive ? 'active' : ''}`}
                onClick={() => {
                  const next = !fillModeActive;
                  setFillModeActive(next);
                  setCanvasCursor(next ? 'cell' : (drawingMode === 'pen' ? 'crosshair' : 'cell'));
                }}
                title={fillModeActive ? 'Fill mode on -- click inside a closed drawing' : 'Fill closed shape'}
              >
                <FaTint />
              </button>
            )}
            {/* B / I buttons -- shown in toolbar when text tool/shape is active */}
            {(activeShapeTool === 'text' || currentControlSection === 'shapes-text') && (
              <>
                <button
                  className={`control-btn vertical ${shapeTextBold ? 'active' : ''}`}
                  onClick={() => {
                    const v = !shapeTextBold; setShapeTextBold(v);
                    if (selectedShapeId) setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, bold: v } : s));
                  }}
                  title="Bold"
                ><strong>B</strong></button>
                <button
                  className={`control-btn vertical ${shapeTextItalic ? 'active' : ''}`}
                  onClick={() => {
                    const v = !shapeTextItalic; setShapeTextItalic(v);
                    if (selectedShapeId) setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, italic: v } : s));
                  }}
                  title="Italic"
                ><em style={{ fontStyle: 'italic' }}>I</em></button>
              </>
            )}
            {/* Format Painter -- shown for draw and text shapes when not in select/move mode */}
            {selectedShapeId && (currentControlSection === 'shapes-draw' || currentControlSection === 'shapes-text') && !imageSelectionMode && (
              <button
                className={`control-btn vertical ${formatPainterActive ? 'active' : ''}`}
                onClick={() => {
                  if (formatPainterActive) {
                    setFormatPainterActive(false);
                    formatPainterDataRef.current = null;
                    return;
                  }
                  const shape = canvasShapes.find(s => s.id === selectedShapeId);
                  if (shape) {
                    if (shape.type === 'text') {
                      formatPainterDataRef.current = {
                        _isText: true,
                        strokeColor: shape.strokeColor,
                        fontSize: shape.fontSize,
                        fontFamily: shape.fontFamily,
                        bold: shape.bold,
                        italic: shape.italic,
                      };
                    } else {
                      formatPainterDataRef.current = {
                        strokeColor: shape.strokeColor,
                        fillColor: shape.fillColor,
                        strokeWidth: shape.strokeWidth,
                        dashed: shape.dashed,
                      };
                    }
                    setFormatPainterActive(true);
                  }
                }}
                title="Format Painter"
              >
                <FaPaintBrush />
              </button>
            )}
            {/* Hologram embed -- left of last-used-shape, Admin/Super only */}
            {['Admin','Super'].includes(userRole) && selectedImage && studioStamps.some(s => s.isHologram) && (() => {
              const _holo = studioStamps.find(s => s.isHologram);
              return (
                <button
                  className="control-btn vertical"
                  onClick={handleHologramStamp}
                  title="Embed hologram stamp into image"
                  disabled={isSaving}
                >
                  <img src={_holo.url} style={{ width: 18, height: 18, objectFit: 'contain' }} alt="hologram" />
                </button>
              );
            })()}
            {/* Last used shape -- shown only on mobile (hidden on tablet/desktop via CSS) */}
            {lastUsedShapeTool && (() => {
              const _toolIconMap = {
                line:        <FaMinus />,
                arrow:       <FaArrowRight />,
                circle:      <FaRegCircle />,
                triangle:    <span style={{ display:'inline-block', width:0, height:0, borderLeft:'8px solid transparent', borderRight:'8px solid transparent', borderBottom:'13px solid currentColor', verticalAlign:'middle' }} />,
                rectangle:   <FaRegSquare />,
                handwriting: <FaPencilAlt />,
                text:        <FaFont />,
                emoji:       <FaImage />,
              };
              const _icon = _toolIconMap[lastUsedShapeTool];
              if (!_icon) return null;
              const _isActive = activeShapeTool === lastUsedShapeTool;
              return (
                <button
                  className={`control-btn vertical last-shape-btn${_isActive ? ' active' : ''}`}
                  style={{ color: shapeStrokeColor }}
                  onClick={() => {
                    setShowToolsPanel(true);
                    handleShapeToolSelect(lastUsedShapeTool);
                  }}
                  title={lastUsedShapeTool === 'emoji' ? 'Stamp' : lastUsedShapeTool.charAt(0).toUpperCase() + lastUsedShapeTool.slice(1)}
                >
                  {_icon}
                </button>
              );
            })()}

          </div>
        {/* EXPANDABLE SHAPES CONTENT */}
        <div
          className={`is-tools-content ${showToolsPanel ? 'expanded' : ''}`}
          ref={toolsContentRef}
        >

          {/* Shapes tools */}
          <div className="is-tools-row is-tools-row-shapes">
              {[
                { tool: 'line',        icon: <FaMinus />,       title: 'Line' },
                { tool: 'arrow',       icon: <FaArrowRight />,  title: 'Arrow' },
                { tool: 'circle',      icon: <FaRegCircle />,   title: 'Circle' },
                { tool: 'triangle',    icon: <span style={{ display:'inline-block', width:0, height:0, borderLeft:'8px solid transparent', borderRight:'8px solid transparent', borderBottom:'13px solid currentColor', verticalAlign:'middle' }} />, title: 'Triangle' },
                { tool: 'rectangle',   icon: <FaRegSquare />,   title: 'Rectangle' },
                { tool: 'handwriting', icon: <FaPencilAlt />,   title: 'Handwriting' },
                { tool: 'text',        icon: <FaFont />,        title: 'Text' },
                { tool: 'emoji',       icon: <FaImage />, title: 'Stamps' },
              ].map(({ tool, icon, title }) => (
                tool === 'emoji' ? (
                  <button
                    key={tool}
                    ref={emojiBtnRef}
                    className={`control-btn vertical ${activeShapeTool === 'emoji' ? 'active' : ''}`}
                    onClick={(e) => {
                      handleShapeToolSelect('emoji');
                      const btnRect = e.currentTarget.getBoundingClientRect();
                      const PANEL_W = 240;
                      const PANEL_H = 300;
                      const vw = window.innerWidth;
                      const vh = window.innerHeight;
                      // Prefer right of button, then left of button; clamp inside viewport
                      let left = btnRect.right + 6;
                      if (left + PANEL_W > vw - 6) left = btnRect.left - PANEL_W - 6;
                      if (left < 6) left = 6;
                      let top = btnRect.top;
                      if (top + PANEL_H > vh - 6) top = vh - PANEL_H - 6;
                      if (top < 6) top = 6;
                      setEmojiPopdownPos({ top, left });
                      setShowEmojiPopdown(v => !v);
                    }}
                    title="Stamps"
                  >
                    <FaImage />
                  </button>
                ) : (
                  <button
                    key={tool}
                    className={`control-btn vertical ${activeShapeTool === tool ? 'active' : ''}`}
                    onClick={() => handleShapeToolSelect(tool)}
                    title={title}
                  >
                    {icon}
                  </button>
                )
              ))}
            </div>
        </div>{/* end is-tools-content */}
        </div>{/* end is-tools-panel-wrapper */}

        {/* AI processing progress bar -- removed, now inside magic-wand-toolbar */}

        {/* MAIN CANVAS AREA */}
        <div
          className="canvas-main-area"
          ref={canvasAreaRef}
          onMouseDown={handleAreaMouseDown}
          onMouseMove={handleAreaMouseMove}
          onMouseUp={handleAreaMouseUp}
          onMouseLeave={handleAreaMouseUp}
          onClick={handleAreaDeselect}
          onTouchEnd={handleAreaDeselect}
          style={fromCustom
            ? {}
            : (vpSize.w > vpSize.h && vpSize.h < 550)
              ? { top: '0', left: '0', right: '0', bottom: '54px' }
              : { top: '46px' }
          }
        >
          <div className="canvas-container">
            {(() => {
              const ps = PRINT_SIZES[printSize];
              const ar = ps.width / ps.height;
              const isLs = vpSize.w > vpSize.h && vpSize.h < 550;
              const SIDE = 54; // sidebar width in landscape
              const PAD = 24;
              const isMagicChat = activeTool === 'magic-wand' && magicSubTool === 'chat' && !fromCustom;
              const bottomReserve = isMagicChat ? 84 : 50; // chat panel pushes bottom up
              const labelH = activeTool === 'magic-wand' ? 0 : 20; // pso-label hidden in magic-wand mode
              const maxH = fromCustom
                ? vpSize.h - 40 - PAD              // fromCustom: only magic toolbar row (~40px)
                : isLs
                  ? vpSize.h - 54 - PAD              // landscape: bottom tools strip (54px)
                  : vpSize.h - 46 - 62 - bottomReserve - PAD - labelH;  // portrait: top bar + tools row + bottom bar + label
              const maxW = isLs
                ? vpSize.w - PAD                    // landscape: no side strips, full width
                : Math.min(vpSize.w - PAD, 800);    // portrait: normal
              // Fit the aspect ratio in the available space
              let cW = maxW;
              let cH = Math.round(cW / ar);
              if (cH > maxH) { cH = maxH; cW = Math.round(cH * ar); }
              cW = Math.max(cW, 120);
              cH = Math.max(cH, 80);
              return (
            <>
            <div 
              ref={canvasRef}
              className="a4-canvas"
              style={{
                cursor: canvasCursor,
                width: `${Math.round(cW)}px`,
                height: `${cH}px`,
                backgroundColor: canvasBackground !== 'transparent' ? canvasBackground : undefined,
              }}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={(e) => {
                // When in any crop/selection mode, mouse leaving must NOT clear the overlay.
                // Just stop any active drag and let the user come back to confirm.
                if (activeTool === 'selection' && selectionMode) {
                  circleCropInteractRef.current = null;
                } else {
                  handleMouseUp(e);
                }
                setEraserPos(null);
              }}
              onClick={handleCanvasClick}
              onMouseDown={startDrawing}
              onTouchStart={(e) => {
                const touch = e.touches[0];
                const canvas = canvasRef.current;
                if (!canvas) return;
                
                const rect = canvas.getBoundingClientRect();
                const x = touch.clientX - rect.left;
                const y = touch.clientY - rect.top;
                
                if (activeTool === 'draw' && (drawingMode === 'pen' || drawingMode === 'pencil' || drawingMode === 'eraser' || drawingMode === 'spray') && !fillModeActive) {
                  e.preventDefault();
                  setIsDrawing(true);
                  if (drawingMode === 'spray') {
                    const N = 25, radius = brushSize * 1.2;
                    const initDots = [];
                    for (let i = 0; i < N; i++) {
                      const angle = (i / N) * Math.PI * 2 + Math.random() * 0.4;
                      const r = (0.2 + Math.random() * 0.8) * radius;
                      initDots.push({ x: x + r * Math.cos(angle), y: y + r * Math.sin(angle) });
                    }
                    setCurrentPath(initDots);
                  } else {
                    setCurrentPath([{ x, y }]);
                    if (drawingMode === 'eraser') setEraserPos({ x, y });
                  }
                } else if (activeTool === 'selection' && selectionMode) {
                  e.preventDefault();
                  if (selectionMode === 'smart') {
                    // Touch: if detection result already showing, ignore (let user tap ✓)
                    if (!smartDetectedRef.current) {
                      smartDetectedRef.current = false;
                      setSmartDetected(false);
                      setIsSelecting(true);
                      touchSelectionActiveRef.current = true;
                      // Lift the drawing tip above the finger so it isn't hidden by the thumb.
                      const _tipX = x;
                      const _tipY = Math.max(0, y - TOUCH_PEN_OFFSET_Y);
                      smartLassoPathRef.current = [{ x: _tipX, y: _tipY }];
                      setSelectionPath([{ x: _tipX, y: _tipY }]);
                      setSelectionStart({ x: _tipX, y: _tipY });
                      setSelectionEnd({ x: _tipX, y: _tipY });
                      setSelectionTouchPos({ x: _tipX, y: _tipY });
                    }
                  } else {
                    setIsSelecting(true);
                    // Circle crop: interact with the pre-placed overlay (move or resize)
                    if (selectionMode === 'circle' && selectionStart.x !== 0) {
                      const _cx = (selectionStart.x + selectionEnd.x) / 2;
                      const _cy = (selectionStart.y + selectionEnd.y) / 2;
                      const _rx = Math.abs(selectionEnd.x - selectionStart.x) / 2;
                      const _ry = Math.abs(selectionEnd.y - selectionStart.y) / 2;
                      const _dx = x - _cx, _dy = y - _cy;
                      const _normDist = (_ry > 0 && _rx > 0) ? Math.sqrt((_dx / _rx) ** 2 + (_dy / _ry) ** 2) : 2;
                      if (_normDist > 1.3) return;
                      const _iType = _normDist < 0.85 ? 'move' : 'resize';
                      circleCropInteractRef.current = { type: _iType, startX: x, startY: y, origStart: { ...selectionStart }, origEnd: { ...selectionEnd } };
                      setCanvasCursor(_iType === 'move' ? 'grabbing' : 'nwse-resize');
                      return;
                    }
                    // Rectangle crop: interact with the pre-placed overlay (move or resize)
                    if (selectionMode === 'rectangle' && selectionStart.x !== 0) {
                      const _rx1 = Math.min(selectionStart.x, selectionEnd.x);
                      const _ry1 = Math.min(selectionStart.y, selectionEnd.y);
                      const _rx2 = Math.max(selectionStart.x, selectionEnd.x);
                      const _ry2 = Math.max(selectionStart.y, selectionEnd.y);
                      const TEDGE = 24; // slightly larger hit zone for touch fingers
                      const _insideX = x > _rx1 + TEDGE && x < _rx2 - TEDGE;
                      const _insideY = y > _ry1 + TEDGE && y < _ry2 - TEDGE;
                      const _outside = x < _rx1 - TEDGE || x > _rx2 + TEDGE || y < _ry1 - TEDGE || y > _ry2 + TEDGE;
                      if (_outside) return;
                      const _iType = (_insideX && _insideY) ? 'move' : 'resize';
                      circleCropInteractRef.current = { type: _iType, startX: x, startY: y, origStart: { ...selectionStart }, origEnd: { ...selectionEnd } };
                      setCanvasCursor(_iType === 'move' ? 'grabbing' : 'nwse-resize');
                      return;
                    }
                    // Freeform: start a new path at touch point with the tip lifted above the finger.
                    if (selectionMode === 'freeform') {
                      touchSelectionActiveRef.current = true;
                      const _tipX = x;
                      const _tipY = Math.max(0, y - TOUCH_PEN_OFFSET_Y);
                      setSelectionStart({ x: _tipX, y: _tipY });
                      setSelectionPath([{ x: _tipX, y: _tipY }]);
                      setSelectionTouchPos({ x: _tipX, y: _tipY });
                    } else {
                      setSelectionStart({ x, y });
                      setSelectionEnd({ x, y });
                    }
                  }
                } else if (imageSelectionMode) {
                  e.preventDefault();
                  const xPct = (x / rect.width) * 100;
                  const yPct = (y / rect.height) * 100;
                  isMarqueeSelectingRef.current = true;
                  const mq = { x1: xPct, y1: yPct, x2: xPct, y2: yPct };
                  marqueeRectRef.current = mq;
                  setMarqueeRect(mq);
                  setSelectedShapeIds(new Set());
                  setSelectedShapeId(null);
                  setShowTopControls(false);
                  setCurrentControlSection(null);
                } else if (activeTool === 'shapes' && activeShapeTool) {
                  e.preventDefault();
                  const xPct = (x / rect.width) * 100;
                  const yPct = (y / rect.height) * 100;
                  if (activeShapeTool === 'text') {
                    // Text is created on click (onClick fires after touchEnd on mobile)
                  } else if (activeShapeTool === 'emoji') {
                    if (selectedShapeId) {
                      setSelectedShapeId(null);
                      setActiveShapeTool(null);
                      setActiveTool(null);
                      setCanvasCursor('default');
                      setCurrentControlSection(null);
                      setShowTopControls(false);
                      return;
                    }
                    if (!selectedEmoji) {
                      setActiveShapeTool(null);
                      setActiveTool(null);
                      setCanvasCursor('default');
                      return;
                    }
                    const _eBefore = canvasShapesRef.current;
                    const _cW = canvasRef.current?.offsetWidth  || 595;
                    const _cH = canvasRef.current?.offsetHeight || 841;
                    const _stampW = 20;
                    const _stampH = 20;
                    const newShape = {
                      id: Date.now(), type: 'emoji',
                      x1: xPct - _stampW / 2, y1: yPct - _stampH / 2,
                      x2: xPct + _stampW / 2, y2: yPct + _stampH / 2,
                      strokeColor: shapeStrokeColor, fillColor: 'none',
                      strokeWidth: shapeStrokeWidth, emoji: selectedEmoji, emojiSize: 0,
                      zOrder: canvasShapes.length, layerIndex: ++layerCounterRef.current,
                    };
                    const _eAfter = [..._eBefore, newShape];
                    setCanvasShapes(_eAfter);
                    setSelectedShapeId(newShape.id);
                    pushShapeHistory(_eBefore, _eAfter);
                    markDirty();
                    setSelectedEmoji(null);
                    setActiveShapeTool(null);
                    setActiveTool(null);
                    setCanvasCursor('default');
                    setShowToolsPanel(false);
                  } else {
                    setIsDrawingShape(true);
                    setShapeDrawStart({ x: xPct, y: yPct });
                    setShapeDrawEnd({ x: xPct, y: yPct });
                  }
                }
              }}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchEnd}
            >
              {/* Print size dotted frame overlay -- hidden in fromCustom mode */}
              {!fromCustom && <div className="print-size-overlay" style={{ width: '100%', height: '100%' }} />}
              {/* Eraser cursor circle overlay */}
              {activeTool === 'draw' && drawingMode === 'eraser' && eraserPos && (
                <div style={{
                  position: 'absolute',
                  left: eraserPos.x - brushSize / 2,
                  top: eraserPos.y - brushSize / 2,
                  width: brushSize,
                  height: brushSize,
                  borderRadius: '50%',
                  border: '2px solid rgba(100,100,100,0.85)',
                  boxShadow: '0 0 0 1px rgba(255,255,255,0.7)',
                  pointerEvents: 'none',
                  zIndex: 10000,
                  boxSizing: 'border-box',
                }} />
              )}
              {/* Drawings -- canvas for reliable destination-out eraser */}
              <canvas
                ref={drawingCanvasRef}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 9999
                }}
              />

              {/* ---- Per-shape SVGs for independent z-index layering ---- */}
              {canvasShapes.map(shape => {
                // Hide the shape currently open in inline text edit to avoid double-render
                if (pendingTextShapeId && shape.id === pendingTextShapeId) return null;
                const el = renderShapeElement(shape, cW, cH);
                if (!el) return null;
                return (
                  <svg key={shape.id} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'visible', zIndex: shape.layerIndex ?? 4, pointerEvents: 'none' }}>
                    {el}
                  </svg>
                );
              })}

              {/* Stamp images -- HTML <img> layer avoids SVG cross-origin restrictions */}
              {canvasShapes
                .filter(s => s.type === 'emoji' && s.emoji)
                .map(s => {
                  const minX = Math.min(s.x1, s.x2);
                  const minY = Math.min(s.y1, s.y2);
                  const w    = Math.abs(s.x2 - s.x1) || 5;
                  const h    = Math.abs(s.y2 - s.y1) || 5;
                  const isSel = selectedShapeId === s.id || selectedShapeIds.has(s.id);
                  return (
                    <img
                      key={`stamp-img-${s.id}`}
                      src={s.emoji}
                      alt="stamp"
                      style={{
                        position: 'absolute',
                        left: `${minX}%`,
                        top: `${minY}%`,
                        width: `${w}%`,
                        height: `${h}%`,
                        objectFit: 'contain',
                        pointerEvents: 'none',
                        zIndex: (s.layerIndex ?? 4),
                        transform: s.rotation ? `rotate(${s.rotation}deg)` : undefined,
                        transformOrigin: 'center center',
                        filter: isSel ? 'drop-shadow(0 0 3px #fa4848) drop-shadow(0 0 6px #fa4848) drop-shadow(0 0 10px rgba(250,72,72,0.7))' : undefined,
                      }}
                    />
                  );
                })
              }
              {/* Marquee selection rectangle -- shown while drag-selecting in imageSelectionMode */}
              {marqueeRect && imageSelectionMode && (
                <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'visible', zIndex: 9010, pointerEvents: 'none' }}>
                  <rect
                    x={`${Math.min(marqueeRect.x1, marqueeRect.x2)}%`}
                    y={`${Math.min(marqueeRect.y1, marqueeRect.y2)}%`}
                    width={`${Math.abs(marqueeRect.x2 - marqueeRect.x1)}%`}
                    height={`${Math.abs(marqueeRect.y2 - marqueeRect.y1)}%`}
                    fill="rgba(74,144,226,0.1)"
                    stroke="#4a90e2"
                    strokeWidth={1.5}
                    strokeDasharray="6 3"
                  />
                </svg>
              )}
              {isDrawingShape && shapeDrawStart && shapeDrawEnd && (
                <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'visible', zIndex: 9000, pointerEvents: 'none' }}>
                  {renderShapeElement({
                    id: 'preview', type: activeShapeTool,
                    x1: shapeDrawStart.x, y1: shapeDrawStart.y,
                    x2: shapeDrawEnd.x,   y2: shapeDrawEnd.y,
                    strokeColor: shapeStrokeColor, fillColor: shapeFillColor,
                    strokeWidth: shapeStrokeWidth,
                  }, cW, cH)}
                </svg>
              )}
              {/* Rotation handle for selected image -- stem + circle above the image top edge */}
              {imageSelectionMode && selectedImage && (() => {
                const img = canvasImages.find(i => i.id === selectedImage);
                if (!img) return null;
                const cx = (img.x / 100 + (img.width / 21) / 2) * cW;
                const cy = (img.y / 100 + (img.height / 29.7) / 2) * cH;
                const h_px = (img.height / 29.7) * cH;
                const topY = cy - h_px / 2;
                const rot = img.rotation || 0;
                const startRotateImg = (e) => {
                  e.stopPropagation();
                  shapeClickedRef.current = true; // absorb the following click so handleCanvasClick won't clear state
                  const canvas = canvasRef.current;
                  if (!canvas) return;
                  const rect = canvas.getBoundingClientRect();
                  rotateImageCenterRef.current = {
                    id: img.id,
                    cx: (img.x / 100 + (img.width / 21) / 2) * rect.width,
                    cy: (img.y / 100 + (img.height / 29.7) / 2) * rect.height,
                    before: canvasImagesRef.current,
                  };
                  setRotatingImage(img.id);
                  setSelectMoveControlMode('rotate');
                  setCurrentControlSection('selectmove');
                  setShowTopControls(true);
                };
                return (
                  <svg key={`rot-handle-${img.id}`} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'visible', zIndex: 9002, pointerEvents: 'none' }}>
                    <g transform={`rotate(${rot} ${cx} ${cy})`} style={{ pointerEvents: 'all' }}>
                      <line x1={cx} y1={topY - 3} x2={cx} y2={topY - 22}
                        stroke="#fa4848" strokeWidth={1.5} strokeDasharray="3 2"
                        style={{ pointerEvents: 'none' }} />
                      <circle cx={cx} cy={topY - 30} r={8} fill="white" stroke="#fa4848" strokeWidth={1.5}
                        style={{ cursor: 'grab', pointerEvents: 'all' }}
                        onMouseDown={e => startRotateImg(e)}
                        onTouchStart={e => { e.preventDefault(); startRotateImg(e); }}
                      />
                      <text x={cx} y={topY - 26} textAnchor="middle" dominantBaseline="middle"
                        fontSize={10} fill="#fa4848"
                        style={{ pointerEvents: 'none', userSelect: 'none' }}>&#x21BB;</text>
                    </g>
                  </svg>
                );
              })()}

              {/* Resize/rotate handles for selected shape -- only in select/move mode */}
              {imageSelectionMode && selectedShapeId && (() => {
                const selShape = canvasShapes.find(s => s.id === selectedShapeId);
                if (!selShape) return null;
                const hx1 = (selShape.x1 / 100) * cW;
                const hy1 = (selShape.y1 / 100) * cH;
                const hx2 = (selShape.x2 / 100) * cW;
                const hy2 = (selShape.y2 / 100) * cH;
                const minX = Math.min(hx1, hx2), maxX = Math.max(hx1, hx2);
                const minY = Math.min(hy1, hy2), maxY = Math.max(hy1, hy2);
                const mx = (minX + maxX) / 2, my = (minY + maxY) / 2;
                const isEndpoint   = selShape.type === 'line' || selShape.type === 'arrow';
                const isTextLike   = selShape.type === 'text' || selShape.type === 'emoji';
                const rot          = selShape.rotation || 0;
                const startResize  = (e, corner) => { e.stopPropagation(); shapeBeforeRef.current = canvasShapesRef.current; resizingShapeRef.current = { corner, id: selectedShapeId }; setIsResizingShape(true); setSelectMoveControlMode('resize'); shapeClickedRef.current = true; if (imageSelectionMode) { setCurrentControlSection('selectmove'); setShowTopControls(true); } };
                const startRotate  = (e) => { e.stopPropagation(); shapeBeforeRef.current = canvasShapesRef.current; rotatingShapeRef.current = { id: selectedShapeId, cx: mx, cy: my }; setIsRotatingShape(true); setSelectMoveControlMode('rotate'); shapeClickedRef.current = true; if (imageSelectionMode) { setCurrentControlSection('selectmove'); setShowTopControls(true); } };
                const cornerHandles = isEndpoint
                  ? [{ id: 'p1', cx: hx1, cy: hy1 }, { id: 'p2', cx: hx2, cy: hy2 }]
                  : [{ id: 'nw', cx: minX, cy: minY }, { id: 'ne', cx: maxX, cy: minY },
                     { id: 'sw', cx: minX, cy: maxY }, { id: 'se', cx: maxX, cy: maxY }];
                return (
                  <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'visible', zIndex: 9001, pointerEvents: 'none' }}>
                    <g transform={`rotate(${rot} ${mx} ${my})`} style={{ pointerEvents: 'all' }}>
                      {/* Dashed bounding rect for all non-endpoint shapes */}
                      {!isEndpoint && (
                        <rect x={minX - 3} y={minY - 3} width={maxX - minX + 6} height={maxY - minY + 6}
                          fill="none" stroke="#fa4848" strokeWidth={1.5} strokeDasharray="6 3"
                          style={{ pointerEvents: 'none' }} />
                      )}
                      {/* Corner / endpoint handles */}
                      {cornerHandles.map(h => (
                        <circle key={h.id} cx={h.cx} cy={h.cy} r={6} fill="white" stroke="#fa4848" strokeWidth={1.5}
                          style={{ cursor: isEndpoint ? 'crosshair' : 'pointer', pointerEvents: 'all' }}
                          onMouseDown={e => startResize(e, h.id)}
                          onTouchStart={e => startResize(e, h.id)}
                        />
                      ))}
                      {/* Rotation handle -- stem + circle -- for all non-endpoint shapes */}
                      {!isEndpoint && (
                        <>
                          <line x1={mx} y1={minY} x2={mx} y2={minY - 22}
                            stroke="#fa4848" strokeWidth={1.5} strokeDasharray="3 2"
                            style={{ pointerEvents: 'none' }} />
                          <circle cx={mx} cy={minY - 30} r={8} fill="white" stroke="#fa4848" strokeWidth={1.5}
                            style={{ cursor: 'grab', pointerEvents: 'all' }}
                            onMouseDown={e => startRotate(e)}
                            onTouchStart={e => { e.preventDefault(); startRotate(e); }}
                          />
                          <text x={mx} y={minY - 26} textAnchor="middle" dominantBaseline="middle"
                            fontSize={10} fill="#fa4848"
                            style={{ pointerEvents: 'none', userSelect: 'none' }}>↻</text>
                        </>
                      )}
                    </g>
                  </svg>
                );
              })()}

              {/* ---- Pending text input overlay ---- */}
              {pendingTextPos && (
                <div
                  style={{
                    position: 'absolute',
                    left: `${pendingTextPos.x}%`,
                    top: `${pendingTextPos.y}%`,
                    zIndex: 20,
                  }}
                  onMouseDown={e => e.stopPropagation()}
                  onMouseUp={e => e.stopPropagation()}
                  onClick={e => e.stopPropagation()}
                >
                  <input
                    autoFocus
                    value={pendingTextValue}
                    onChange={e => setPendingTextValue(e.target.value)}
                    onBlur={finalizePendingText}
                    onKeyDown={e => {
                      if (e.key === 'Enter') finalizePendingText();
                      if (e.key === 'Escape') { setPendingTextPos(null); setPendingTextValue(''); }
                    }}
                    placeholder="Type text..."
                    style={{
                      fontSize: shapeFontSize,
                      fontFamily: shapeFontFamily,
                      fontWeight: shapeTextBold ? 'bold' : 'normal',
                      fontStyle: shapeTextItalic ? 'italic' : 'normal',
                      color: shapeStrokeColor,
                      background: 'transparent',
                      border: '1px dashed #fa4848',
                      borderRadius: 3,
                      padding: '2px 5px',
                      minWidth: 80,
                      outline: 'none',
                    }}
                  />
                </div>
              )}



              {/* Images on canvas */}
              {canvasImages.map((img) => (
                <div
                  key={img.id}
                  data-image-id={img.id}
                  className={`canvas-image ${(selectedImage === img.id || selectedShapeIds.has(img.id)) ? 'selected' : ''}`}
                  style={{
                    position: 'absolute',
                    left: `${img.x}%`,
                    top: `${img.y}%`,
                    width: `${(img.width / 21) * 100}%`,
                    height: `${(img.height / 29.7) * 100}%`,
                    border: (activeTool === 'magic-wand' && magicSubTool === 'chat' && magicChatImages.includes(img.id))
                      ? '2px solid #4a90e2'
                      : (selectedImage === img.id || selectedShapeIds.has(img.id))
                      ? '2px solid #fa4848'
                      : (img.cropShape === 'circle' || img.source === 'crop')
                      ? 'none'
                      : '1px solid rgba(0,0,0,0.1)',
                    outline: selectedShapeIds.has(img.id) && selectedImage !== img.id ? '2px dashed #fa4848' : undefined,
                    borderRadius: img.cropShape === 'circle' ? '50%' : undefined,
                    // Cropped pieces (smart / freeform / rectangle / circle crops) have
                    // transparent regions in their PNG. The default .canvas-image
                    // box-shadow would outline the whole rectangle, making the piece
                    // look "elevated" against light backgrounds. Suppress it.
                    boxShadow: (img.source === 'crop' && (selectedImage !== img.id && !selectedShapeIds.has(img.id))) ? 'none' : undefined,
                    cursor: activeTool === 'draw' 
                      ? canvasCursor 
                      : activeTool === 'selection'
                      ? 'crosshair' // Show crosshair when selection tool is active
                      : draggingImage === img.id 
                        ? 'grabbing' 
                        : 'move',
                    overflow: 'visible',
                    zIndex: img.layerIndex ?? 1,
                    pointerEvents: (activeTool === 'draw' || activeTool === 'shapes' || activeTool === 'selection') ? 'none' : 'auto',
                    touchAction: 'none',
                    backgroundColor: 'transparent',
                    transform: img.rotation ? `rotate(${img.rotation}deg)` : undefined,
                    transformOrigin: 'center center',
                  }}
                  onClick={(e) => {
                    if (activeTool !== 'selection') {
                      handleImageClick(img.id, e);
                    }
                  }}
                  onMouseDown={(e) => {
                    if (activeTool !== 'selection' && activeTool !== 'draw') {
                      handleMouseDown(e, img.id, 'move');
                    }
                  }}
                  onTouchStart={(e) => {
                    // Track start position so onTouchEnd can detect a tap vs drag
                    if (activeTool === 'magic-wand' && e.touches[0]) {
                      magicTouchStartRef.current = { id: img.id, x: e.touches[0].clientX, y: e.touches[0].clientY };
                    }
                    if (activeTool !== 'selection' && activeTool !== 'draw') {
                      e.stopPropagation();
                      handleTouchStart(e, img.id, 'move');
                    }
                  }}
                  onTouchEnd={(e) => {
                    // In magic-wand mode, detect a tap (< 12px movement) and fire selection logic
                    if (activeTool === 'magic-wand' && magicTouchStartRef.current?.id === img.id) {
                      const touch = e.changedTouches[0];
                      if (touch) {
                        const dx = touch.clientX - magicTouchStartRef.current.x;
                        const dy = touch.clientY - magicTouchStartRef.current.y;
                        if (Math.sqrt(dx * dx + dy * dy) < 12) {
                          e.stopPropagation();
                          // Replicate handleImageClick logic for magic-wand inline (avoids e.preventDefault conflict)
                          const tappedImg = canvasImages.find(i => i.id === img.id);
                          if (tappedImg) {
                            setSelectedImage(img.id);
                            setShowImageControls(img.id);
                            if (magicSubTool === 'chat') {
                              setMagicChatImages(prev => {
                                if (prev.includes(img.id)) return prev.filter(id => id !== img.id);
                                const next = prev.length >= 2 ? [...prev.slice(1), img.id] : [...prev, img.id];
                                return next;
                              });
                            } else if (magicSubTool === 'contrast' || magicSubTool === 'brightness') {
                              setMagicSubTool(null);
                              setCurrentControlSection(null); setShowTopControls(false);
                            } else if (magicSubTool === 'color') {
                              setCurrentControlSection('magic-color'); setShowTopControls(true);
                            }
                          }
                        }
                      }
                      magicTouchStartRef.current = null;
                    }
                  }}
                >
                  {/* AI chat role badge -- only when 2 images are selected AND both exist on canvas */}
                  {activeTool === 'magic-wand' && magicSubTool === 'chat' && magicChatImages.length === 2 && magicChatImages.includes(img.id) && magicChatImages.every(id => canvasImages.some(i => i.id === id)) && (() => {
                    const sorted = [...magicChatImages].sort((a, b) => {
                      const ia = canvasImages.find(i => i.id === a);
                      const ib = canvasImages.find(i => i.id === b);
                      return (ia?.y ?? 0) - (ib?.y ?? 0);
                    });
                    const isEdit = sorted[0] === img.id;
                    return (
                      <span style={{
                        position: 'absolute', top: 3, left: 3, zIndex: 10001,
                        fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
                        background: isEdit ? 'rgba(74,144,226,0.9)' : 'rgba(120,80,200,0.9)',
                        color: '#fff', padding: '2px 5px', borderRadius: 3,
                        pointerEvents: 'none', textTransform: 'uppercase',
                      }}>{isEdit ? 'Edit' : 'Ref'}</span>
                    );
                  })()}

                  {/* Main Image */}
                  <img 
                    src={img.url} 
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'fill', // Changed from 'contain' to 'fill' to remove white space
                      pointerEvents: 'none',
                      display: 'block',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      clipPath: img.cropShape === 'circle' ? 'ellipse(50% 50% at center)' : undefined,
                      // Live preview while slider is being dragged -- zero means no change
                      filter: (img.id === selectedImage && (magicFilterPreview.brightness !== 0 || magicFilterPreview.contrast !== 0))
                        ? `brightness(${1 + magicFilterPreview.brightness / 50}) contrast(${1 + magicFilterPreview.contrast / 50})`
                        : undefined,
                    }}
                    onError={(e) => {
                      console.error('Failed to load image:', img.url);
                      e.target.style.backgroundColor = '#ffebee';
                      e.target.style.display = 'flex';
                      e.target.style.alignItems = 'center';
                      e.target.style.justifyContent = 'center';
                      e.target.innerHTML = '<div style="color: #c62828; font-size: 11px;">Failed to load</div>';
                    }}
                  />
                  
                  
                  {/* Invisible resize handles -- only in select/move mode */}
                  {imageSelectionMode && selectedImage === img.id && [
                    { dir: 'nw', style: { top: 0,    left: 0,    cursor: 'nw-resize', transform: 'translate(-50%, -50%)' } },
                    { dir: 'n',  style: { top: 0,    left: '50%',cursor: 'n-resize',  transform: 'translate(-50%, -50%)' } },
                    { dir: 'ne', style: { top: 0,    right: 0,   cursor: 'ne-resize', transform: 'translate(50%, -50%)' } },
                    { dir: 'e',  style: { top: '50%',right: 0,   cursor: 'e-resize',  transform: 'translate(50%, -50%)' } },
                    { dir: 'se', style: { bottom: 0, right: 0,   cursor: 'se-resize', transform: 'translate(50%, 50%)' } },
                    { dir: 's',  style: { bottom: 0, left: '50%',cursor: 's-resize',  transform: 'translate(-50%, 50%)' } },
                    { dir: 'sw', style: { bottom: 0, left: 0,    cursor: 'sw-resize', transform: 'translate(-50%, 50%)' } },
                    { dir: 'w',  style: { top: '50%',left: 0,    cursor: 'w-resize',  transform: 'translate(-50%, -50%)' } },
                  ].map(({ dir, style }) => (
                    <div
                      key={dir}
                      style={{
                        position: 'absolute',
                        width: 44,
                        height: 44,
                        background: 'transparent',
                        zIndex: 100,
                        touchAction: 'none',
                        ...style,
                        cursor: getRotatedCursor(dir, img.rotation),
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const canvas = canvasRef.current;
                        if (!canvas) return;
                        const rect = canvas.getBoundingClientRect();
                        resizeDirectionRef.current = dir;
                        resizeStartImagesRef.current = canvasImagesRef.current;
                        setResizingImage(img.id);
                        dragOffsetRef.current = ({ x: e.clientX - rect.left, y: e.clientY - rect.top });
                        setSelectMoveControlMode('resize');
                        setCurrentControlSection('selectmove');
                        setShowTopControls(true);
                      }}
                      onTouchStart={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const touch = e.touches[0];
                        if (!touch) return;
                        const canvas = canvasRef.current;
                        if (!canvas) return;
                        const rect = canvas.getBoundingClientRect();
                        resizeDirectionRef.current = dir;
                        resizeStartImagesRef.current = canvasImagesRef.current;
                        setResizingImage(img.id);
                        dragOffsetRef.current = ({ x: touch.clientX - rect.left, y: touch.clientY - rect.top });
                        setSelectMoveControlMode('resize');
                        setCurrentControlSection('selectmove');
                        setShowTopControls(true);
                      }}
                    />
                  ))}
                </div>
              ))}

              {/* Selection visualization -- rendered last so it always paints above images */}
              {isSelecting && selectionMode === 'circle' && selectionStart && selectionEnd && (() => {
                const _cx = (selectionStart.x + selectionEnd.x) / 2;
                const _cy = (selectionStart.y + selectionEnd.y) / 2;
                const _rx = Math.abs(selectionEnd.x - selectionStart.x) / 2;
                const _ry = Math.abs(selectionEnd.y - selectionStart.y) / 2;
                return (
                  <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 10000 }}>
                    {/* dark scrim outside circle */}
                    <defs>
                      <mask id="circle-crop-mask">
                        <rect width="100%" height="100%" fill="white" />
                        <ellipse cx={_cx} cy={_cy} rx={_rx} ry={_ry} fill="black" />
                      </mask>
                    </defs>
                    <rect width="100%" height="100%" fill="rgba(0,0,0,0.35)" mask="url(#circle-crop-mask)" />
                    {/* circle border */}
                    <ellipse
                      cx={_cx} cy={_cy} rx={_rx} ry={_ry}
                      fill="none"
                      stroke="rgba(255,255,255,0.9)"
                      strokeWidth="1.5"
                      strokeDasharray="6 3"
                    />
                    {/* resize handle at SE */}
                    <circle cx={_cx + _rx * 0.707} cy={_cy + _ry * 0.707} r="7"
                      fill="rgba(255,255,255,0.95)" stroke="rgba(80,120,240,0.9)" strokeWidth="1.5" />
                    <text x={_cx} y={_cy + _ry + 22} textAnchor="middle"
                      fill="rgba(255,255,255,0.85)" fontSize="11" fontFamily="sans-serif">
                      Drag inside to move · Drag edge to resize
                    </text>
                  </svg>
                );
              })()}
              {isSelecting && selectionMode === 'rectangle' && selectionStart && selectionEnd && (() => {
                const _rx = Math.min(selectionStart.x, selectionEnd.x);
                const _ry = Math.min(selectionStart.y, selectionEnd.y);
                const _rw = Math.abs(selectionEnd.x - selectionStart.x);
                const _rh = Math.abs(selectionEnd.y - selectionStart.y);
                return (
                  <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 10000 }}>
                    <defs>
                      <mask id="rect-crop-mask">
                        <rect width="100%" height="100%" fill="white" />
                        <rect x={_rx} y={_ry} width={_rw} height={_rh} fill="black" />
                      </mask>
                    </defs>
                    {/* dark scrim outside rectangle */}
                    <rect width="100%" height="100%" fill="rgba(0,0,0,0.35)" mask="url(#rect-crop-mask)" />
                    {/* rectangle border */}
                    <rect x={_rx} y={_ry} width={_rw} height={_rh}
                      fill="none"
                      stroke="rgba(255,255,255,0.9)"
                      strokeWidth="1.5"
                      strokeDasharray="6 3"
                    />
                    {/* resize handle at SE corner */}
                    <circle cx={_rx + _rw} cy={_ry + _rh} r="7"
                      fill="rgba(255,255,255,0.95)" stroke="rgba(80,120,240,0.9)" strokeWidth="1.5" />
                    <text x={_rx + _rw / 2} y={_ry + _rh + 22} textAnchor="middle"
                      fill="rgba(255,255,255,0.85)" fontSize="11" fontFamily="sans-serif">
                      Drag inside to move · Drag edge to resize
                    </text>
                  </svg>
                );
              })()}
              {isSelecting && selectionMode === 'smart' && (() => {
                const isDetected = smartDetected;
                const isLassoing = !smartCropRunning && !isDetected;
                const polyPoints = isDetected ? selectionPath.map(p => `${p.x},${p.y}`).join(' ') : '';
                const lassoPoints = isLassoing && selectionPath.length > 1 ? selectionPath.map(p => `${p.x},${p.y}`).join(' ') : '';
                // Bounding box for hint text
                const hintX = selectionStart && selectionEnd ? (selectionStart.x + selectionEnd.x) / 2 : 0;
                const hintY = selectionStart ? Math.min(selectionStart.y, selectionEnd?.y ?? selectionStart.y) - 7 : 0;
                return (
                  <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 10000 }}>
                    <defs>
                      <filter id="smart-glow">
                        <feGaussianBlur stdDeviation="2.5" result="blur" />
                        <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                      </filter>
                    </defs>
                    {isLassoing && lassoPoints && (
                      /* Lasso path while user is drawing */
                      <polyline points={lassoPoints} fill="none" stroke="#00ffcc" strokeWidth="2" strokeDasharray="6 4" />
                    )}
                    {smartCropRunning && (
                      /* Pulsing ring while detecting */
                      <rect x={Math.min(selectionStart?.x??0, selectionEnd?.x??0)} y={Math.min(selectionStart?.y??0, selectionEnd?.y??0)}
                        width={Math.abs((selectionEnd?.x??0)-(selectionStart?.x??0))} height={Math.abs((selectionEnd?.y??0)-(selectionStart?.y??0))}
                        fill="none" stroke="rgba(0,255,200,0.4)" strokeWidth="3" strokeDasharray="6 4" />
                    )}
                    {isDetected && (
                      <>
                        <polygon points={polyPoints} fill="none" stroke="rgba(0,255,200,0.35)" strokeWidth="6" filter="url(#smart-glow)" />
                        <polygon points={polyPoints} fill="rgba(0,255,180,0.10)" stroke="#00ffcc" strokeWidth="2" strokeDasharray="8 5" />
                        <text x={hintX} y={hintY} textAnchor="middle" fill="#00ffcc" fontSize="10" fontFamily="sans-serif" opacity="0.9">Tap ✓ to crop</text>
                      </>
                    )}
                  </svg>
                );
              })()}
              {selectionMode === 'freeform' && selectionPath.length > 1 && (
                <svg className="selection-freeform-svg" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 10000 }}>
                  <polyline
                    points={selectionPath.map(p => `${p.x},${p.y}`).join(' ')}
                    fill="rgba(250, 55, 55, 0.15)"
                    stroke="#fa4848"
                    strokeWidth="2.5"
                    strokeDasharray="5,4"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                  {selectionPath.length > 0 && selectionTouchPos && (
                    <line
                      x1={selectionPath[0].x}
                      y1={selectionPath[0].y}
                      x2={selectionTouchPos.x}
                      y2={selectionTouchPos.y}
                      stroke="#fa4848"
                      strokeWidth="1.5"
                      strokeDasharray="4,4"
                      opacity="0.6"
                    />
                  )}
                </svg>
              )}
              {/* Touch pen-cursor for freeform and smart: a short vertical pencil with
                  a sharp triangular tip at the BOTTOM (the drawing point) and a small
                  grip-dot at the TOP. The pencil body sits above the finger so the
                  thumb never hides the action. */}
              {(selectionMode === 'freeform' || selectionMode === 'smart') && selectionTouchPos && (() => {
                const tipColor = selectionMode === 'smart' ? '#00ffcc' : '#fa4848';
                // SVG is 14x32 px; the sharp tip is at (7, 32), so positioning the SVG
                // with `left = touchX - 7` and `top = touchY - 32` aligns the tip
                // exactly with selectionTouchPos.
                return (
                  <svg
                    style={{
                      position: 'absolute',
                      left: selectionTouchPos.x - 7,
                      top: selectionTouchPos.y - 32,
                      width: 14,
                      height: 32,
                      pointerEvents: 'none',
                      zIndex: 10001,
                      filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.8))',
                    }}
                    viewBox="0 0 14 32"
                  >
                    {/* Grip dot at the top (where you'd hold a real pencil) */}
                    <circle cx="7" cy="3" r="3" fill={tipColor} stroke="white" strokeWidth="0.8" />
                    {/* Pencil body / shaft */}
                    <rect x="4" y="5" width="6" height="18" fill={tipColor} />
                    {/* Sharp tip at the bottom (the actual drawing point) */}
                    <polygon points="4,23 10,23 7,32" fill={tipColor} stroke="white" strokeWidth="0.5" />
                  </svg>
                );
              })()}
            </div>
            <div className="pso-label" style={{ display: (fromCustom || activeTool === 'magic-wand') ? 'none' : undefined }}>
              <button
                className="canvas-bg-toggle-btn"
                onMouseDown={e => e.stopPropagation()}
                onTouchStart={e => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  setCanvasBackground(prev => prev === '#2b2b2b' ? '#ffffff' : '#2b2b2b');
                }}
                title={canvasBackground === '#2b2b2b' ? 'Preview on white background' : 'Preview on dark background'}
              >
                <span className={`bg-toggle-icon${canvasBackground === '#2b2b2b' ? ' dark' : ''}`} />
              </button>
              {`${PRINT_SIZES[printSize].display}  ${PRINT_SIZES[printSize].width}x${PRINT_SIZES[printSize].height}cm`}
              <button
                className="canvas-bg-toggle-btn"
                onMouseDown={e => e.stopPropagation()}
                onTouchStart={e => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); cyclePrintSize(); }}
                title={`Layout: ${PRINT_SIZES[printSize].display}`}
              >
                <span className={`psi-rect-sm${PRINT_SIZES[printSize].height > PRINT_SIZES[printSize].width ? ' portrait' : ' landscape'}`} />
              </button>
            </div>
            </>
          );
          })()}
          </div>
        </div>

        {/* Magic Wand AI Chat panel -- single input strip between canvas and bottom bar */}
        {activeTool === 'magic-wand' && magicSubTool === 'chat' && ['Admin', 'Super'].includes(userRole) && (
          <div className="magic-chat-panel" style={canvasRect.width > 0 ? { left: canvasRect.left, width: canvasRect.width } : undefined}>
            {/* Prompt history dropdown */}
            {showPromptHistory && magicPromptHistory.length > 0 && (
              <div className="magic-prompt-history">
                {magicPromptHistory.map((p, i) => (
                  <div key={i} className="magic-prompt-history-item">
                    <button
                      className="magic-prompt-history-text"
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => { setMagicChatInput(p); setShowPromptHistory(false); }}
                    >
                      {p}
                    </button>
                    <button
                      className="magic-prompt-history-delete"
                      onMouseDown={e => e.preventDefault()}
                      onClick={e => {
                        e.stopPropagation();
                        setMagicPromptHistory(prev => prev.filter((_, idx) => idx !== i));
                      }}
                      title="Remove from history"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="magic-chat-input-row">
              <textarea
                className="magic-chat-input"
                value={magicChatInput}
                rows={1}
                onChange={e => setMagicChatInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleMagicChat(); } }}
                onFocus={(e) => {
                  // Only close history if focus didn't come from a history item button
                  if (!e.relatedTarget?.classList?.contains('magic-prompt-history-item')) {
                    setShowPromptHistory(false);
                  }
                }}
                placeholder="Describe the edit..."
                disabled={magicChatLoading}
              />
              <button
                className={`magic-chat-icon-btn${showPromptHistory ? ' active' : ''}`}
                onMouseDown={e => e.preventDefault()}
                onClick={() => setShowPromptHistory(s => !s)}
                title="Recent prompts"
                tabIndex={-1}
                disabled={magicPromptHistory.length === 0}
              >
                <FaHistory />
              </button>
              <button
                className="magic-chat-icon-btn magic-chat-icon-btn--send"
                onClick={handleMagicChat}
                disabled={magicChatLoading || !magicChatInput.trim()}
                title="Send"
              >
                {magicChatLoading ? <span className="magic-chat-dots"><span/><span/><span/></span> : <FaPaperPlane />}
              </button>
            </div>
          </div>
        )}

        {/* RIGHT PANEL REMOVED - buttons moved to top toolbar */}

        {/* Bucket Modal */}
        <Modal
          isOpen={showBucketModal}
          onRequestClose={() => {
            setShowBucketModal(false);
            setBucketModalMode('select');
            setSelectedBucketImages([]);
            setConfirmDeleteBucket(false);
            setBucketDeleteMsg(null);
            setBucketSearch('');
          }}
          className="image-studio-bucket-modal"
          overlayClassName="image-studio-modal-overlay"
        >
          <div className="bucket-modal-header">
            <div className="bucket-search-wrap">
              <input
                className="bucket-search-input"
                type="text"
                placeholder="Search images..."
                value={bucketSearch}
                onChange={e => setBucketSearch(e.target.value)}
              />
              {bucketSearch.length > 0 && (
                <button
                  className="bucket-search-clear"
                  onClick={() => setBucketSearch('')}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
            <button
              className="close-modal-btn"
              onClick={() => {
                setShowBucketModal(false);
                setBucketModalMode('select');
                setSelectedBucketImages([]);
                setConfirmDeleteBucket(false);
                setBucketDeleteMsg(null);
                setBucketSearch('');
              }}
            >
              ✕
            </button>
          </div>

          <div className="bucket-modal-content">
            {isLoading ? (
              <div className="loading-bucket">Loading images...</div>
            ) : bucketImages.length === 0 ? (
              <div className="no-bucket-images">No images found in Cloud</div>
            ) : (
              <>
                {(() => {
                  const _displayed = getDisplayedBucketImages(bucketImages, bucketSearch, bucketSortBy, bucketSortDir);
                  const _displayedNames = _displayed.map(img => img.fileName);
                  const _allSelected = _displayedNames.length > 0 && _displayedNames.every(n => selectedBucketImages.includes(n));
                  const _sortLabel = bucketSortBy === 'date' ? 'by Date' : bucketSortBy === 'size' ? 'by Size' : 'by Name';
                  const _nextSort = bucketSortBy === 'date' ? 'size' : bucketSortBy === 'size' ? 'name' : 'date';
                  return (
                    <>
                      <div className="bucket-selection-info">
                        {/* Row layout depends on state:
                            No selection  → [All ☐] | [count]          | [sort]
                            Selected      → [Delete] | [All ☐ · count]  | [Insert]
                            Confirming    → [empty]  | [confirm dialog]  | [Insert]
                            Feedback      → [empty]  | [msg]             | [Insert]  */}
                        <div className="selection-row">
                          {/* Left */}
                          <div className="bucket-hdr-left">
                            {selectedBucketImages.length === 0 ? (
                              /* No selection: All checkbox lives on the left */
                              <button className="select-all-btn" onClick={toggleSelectAllBucketImages}>
                                {_allSelected
                                  ? <FaCheckSquare style={{ color: '#fa4848', fontSize: 18 }} />
                                  : <FaRegSquare style={{ color: '#bbb', fontSize: 18 }} />
                                }
                                <span className="select-all-label">All</span>
                              </button>
                            ) : (!confirmDeleteBucket && !bucketDeleteMsg) ? (
                              /* Items selected, not confirming: Delete button on the left */
                              <button
                                className="bucket-hdr-btn bucket-hdr-btn--delete"
                                title={`Delete ${selectedBucketImages.length} image(s)`}
                                onClick={() => setConfirmDeleteBucket(true)}
                                disabled={deleting}
                              >
                                <FaTrash />
                              </button>
                            ) : null}
                          </div>

                          {/* Center */}
                          <div className="selection-count">
                            {bucketDeleteMsg ? (
                              /* Feedback message after delete */
                              <span className="bucket-confirm-inline">
                                <span className="bucket-confirm-text" style={{ color: bucketDeleteMsg.ok ? '#2e7d32' : '#c0392b' }}>
                                  {bucketDeleteMsg.text}
                                </span>
                              </span>
                            ) : confirmDeleteBucket ? (
                              /* Confirm dialog — All hidden */
                              <span className="bucket-confirm-inline">
                                <span className="bucket-confirm-text">
                                  Delete {selectedBucketImages.length} image{selectedBucketImages.length !== 1 ? 's' : ''}?
                                </span>
                                <button className="bucket-delete-btn" onClick={handleDeleteMultipleFromBucket} disabled={deleting}>
                                  {deleting ? 'Deleting…' : 'Yes'}
                                </button>
                                <button className="bucket-cancel-btn" onClick={() => setConfirmDeleteBucket(false)} disabled={deleting}>
                                  No
                                </button>
                              </span>
                            ) : selectedBucketImages.length > 0 ? (
                              /* Items selected: All checkbox beside count */
                              <>
                                <button className="select-all-btn" onClick={toggleSelectAllBucketImages} style={{ marginRight: 5 }}>
                                  {_allSelected
                                    ? <FaCheckSquare style={{ color: '#fa4848', fontSize: 16 }} />
                                    : <FaRegSquare style={{ color: '#bbb', fontSize: 16 }} />
                                  }
                                  <span className="select-all-label">All</span>
                                </button>
                                <span>{selectedBucketImages.length}/{_displayed.length}</span>
                              </>
                            ) : (
                              /* No selection: plain count */
                              <span>{selectedBucketImages.length}/{_displayed.length}</span>
                            )}
                          </div>

                          {/* Right */}
                          <div className="bucket-hdr-right">
                            {selectedBucketImages.length > 0 ? (
                              <button
                                className="bucket-hdr-btn bucket-hdr-btn--insert"
                                title={`Insert ${selectedBucketImages.length} image(s) to canvas`}
                                onClick={handleInsertMultipleFromBucket}
                                disabled={isLoading}
                              >
                                <FaPlus style={{ fontSize: 18 }} />
                              </button>
                            ) : (
                              <>
                                <button
                                  className="bucket-hdr-btn bucket-hdr-btn--sort"
                                  title={`Currently: ${_sortLabel}. Click to cycle`}
                                  onClick={() => setBucketSortBy(_nextSort)}
                                >
                                  {_sortLabel}
                                </button>
                                <button
                                  className="bucket-hdr-btn bucket-hdr-btn--sort-dir"
                                  title={bucketSortDir === 'desc' ? 'Descending' : 'Ascending'}
                                  onClick={() => setBucketSortDir(d => d === 'desc' ? 'asc' : 'desc')}
                                >
                                  {bucketSortDir === 'desc' ? '▼' : '▲'}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="bucket-images-grid">
                        {_displayed.map((img, index) => {
                          const _isRenaming = renamingBucketFile?.fileName === img.fileName;
                          const _baseName = img.displayName.includes('.')
                            ? img.displayName.substring(0, img.displayName.lastIndexOf('.'))
                            : img.displayName;
                          return (
                            <div
                              key={index}
                              className={`bucket-image-item ${selectedBucketImages.includes(img.fileName) ? 'selected' : ''}`}
                              onClick={(e) => {
                                if (_isRenaming) return;
                                e.stopPropagation();
                                handleBucketImageToggle(img.fileName);
                              }}
                            >
                              <div className="bucket-image-checkbox">
                                <input
                                  type="checkbox"
                                  checked={selectedBucketImages.includes(img.fileName)}
                                  onChange={(e) => { e.stopPropagation(); handleBucketImageToggle(img.fileName); }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                              <div className="bucket-image-preview">
                                {img.previewUrl ? (
                                  <img
                                    src={img.previewUrl}
                                    alt={img.displayName}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.parentElement.innerHTML = '<div class="preview-error">No Preview</div>';
                                    }}
                                  />
                                ) : (
                                  <div className="preview-error">No Preview</div>
                                )}
                              </div>
                              <div className="bucket-image-info">
                                {_isRenaming ? (
                                  <input
                                    className="bucket-rename-input"
                                    autoFocus
                                    value={renamingBucketFile.newName}
                                    onClick={e => e.stopPropagation()}
                                    onChange={e => setRenamingBucketFile(r => ({ ...r, newName: e.target.value }))}
                                    onBlur={() => handleBucketRename(img, renamingBucketFile.newName)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') handleBucketRename(img, renamingBucketFile.newName);
                                      if (e.key === 'Escape') setRenamingBucketFile(null);
                                    }}
                                  />
                                ) : (
                                  <div
                                    className="bucket-image-name"
                                    title="Click to rename"
                                    onClick={e => {
                                      e.stopPropagation();
                                      setRenamingBucketFile({ fileName: img.fileName, displayName: img.displayName, newName: _baseName });
                                    }}
                                  >
                                    {img.displayName}
                                  </div>
                                )}
                                <div className="bucket-image-meta">
                                  <span className="bucket-image-size">{img.size ? formatFileSize(img.size) : '—'}</span>
                                  {img.updated && (
                                    <span className="bucket-image-date">
                                      {new Date(img.updated).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' })}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {_displayed.length === 0 && (
                          <div className="no-bucket-images">No images match your search</div>
                        )}
                      </div>
                    </>
                  );
                })()}

              </>
            )}
          </div>
        </Modal>
        <div
          onClick={(e) => {
            if (!isDirty) return;
            const link = e.target.closest('a[href]');
            if (!link) return;
            e.preventDefault();
            e.stopPropagation();
            handleGuardedNavigate(new URL(link.href).pathname);
          }}
          style={{ display: 'contents' }}
        >
          {/* ImageStudio bottom bar replaces the app bottom menu */}
          <div className="is-bottom-bar" style={{ display: fromCustom ? 'none' : undefined }}>
            <button
              className={`is-bottom-btn${pendingNavPath ? ' save-blink' : ''}${activeTool === 'save' ? ' active' : ''}`}
              onClick={() => {
                if (pendingNavPath) { setPendingNavPath(null); setSaveMessage(null); }
                handleToolSelect('save');
              }}
              title="Save"
            >
              <FaSave />
              <span className="is-bottom-label">Save</span>
            </button>
            {['Admin', 'Super'].includes(userRole) && (
            <button
              className={`is-bottom-btn${activeTool === 'magic-wand' ? ' active' : ''}`}
              onClick={() => {
                if (activeTool === 'magic-wand') {
                  setActiveTool(null);
                  setCurrentControlSection(null);
                  setShowTopControls(false);
                  setMagicSubTool(null);
                } else {
                  handleToolSelect('magic-wand');
                }
              }}
              title="Magic Wand (AI)"
            >
              <FaMagic />
              <span className="is-bottom-label">Magic</span>
            </button>
            )}
            {isMobileWeb ? (
              /* Mobile: Share button → direct in-app chat share */
              <button
                className="is-bottom-btn"
                onClick={handleChatShare}
                title="Share to Chat"
              >
                <FaComment />
                <span className="is-bottom-label">Chat</span>
              </button>
            ) : (
              /* Desktop: regular Share options panel */
              <button
                className={`is-bottom-btn${activeTool === 'share' ? ' active' : ''}`}
                onClick={() => handleToolSelect('share')}
                title="Share"
              >
                <FaShareAlt />
                <span className="is-bottom-label">Share</span>
              </button>
            )}
            <button
              className={`is-bottom-btn${activeTool === 'print' ? ' active' : ''}`}
              onClick={() => {
                const hasContent = canvasImages.length > 0 || drawingPaths.length > 0 || canvasShapes.length > 0;
                if (!hasContent) { handleToolSelect('print'); } else { if (activeTool !== 'magic-wand') setActiveTool('print'); handlePrint(); }
              }}
              title={isMobileWeb ? 'Share / Print' : 'Print'}
            >
              {isMobileWeb ? <FaShareAlt /> : <FaPrint />}
              <span className="is-bottom-label">{isMobileWeb ? 'Share' : 'Print'}</span>
            </button>
          </div>
        </div>
      </div>
      {/* Draw palette dropdown portal */}
      {showDrawPalette && createPortal(
        <div
          ref={drawPaletteRef}
          onMouseDown={e => e.preventDefault()}
          style={{
            position: 'fixed', top: drawPalettePos.top, left: drawPalettePos.left,
            zIndex: 9999,
            background: 'var(--bg-surface, #fff)',
            border: '1px solid var(--border-color, #ddd)',
            color: 'var(--text-primary, #333)',
            borderRadius: 8, padding: '4px',
            display: 'flex', flexDirection: 'column', gap: 1,
            minWidth: 48, boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          }}
        >
          {[
            { mode: 'pen',    icon: <FaPen />,        label: 'Pen'      },
            { mode: 'pencil', icon: <FaPencilAlt />,  label: 'Pencil'   },
            { mode: 'spray',  icon: <FaSprayCan />,   label: 'Spray'    },
            { mode: 'eraser', icon: <FaEraser />,     label: 'Eraser'   },
            { mode: 'eyedropper', icon: <FaEyeDropper />, label: 'Eyedropper' },
          ].map(({ mode, icon, label }) => (
            <button key={mode}
              onMouseDown={e => e.preventDefault()}
              onClick={() => {
                if (mode === 'eyedropper') {
                  setEyedropperActive(true);
                  setCanvasCursor('crosshair');
                } else {
                  setDrawingMode(mode);
                }
                setShowDrawPalette(false);
              }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '6px 12px', border: 'none', borderRadius: 4, cursor: 'pointer',
                background: (mode === 'eyedropper' ? eyedropperActive : drawingMode === mode)
                  ? 'rgba(250,72,72,0.18)' : 'transparent',
                color: (mode === 'eyedropper' ? eyedropperActive : drawingMode === mode)
                  ? '#fa4848' : 'inherit',
                fontSize: 15,
              }}
              title={label}
            >{icon}</button>
          ))}
        </div>,
        document.body
      )}
      {/* Brush size portal */}
      {showBrushSizeMenu && createPortal(
        <div
          ref={brushMenuRef}
          onMouseDown={e => e.preventDefault()}
          style={{
            position: 'fixed', top: brushMenuPos.top, left: brushMenuPos.left,
            transform: 'translateX(-50%)', zIndex: 9999,
            background: 'var(--bg-surface, #fff)', border: '1px solid var(--border-color, #ddd)',
            color: 'var(--text-primary, #333)',
            borderRadius: 8, padding: '4px', display: 'flex', flexDirection: 'column', gap: 1,
            minWidth: 110, boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
          }}
        >
          {[{label: '1px', val: 1}, {label: '2px', val: 2}, {label: '3px', val: 3}, {label: '5px', val: 5}, {label: '8px', val: 8}, {label: '12px', val: 12}, {label: '15px', val: 15}].map(opt => (
            <button key={opt.label}
              onMouseDown={e => e.preventDefault()}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 8px', border: 'none',
                background: brushSize === opt.val ? 'rgba(250,72,72,0.18)' : 'transparent',
                borderRadius: 4, cursor: 'pointer', color: 'inherit' }}
              onClick={() => { setBrushSize(opt.val); setShowBrushSizeMenu(false); }}
            >
              <svg width="44" height="10" style={{ display: 'block', flexShrink: 0 }}>
                <line x1="2" y1="5" x2="42" y2="5" stroke="currentColor"
                  strokeWidth={Math.min(opt.val * 0.8, 9)} strokeLinecap="round" />
              </svg>
              <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{opt.label}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
      {/* Emoji popdown portal -- renders at fixed position, unclipped by any overflow */}
      {showStrokeWidthMenu && createPortal(
        <div
          ref={strokeMenuRef}
          onMouseDown={e => e.preventDefault()}
          style={{
            position: 'fixed', top: strokeMenuPos.top, left: strokeMenuPos.left,
            transform: 'translateX(-50%)', zIndex: 9999,
            background: 'var(--bg-surface, #fff)', border: '1px solid var(--border-color, #ddd)',
            color: 'var(--text-primary, #333)',
            borderRadius: 8, padding: '4px', display: 'flex', flexDirection: 'column', gap: 1,
            minWidth: 110, boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
          }}
        >
          {[{label: '0.5mm', val: 0.5}, {label: '1mm', val: 1}, {label: '1.5mm', val: 1.5}, {label: '2mm', val: 2}, {label: '3mm', val: 3}, {label: '5mm', val: 5}].map(opt => (
            <button key={opt.label}
              onMouseDown={e => e.preventDefault()}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 8px', border: 'none',
                background: shapeStrokeWidth === opt.val ? 'rgba(250,72,72,0.18)' : 'transparent',
                borderRadius: 4, cursor: 'pointer', color: 'inherit' }}
              onClick={() => {
                setShapeStrokeWidth(opt.val);
                if (selectedShapeId) setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, strokeWidth: opt.val } : s));
                else if (selectedShapeIds.size > 0) setCanvasShapes(prev => prev.map(s => selectedShapeIds.has(s.id) ? { ...s, strokeWidth: opt.val } : s));
                setShowStrokeWidthMenu(false);
              }}
            >
              <svg width="44" height="10" style={{ display: 'block', flexShrink: 0 }}>
                <line x1="2" y1="5" x2="42" y2="5" stroke="currentColor"
                  strokeWidth={Math.min(opt.val * 1.8, 9)} strokeLinecap="round" />
              </svg>
              <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{opt.label}</span>
            </button>
          ))}
          <div style={{ borderTop: '1px solid var(--border-color, #eee)', margin: '2px 4px' }} />
          <button
            onMouseDown={e => e.preventDefault()}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 8px', border: 'none',
              background: shapeDashed ? 'rgba(250,72,72,0.18)' : 'transparent',
              borderRadius: 4, cursor: 'pointer', color: 'inherit' }}
            onClick={() => {
              const nd = !shapeDashed;
              setShapeDashed(nd);
              if (selectedShapeId) setCanvasShapes(prev => prev.map(s => s.id === selectedShapeId ? { ...s, dashed: nd } : s));
              setShowStrokeWidthMenu(false);
            }}
          >
            <svg width="44" height="10" style={{ display: 'block', flexShrink: 0 }}>
              <line x1="2" y1="5" x2="42" y2="5" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeDasharray="5 3" />
            </svg>
            <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>Dotted</span>
          </button>
        </div>,
        document.body
      )}
      {showColorPopdown && createPortal(
        <div
          ref={colorPopdownRef}
          className="emoji-popdown color-popdown"
          style={{ position: 'fixed', top: colorPopdownPos.top, left: colorPopdownPos.left, width: 230 }}
          onMouseDown={e => e.stopPropagation()}
        >
          {magicCakeColorBase ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, padding: '2px 4px' }}>
              {cakePalette[magicCakeColorBase].map(shade => (
                <button
                  key={shade}
                  className="base-color-option"
                  style={{ backgroundColor: shade, width: 28, height: 28, minWidth: 28, minHeight: 28 }}
                  onClick={() => {
                    if (!magicProcessing && effectiveImageId) {
                      handleMagicColorChange(shade, magicCakeColorBase);
                      setMagicCakeColorBase(null);
                      setShowColorPopdown(false);
                    }
                  }}
                  disabled={magicProcessing || !effectiveImageId}
                  title={shade}
                />
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, padding: '4px' }}>
              {cakePaletteNames.map(name => (
                <button
                  key={name}
                  className="base-color-option"
                  style={{ backgroundColor: cakePalette[name][0], width: 28, height: 28, minWidth: 28, minHeight: 28 }}
                  onClick={() => setMagicCakeColorBase(name)}
                  title={name}
                />
              ))}
            </div>
          )}
        </div>,
        document.body
      )}
      {/* Background colour options popdown for Remove Image Background button */}
      {showBgColorPopdown && createPortal(
        <div
          ref={bgColorPopdownRef}
          className="emoji-popdown color-popdown"
          style={{ position: 'fixed', top: bgColorPopdownPos.top, left: bgColorPopdownPos.left, width: 220 }}
          onMouseDown={e => e.stopPropagation()}
        >
          <div style={{ padding: '4px 6px 2px', fontSize: 10, opacity: 0.6, borderBottom: '1px solid var(--border-color,#ddd)', marginBottom: 4 }}>Background</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, padding: '2px 6px 6px' }}>
            {/* No background -- transparent */}
            <button
              className="base-color-option"
              style={{ width: 28, height: 28, minWidth: 28, minHeight: 28, background: 'repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 0 0/10px 10px', border: '1px solid #aaa' }}
              onClick={() => { handleMagicRemoveImageBg(); setShowBgColorPopdown(false); }}
              disabled={magicProcessing || !effectiveImageId}
              title="No background (transparent)"
            />
            {[
              { hex: '#FFFFFF', label: 'White' },
              { hex: '#000000', label: 'Black' },
              { hex: '#F5F0E8', label: 'Cream' },
              { hex: '#FDDDE6', label: 'Blush' },
              { hex: '#87CEEB', label: 'Sky Blue' },
              { hex: '#90EE90', label: 'Light Green' },
              { hex: '#CCCCCC', label: 'Gray' },
              { hex: '#FFD700', label: 'Gold' },
              { hex: '#FFB3B3', label: 'Light Red' },
              { hex: '#D8B4FE', label: 'Lavender' },
              { hex: '#FED7AA', label: 'Peach' },
              { hex: '#BAE6FD', label: 'Light Blue' },
            ].map(({ hex, label }) => (
              <button
                key={hex}
                className="base-color-option"
                style={{ backgroundColor: hex, width: 28, height: 28, minWidth: 28, minHeight: 28, border: hex === '#FFFFFF' ? '1px solid #ccc' : undefined }}
                onClick={() => { handleMagicRemoveImageBgWithColor(hex, label); setShowBgColorPopdown(false); }}
                disabled={magicProcessing || !effectiveImageId}
                title={label}
              />
            ))}
          </div>
        </div>,
        document.body
      )}
      {showEmojiPopdown && createPortal(
        <div
          ref={emojiPopdownRef}
          className="emoji-popdown stamp-popdown"
          style={{ position: 'fixed', top: emojiPopdownPos.top, left: emojiPopdownPos.left }}
          onMouseDown={e => e.stopPropagation()}
        >
          {/* -- Top menu bar -- */}
          {['Admin','Super'].includes(userRole) && (
            <div className="stamp-menu-bar">
              <div className="stamp-menu-actions">
                {!stampSelectMode ? (
                  <button
                    className="stamp-action-btn"
                    onClick={handleUploadStampRemoveBg}
                    disabled={stampUploading || stampRemoving}
                    title="Save canvas design as stamp (background removed)">
                    {stampRemoving ? '⏳' : <FaSave />}
                  </button>
                ) : (
                  <>
                    {selectedStampIds.size === 1 && (
                      <button
                        className="stamp-action-btn stamp-hologram-btn"
                        onClick={handleMarkAsHologram}
                        disabled={stampUploading}
                        title="Mark as hologram stamp">
                        {stampUploading ? '⏳' : <FaGem />}
                      </button>
                    )}
                    <button
                      className="stamp-action-btn stamp-del-btn"
                      onClick={handleDeleteStamps}
                      disabled={stampUploading || selectedStampIds.size === 0}
                      title={`Delete ${selectedStampIds.size} stamp(s)`}>
                      {stampUploading ? '⏳' : <FaTrash />}
                    </button>
                  </>
                )}
              </div>
              <label className="stamp-select-label" title="Select stamps">
                <input
                  type="checkbox"
                  checked={stampSelectMode}
                  onChange={() => {
                    setStampSelectMode(v => !v);
                    setSelectedStampIds(new Set());
                  }}
                />
              </label>
            </div>
          )}
          {/* -- Stamp grid -- */}
          {studioStamps.length === 0 && !stampUploading && (
            <div className="stamp-empty-msg">No stamps yet</div>
          )}
          <div
            className="stamp-grid"
            ref={stampGridRef}
            onMouseDown={e => {
              const el = stampGridRef.current;
              if (!el) return;
              const startY = e.clientY;
              const startScroll = el.scrollTop;
              const onMove = me => { el.scrollTop = startScroll - (me.clientY - startY); };
              const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
              };
              document.addEventListener('mousemove', onMove);
              document.addEventListener('mouseup', onUp);
            }}
          >
            {studioStamps.map(s => {
              const isChecked = selectedStampIds.has(s.id);
              return (
                <div key={s.id} className={`stamp-thumb-wrap${isChecked ? ' stamp-checked' : ''}${s.isHologram ? ' stamp-is-hologram' : ''}`}>
                  <button
                    className={`stamp-thumb-btn${selectedEmoji === s.url && !isChecked ? ' active' : ''}`}
                    onClick={() => {
                      if (stampSelectMode) {
                        setSelectedStampIds(prev => {
                          const next = new Set(prev);
                          if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                          return next;
                        });
                        return;
                      }
                      setSelectedEmoji(s.url);
                      setShowEmojiPopdown(false);
                      setSelectedStampIds(new Set());
                    }}
                    title={stampSelectMode ? 'Select stamp' : 'Use stamp'}>
                    <img src={s.url} alt="stamp" />
                    {s.isHologram && <FaGem className="stamp-hologram-badge" />}
                  </button>
                  {stampSelectMode && (
                    <input
                      type="checkbox"
                      className="stamp-delete-checkbox"
                      checked={isChecked}
                      onChange={e => {
                        e.stopPropagation();
                        setSelectedStampIds(prev => {
                          const next = new Set(prev);
                          if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                          return next;
                        });
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ImageStudio;
